import React, { useState, useEffect, useRef } from "react";
import { 
  Shield, Scale, FileText, PhoneCall, UserCheck, CreditCard, Bell, LogOut, 
  Search, CheckCircle2, AlertCircle, Clock, ChevronRight, FileCheck, DollarSign, 
  Users, Building, HelpCircle, Download, ExternalLink, Send, ArrowRight, Menu, X, Lock, Sparkles, Heart,
  Mic, MicOff, Copy as CopyIcon, MapPin, Wrench, Trophy,
  Facebook, Youtube, Instagram, Twitter
} from "lucide-react";
import { MOCK_DISCLAIMER } from "./mock";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==== Global date formatter (DD-MMM-YYYY [Weekday]) ====
// Mirror of the mobile PWA formatter so every user-facing date reads the same
// across the site, admin panel, advocate panel, and Friday digest emails.
const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const _WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export function fmtDate(iso) {
  if (!iso) return "";
  const s = String(iso);
  // Match leading YYYY-MM-DD; ignore any time suffix.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const y = +m[1], mo = +m[2] - 1, d = +m[3];
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return s;
  const dt = new Date(Date.UTC(y, mo, d));
  return `${String(d).padStart(2, "0")}-${_MONTHS[mo]}-${y} [${_WEEKDAYS[dt.getUTCDay()]}]`;
}
// Pretty datetime: DD-MMM-YYYY [Weekday] · HH:MM (24h) — used for created_at / updated_at labels.
export function fmtDateTime(iso) {
  if (!iso) return "";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return fmtDate(s);
  return `${fmtDate(s)} · ${m[4]}:${m[5]}`;
}

// ==== Global JWTs — attached automatically to every axios request while set ====
// Admin token has precedence; if absent, client token (from OTP login) is used.
let ADMIN_TOKEN = null;
let CLIENT_TOKEN = null;
let ADVOCATE_TOKEN = null;
// Super advocates — kept as a frontend constant for pretty labelling.
// Backend always enforces this; the frontend just uses it to render the star badge
// and prevent removal in the picker UI.
const SUPER_ADVOCATE_EMAILS_FRONT = new Set(["master@hrcpc.org", "poornima@hrcpc.org"]);

// Focus areas — labels + colours for badges and filters
const FOCUS_AREA_META = {
  consumer_protection: { label: "Consumer Protection", cls: "bg-orange-100 text-orange-800 border-orange-200" },
  human_rights:        { label: "Human Rights",        cls: "bg-rose-100 text-rose-800 border-rose-200" },
  rti_transparency:    { label: "RTI / Transparency",  cls: "bg-slate-100 text-slate-800 border-slate-300" },
  environmental:       { label: "Environmental",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  medico_legal:        { label: "Medico-Legal",        cls: "bg-amber-100 text-amber-900 border-amber-300" },
  family_maintenance:  { label: "Family / Maintenance",cls: "bg-violet-100 text-violet-800 border-violet-200" },
  tenant_landlord:     { label: "Tenant / Landlord",   cls: "bg-sky-100 text-sky-800 border-sky-200" },
  cheque_bounce:       { label: "Cheque Bounce",       cls: "bg-lime-100 text-lime-800 border-lime-200" },
  other:               { label: "Other",               cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const FOCUS_AREA_KEYS = Object.keys(FOCUS_AREA_META);

// Phase-II vocabularies used by the case form
const CLIENT_ROLES = [
  "Complainant", "Defendant", "Petitioner", "Respondent",
  "Applicant", "Accused", "Third-party", "Informant", "Witness",
];
const FORUM_TYPES = [
  "Consumer Forum", "Civil Court", "Sessions Court", "Magistrate Court",
  "High Court", "Supreme Court", "NGT", "Tribunal", "Police Station",
  "Authority", "Regulator", "Arbitration", "Other",
];
const CASE_PRIORITY_LEVELS = ["Low", "Normal", "High", "Critical"];
const PROCEEDING_EVENT_TYPES_FRONT = [
  "Filing", "Notice Issued", "Notice Served", "Reply Filed",
  "Hearing", "Adjournment", "Interim Order", "Evidence",
  "Arguments", "Judgment", "Order", "Appeal", "Settlement",
  "Compliance", "Follow-up", "Correspondence", "Meeting", "Other",
];
// Phase-II strict: only these emails may ever see fee amounts (mirror of backend)
const SUPER_FINANCE_EMAILS_FRONT = new Set(["master@hrcpc.org", "poornima@hrcpc.org"]);

// ---------- Admin: Manage Advocates (super-only CRUD on allowlist) ----------
function AdminAdvocatesPanel({ currentUser }) {
  const [advos, setAdvos] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [showAdd, setShowAdd] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [reload, setReload] = React.useState(0);
  const myEmail = String(currentUser?.data?.email || "").toLowerCase();
  const isSuper = SUPER_FINANCE_EMAILS_FRONT.has(myEmail);

  React.useEffect(() => {
    axios.get(`${API}/admin/advocates`).then((r) => setAdvos(r.data || [])).catch(() => setAdvos([]));
  }, [reload]);

  const filtered = React.useMemo(() => {
    const list = (advos || []).slice().sort((a, b) => (a.email > b.email ? 1 : -1));
    if (!q.trim()) return list;
    const s = q.trim().toLowerCase();
    return list.filter((a) => (a.email || "").toLowerCase().includes(s) || (a.name || "").toLowerCase().includes(s));
  }, [advos, q]);

  const isProtected = (email) => SUPER_FINANCE_EMAILS_FRONT.has(String(email || "").toLowerCase());

  const doDeactivate = async (adv) => {
    if (!window.confirm(`Remove ${adv.name || adv.email} from the advocate panel? They will lose login access.`)) return;
    try {
      await axios.delete(`${API}/admin/advocates/${encodeURIComponent(adv.email)}`);
      toast.success(`Removed ${adv.email} from the panel`);
      setReload((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove advocate"); }
  };
  const doReactivate = async (adv) => {
    try {
      await axios.patch(`${API}/admin/advocates/${encodeURIComponent(adv.email)}`, { active: true });
      toast.success(`${adv.email} re-activated`);
      setReload((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-activate"); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="admin-advocates-panel">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-lg text-slate-900">HRCPC Panel Advocates</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage who can log in as an advocate. Once added, they sign in with a one-time code sent to their email.
          </p>
        </div>
        {isSuper ? (
          <button onClick={() => setShowAdd(true)} data-testid="btn-add-advocate"
            className="bg-teal-700 hover:bg-teal-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
            <span>＋</span> Add Advocate
          </button>
        ) : (
          <div className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2">
            Only Super Admins (master@ / poornima@) can add or remove advocates.
          </div>
        )}
      </div>

      <input
        placeholder="Search by email or name…" value={q} onChange={(e) => setQ(e.target.value)}
        data-testid="advocate-search"
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
      />

      {!advos ? (
        <div className="text-slate-400 text-sm py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-500 text-sm py-6 text-center italic">No advocates match your search.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Added</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const active = a.active !== false;
                const prot = isProtected(a.email);
                return (
                  <tr key={a.email} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`advocate-row-${a.email}`}>
                    <td className="p-3 font-mono text-xs">{a.email}</td>
                    <td className="p-3 font-semibold">{a.name || "—"}</td>
                    <td className="p-3">
                      {a.is_super ? (
                        <span className="bg-purple-100 text-purple-800 text-[10px] font-bold px-2 py-0.5 rounded-full">Super Admin ★</span>
                      ) : (
                        <span className="bg-slate-100 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Advocate</span>
                      )}
                      {prot && <span className="ml-1 text-[10px] text-slate-500 italic">protected</span>}
                    </td>
                    <td className="p-3">
                      {active ? (
                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">● Active</span>
                      ) : (
                        <span className="bg-rose-100 text-rose-800 text-[10px] font-bold px-2 py-0.5 rounded-full">● Inactive</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-slate-500">{fmtDate((a.created_at || "").slice(0, 10))}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {isSuper && (
                        <>
                          <button onClick={() => setEditing(a)} data-testid={`advocate-edit-${a.email}`}
                            className="text-xs bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded font-semibold mr-1">Edit</button>
                          {active ? (
                            <button onClick={() => doDeactivate(a)} disabled={prot} data-testid={`advocate-deactivate-${a.email}`}
                              className={`text-xs px-2.5 py-1 rounded font-semibold ${prot ? "bg-slate-100 text-slate-300 cursor-not-allowed" : "bg-rose-100 hover:bg-rose-200 text-rose-800"}`}>
                              Remove
                            </button>
                          ) : (
                            <button onClick={() => doReactivate(a)} data-testid={`advocate-reactivate-${a.email}`}
                              className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2.5 py-1 rounded font-semibold">
                              Re-activate
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AdvocateFormModal
          mode="add"
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); setReload((n) => n + 1); }}
        />
      )}
      {editing && (
        <AdvocateFormModal
          mode="edit"
          advocate={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setReload((n) => n + 1); }}
        />
      )}
    </div>
  );
}

// ---------- Data Ops panel (Super Admin only) ----------
function AdminDataOpsPanel({ currentUser }) {
  const myEmail = String(currentUser?.data?.email || "").toLowerCase();
  const isSuper = SUPER_FINANCE_EMAILS_FRONT.has(myEmail);

  const [counts, setCounts] = React.useState(null);
  const [templates, setTemplates] = React.useState(null);
  const [confirmText, setConfirmText] = React.useState("");
  const [wipeBusy, setWipeBusy] = React.useState(false);
  const [wipeResult, setWipeResult] = React.useState(null);
  const [importMembersBusy, setImportMembersBusy] = React.useState(false);
  const [importCasesBusy, setImportCasesBusy] = React.useState(false);
  const [importPartiesBusy, setImportPartiesBusy] = React.useState(false);
  const [importProcsBusy, setImportProcsBusy] = React.useState(false);
  const [importMembersResult, setImportMembersResult] = React.useState(null);
  const [importCasesResult, setImportCasesResult] = React.useState(null);
  const [importPartiesResult, setImportPartiesResult] = React.useState(null);
  const [importProcsResult, setImportProcsResult] = React.useState(null);
  const memberFileRef = React.useRef(null);
  const caseFileRef = React.useRef(null);
  const partyFileRef = React.useRef(null);
  const procFileRef = React.useRef(null);

  const refreshCounts = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/data/counts`);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load data counts");
    }
  }, []);

  React.useEffect(() => {
    if (!isSuper) return;
    refreshCounts();
    axios.get(`${API}/admin/data/csv-templates`).then((r) => setTemplates(r.data)).catch(() => setTemplates(null));
  }, [isSuper, refreshCounts]);

  if (!isSuper) {
    return (
      <div className="bg-white rounded-2xl border border-rose-200 p-8 text-center" data-testid="dataops-forbidden">
        <div className="text-4xl mb-2">🔒</div>
        <h3 className="font-bold text-slate-800 text-lg">Super Admin only</h3>
        <p className="text-xs text-slate-500 mt-1">Data Ops (wipe + CSV import) is restricted to master@ / poornima@.</p>
      </div>
    );
  }

  const doWipe = async () => {
    if (confirmText !== "DELETE") return;
    if (!window.confirm("This will DELETE all Members, Cases, Proceedings and Tasks.\n\nAdvocates, Super Admin accounts, site settings, tickets, donations, volunteers, wins, audit log, and push subscriptions will be PRESERVED.\n\nProceed?")) return;
    setWipeBusy(true);
    setWipeResult(null);
    try {
      const r = await axios.post(`${API}/admin/data/wipe`, { confirm: "DELETE" });
      setWipeResult(r.data);
      setConfirmText("");
      await refreshCounts();
      toast.success("Test / demo data wiped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete wipe");
    } finally { setWipeBusy(false); }
  };

  const doImport = async (kind, ref, setBusy, setResult) => {
    const file = ref.current?.files?.[0];
    if (!file) { toast.error("Choose a CSV file first"); return; }
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("File must be a .csv"); return; }
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/data/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      await refreshCounts();
      const p = r.data || {};
      toast.success(`Imported ${p.imported || 0} · Skipped ${p.skipped_duplicates || 0} · Errors ${(p.errors || []).length}`);
      if (ref.current) ref.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Import failed`);
    } finally { setBusy(false); }
  };

  const CountsBar = ({ obj, label }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {["clients", "cases", "case_proceedings", "tasks"].map((k) => (
        <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k === "clients" ? "Members" : k === "case_proceedings" ? "Proceedings" : k.charAt(0).toUpperCase() + k.slice(1)}</div>
          <div className="font-mono font-bold text-slate-900" data-testid={`dataops-${label}-${k}`}>{obj?.[k] ?? "—"}</div>
        </div>
      ))}
    </div>
  );

  const downloadBlankTemplate = (kind) => {
    const t = templates?.[kind];
    if (!t) { toast.error("Column list not loaded yet"); return; }
    const cols = [...(t.required || []), ...(t.optional || [])];
    // Escape CSV cells per RFC 4180 — quote if the value contains comma/quote/newline;
    // double any embedded quotes. Feb 14, 2026 Sprint 6.2: also emit a fictional
    // sample row so operators see the expected DD-MM-YYYY dates, Yes/No flags, etc.
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.join(",") + "\n";
    let sampleRow = "";
    if (t.sample && typeof t.sample === "object") {
      sampleRow = cols.map((c) => esc(t.sample[c])).join(",") + "\n";
    }
    const csv = header + sampleRow;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hrcpc-${kind}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${kind} template (${cols.length} columns${sampleRow ? " + 1 sample row" : ""})`);
  };

  return (
    <div className="space-y-5" data-testid="admin-dataops-panel">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-lg text-slate-900">Data Ops — Wipe & CSV Import</h3>
            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
              Super-Admin only. Wipe test/demo Members, Cases, Proceedings and Tasks in one click,
              then bulk-import real records via CSV. Advocates, Super Admin accounts,
              site settings, tickets, donations, volunteers, wins, audit log, and push
              subscriptions are always preserved.
            </p>
          </div>
          <button onClick={refreshCounts} data-testid="dataops-refresh"
            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-3 py-2 rounded-lg">
            ↻ Refresh counts
          </button>
        </div>
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Current row counts</div>
          <CountsBar obj={counts} label="current" />
        </div>
      </div>

      {/* Operator help — Feb 14, 2026 Sprint 6.1 + Sprint 6.2 format rules */}
      <div className="bg-indigo-50 rounded-2xl border border-indigo-200 p-5 text-xs text-slate-700 space-y-3" data-testid="dataops-operator-help">
        <div>
          <div className="font-bold text-indigo-900 text-sm">Operator Guide — import order & multi-case model</div>
          <ol className="list-decimal ml-5 space-y-1 mt-2">
            <li><b>Members</b> first, then <b>Cases</b>, then <b>Parties &amp; Counsels</b>, then <b>Proceedings</b>. Later imports link back by <span className="font-mono">client_mobile</span> + <span className="font-mono">case_number</span>.</li>
            <li><b>Multi-case</b>: the same mobile can appear on many Case rows — one Member with many matters. No new Member row needed each time.</li>
            <li><b>Multi-proceeding</b>: long chronologies (FA hearings, RTI back-and-forth) go into <b>many Proceedings rows on the same case</b> — <em>not</em> a new Case per adjournment.</li>
            <li><b>SPA / principal abroad</b>: Member = principal; fill <span className="font-mono">representative_name</span>, <span className="font-mono">representative_mobile</span>, <span className="font-mono">representative_relation</span> = "SPA Holder", <span className="font-mono">representative_spa_date</span>. Attach the SPA PDF as a case document from the case detail.</li>
            <li><b>Company / firm / society</b>: set <span className="font-mono">member_type</span> and put the entity's legal name in <span className="font-mono">full_name</span>; fill <span className="font-mono">authorised_signatory</span>. Aadhaar / father's name are not required for organisations.</li>
            <li><b>Bank details</b> are always optional — never blocks create/import/KYC.</li>
            <li><b>Fees</b> stay Super-Admin only. Party / proceeding CSVs have no fee columns by design.</li>
          </ol>
        </div>
        <div className="pt-3 border-t border-indigo-200">
          <div className="font-bold text-indigo-900 text-sm">Format Rules (all CSVs)</div>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li><b>Dates:</b> <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-indigo-200">DD-MM-YYYY</span> only (example <span className="font-mono">14-08-2026</span>). Applies to <span className="font-mono">dob</span>, <span className="font-mono">representative_spa_date</span>, <span className="font-mono">next_hearing_date</span>, <span className="font-mono">proceeding_date</span>. On the website / app / PDFs, dates continue to render as <span className="font-mono">DD-MMM-YYYY [Weekday]</span>.</li>
            <li><b>Mobile:</b> 10 digits only. Country code / plus sign / spaces are stripped automatically.</li>
            <li><b>Aadhaar / bank number:</b> last 4 digits only (<span className="font-mono">aadhaar_last4</span>, <span className="font-mono">bank_ac_number_last4</span>). NEVER enter the full number.</li>
            <li><b>Yes/No fields</b> (<span className="font-mono">visible_to_member</span>, <span className="font-mono">is_primary_contact</span>): accept Yes/No, true/false, 1/0. Blank = No.</li>
            <li><b>Multiple advocates:</b> <span className="font-mono">assigned_advocate_emails</span> is semicolon-separated. Each email must already exist in Admin → Advocates.</li>
            <li><b>Encoding:</b> save as <span className="font-mono">CSV UTF-8 (Comma delimited)</span> in Excel / Google Sheets. Use straight commas.</li>
            <li><b>Sample row</b> in every downloaded template is fictional — <b>replace before uploading</b>.</li>
          </ul>
        </div>
      </div>

      {/* Wipe section */}
      <div className="bg-rose-50 rounded-2xl border-2 border-rose-200 p-6" data-testid="dataops-wipe-card">
        <div className="flex items-start gap-3">
          <div className="text-3xl">⚠️</div>
          <div className="flex-1">
            <h4 className="font-bold text-rose-900">Wipe test / demo data</h4>
            <p className="text-xs text-slate-700 mt-1">
              Deletes ALL rows from <b>Members · Cases · Proceedings · Tasks</b>.
              Type <span className="font-mono bg-white border border-rose-300 px-1.5 py-0.5 rounded text-rose-800">DELETE</span> below and click the red button. Cannot be undone.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                data-testid="dataops-wipe-confirm"
                placeholder='Type DELETE to confirm'
                className="w-56 px-3 py-2 border border-rose-300 rounded-lg font-mono text-sm bg-white" />
              <button onClick={doWipe} disabled={confirmText !== "DELETE" || wipeBusy}
                data-testid="dataops-wipe-btn"
                className={`px-4 py-2 rounded-lg font-bold text-white ${confirmText === "DELETE" && !wipeBusy ? "bg-rose-700 hover:bg-rose-600" : "bg-slate-300 cursor-not-allowed"}`}>
                {wipeBusy ? "Wiping…" : "🗑 Wipe all test/demo data"}
              </button>
            </div>
            {wipeResult && (
              <div className="mt-4 bg-white border border-rose-200 rounded-lg p-3 text-xs" data-testid="dataops-wipe-result">
                <div className="font-semibold text-slate-800 mb-2">Wipe report</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div><div className="text-[10px] uppercase text-slate-500 font-bold">Before</div><CountsBar obj={wipeResult.before} label="wipe-before" /></div>
                  <div><div className="text-[10px] uppercase text-slate-500 font-bold">Wiped</div><CountsBar obj={wipeResult.wiped} label="wipe-wiped" /></div>
                  <div><div className="text-[10px] uppercase text-slate-500 font-bold">After</div><CountsBar obj={wipeResult.after} label="wipe-after" /></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Members */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="dataops-import-members-card">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h4 className="font-bold text-slate-900">Import Members (CSV)</h4>
            <p className="text-xs text-slate-500 mt-1">
              Auto-approved. dpdp_consent stamped as admin-collected. Duplicates by mobile are skipped.
              Aadhaar / bank account: only last-4 stored (server truncates).
            </p>
          </div>
          {templates?.members && (
            <div className="text-[11px] text-slate-500 max-w-md">
              <div><b className="text-slate-700">Required:</b> <span className="font-mono">{templates.members.required.join(", ")}</span></div>
              <div className="mt-0.5"><b className="text-slate-700">Optional:</b> <span className="font-mono">{templates.members.optional.join(", ")}</span></div>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,text/csv" ref={memberFileRef} data-testid="dataops-import-members-file"
            className="text-xs file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:font-semibold file:bg-teal-100 file:text-teal-800 hover:file:bg-teal-200" />
          <button onClick={() => doImport("members", memberFileRef, setImportMembersBusy, setImportMembersResult)}
            disabled={importMembersBusy} data-testid="dataops-import-members-btn"
            className={`px-4 py-2 rounded-lg font-bold text-white ${importMembersBusy ? "bg-slate-300 cursor-not-allowed" : "bg-teal-700 hover:bg-teal-600"}`}>
            {importMembersBusy ? "Importing…" : "⬆ Upload & Import Members"}
          </button>
          <button onClick={() => downloadBlankTemplate("members")} data-testid="dataops-download-members-template"
            className="ml-auto text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-2 rounded-lg border border-slate-200">
            ⬇ Download blank CSV template
          </button>
        </div>
        {importMembersResult && (
          <ImportReport result={importMembersResult} kind="members" />
        )}
      </div>

      {/* Import Cases */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="dataops-import-cases-card">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h4 className="font-bold text-slate-900">Import Cases (CSV)</h4>
            <p className="text-xs text-slate-500 mt-1">
              Each case must reference an existing Member by <span className="font-mono">client_mobile</span>. Import Members first.
              Super advocates are auto-added to every case's assignment list.
              <b> No fee fabrication</b> — total_fee / fee_paid are stored only if provided (else 0); fees remain visible to Super Admins only.
            </p>
          </div>
          {templates?.cases && (
            <div className="text-[11px] text-slate-500 max-w-md">
              <div><b className="text-slate-700">Required:</b> <span className="font-mono">{templates.cases.required.join(", ")}</span></div>
              <div className="mt-0.5"><b className="text-slate-700">Optional:</b> <span className="font-mono">{templates.cases.optional.join(", ")}</span></div>
              <div className="mt-0.5 text-slate-400 italic">assigned_advocate_emails accepts semicolon-separated list</div>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,text/csv" ref={caseFileRef} data-testid="dataops-import-cases-file"
            className="text-xs file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:font-semibold file:bg-teal-100 file:text-teal-800 hover:file:bg-teal-200" />
          <button onClick={() => doImport("cases", caseFileRef, setImportCasesBusy, setImportCasesResult)}
            disabled={importCasesBusy} data-testid="dataops-import-cases-btn"
            className={`px-4 py-2 rounded-lg font-bold text-white ${importCasesBusy ? "bg-slate-300 cursor-not-allowed" : "bg-teal-700 hover:bg-teal-600"}`}>
            {importCasesBusy ? "Importing…" : "⬆ Upload & Import Cases"}
          </button>
          <button onClick={() => downloadBlankTemplate("cases")} data-testid="dataops-download-cases-template"
            className="ml-auto text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-2 rounded-lg border border-slate-200">
            ⬇ Download blank CSV template
          </button>
        </div>
        {importCasesResult && (
          <ImportReport result={importCasesResult} kind="cases" />
        )}
      </div>

      {/* Import Case Parties & Counsels */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="dataops-import-parties-card">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h4 className="font-bold text-slate-900">Import Case Parties & Counsels (CSV)</h4>
            <p className="text-xs text-slate-500 mt-1">
              Bulk-load multiple parties + their counsels for existing cases (Applicants, Respondents, Intervenors…).
              Each row must link to an existing case by <span className="font-mono">case_number</span> + <span className="font-mono">client_mobile</span>.
              Import Members and Cases first.
            </p>
          </div>
          {templates?.parties && (
            <div className="text-[11px] text-slate-500 max-w-md">
              <div><b className="text-slate-700">Required:</b> <span className="font-mono">{templates.parties.required.join(", ")}</span></div>
              <div className="mt-0.5"><b className="text-slate-700">Optional:</b> <span className="font-mono">{templates.parties.optional.join(", ")}</span></div>
              <div className="mt-0.5 text-slate-400 italic">
                party_side must be one of: Applicant-Petitioner-Complainant / Respondent-Defendant-Accused / Proforma / Intervenor / Other
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,text/csv" ref={partyFileRef} data-testid="dataops-import-parties-file"
            className="text-xs file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:font-semibold file:bg-teal-100 file:text-teal-800 hover:file:bg-teal-200" />
          <button onClick={() => doImport("parties", partyFileRef, setImportPartiesBusy, setImportPartiesResult)}
            disabled={importPartiesBusy} data-testid="dataops-import-parties-btn"
            className={`px-4 py-2 rounded-lg font-bold text-white ${importPartiesBusy ? "bg-slate-300 cursor-not-allowed" : "bg-teal-700 hover:bg-teal-600"}`}>
            {importPartiesBusy ? "Importing…" : "⬆ Upload & Import Parties"}
          </button>
          <button onClick={() => downloadBlankTemplate("parties")} data-testid="dataops-download-parties-template"
            className="ml-auto text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-2 rounded-lg border border-slate-200">
            ⬇ Download blank CSV template
          </button>
        </div>
        {importPartiesResult && (
          <ImportReport result={importPartiesResult} kind="parties" />
        )}
      </div>

      {/* Import Proceedings (chronology) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="dataops-import-proceedings-card">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h4 className="font-bold text-slate-900">Import Proceedings Chronology (CSV)</h4>
            <p className="text-xs text-slate-500 mt-1">
              Bulk-load hearings / adjournments / RTI replies / orders across many cases.
              One row per event. Each row must resolve to <b>exactly one</b> existing case for the given <span className="font-mono">client_mobile</span>.
              Matching rule: prefer <span className="font-mono">case_number</span>, else <span className="font-mono">case_title_keyword</span> (case-insensitive contains). Ambiguous or missing matches are reported per row.
            </p>
          </div>
          {templates?.proceedings && (
            <div className="text-[11px] text-slate-500 max-w-md">
              <div><b className="text-slate-700">Required:</b> <span className="font-mono">{templates.proceedings.required.join(", ")}</span></div>
              <div className="mt-0.5"><b className="text-slate-700">Optional:</b> <span className="font-mono">{templates.proceedings.optional.join(", ")}</span></div>
              <div className="mt-0.5 text-slate-400 italic">
                event_type ∈ Filing / Hearing / Adjournment / Order / Judgment / Interim Order / Correspondence / RTI Reply-equivalent (see full list in field description). visible_to_member = Yes/No.
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,text/csv" ref={procFileRef} data-testid="dataops-import-proceedings-file"
            className="text-xs file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:font-semibold file:bg-teal-100 file:text-teal-800 hover:file:bg-teal-200" />
          <button onClick={() => doImport("proceedings", procFileRef, setImportProcsBusy, setImportProcsResult)}
            disabled={importProcsBusy} data-testid="dataops-import-proceedings-btn"
            className={`px-4 py-2 rounded-lg font-bold text-white ${importProcsBusy ? "bg-slate-300 cursor-not-allowed" : "bg-teal-700 hover:bg-teal-600"}`}>
            {importProcsBusy ? "Importing…" : "⬆ Upload & Import Proceedings"}
          </button>
          <button onClick={() => downloadBlankTemplate("proceedings")} data-testid="dataops-download-proceedings-template"
            className="ml-auto text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-2 rounded-lg border border-slate-200">
            ⬇ Download blank CSV template
          </button>
        </div>
        {importProcsResult && (
          <ImportReport result={importProcsResult} kind="proceedings" />
        )}
      </div>
    </div>
  );
}

function ImportReport({ result, kind }) {
  const errs = result?.errors || [];
  return (
    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid={`dataops-import-${kind}-report`}>
      <div className="flex flex-wrap gap-3 mb-2">
        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">Imported: {result.imported || 0}</span>
        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">Skipped duplicates: {result.skipped_duplicates || 0}</span>
        <span className={`${errs.length ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"} px-2 py-0.5 rounded-full font-semibold`}>Errors: {errs.length}</span>
        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-semibold">Total rows: {result.total_rows || 0}</span>
      </div>
      {errs.length > 0 && (
        <div className="max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-100 text-slate-600 uppercase tracking-wider">
              <tr><th className="text-left p-2">Row</th><th className="text-left p-2">Reason</th></tr>
            </thead>
            <tbody>
              {errs.map((e, i) => (
                <tr key={`${e.row}-${i}`} className="border-t border-slate-100">
                  <td className="p-2 font-mono">{e.row}</td>
                  <td className="p-2 text-rose-700">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdvocateFormModal({ mode, advocate, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail] = React.useState(advocate?.email || "");
  const [name, setName] = React.useState(advocate?.name || "");
  const [isSuper, setIsSuper] = React.useState(!!advocate?.is_super);
  const [sendEmail, setSendEmail] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const emailOk = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim());
  const canSave = (isEdit || (emailOk && !busy)) && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/advocates/${encodeURIComponent(advocate.email)}`, {
          name: name.trim() || null, is_super: !!isSuper,
        });
        toast.success("Advocate updated");
      } else {
        await axios.post(`${API}/admin/advocates`, {
          email: email.trim().toLowerCase(), name: name.trim(),
          is_super: !!isSuper, send_welcome_email: !!sendEmail,
        });
        toast.success(sendEmail ? "Advocate added — welcome email queued" : "Advocate added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex justify-between items-start border-b border-slate-100 pb-3">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? "Edit advocate" : "Add advocate to panel"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">✕</button>
        </div>
        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Email {!isEdit && <span className="text-rose-600">*</span>}</div>
            <input type="email" value={email} disabled={isEdit} onChange={(e) => setEmail(e.target.value)}
              data-testid="af-email"
              placeholder="advocate@hrcpc.org"
              className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${isEdit ? "bg-slate-100 text-slate-600" : email && !emailOk ? "border-rose-400 bg-rose-50" : "border-slate-300"}`} />
          </label>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Full name</div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="af-name"
              placeholder="Adv. Full Name"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </label>
          <label className="flex items-center gap-2 text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
            <input type="checkbox" checked={isSuper} onChange={(e) => setIsSuper(e.target.checked)} data-testid="af-is-super" />
            <span><b>Super Admin</b> — can view fees, manage advocates, send Cover Briefs.</span>
          </label>
          {!isEdit && (
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="af-send-email" />
              Email login instructions to this advocate now
            </label>
          )}
        </div>
        <div className="flex gap-2 mt-5 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl">Cancel</button>
          <button onClick={submit} disabled={!canSave} data-testid="af-save"
            className={`flex-[2] px-4 py-2.5 rounded-xl font-bold text-white ${canSave ? "bg-teal-700 hover:bg-teal-600" : "bg-slate-300 cursor-not-allowed"}`}>
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add advocate")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Cover Brief modal (Super-Admin only) ----------
function CoverBriefModal({ caseObj, onClose, onSent }) {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [hearing, setHearing] = React.useState(caseObj?.next_hearing_date || new Date().toISOString().slice(0, 10));
  const [suggestions, setSuggestions] = React.useState("1) Seek short adjournment.\n2) File Vakalatnama at first hearing.\n3) Do not consent to any interim orders without instructions.");
  const [note, setNote] = React.useState("");
  const [recordTimeline, setRecordTimeline] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const emailValid = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email.trim());
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(hearing);
  const canSend = emailValid && dateValid && suggestions.trim().length > 0 && !busy;
  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cases/${encodeURIComponent(caseObj.case_number)}/cover-brief`, {
        covering_email: email.trim(),
        covering_name: name.trim(),
        hearing_date: hearing,
        suggestions: suggestions.trim(),
        note: note.trim(),
        record_in_timeline: recordTimeline,
      });
      const data = r.data || {};
      if (data.email?.skipped) {
        toast.warning(`Cover brief PDF built (${Math.round((data.pdf_size||0)/1024)} KB) but email skipped — Resend not configured in this environment.`);
      } else {
        toast.success(`Cover Brief sent to ${data.sent_to || email.trim()} · Hearing ${data.hearing_pretty}`);
      }
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send cover brief");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cover-brief-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">📨 Send Cover Brief</h2>
            <div className="text-xs text-slate-500 mt-1">
              {caseObj?.title || caseObj?.case_number} · <span className="font-mono">{caseObj?.case_number}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="cover-brief-close" className="text-slate-400 hover:text-slate-600 text-2xl leading-none">✕</button>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
            The Cover Brief is a self-contained PDF sent by email so a temporary advocate can appear in Court <b>without any login</b>. Fees, Aadhaar and bank details are always excluded.
          </div>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Covering advocate email <span className="text-rose-600">*</span></div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="adv@example.com"
              data-testid="cb-email" className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${email && !emailValid ? "border-rose-400 bg-rose-50" : "border-slate-300"}`} />
          </label>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Covering advocate name (optional)</div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Adv. Kiran Substitute"
              data-testid="cb-name" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Hearing date <span className="text-rose-600">*</span></div>
            <input type="date" value={hearing} onChange={(e) => setHearing(e.target.value)} data-testid="cb-hearing"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm" />
            {dateValid && <div className="text-[11px] text-emerald-700 mt-1 font-semibold">→ {fmtDate(hearing)}</div>}
          </label>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Suggestions from Super Admin (master@ / poornima@) <span className="text-rose-600">*</span></div>
            <textarea value={suggestions} onChange={(e) => setSuggestions(e.target.value)} rows={5} data-testid="cb-suggestions"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-y font-mono"
              placeholder="e.g. Seek short adjournment. File Vakalatnama. Do not consent to any interim orders." />
          </label>
          <label className="block">
            <div className="font-semibold text-slate-700 mb-1">Optional short note</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cb-note"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-y"
              placeholder="Anything else the covering advocate should know." />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={recordTimeline} onChange={(e) => setRecordTimeline(e.target.checked)} data-testid="cb-record" />
            Record this dispatch as an internal note in the case timeline (recommended)
          </label>
        </div>

        <div className="flex gap-2 mt-5 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl">Cancel</button>
          <button onClick={submit} disabled={!canSend} data-testid="cb-send"
            className={`flex-[2] px-4 py-2.5 rounded-xl font-bold text-white ${canSend ? "bg-teal-700 hover:bg-teal-600" : "bg-slate-300 cursor-not-allowed"}`}>
            {busy ? "Sending…" : `📤 Send Cover Brief (PDF attached)`}
          </button>
        </div>
      </div>
    </div>
  );
}
try { ADMIN_TOKEN = window.localStorage.getItem("hrcpc_admin_token") || null; } catch (_) {}
try { CLIENT_TOKEN = window.localStorage.getItem("hrcpc_client_token") || null; } catch (_) {}
try { ADVOCATE_TOKEN = window.localStorage.getItem("hrcpc_advocate_token") || null; } catch (_) {}

window.__hrcpcSetClientToken = (tok) => {
  CLIENT_TOKEN = tok || null;
  try {
    if (tok) window.localStorage.setItem("hrcpc_client_token", tok);
    else window.localStorage.removeItem("hrcpc_client_token");
  } catch (_) {}
};

window.__hrcpcSetAdvocateToken = (tok) => {
  ADVOCATE_TOKEN = tok || null;
  try {
    if (tok) window.localStorage.setItem("hrcpc_advocate_token", tok);
    else window.localStorage.removeItem("hrcpc_advocate_token");
  } catch (_) {}
};

axios.interceptors.request.use((config) => {
  // Admin > Advocate > Client priority
  const token = ADMIN_TOKEN || ADVOCATE_TOKEN || CLIENT_TOKEN;
  if (token && config.url && config.url.startsWith(API)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err?.config?.url || "";
      const isAuthEndpoint = /\/auth\/(admin-login|admin\/otp-send|admin\/otp-verify|advocate\/otp-send|advocate\/otp-verify|otp-login|otp-send)/.test(url);
      if (!isAuthEndpoint && (ADMIN_TOKEN || ADVOCATE_TOKEN || CLIENT_TOKEN)) {
        ADMIN_TOKEN = null;
        ADVOCATE_TOKEN = null;
        CLIENT_TOKEN = null;
        try { window.localStorage.removeItem("hrcpc_admin_token"); } catch (_) {}
        try { window.localStorage.removeItem("hrcpc_advocate_token"); } catch (_) {}
        try { window.localStorage.removeItem("hrcpc_client_token"); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent("hrcpc:session-expired")); } catch (_) {}
        try { toast.error("Session expired. Please log in again."); } catch (_) {}
      }
    }
    return Promise.reject(err);
  }
);

// Rotating Golden Sudarshana Chakra (Lord Vishnu's celestial discus)
// Uses the uploaded high-detail photograph (public/img/sudarshana-chakra.jpg)
// rotated continuously via the `chakra-spin` keyframe in index.css.
// The image is presented inside a circular mask so the dark background of
// the source photo blends naturally with any hero backdrop.
function DharmaChakra({ size = 260 }) {
  return (
    <div
      style={{ width: size, height: size }}
      className="relative select-none"
      data-testid="dharma-chakra"
    >
      <img
        src="/img/sudarshana-chakra.png"
        alt="Rotating Golden Sudarshana Chakra"
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        draggable={false}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          objectPosition: "center",
          // Soft golden aura around the free-standing disc (no dark square backdrop).
          filter: "drop-shadow(0 0 18px rgba(255, 200, 60, 0.45)) drop-shadow(0 0 42px rgba(255, 170, 30, 0.22))",
          userSelect: "none",
        }}
      />
    </div>
  );
}

function AnimatedCounter({ end, duration = 1600, prefix = "", suffix = "", format = "int" }) {
  const [n, setN] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || started) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setStarted(true);
    }, { threshold: 0.35 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const target = Number(end) || 0;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);
  const display = format === "int" ? n.toLocaleString() : String(n);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}


// Tiny, safe markdown renderer for AI replies. Handles **bold**, horizontal
// rules (---), and preserves newlines. No new dependency.
function RichAIText({ text }) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  return (
    <div className="space-y-1.5 leading-relaxed">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        // Horizontal rule
        if (/^---+$/.test(trimmed)) return <hr key={i} className="my-2 border-slate-200" />;
        // Empty line = spacer
        if (!trimmed) return <div key={i} className="h-1" />;
        // Split on **bold** — keep it simple, no nesting
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <div key={i}>
            {parts.map((p, j) => {
              if (p.startsWith("**") && p.endsWith("**")) {
                return <strong key={j} className="font-bold text-slate-900">{p.slice(2, -2)}</strong>;
              }
              return <span key={j}>{p}</span>;
            })}
          </div>
        );
      })}
    </div>
  );
}


/**
 * CaseTimeline — expandable unified timeline of status changes, notes,
 * documents, payments, expenses for a single case. Lazy-loads from
 * GET /api/case-timeline?case_id=. Auth is inferred from axios defaults.
 */
function CaseTimeline({ caseNumber, apiBase, testIdSuffix }) {
  const [open, setOpen] = React.useState(false);
  const [events, setEvents] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/case-timeline`, { params: { case_id: caseNumber } });
      setEvents(res.data?.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load timeline");
    } finally {
      setLoading(false);
    }
  };
  const iconFor = (kind) => {
    const map = {
      note:     { cls: "bg-slate-100 text-slate-700 border-slate-200" },
      document: { cls: "bg-sky-100 text-sky-800 border-sky-200" },
      payment:  { cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
      expense:  { cls: "bg-rose-100 text-rose-800 border-rose-200" },
      status:   { cls: "bg-amber-100 text-amber-900 border-amber-200" },
    };
    return map[kind] || map.status;
  };
  const printDossier = async () => {
    let evs = events;
    if (!evs) {
      // Load first if not open
      try {
        const res = await axios.get(`${apiBase}/case-timeline`, { params: { case_id: caseNumber } });
        evs = res.data?.events || [];
        setEvents(evs);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load timeline");
        return;
      }
    }
    const today = new Date().toISOString().slice(0, 10);
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rows = (evs || []).map(ev => `
      <tr>
        <td style="padding:6pt 8pt;border-bottom:0.5pt solid #e5e7eb;font-size:9pt;font-family:'Courier New',monospace;color:#475569;white-space:nowrap">${esc(ev.date)}</td>
        <td style="padding:6pt 8pt;border-bottom:0.5pt solid #e5e7eb;font-size:9pt;text-transform:uppercase;letter-spacing:0.5pt;color:#334155">${esc(ev.kind)}</td>
        <td style="padding:6pt 8pt;border-bottom:0.5pt solid #e5e7eb;font-size:10pt;color:#0f172a">
          <div style="font-weight:bold">${esc(ev.title)}</div>
          ${ev.body ? `<div style="color:#475569;margin-top:2pt">${esc(ev.body)}</div>` : ''}
          ${ev.author ? `<div style="color:#94a3b8;font-style:italic;font-size:8pt;margin-top:2pt">— ${esc(ev.author)}${ev.author_role ? ` (${esc(ev.author_role)})` : ''}</div>` : ''}
        </td>
      </tr>`).join("");
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>HRCPC Trust — Case Dossier ${esc(caseNumber)}</title>
<style>
  @page { size: A4; margin: 1.8cm 1.8cm 1.5cm 1.8cm; }
  body { font-family: 'Times New Roman', serif; color: #111; margin: 0; padding: 0; }
  .brandbar { background: linear-gradient(90deg,#c2410c,#f97316); color: #fff; padding: 14pt 18pt; border-radius: 6pt 6pt 0 0; }
  .brand-title { font-size: 9pt; letter-spacing: 2pt; text-transform: uppercase; opacity: 0.85; }
  .case-title { font-size: 20pt; font-weight: bold; margin: 4pt 0 0 0; }
  .meta { color: #475569; font-size: 10pt; margin: 12pt 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 8pt; }
  h2 { color: #7c2d12; font-size: 12pt; margin: 20pt 0 6pt 0; border-bottom: 1pt solid #f97316; padding-bottom: 4pt; }
  .disclaimer { border-left: 3pt solid #f59e0b; background: #fffbeb; padding: 8pt 10pt; margin-top: 22pt; font-size: 9pt; color: #78350f; border-radius: 0 3pt 3pt 0; }
  @media print { .no-print { display: none; } }
</style>
</head>
<body>
  <div class="no-print" style="text-align:right;padding:8pt 12pt;background:#f1f5f9">
    <button onclick="window.print()" style="background:#c2410c;color:#fff;border:none;padding:8pt 14pt;font-size:11pt;font-weight:bold;border-radius:4pt;cursor:pointer">🖨️  Print / Save as PDF</button>
  </div>
  <div class="brandbar">
    <div class="brand-title">HRCPC Trust · Human Rights &amp; Consumer Protection Cell · Hyderabad</div>
    <div class="case-title">Case Dossier — ${esc(caseNumber)}</div>
  </div>
  <div style="padding: 18pt">
    <div class="meta">Generated on <strong>${today}</strong> · ${(evs || []).length} events in timeline</div>
    <h2>Chronological Timeline</h2>
    <table>
      <thead><tr style="background:#fff7ed">
        <th style="padding:8pt;text-align:left;font-size:9pt;color:#7c2d12;text-transform:uppercase;letter-spacing:1pt">Date</th>
        <th style="padding:8pt;text-align:left;font-size:9pt;color:#7c2d12;text-transform:uppercase;letter-spacing:1pt">Kind</th>
        <th style="padding:8pt;text-align:left;font-size:9pt;color:#7c2d12;text-transform:uppercase;letter-spacing:1pt">Event</th>
      </tr></thead>
      <tbody>${rows || '<tr><td colspan="3" style="padding:12pt;color:#94a3b8;font-style:italic;text-align:center">No events recorded for this case yet.</td></tr>'}</tbody>
    </table>
    <div class="disclaimer">
      <strong>Disclaimer:</strong> This dossier is generated from HRCPC Trust's internal case-file system for reference only. HRCPC Trust is a public-spirited NGO and NOT a law firm. Please consult your family advocate before acting on any information in this document.
    </div>
  </div>
</body>
</html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error("Please allow pop-ups to print the dossier"); return; }
    // Safe DOM write: use a Blob URL instead of document.write. All user-supplied
    // fields above are already HTML-escaped via esc(), so the payload is
    // structurally safe; Blob-loading removes the document.write() API surface.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    w.location.href = url;
    // Trigger print once the popup finishes loading, then release the Blob URL.
    setTimeout(() => {
      try { w.focus(); w.print(); } catch (err) { console.error('[printDossier] print failed:', err); }
      try { URL.revokeObjectURL(url); } catch (err) { /* noop */ }
    }, 800);
  };
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200" data-testid={`case-timeline-${testIdSuffix}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Case Timeline</div>
        <div className="flex items-center gap-2">
          <button
            onClick={printDossier}
            className="text-xs bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1 rounded-lg flex items-center gap-1"
            data-testid={`case-timeline-print-${testIdSuffix}`}
          >
            🖨 Print PDF
          </button>
          <button
            onClick={() => { if (!open && !events) load(); setOpen(o => !o); }}
            className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1 rounded-lg"
            data-testid={`case-timeline-toggle-${testIdSuffix}`}
          >
            {open ? "Hide timeline" : (events ? `Show timeline (${events.length})` : "Show timeline")}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3">
          {loading && <div className="text-xs text-slate-500 italic">Loading timeline…</div>}
          {!loading && events && events.length === 0 && (
            <div className="text-xs text-slate-400 italic">No events recorded for this case yet.</div>
          )}
          {!loading && events && events.length > 0 && (
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3 max-h-96 overflow-y-auto pr-2">
              {events.map(ev => {
                const meta = iconFor(ev.kind);
                return (
                  <li key={ev.id} className="ml-4 relative" data-testid={`timeline-event-${ev.id}`}>
                    <span className={`absolute -left-[22px] top-1.5 w-3 h-3 rounded-full border-2 border-white ring-2 ring-slate-200 ${meta.cls.split(' ')[0]}`}></span>
                    <div className="text-[10px] font-mono text-slate-500">{ev.date}</div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{ev.kind}</span>
                      <span className="text-xs font-semibold text-slate-900">{ev.title}</span>
                    </div>
                    {ev.body && <div className="text-xs text-slate-600 mt-0.5">{ev.body}</div>}
                    {ev.author && <div className="text-[10px] text-slate-500 italic mt-0.5">— {ev.author}{ev.author_role ? ` (${ev.author_role})` : ''}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * CaseProceedings — Phase-II Sprint 3.
 * Structured event log per case (Filing, Hearing, Order, Notice Sent, RTI Reply,
 * Member Meeting, Document Received, Status Change, Other). Backed by
 * /api/case-proceedings — GET (list), POST (add), PATCH (edit), DELETE (remove).
 * The backend enforces auth: admin OR super advocate OR advocate assigned to
 * this case; regular advocates on an unassigned case receive 403 (surfaced here).
 */
const PROCEEDING_TYPES = [
  { key: "Filing",             cls: "bg-orange-100 text-orange-800 border-orange-200" },
  { key: "Hearing",            cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "Order",              cls: "bg-amber-100 text-amber-900 border-amber-300" },
  { key: "Notice Issued",      cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "Notice Served",      cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "Reply Filed",        cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "Evidence",           cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "Arguments",          cls: "bg-lime-100 text-lime-800 border-lime-200" },
  { key: "Adjournment",        cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { key: "Judgment",           cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "Appeal",             cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "Settlement",         cls: "bg-teal-100 text-teal-800 border-teal-200" },
  { key: "Compliance",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "Follow-up",          cls: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "Correspondence",     cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "Meeting",            cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "Interim Order",      cls: "bg-amber-100 text-amber-900 border-amber-300" },
  { key: "Other",              cls: "bg-slate-100 text-slate-500 border-slate-200" },
];
const PROCEEDING_TYPE_CLS = Object.fromEntries(PROCEEDING_TYPES.map(t => [t.key, t.cls]));

function CaseProceedings({ caseNumber, apiBase, testIdSuffix, autoOpen = false, onCountChange }) {
  const [events, setEvents] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(!!autoOpen);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    event_date: today,
    event_type: "Hearing",
    details: "",
    advocate_comment: "",
    next_step: "",
    next_step_due: "",
    documents: [],
    visible_to_client: false,
  });
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/case-proceedings`, { params: { case_number: caseNumber } });
      const list = Array.isArray(res.data) ? res.data : [];
      setEvents(list);
      if (typeof onCountChange === "function") onCountChange(list.length);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 403) toast.error("You do not have access to this case's proceedings.");
      else toast.error(err?.response?.data?.detail || "Could not load proceedings");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (expanded && events === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const resetForm = () => setForm({ event_date: today, event_type: "Hearing", details: "", advocate_comment: "", next_step: "", next_step_due: "", documents: [], visible_to_client: false });

  const handleDoc = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(f => new Promise((resolve) => {
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} exceeds 2 MB — skipped.`); return resolve(null); }
      const r = new FileReader();
      r.onload = (ev) => resolve({ name: f.name, type: f.type || "application/octet-stream", data_url: ev.target.result });
      r.readAsDataURL(f);
    }));
    Promise.all(readers).then(res => {
      const good = res.filter(Boolean);
      setForm(prev => ({ ...prev, documents: [...prev.documents, ...good] }));
      if (good.length) toast.success(`${good.length} document(s) attached.`);
    });
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.event_date) { toast.error("Date is required."); return; }
    if (!form.event_type) { toast.error("Event Type is required."); return; }
    if (!form.details.trim()) { toast.error("What Happened is required."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${apiBase}/case-proceedings/${editingId}`, {
          event_date: form.event_date,
          event_type: form.event_type,
          details: form.details,
          advocate_comment: form.advocate_comment,
          next_step: form.next_step,
          next_step_due: form.next_step_due,
          documents: form.documents,
          visible_to_client: !!form.visible_to_client,
        });
        toast.success("Proceeding updated.");
      } else {
        await axios.post(`${apiBase}/case-proceedings`, { case_number: caseNumber, ...form });
        toast.success("Proceeding added.");
      }
      setEditingId(null);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save proceeding");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      event_date: ev.event_date || today,
      event_type: ev.event_type || "Hearing",
      details: ev.details || "",
      advocate_comment: ev.advocate_comment || "",
      next_step: ev.next_step || "",
      next_step_due: ev.next_step_due || "",
      documents: ev.documents || [],
      visible_to_client: !!ev.visible_to_client,
    });
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`Delete this ${ev.event_type} entry from ${ev.event_date}? This is audited.`)) return;
    try {
      await axios.delete(`${apiBase}/case-proceedings/${ev.id}`);
      toast.success("Proceeding deleted.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200" data-testid={`case-proceedings-${testIdSuffix}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          Structured Proceedings
          {events && (
            <span className="bg-orange-100 text-orange-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
              {events.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1 rounded-lg"
          data-testid={`case-proceedings-toggle-${testIdSuffix}`}
        >
          {expanded ? "Hide" : "Open"} Proceedings
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Add / Edit form */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`case-proceedings-form-${testIdSuffix}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {editingId ? "Edit Proceeding" : "Add New Proceeding"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Date *</label>
                <input type="date" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-date-${testIdSuffix}`} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Event Type *</label>
                <select value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-type-${testIdSuffix}`}>
                  {PROCEEDING_TYPES.map(t => (<option key={t.key} value={t.key}>{t.key}</option>))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Next Step (optional)</label>
                <input type="text" value={form.next_step} onChange={(e) => setForm({...form, next_step: e.target.value})} placeholder="e.g. File counter affidavit" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-next-${testIdSuffix}`} />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">What Happened *</label>
              <textarea rows={2} value={form.details} onChange={(e) => setForm({...form, details: e.target.value})} placeholder="Short description of the event…" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-details-${testIdSuffix}`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Advocate / Internal Comment</label>
                <input type="text" value={form.advocate_comment} onChange={(e) => setForm({...form, advocate_comment: e.target.value})} placeholder="Only visible to internal team" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-comment-${testIdSuffix}`} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Next-step Due Date</label>
                <input type="date" value={form.next_step_due} onChange={(e) => setForm({...form, next_step_due: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`proc-nextdue-${testIdSuffix}`} />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-[11px] text-slate-600 flex items-center gap-1.5 cursor-pointer">
                <input type="file" multiple accept="image/*,application/pdf" onChange={handleDoc} className="hidden" data-testid={`proc-file-${testIdSuffix}`} />
                <span className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-2 py-1 rounded font-semibold">📎 Attach Docs</span>
                {form.documents.length > 0 && (
                  <span className="text-slate-500">{form.documents.length} file(s) attached</span>
                )}
              </label>
              <label className="text-[11px] font-semibold text-slate-700 flex items-center gap-2 cursor-pointer bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                <input
                  type="checkbox"
                  checked={!!form.visible_to_client}
                  onChange={(e) => setForm({...form, visible_to_client: e.target.checked})}
                  className="w-3.5 h-3.5 rounded"
                  data-testid={`proc-visible-${testIdSuffix}`}
                />
                <span>👁 Visible to Member</span>
              </label>
              <div className="flex gap-2">                {editingId && (
                  <button onClick={() => { setEditingId(null); resetForm(); }} className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-700 font-semibold" data-testid={`proc-cancel-${testIdSuffix}`}>
                    Cancel Edit
                  </button>
                )}
                <button onClick={submit} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-500 text-white font-bold disabled:opacity-50" data-testid={`proc-save-${testIdSuffix}`}>
                  {saving ? "Saving…" : (editingId ? "Save Changes" : "+ Add Proceeding")}
                </button>
              </div>
            </div>
            {form.documents.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {form.documents.map((d, i) => (
                  <span key={`${d.name}-${i}`} className="text-[10px] bg-white border border-slate-300 px-2 py-0.5 rounded flex items-center gap-1">
                    {d.name}
                    <button onClick={() => setForm(prev => ({ ...prev, documents: prev.documents.filter((_, ix) => ix !== i) }))} className="text-rose-600 hover:text-rose-700">✕</button>
                  </span>
                ))}
              </div>
            )}

            {/* Phase-II Sprint 4.4: live "Client will see" preview. Shows ONLY
                the fields that reach the Client Portal (date, type, details, next step).
                advocate_comment + documents + fee data are strictly excluded. */}
            {form.visible_to_client && (
              <div className="mt-3 border-2 border-emerald-300 bg-emerald-50/50 rounded-xl p-3 space-y-2" data-testid={`proc-visible-preview-${testIdSuffix}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Member will see this
                  </div>
                  <span className="text-[10px] text-emerald-700 font-semibold">Public preview</span>
                </div>
                <div className="border-l-4 border-emerald-400 bg-white rounded-r-lg p-3">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-mono text-slate-500">{form.event_date ? fmtDate(form.event_date) : "(no date)"}</span>
                    <span className="font-semibold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full text-[10px] border border-emerald-200">{form.event_type}</span>
                  </div>
                  <p className="text-sm text-slate-900 font-medium mt-1" data-testid={`proc-visible-preview-details-${testIdSuffix}`}>
                    {form.details.trim() || <span className="italic text-slate-400">Add "What Happened" details above to preview them here…</span>}
                  </p>
                  {form.next_step && (
                    <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 mt-2">
                      <span className="font-bold">Next step:</span> {form.next_step}
                      {form.next_step_due && <span className="ml-2 font-mono text-slate-500">📅 by {fmtDate(form.next_step_due)}</span>}
                    </p>
                  )}
                </div>
                <p className="text-[10px] text-emerald-800/80 leading-snug">
                  🔒 Your internal comment{form.advocate_comment ? ` ("${form.advocate_comment.slice(0, 30)}${form.advocate_comment.length > 30 ? '…' : ''}")` : ''}{form.documents.length ? `, ${form.documents.length} attached document(s),` : ''} and all fee data stay private. Only the fields above are published to the member portal.
                </p>
              </div>
            )}
          </div>

          {/* List */}
          <div className="space-y-2">
            {loading && <p className="text-xs text-slate-500 py-3 text-center">Loading proceedings…</p>}
            {!loading && events && events.length === 0 && (
              <div className="text-xs text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-lg" data-testid={`case-proceedings-empty-${testIdSuffix}`}>
                No proceedings recorded for this case yet. Use the form above to add the first entry.
              </div>
            )}
            {!loading && events && events.map(ev => {
              const cls = PROCEEDING_TYPE_CLS[ev.event_type] || PROCEEDING_TYPE_CLS.Other;
              return (
                <div key={ev.id} className="border border-slate-200 rounded-lg p-3 bg-white space-y-1 text-xs" data-testid={`proc-item-${ev.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-slate-500">{fmtDate(ev.event_date)}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{ev.event_type}</span>
                      {ev.visible_to_client && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" title="Published to Member Portal">
                          👁 Member-visible
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(ev)} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid={`proc-edit-${ev.id}`}>Edit</button>
                      <button onClick={() => removeEvent(ev)} className="text-[10px] px-2 py-0.5 rounded bg-rose-100 hover:bg-rose-200 text-rose-700 font-semibold" data-testid={`proc-delete-${ev.id}`}>Delete</button>
                    </div>
                  </div>
                  <p className="text-slate-900 font-medium">{ev.details}</p>
                  {ev.advocate_comment && (
                    <p className="text-slate-600 italic border-l-2 border-slate-300 pl-2">
                      <span className="font-bold">Internal:</span> {ev.advocate_comment}
                    </p>
                  )}
                  {(ev.next_step || ev.next_step_due) && (
                    <p className="text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                      <span className="font-bold">Next:</span> {ev.next_step || "—"}
                      {ev.next_step_due && <span className="ml-2 font-mono text-emerald-700">📅 due {fmtDate(ev.next_step_due)}</span>}
                    </p>
                  )}
                  {ev.documents && ev.documents.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {ev.documents.map((d, i) => (
                        d.data_url ? (
                          <a key={`${d.name}-${i}`} href={d.data_url} target="_blank" rel="noopener noreferrer" download={d.name} className="text-[10px] bg-sky-50 border border-sky-200 text-sky-800 px-2 py-0.5 rounded hover:bg-sky-100">
                            📎 {d.name}
                          </a>
                        ) : (
                          <span key={`${d.name}-${i}`} className="text-[10px] bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded">📎 {d.name}</span>
                        )
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 pt-1">
                    Recorded by <span className="font-mono">{ev.created_by || "—"}</span>
                    {ev.created_role && <span className="uppercase"> · {ev.created_role}</span>}
                    {ev.created_at && <span> · {fmtDateTime(ev.created_at)}</span>}
                    {ev.updated_at && <span className="italic"> (edited {fmtDateTime(ev.updated_at)})</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * CaseTasks — Phase-II Sprint 4.
 * Structured task list per case with type/priority/status/assignee/due_date.
 * Backed by /api/tasks. Regular advocates can only see and act on tasks
 * whose case is assigned to them (backend enforced).
 */
const TASK_TYPE_META = {
  "RTI":                 { cls: "bg-sky-100 text-sky-800 border-sky-200" },
  "Document Collection": { cls: "bg-violet-100 text-violet-800 border-violet-200" },
  "Notice Drafting":     { cls: "bg-orange-100 text-orange-800 border-orange-200" },
  "Filing":              { cls: "bg-amber-100 text-amber-900 border-amber-300" },
  "Follow-up":           { cls: "bg-slate-100 text-slate-700 border-slate-200" },
  "Member Meeting":      { cls: "bg-teal-100 text-teal-800 border-teal-200" },
  "Court Appearance":    { cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "Research":            { cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  "Payment Follow-up":   { cls: "bg-rose-100 text-rose-800 border-rose-200" },
  "Other":               { cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const TASK_TYPES_LIST = Object.keys(TASK_TYPE_META);
// Legacy display normaliser — DB rows saved before Feb-12-2026 may still hold
// task_type="Client Meeting"; show them as "Member Meeting" to match the rest
// of the app's Member-oriented copy.
const taskTypeDisplay = (v) => v === "Client Meeting" ? "Member Meeting" : v;
const TASK_PRIORITY_META = {
  "Low":    "bg-slate-100 text-slate-600",
  "Medium": "bg-sky-100 text-sky-800",
  "High":   "bg-amber-100 text-amber-900",
  "Urgent": "bg-rose-100 text-rose-800",
};
const TASK_STATUS_META = {
  "Pending":     "bg-slate-100 text-slate-700",
  "In Progress": "bg-sky-100 text-sky-800",
  "Completed":   "bg-emerald-100 text-emerald-800",
  "On Hold":     "bg-amber-100 text-amber-900",
  "Cancelled":   "bg-rose-100 text-rose-800",
};
const TASK_STATUSES_LIST = Object.keys(TASK_STATUS_META);
const TASK_PRIORITIES_LIST = Object.keys(TASK_PRIORITY_META);

// ==========================================================================
// CasePartiesPanel — Feb 14, 2026
// Multi-party & counsel management for a case (all case types). Advocates
// assigned to the case + super admins can add / edit / delete parties.
// Members are DENIED at the API — this panel is never rendered on the
// member view.
// ==========================================================================
const _PARTY_SIDES_LIST = [
  { key: "Applicant-Petitioner-Complainant", label: "Petitioner / Complainant" },
  { key: "Respondent-Defendant-Accused",     label: "Respondent / Defendant / Accused" },
  { key: "Proforma",                         label: "Proforma Party" },
  { key: "Intervenor",                       label: "Intervenor" },
  { key: "Other",                            label: "Other" },
];
const _PARTY_KINDS_LIST = ["Individual", "Organisation", "Government", "Unknown"];

function CasePartiesPanel({ caseNumber, apiBase, testIdSuffix }) {
  const [rows, setRows] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const emptyForm = () => ({
    party_side: "Respondent-Defendant-Accused",
    party_name: "",
    party_kind: "Individual",
    party_mobile: "",
    party_email: "",
    party_address: "",
    counsel_name: "",
    counsel_mobile: "",
    counsel_email: "",
    counsel_for_note: "",
    is_primary_contact: false,
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${apiBase}/case-parties`, { params: { case_number: caseNumber } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error("[CasePartiesPanel.load]", err);
      setRows([]);
    } finally { setLoading(false); }
  };

  // Mount-on-expand fetch only; `load`/`rows` intentionally excluded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (expanded && rows === null) load(); }, [expanded]);

  const beginAdd = () => { setEditingId("new"); setForm(emptyForm()); };
  const beginEdit = (r) => { setEditingId(r.id); setForm({ ...emptyForm(), ...r }); };
  const cancel = () => { setEditingId(null); setForm(null); };

  const save = async () => {
    if (!form?.party_name?.trim()) { toast.error("Party name is required"); return; }
    setSaving(true);
    try {
      if (editingId === "new") {
        await axios.post(`${apiBase}/case-parties`, { ...form, case_number: caseNumber });
        toast.success("Party added");
      } else {
        await axios.patch(`${apiBase}/case-parties/${editingId}`, form);
        toast.success("Party updated");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save party");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.party_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${apiBase}/case-parties/${r.id}`);
      toast.success("Party removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete party");
    }
  };

  const count = rows?.length || 0;
  const sideLabel = (k) => _PARTY_SIDES_LIST.find((s) => s.key === k)?.label || k;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`parties-panel-${testIdSuffix || ""}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-white hover:from-indigo-100 transition">
        <div className="text-left">
          <div className="font-bold text-slate-900 text-sm">Parties & Counsels</div>
          <div className="text-[11px] text-slate-500">Applicants, Respondents, Intervenors + their advocates</div>
        </div>
        <div className="flex items-center gap-2">
          {rows !== null && <span className="text-xs font-mono bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">{count}</span>}
          <span className="text-slate-400 text-lg">{expanded ? "−" : "+"}</span>
        </div>
      </button>
      {expanded && (
        <div className="p-4 space-y-3">
          {loading && <div className="text-xs text-slate-500">Loading…</div>}
          {rows && rows.length === 0 && !editingId && (
            <div className="text-xs text-slate-500 italic">No parties added yet. Click Add to record every counsel.</div>
          )}
          {rows && rows.map((r) => (
            editingId === r.id ? (
              <PartyEditForm key={r.id} form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} />
            ) : (
              <div key={r.id} className="border border-slate-200 rounded-xl p-3 text-xs space-y-1" data-testid={`party-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.party_side.startsWith("Applicant") ? "bg-emerald-100 text-emerald-800" : r.party_side.startsWith("Respondent") ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-700"}`}>
                        {sideLabel(r.party_side)}
                      </span>
                      <span className="text-[10px] text-slate-500 uppercase">{r.party_kind}</span>
                      {r.is_primary_contact && <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">★ Primary contact</span>}
                    </div>
                    <div className="font-bold text-sm text-slate-900 mt-1">{r.party_name}</div>
                    {(r.party_mobile || r.party_email) && (
                      <div className="text-slate-600 mt-0.5 space-x-2">
                        {r.party_mobile && <a href={`tel:${r.party_mobile}`} className="text-indigo-700 hover:underline">📞 {r.party_mobile}</a>}
                        {r.party_email && <a href={`mailto:${r.party_email}`} className="text-indigo-700 hover:underline">✉ {r.party_email}</a>}
                      </div>
                    )}
                    {r.party_address && <div className="text-slate-500 text-[11px] mt-0.5">{r.party_address}</div>}
                    {r.counsel_name && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Counsel</span>
                        <div className="text-slate-800 font-semibold">{r.counsel_name}</div>
                        <div className="space-x-2">
                          {r.counsel_mobile && <a href={`tel:${r.counsel_mobile}`} className="text-indigo-700 hover:underline">📞 {r.counsel_mobile}</a>}
                          {r.counsel_email && <a href={`mailto:${r.counsel_email}`} className="text-indigo-700 hover:underline">✉ {r.counsel_email}</a>}
                        </div>
                        {r.counsel_for_note && <div className="text-[11px] text-slate-500 mt-0.5 italic">{r.counsel_for_note}</div>}
                      </div>
                    )}
                    {r.notes && <div className="text-[11px] text-slate-600 mt-1 italic">📝 {r.notes}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => beginEdit(r)} data-testid={`party-edit-${r.id}`}
                      className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold">Edit</button>
                    <button onClick={() => remove(r)} data-testid={`party-delete-${r.id}`}
                      className="text-[10px] px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold">Delete</button>
                  </div>
                </div>
              </div>
            )
          ))}
          {editingId === "new" && (
            <PartyEditForm form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} />
          )}
          {editingId !== "new" && (
            <button onClick={beginAdd} data-testid="party-add-btn"
              className="w-full text-xs font-bold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500">
              + Add Party / Counsel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PartyEditForm({ form, setForm, onSave, onCancel, saving }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="border-2 border-indigo-300 rounded-xl p-3 bg-indigo-50/40 text-xs space-y-2" data-testid="party-edit-form">
      <div className="grid grid-cols-2 gap-2">
        <select value={form.party_side} onChange={(e) => set("party_side", e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-side">
          {_PARTY_SIDES_LIST.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={form.party_kind} onChange={(e) => set("party_kind", e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-kind">
          {_PARTY_KINDS_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <input placeholder="Party name *" value={form.party_name} onChange={(e) => set("party_name", e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-name" />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Party mobile" value={form.party_mobile || ""} maxLength={10}
          onChange={(e) => set("party_mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-mobile" />
        <input placeholder="Party email" value={form.party_email || ""} onChange={(e) => set("party_email", e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-email" />
      </div>
      <input placeholder="Party address (optional)" value={form.party_address || ""}
        onChange={(e) => set("party_address", e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg border border-slate-300 bg-white" />
      <div className="pt-2 border-t border-indigo-200 space-y-2">
        <div className="text-[10px] font-bold uppercase text-indigo-800">Counsel details</div>
        <input placeholder="Counsel name" value={form.counsel_name || ""} onChange={(e) => set("counsel_name", e.target.value)}
          className="w-full px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-counsel-name" />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Counsel mobile" value={form.counsel_mobile || ""} maxLength={10}
            onChange={(e) => set("counsel_mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
            className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-counsel-mobile" />
          <input placeholder="Counsel email" value={form.counsel_email || ""}
            onChange={(e) => set("counsel_email", e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white" data-testid="party-form-counsel-email" />
        </div>
        <input placeholder='Counsel-for note (e.g. "Vakalat for R9–R11")' value={form.counsel_for_note || ""}
          onChange={(e) => set("counsel_for_note", e.target.value)}
          className="w-full px-2 py-1.5 rounded-lg border border-slate-300 bg-white" />
      </div>
      <input placeholder="Notes (internal)" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg border border-slate-300 bg-white" />
      <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!form.is_primary_contact} onChange={(e) => set("is_primary_contact", e.target.checked)} data-testid="party-form-primary" />
        Mark as primary contact for their side
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-[11px]">Cancel</button>
        <button onClick={onSave} disabled={saving} data-testid="party-form-save"
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 font-bold text-[11px] disabled:bg-slate-300">
          {saving ? "Saving…" : "Save Party"}
        </button>
      </div>
    </div>
  );
}

function CaseTasks({ caseNumber, apiBase, testIdSuffix, autoOpen = false, onCountChange, isAdmin = false }) {
  const [tasks, setTasks] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState(!!autoOpen);
  const [form, setForm] = React.useState({
    task_type: "Follow-up",
    title: "",
    description: "",
    priority: "Medium",
    due_date: "",
    assigned_to: "",
  });
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [completeFor, setCompleteFor] = React.useState(null); // task being completed with notes
  const [completeNotes, setCompleteNotes] = React.useState("");
  // Phase-II Sprint 4.5: admin-only task reassign
  const [reassignFor, setReassignFor] = React.useState(null);
  const [reassignAdvocates, setReassignAdvocates] = React.useState([]);
  const [reassignForm, setReassignForm] = React.useState({ new_assignee: "", reason: "", notify_original: true, keep_as_watcher: false });
  const [reassignSaving, setReassignSaving] = React.useState(false);

  const openReassign = async (t) => {
    setReassignFor(t);
    setReassignForm({ new_assignee: "", reason: "", notify_original: true, keep_as_watcher: false });
    try {
      const res = await axios.get(`${apiBase}/admin/advocates`);
      const list = Array.isArray(res.data) ? res.data : [];
      // exclude the current assignee from the dropdown
      setReassignAdvocates(list.filter(a => (a.email || "").toLowerCase() !== (t.assigned_to || "").toLowerCase()));
    } catch (err) {
      setReassignAdvocates([]);
    }
  };

  const submitReassign = async () => {
    if (!reassignFor) return;
    if (!reassignForm.new_assignee) { toast.error("Pick a new assignee."); return; }
    if (!reassignForm.reason.trim()) { toast.error("Reason is required."); return; }
    setReassignSaving(true);
    try {
      await axios.post(`${apiBase}/tasks/${reassignFor.id}/reassign`, {
        new_assignee: reassignForm.new_assignee,
        reason: reassignForm.reason.trim(),
        notify_original: !!reassignForm.notify_original,
        keep_as_watcher: !!reassignForm.keep_as_watcher,
      });
      const bits = [];
      if (reassignForm.notify_original) bits.push("original notified");
      if (reassignForm.keep_as_watcher) bits.push("kept as watcher");
      toast.success(`Task reassigned to ${reassignForm.new_assignee}${bits.length ? ' · ' + bits.join(' · ') : ''}.`);
      setReassignFor(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassign failed");
    } finally {
      setReassignSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/tasks`, { params: { case_number: caseNumber } });
      const list = Array.isArray(res.data) ? res.data : [];
      setTasks(list);
      if (typeof onCountChange === "function") onCountChange(list.filter(t => t.status !== "Completed" && t.status !== "Cancelled").length);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 403) toast.error("You do not have access to this case's tasks.");
      else toast.error(err?.response?.data?.detail || "Could not load tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (expanded && tasks === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const resetForm = () => setForm({ task_type: "Follow-up", title: "", description: "", priority: "Medium", due_date: "", assigned_to: "" });

  const submit = async () => {
    if (!form.task_type) { toast.error("Task Type is required."); return; }
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${apiBase}/tasks/${editingId}`, form);
        toast.success("Task updated.");
      } else {
        await axios.post(`${apiBase}/tasks`, { case_number: caseNumber, ...form });
        toast.success("Task created.");
      }
      setEditingId(null);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save task");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (t, newStatus) => {
    try {
      await axios.patch(`${apiBase}/tasks/${t.id}`, { status: newStatus });
      toast.success(`Task marked ${newStatus}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    }
  };

  const doComplete = async () => {
    if (!completeFor) return;
    try {
      await axios.patch(`${apiBase}/tasks/${completeFor.id}`, {
        status: "Completed",
        completion_notes: completeNotes || null,
      });
      toast.success("Task completed.");
      setCompleteFor(null);
      setCompleteNotes("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete task");
    }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? This is audited.`)) return;
    try {
      await axios.delete(`${apiBase}/tasks/${t.id}`);
      toast.success("Task deleted.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete task");
    }
  };

  const openCount = (tasks || []).filter(t => t.status !== "Completed" && t.status !== "Cancelled").length;

  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200" data-testid={`case-tasks-${testIdSuffix}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          Case Tasks
          {tasks && (
            <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
              {openCount} open · {tasks.length} total
            </span>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1 rounded-lg"
          data-testid={`case-tasks-toggle-${testIdSuffix}`}
        >
          {expanded ? "Hide" : "Open"} Tasks
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Add / Edit form */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`case-tasks-form-${testIdSuffix}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {editingId ? "Edit Task" : "Add New Task"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Task Type *</label>
                <select value={form.task_type} onChange={(e) => setForm({...form, task_type: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`task-type-${testIdSuffix}`}>
                  {TASK_TYPES_LIST.map(t => (<option key={t} value={t}>{t}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`task-priority-${testIdSuffix}`}>
                  {TASK_PRIORITIES_LIST.map(p => (<option key={p} value={p}>{p}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Due Date</label>
                <input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`task-due-${testIdSuffix}`} />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Title *</label>
              <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. Draft RTI to DCDRC-I" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`task-title-${testIdSuffix}`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Assign To (email)</label>
                <input type="text" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value.toLowerCase()})} placeholder="kiran@hrcpc.org (optional)" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs font-mono" data-testid={`task-assignee-${testIdSuffix}`} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-0.5">Description / Notes</label>
                <input type="text" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Optional additional context" className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs" data-testid={`task-desc-${testIdSuffix}`} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {editingId && (
                <button onClick={() => { setEditingId(null); resetForm(); }} className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-700 font-semibold" data-testid={`task-cancel-${testIdSuffix}`}>
                  Cancel Edit
                </button>
              )}
              <button onClick={submit} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold disabled:opacity-50" data-testid={`task-save-${testIdSuffix}`}>
                {saving ? "Saving…" : (editingId ? "Save Changes" : "+ Add Task")}
              </button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-2">
            {loading && <p className="text-xs text-slate-500 py-3 text-center">Loading tasks…</p>}
            {!loading && tasks && tasks.length === 0 && (
              <div className="text-xs text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-lg" data-testid={`case-tasks-empty-${testIdSuffix}`}>
                No tasks recorded for this case yet. Use the form above to add the first task.
              </div>
            )}
            {!loading && tasks && tasks.map(t => {
              const typeCls = (TASK_TYPE_META[t.task_type] || TASK_TYPE_META.Other).cls;
              const priCls = TASK_PRIORITY_META[t.priority] || "bg-slate-100 text-slate-600";
              const statCls = TASK_STATUS_META[t.status] || "bg-slate-100 text-slate-700";
              const isDone = t.status === "Completed" || t.status === "Cancelled";
              return (
                <div key={t.id} className={`border border-slate-200 rounded-lg p-3 space-y-1 text-xs ${isDone ? 'bg-slate-50/60' : 'bg-white'}`} data-testid={`task-item-${t.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeCls}`}>{taskTypeDisplay(t.task_type)}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${priCls}`}>{t.priority}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statCls}`}>{t.status}</span>
                      {t.due_date && <span className="text-[10px] font-mono text-slate-500">📅 due {fmtDate(t.due_date)}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <select
                        value={t.status}
                        onChange={(e) => {
                          if (e.target.value === "Completed") {
                            setCompleteFor(t);
                            setCompleteNotes("");
                          } else {
                            changeStatus(t, e.target.value);
                          }
                        }}
                        className="text-[10px] border border-slate-300 bg-white rounded px-1 py-0.5"
                        data-testid={`task-status-select-${t.id}`}
                      >
                        {TASK_STATUSES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => { setEditingId(t.id); setForm({ task_type: t.task_type, title: t.title, description: t.description || "", priority: t.priority, due_date: t.due_date || "", assigned_to: t.assigned_to || "" }); }} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid={`task-edit-${t.id}`}>Edit</button>
                      {isAdmin && (
                        <button onClick={() => openReassign(t)} className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 font-semibold" data-testid={`task-reassign-${t.id}`} title="Reassign to another advocate">↪ Reassign</button>
                      )}
                      <button onClick={() => removeTask(t)} className="text-[10px] px-2 py-0.5 rounded bg-rose-100 hover:bg-rose-200 text-rose-700 font-semibold" data-testid={`task-delete-${t.id}`}>Delete</button>
                    </div>
                  </div>
                  <p className={`text-slate-900 font-semibold ${isDone ? 'line-through text-slate-500' : ''}`}>{t.title}</p>
                  {t.description && <p className="text-slate-600">{t.description}</p>}
                  <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                    {t.assigned_to && <span>👤 <span className="font-mono">{t.assigned_to}</span></span>}
                    <span>By <span className="font-mono">{t.created_by}</span></span>
                    <span>{fmtDate(String(t.created_at || "").slice(0, 10))}</span>
                    {t.unique_code && <span className="font-mono text-slate-400">{t.unique_code}</span>}
                  </div>
                  {t.completion_notes && (
                    <p className="text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mt-1">
                      <span className="font-bold">✓ Done ({fmtDate(t.completed_date)}):</span> {t.completion_notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase-II Sprint 4.5: Reassign modal (admin only) */}
      {reassignFor && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`task-reassign-modal-${testIdSuffix}`}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl space-y-3">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <h3 className="font-bold text-slate-900 text-base">Reassign Task</h3>
              <button onClick={() => setReassignFor(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5">
              <div><b>Task:</b> {reassignFor.title}</div>
              <div><b>Case:</b> <span className="font-mono text-orange-700">{reassignFor.case_number}</span></div>
              <div><b>Currently assigned to:</b> <span className="font-mono">{reassignFor.assigned_to || <em>unassigned</em>}</span></div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">New Assignee *</label>
              <select
                value={reassignForm.new_assignee}
                onChange={(e) => setReassignForm({ ...reassignForm, new_assignee: e.target.value })}
                className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs"
                data-testid={`task-reassign-select-${testIdSuffix}`}
              >
                <option value="">-- Pick advocate --</option>
                {reassignAdvocates.map(a => (
                  <option key={a.email} value={a.email}>
                    {a.name || a.email} ({a.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Reason for reassignment *</label>
              <textarea
                rows={3}
                value={reassignForm.reason}
                onChange={(e) => setReassignForm({ ...reassignForm, reason: e.target.value })}
                placeholder="e.g. Kiran on leave next week — please cover the hearing on 2026-02-25"
                className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-xs"
                data-testid={`task-reassign-reason-${testIdSuffix}`}
              />
            </div>
            {reassignFor.assigned_to && (
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer bg-emerald-50 border border-emerald-200 rounded p-2">
                  <input
                    type="checkbox"
                    checked={!!reassignForm.notify_original}
                    onChange={(e) => setReassignForm({ ...reassignForm, notify_original: e.target.checked })}
                    className="w-3.5 h-3.5 mt-0.5"
                    data-testid={`task-reassign-notify-${testIdSuffix}`}
                  />
                  <span>Notify <span className="font-mono">{reassignFor.assigned_to}</span> by email (why + who took over)</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer bg-sky-50 border border-sky-200 rounded p-2">
                  <input
                    type="checkbox"
                    checked={!!reassignForm.keep_as_watcher}
                    onChange={(e) => setReassignForm({ ...reassignForm, keep_as_watcher: e.target.checked })}
                    className="w-3.5 h-3.5 mt-0.5"
                    data-testid={`task-reassign-watcher-${testIdSuffix}`}
                  />
                  <span>👁 Keep <span className="font-mono">{reassignFor.assigned_to}</span> as a <b>watcher</b> — they can still see the task (read-only) but not change status.</span>
                </label>
              </div>
            )}
            {reassignFor.reassignment_history && reassignFor.reassignment_history.length > 0 && (
              <div className="text-[10px] text-slate-500 border-t border-slate-100 pt-2 max-h-20 overflow-y-auto space-y-0.5">
                <div className="font-bold text-slate-600">Previous reassignments:</div>
                {reassignFor.reassignment_history.map((h, i) => (
                  <div key={`${h.at}-${i}`}>· {String(h.at).slice(0,10)}: {h.from || '—'} → {h.to} <em>({h.reason})</em></div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setReassignFor(null)} className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-700 font-semibold">Cancel</button>
              <button
                onClick={submitReassign}
                disabled={reassignSaving || !reassignForm.new_assignee || !reassignForm.reason.trim()}
                className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-bold disabled:opacity-50"
                data-testid={`task-reassign-confirm-${testIdSuffix}`}
              >
                {reassignSaving ? "Reassigning…" : "Reassign Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion notes modal */}
      {completeFor && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`task-complete-modal-${testIdSuffix}`}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl space-y-3">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <h3 className="font-bold text-slate-900 text-base">Complete Task</h3>
              <button onClick={() => setCompleteFor(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <p className="text-xs text-slate-600">Task: <b>{completeFor.title}</b></p>
            <label className="block text-xs font-semibold text-slate-700">Completion Notes (optional)</label>
            <textarea rows={3} value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} placeholder="e.g. RTI reply received on 2026-02-18 — filed under case docs." className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs" data-testid={`task-complete-notes-${testIdSuffix}`} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCompleteFor(null)} className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-700 font-semibold">Cancel</button>
              <button onClick={doComplete} className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold" data-testid={`task-complete-confirm-${testIdSuffix}`}>Mark Completed</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * MyTasksDashboard — Phase-II Sprint 4.1.
 * A single place for a logged-in advocate to see EVERY open task assigned to
 * them across all their cases. Overdue-first sort, quick status change, and
 * quick Mark Completed with notes. Clicking the case badge scrolls to that
 * case's card in the same Advocate Panel view.
 */
const PRIORITY_ORDER = { "Urgent": 0, "High": 1, "Medium": 2, "Low": 3 };

function MyTasksDashboard({ apiBase, myEmail, refreshKey }) {
  const [tasks, setTasks] = React.useState(null);
  const [watchingTasks, setWatchingTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [completeFor, setCompleteFor] = React.useState(null);
  const [completeNotes, setCompleteNotes] = React.useState("");
  const [collapsed, setCollapsed] = React.useState(false);

  const load = async () => {
    if (!myEmail) return;
    setLoading(true);
    try {
      // Phase-II Sprint 4.6 — fetch ALL tasks the caller can see (assigned +
      // watching + case-visible) and split into Assigned / Watching client-side.
      const res = await axios.get(`${apiBase}/tasks`);
      const raw = Array.isArray(res.data) ? res.data : [];
      const openOnly = raw.filter(t => t.status !== "Completed" && t.status !== "Cancelled");
      const my = openOnly.filter(t => (t.assigned_to || "").toLowerCase() === myEmail.toLowerCase());
      const watching = openOnly.filter(t =>
        (t.assigned_to || "").toLowerCase() !== myEmail.toLowerCase() &&
        Array.isArray(t.watchers) && t.watchers.map(w => (w || "").toLowerCase()).includes(myEmail.toLowerCase())
      );
      const today = new Date().toISOString().slice(0, 10);
      const sortFn = (a, b) => {
        const aOver = a.due_date && a.due_date < today ? 0 : 1;
        const bOver = b.due_date && b.due_date < today ? 0 : 1;
        if (aOver !== bOver) return aOver - bOver;
        if (a.due_date && b.due_date && a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
        if (a.due_date && !b.due_date) return -1;
        if (!a.due_date && b.due_date) return 1;
        return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
      };
      my.sort(sortFn);
      watching.sort(sortFn);
      setTasks(my);
      setWatchingTasks(watching);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load your tasks");
      setTasks([]);
      setWatchingTasks([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail, refreshKey]);

  const changeStatus = async (t, newStatus) => {
    if (newStatus === "Completed") {
      setCompleteFor(t);
      setCompleteNotes("");
      return;
    }
    try {
      await axios.patch(`${apiBase}/tasks/${t.id}`, { status: newStatus });
      toast.success(`Task marked ${newStatus}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    }
  };

  const doComplete = async () => {
    if (!completeFor) return;
    try {
      await axios.patch(`${apiBase}/tasks/${completeFor.id}`, {
        status: "Completed",
        completion_notes: completeNotes || null,
      });
      toast.success("Task completed.");
      setCompleteFor(null);
      setCompleteNotes("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete task");
    }
  };

  const jumpToCase = (caseNumber) => {
    const el = document.querySelector(`[data-testid="adv-case-${CSS.escape ? CSS.escape(caseNumber) : caseNumber}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = (tasks || []).filter(t => t.due_date && t.due_date < today).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="my-tasks-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            📌 My Open Tasks
            {tasks && (
              <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full" data-testid="my-tasks-count">
                {tasks.length}
              </span>
            )}
            {overdueCount > 0 && (
              <span className="text-xs bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded-full" data-testid="my-tasks-overdue-count">
                {overdueCount} overdue
              </span>
            )}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Everything assigned to <span className="font-mono">{myEmail}</span> across all your cases — overdue first, then by due date.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-3 py-1 rounded" data-testid="my-tasks-refresh">Refresh</button>
          <button onClick={() => setCollapsed(!collapsed)} className="text-xs bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1 rounded" data-testid="my-tasks-toggle">
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {loading && <p className="text-xs text-slate-500 py-6 text-center">Loading your tasks…</p>}
          {!loading && tasks && tasks.length === 0 && (
            <div className="text-xs text-slate-500 py-8 text-center border border-dashed border-slate-300 rounded-xl bg-slate-50" data-testid="my-tasks-empty">
              🎉 No open tasks assigned to you. Enjoy the quiet.
            </div>
          )}
          {!loading && tasks && tasks.length > 0 && (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 pr-2">Task</th>
                    <th className="py-2 pr-2">Case</th>
                    <th className="py-2 pr-2">Priority</th>
                    <th className="py-2 pr-2">Due</th>
                    <th className="py-2 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(t => {
                    const typeCls = (TASK_TYPE_META[t.task_type] || TASK_TYPE_META.Other).cls;
                    const priCls = TASK_PRIORITY_META[t.priority] || "bg-slate-100 text-slate-600";
                    const isOverdue = t.due_date && t.due_date < today;
                    return (
                      <tr key={t.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`my-task-row-${t.id}`}>
                        <td className="py-2 pr-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeCls}`}>{taskTypeDisplay(t.task_type)}</span>
                        </td>
                        <td className="py-2 pr-2">
                          <div className="font-semibold text-slate-900">{t.title}</div>
                          {t.description && <div className="text-slate-500 text-[11px] line-clamp-1">{t.description}</div>}
                        </td>
                        <td className="py-2 pr-2">
                          <button
                            onClick={() => jumpToCase(t.case_number)}
                            className="text-orange-700 hover:underline font-mono font-semibold text-[11px]"
                            title="Jump to case card below"
                            data-testid={`my-task-case-${t.id}`}
                          >
                            {t.case_number}
                          </button>
                          {t.client_name && <div className="text-[10px] text-slate-500">{t.client_name}</div>}
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${priCls}`}>{t.priority}</span>
                        </td>
                        <td className="py-2 pr-2">
                          {t.due_date ? (
                            <span className={`font-mono text-[11px] ${isOverdue ? 'text-rose-700 font-bold' : 'text-slate-700'}`} data-testid={`my-task-due-${t.id}`}>
                              {isOverdue ? "⚠ " : ""}{fmtDate(t.due_date)}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[11px] italic">no due</span>
                          )}
                        </td>
                        <td className="py-2 pr-2">
                          <select
                            value={t.status}
                            onChange={(e) => changeStatus(t, e.target.value)}
                            className="text-[10px] border border-slate-300 bg-white rounded px-1 py-0.5"
                            data-testid={`my-task-status-${t.id}`}
                          >
                            {TASK_STATUSES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Phase-II Sprint 4.6 — Watching section (read-only) */}
          {!loading && watchingTasks && watchingTasks.length > 0 && (
            <div className="mt-4 pt-4 border-t-2 border-dashed border-sky-200" data-testid="my-tasks-watching">
              <div className="text-xs font-bold uppercase tracking-wider text-sky-700 flex items-center gap-2 mb-2">
                👁 Watching (read-only)
                <span className="bg-sky-100 text-sky-800 text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="my-tasks-watching-count">{watchingTasks.length}</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">Tasks reassigned away from you but you've been kept in the loop. You can see them but cannot change status. Contact the current assignee or admin to update.</p>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-2">Type</th>
                      <th className="py-2 pr-2">Task</th>
                      <th className="py-2 pr-2">Case</th>
                      <th className="py-2 pr-2">Assignee</th>
                      <th className="py-2 pr-2">Due</th>
                      <th className="py-2 pr-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {watchingTasks.map(t => {
                      const typeCls = (TASK_TYPE_META[t.task_type] || TASK_TYPE_META.Other).cls;
                      const statCls = TASK_STATUS_META[t.status] || "bg-slate-100 text-slate-700";
                      const today = new Date().toISOString().slice(0, 10);
                      const isOverdue = t.due_date && t.due_date < today;
                      return (
                        <tr key={t.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60" data-testid={`watching-task-row-${t.id}`}>
                          <td className="py-2 pr-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeCls}`}>{taskTypeDisplay(t.task_type)}</span></td>
                          <td className="py-2 pr-2">
                            <div className="font-semibold text-slate-700">{t.title}</div>
                          </td>
                          <td className="py-2 pr-2">
                            <button onClick={() => jumpToCase(t.case_number)} className="text-orange-700 hover:underline font-mono font-semibold text-[11px]">{t.case_number}</button>
                          </td>
                          <td className="py-2 pr-2 font-mono text-[10px] text-slate-600">{t.assigned_to || "—"}</td>
                          <td className="py-2 pr-2">
                            {t.due_date ? (
                              <span className={`font-mono text-[11px] ${isOverdue ? 'text-rose-700 font-bold' : 'text-slate-700'}`}>
                                {isOverdue ? "⚠ " : ""}{fmtDate(t.due_date)}
                              </span>
                            ) : <span className="text-slate-400 italic text-[11px]">no due</span>}
                          </td>
                          <td className="py-2 pr-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statCls}`}>{t.status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {completeFor && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="my-tasks-complete-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl space-y-3">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <h3 className="font-bold text-slate-900 text-base">Complete Task</h3>
              <button onClick={() => setCompleteFor(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <p className="text-xs text-slate-600">Task: <b>{completeFor.title}</b> · Case <span className="font-mono">{completeFor.case_number}</span></p>
            <label className="block text-xs font-semibold text-slate-700">Completion Notes (optional)</label>
            <textarea rows={3} value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} placeholder="e.g. Filed reply in DCDRC-I on 2026-02-20; hearing on 2026-03-02." className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded text-xs" data-testid="my-tasks-complete-notes" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCompleteFor(null)} className="text-xs px-3 py-1.5 rounded bg-slate-200 text-slate-700 font-semibold">Cancel</button>
              <button onClick={doComplete} className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold" data-testid="my-tasks-complete-confirm">Mark Completed</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}










/**
 * ClientCaseUpdates — Phase-II Sprint 4.3.
 * Client-portal view of proceedings marked visible_to_client=true.
 * Uses /api/client/case-updates which strips internal advocate_comment.
 */
function ClientCaseUpdates({ caseNumber, apiBase }) {
  const [events, setEvents] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/client/case-updates`, { params: { case_number: caseNumber } });
        if (!cancelled) setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseNumber, apiBase]);

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-4" data-testid="client-case-updates">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg text-slate-900">📢 Case Updates</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Milestones your advocate has published for you. Internal working notes stay private.</p>
        </div>
        {events && (
          <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full" data-testid="client-updates-count">
            {events.length}
          </span>
        )}
      </div>

      {loading && <p className="text-xs text-slate-500 py-4 text-center">Loading updates…</p>}

      {!loading && events && events.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-300 rounded-xl bg-slate-50" data-testid="client-updates-empty">
          No updates published yet. Your advocate will publish key milestones (hearings, orders, judgments) here as your case progresses.
        </div>
      )}

      {!loading && events && events.length > 0 && (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="border-l-4 border-emerald-400 bg-emerald-50/40 rounded-r-lg p-3" data-testid={`client-update-${ev.id}`}>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-mono text-slate-500">{fmtDate(ev.event_date)}</span>
                <span className="font-semibold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full text-[10px] border border-emerald-200">{ev.event_type}</span>
              </div>
              <p className="text-sm text-slate-900 font-medium mt-1">{ev.details}</p>
              {ev.next_step && (
                <p className="text-xs text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 mt-2">
                  <span className="font-bold">Next step:</span> {ev.next_step}
                  {ev.next_step_due && <span className="ml-2 font-mono text-slate-500">📅 by {fmtDate(ev.next_step_due)}</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function App() {
  // --- Deep-link support: map URL pathname <-> activeTab (shareable links) ---
  const PATH_TO_TAB = {
    '/': 'public',
    '/register': 'kyc',
    '/client-login': 'client-portal',
    '/support-pay': 'support-pay',
    '/ai-hub': 'ai-hub',
    '/voice-grievance': 'voice-grievance',
    '/templates': 'templates',
    '/volunteer': 'volunteer-register',
  };
  const TAB_TO_PATH = { public: '/', kyc: '/register', 'client-portal': '/client-login', 'support-pay': '/support-pay', 'ai-hub': '/ai-hub', 'voice-grievance': '/voice-grievance', templates: '/templates', 'volunteer-register': '/volunteer' };

  const _initialTabFromPath = () => {
    if (typeof window === 'undefined') return 'public';
    const p = window.location.pathname.replace(/\/$/, '') || '/';
    if (p.startsWith('/volunteer-id/')) return 'volunteer-id';
    if (p.startsWith('/volunteer-undertaking/')) return 'volunteer-undertaking';
    return PATH_TO_TAB[p] || 'public';
  };

  // Extract volunteer code from URL for the print pages
  const _volunteerCodeFromPath = () => {
    if (typeof window === 'undefined') return null;
    const p = window.location.pathname;
    const m = p.match(/\/volunteer-(?:id|undertaking)\/([A-Za-z0-9-]+)$/);
    return m ? m[1] : null;
  };

  // Navigation & Role states
  const [activeTab, setActiveTab] = useState(_initialTabFromPath()); // hydrated from URL on first render
  const [currentUser, setCurrentUser] = useState(null); // { role: 'client'|'admin'|'advocate', data: ..., token?: string }
  const [adminLoginOpen, setAdminLoginOpen] = useState(false);
  // Two-step: step="email" (send OTP) then step="otp" (verify)
  const [adminLoginForm, setAdminLoginForm] = useState({ email: "", otp: "", step: "email" });
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminLoginNotice, setAdminLoginNotice] = useState(null);

  // Advocate login (email OTP — same UX as admin, separate endpoints)
  const [advocateLoginOpen, setAdvocateLoginOpen] = useState(false);
  // Advocate case edits — pending status + next hearing per case number
  const [advocateCaseEdits, setAdvocateCaseEdits] = useState({}); // { [caseNumber]: { status, next_hearing_date } }
  const [advocateSaving, setAdvocateSaving] = useState({}); // { [caseNumber]: bool }
  const [advocateUploading, setAdvocateUploading] = useState({}); // { [caseNumber]: bool }
  const [advocateLoginForm, setAdvocateLoginForm] = useState({ email: "", otp: "", step: "email" });
  const [advocateLoginLoading, setAdvocateLoginLoading] = useState(false);
  // Full advocate directory (fetched only by admin; used by the case-assignment picker)
  const [advocateUsers, setAdvocateUsers] = useState([]);
  // Case → advocates assignment modal state
  const [assignCase, setAssignCase] = useState(null);
  const [assignSelected, setAssignSelected] = useState(new Set());
  const [assignSaving, setAssignSaving] = useState(false);
  // Fee & Expense management modal state (super-only)
  const [financeCase, setFinanceCase] = useState(null);
  // Phase-II Sprint 3: Structured Proceedings modal state
  const [proceedingsCase, setProceedingsCase] = useState(null);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [feeAgreedForm, setFeeAgreedForm] = useState({ total_fee: "", fee_notes: "" });
  const [instalmentForm, setInstalmentForm] = useState({ due_date: "", amount: "", notes: "" });
  const [expenseForm, setExpenseForm] = useState({ date: "", category: "court_fee", description: "", amount: "" });
  const [financeLoading, setFinanceLoading] = useState(false);
  // Audit log tab
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ actor: "", action: "", target_type: "" });
  const [auditLoading, setAuditLoading] = useState(false);
  // Donor wall admin tab
  const [adminDonations, setAdminDonations] = useState({ items: [], total_amount: 0 });
  const [adminDonationForm, setAdminDonationForm] = useState({ donor_name: "", donor_city: "", donor_email: "", amount: "", mode: "SBI Bank Transfer", note: "" });
  const [adminDonationLoading, setAdminDonationLoading] = useState(false);
  // Wins Wall (public) + admin
  const [publicWins, setPublicWins] = useState({ count: 0, items: [] });
  const [publicWinsIdx, setPublicWinsIdx] = useState(0);
  const [adminWins, setAdminWins] = useState({ items: [] });
  const [adminWinForm, setAdminWinForm] = useState({ focus_area: "consumer_protection", case_type: "", outcome: "", year: new Date().getFullYear(), city: "" });
  // Volunteers Map (public)
  const [volMap, setVolMap] = useState({ total_approved: 0, cities: [] });
  // Impact Digest (Newsletter)
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestRecipients, setDigestRecipients] = useState("");
  const [digestSegment, setDigestSegment] = useState("everyone");
  const [digestSegmentCount, setDigestSegmentCount] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  // Advocate Weekly Digest
  const [advocateDigestList, setAdvocateDigestList] = useState({ advocates: [] });
  const [advocateDigestPreview, setAdvocateDigestPreview] = useState(null);
  const [advocateDigestSending, setAdvocateDigestSending] = useState(false);
  const [selectedAdvocateEmails, setSelectedAdvocateEmails] = useState(() => new Set());
  const [advocateDigestSchedule, setAdvocateDigestSchedule] = useState(null);
  // KYC bulk selection
  const [selectedKycIds, setSelectedKycIds] = useState(() => new Set());
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onPop = () => {
      const p2 = window.location.pathname.replace(/\/$/, '') || '/';
      if (PATH_TO_TAB[p2]) setActiveTab(PATH_TO_TAB[p2]);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const desired = TAB_TO_PATH[activeTab];
    if (desired && window.location.pathname !== desired) {
      window.history.pushState({}, '', desired);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Client OTP Login state
  const [loginPhone, setLoginPhone] = useState("9876543210");
  const [otpStep, setOtpStep] = useState(1); // 1: enter phone, 2: enter otp
  const [otpCode, setOtpCode] = useState("");

  // Client Portal selected case state
  const [selectedCaseId, setSelectedCaseId] = useState("");

  // Admin state
  const [adminTab, setAdminTab] = useState("cases"); // cases, clients, advocates, kyc, payments
  const [clientsList, setClientsList] = useState([]);
  const [casesList, setCasesList] = useState([]);
  const [advocatesList, setAdvocatesList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [notificationsList, setNotificationsList] = useState([
    { id: "notif_1", client_mobile: "9876543210", title: "Hearing Reminder", message: "Your case CC/412/2025 has next hearing on 18th Apr 2026 at District Consumer Forum-I.", date: "2026-03-25", read: false },
    { id: "notif_2", client_mobile: "9876543210", title: "Fee Balance Reminder", message: "Pending balance of ₹10,000 for CC/412/2025. Click here to pay via Razorpay.", date: "2026-03-24", read: true },
    { id: "notif_3", client_mobile: "9123456780", title: "New Document Uploaded", message: "Adv. Priya Sharma uploaded 'Mediation_Status_Report.pdf' to your case.", date: "2026-03-22", read: false }
  ]);

  // New Case Modal / Edit Case
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [caseFocusFilter, setCaseFocusFilter] = useState("all"); // 'all' | one of FOCUS_AREA_KEYS
  // Super-Admin only — Cover Brief dispatch modal (email + PDF pack for a substitute advocate)
  const [coverBriefCase, setCoverBriefCase] = useState(null);

  // KYC Review filter & remarks dialog
  const [kycStatusFilter, setKycStatusFilter] = useState("all"); // all | Under Review | Approved | Rejected
  const [kycActionModal, setKycActionModal] = useState(null); // { client, action: 'approve'|'reject', remarks }
  const [kycSearchQuery, setKycSearchQuery] = useState("");
  const [clientSearchQuery, setClientSearchQuery] = useState("");

  // Admin Add Client Modal
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [addClientForm, setAddClientForm] = useState({
    full_name: "", mobile: "", email: "", aadhaar_last4: "",
    address: "", city: "Hyderabad", state: "Telangana", pincode: "500001",
    purpose: "", preferred_language: "Telugu / English"
  });

  // AI Legal Chat state
  const [aiQuery, setAiQuery] = useState("");
  const [aiChatHistory, setAiChatHistory] = useState([
    { role: "assistant", text: "Namaste! I am the HRCPC Legal Help Assistant. How can I assist you with your legal query today under Indian law?" }
  ]);
  const [aiLoading, setAiLoading] = useState(false);

  // Voice Grievance state
  const [voiceLangOut, setVoiceLangOut] = useState("en");
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  // Feb 14, 2026 (MEDIUM code-review fix): keep ONE URL per blob and revoke
  // it on cleanup so the voice-grievance screen no longer leaks a fresh audio
  // buffer on every re-render.
  const [voiceBlobUrl, setVoiceBlobUrl] = useState(null);
  useEffect(() => {
    if (!voiceBlob) { setVoiceBlobUrl(null); return; }
    const url = URL.createObjectURL(voiceBlob);
    setVoiceBlobUrl(url);
    return () => {
      try { URL.revokeObjectURL(url); } catch (err) { console.error('[voiceBlobUrl] revoke failed:', err); }
    };
  }, [voiceBlob]);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null); // { transcript, complaint, disclaimer, language_out_name }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Support tickets ("Ask HRCPC Legal Assistant" — human review by advocates)
  const [supportTicketsList, setSupportTicketsList] = useState([]);
  const [supportTicketModalOpen, setSupportTicketModalOpen] = useState(false);
  const emptySupportForm = { name: "", mobile: "", email: "", subject: "", message: "", category: "General Legal Query" };
  const [supportTicketForm, setSupportTicketForm] = useState(emptySupportForm);
  const [supportTicketSaving, setSupportTicketSaving] = useState(false);
  const [supportTicketSubmitted, setSupportTicketSubmitted] = useState(null); // response after submit
  const [ticketRespondModal, setTicketRespondModal] = useState(null); // ticket obj for admin reply

  // Soft "Register First" guidance (never blocks — just recommends registering before deeper follow-up)
  const [registerGuideOpen, setRegisterGuideOpen] = useState(false);
  const [registerGuidePending, setRegisterGuidePending] = useState(null); // () => Promise|void
  const [registerGuideContext, setRegisterGuideContext] = useState("");

  // KYC Form state
  const [kycForm, setKycForm] = useState({
    full_name: "",
    fathers_husbands_name: "",
    mobile: "",
    email: "",
    aadhaar_last4: "",
    address: "",
    city: "Hyderabad",
    state: "Telangana",
    pincode: "500001",
    purpose: "",
    preferred_language: "Telugu / English",
    consent: false,
    // Phase-II optional fields
    dob: "",
    gender: "",
    id_type: "",
    id_number_last4: "",
    photo_data_url: "",
    id_front_data_url: "",
    id_back_data_url: "",
    bank_ac_name: "",
    bank_ac_number_last4: "",
    bank_ifsc: "",
    bank_upi: "",
    dpdp_consent: false,
  });
  const [kycSubmitted, setKycSubmitted] = useState(false);
  // Phase-II Sprint 2: Public self-registration wizard state
  const [kycStep, setKycStep] = useState(1); // 1 = verify mobile, 2 = KYC form, 3 = done
  const [kycOtpSent, setKycOtpSent] = useState(false);
  const [kycOtpInput, setKycOtpInput] = useState("");
  const [kycTicket, setKycTicket] = useState("");
  const [kycOtpBusy, setKycOtpBusy] = useState(false);

  // Member Referral Nudge (Feb 12, 2026)
  // - `memberReferral` = data returned from GET /api/client/referral for the
  //   logged-in Approved Member (link + share_message + wa/sms + counts).
  // - `refCode` / `refReferrerName` = captured from ?ref= on /register page
  //   so the KYC submission stores `referred_by_code` on the new Member record.
  const [memberReferral, setMemberReferral] = useState(null);
  const [refCode, setRefCode] = useState("");
  const [refReferrerName, setRefReferrerName] = useState("");

  // Email-on-file Nudge (Feb 12, 2026) — closes the OTP delivery gap for
  // Members with missing / placeholder emails. `emailNudge` = payload from
  // GET /client/profile-status; drives the one-time modal below.
  const [emailNudge, setEmailNudge] = useState(null);
  const [emailNudgeInput, setEmailNudgeInput] = useState("");
  const [emailNudgeSaving, setEmailNudgeSaving] = useState(false);

  // Recap Preview In-App (Feb 12, 2026) — Advocate Portal button that renders
  // the same HTML their Friday recap will contain, inside a modal.
  const [recapPreviewOpen, setRecapPreviewOpen] = useState(false);
  const [recapPreviewHtml, setRecapPreviewHtml] = useState("");
  const [recapPreviewLoading, setRecapPreviewLoading] = useState(false);

  // Referral Leaderboard (Feb 12, 2026) — Admin panel widget.
  const [referralLeaderboard, setReferralLeaderboard] = useState(null);
  const [leaderboardMonth, setLeaderboardMonth] = useState("");
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Payment Modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null); // { title, amount, case_id }
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentMethodTab, setPaymentMethodTab] = useState('razorpay'); // 'razorpay' | 'direct'

  // Site settings (admin uploads: founder photo, QR) + editable bank details
  const defaultBank = {
    sbi: { ac_name: "Human Rights & Consumer Protection Cell Trust", ac_no: "62442953903", ifsc: "SBIN0021053", upi: "hrcpctrust@sbi" },
    dbs: { ac_name: "Human Rights & Consumer Protection Cell Trust", ac_no: "8612210000042839", ifsc: "DBSS0IN0612", upi: "hrcpctrust@dbs" }
  };
  const defaultContact = {
    address_line1: "530, A-Block, Sri Sneha BHEL Lake View Residency,",
    address_line2: "R.C. Puram, Hyderabad – 502032, Telangana, India",
    phone_primary: "08455 296263",
    phone_primary_label: "Landline",
    phone_secondary: "+91 88855 52997",
    phone_secondary_label: "Mobile",
    email: "support@hrcpc.org",
    map_url: "https://maps.app.goo.gl/dAd7vD28aiHjC3fQA"
  };
  const [siteSettings, setSiteSettings] = useState({
    founder_photo_data_url: null,
    qr_code_data_url: null,
    qr_sbi_data_url: null,
    qr_dbs_data_url: null,
    bank_details: defaultBank,
    contact_info: defaultContact,
    trust_legal: null
  });
  const [bankEditForm, setBankEditForm] = useState(defaultBank);
  const [bankEditSaving, setBankEditSaving] = useState(false);
  const [contactEditForm, setContactEditForm] = useState(defaultContact);
  const [contactEditSaving, setContactEditSaving] = useState(false);
  const [trustLegalEditForm, setTrustLegalEditForm] = useState({
    reg_no: "", reg_authority: "Sub-Registrar Office, Hyderabad",
    pan: "", tan: "",
    s80g_status: "Applied", s80g_reference: "",
    s12a_status: "Applied", s12a_reference: "",
    note: "HRCPC Trust is a registered public charitable trust dedicated to citizen awareness, advocacy and pro-bono support."
  });
  const [trustLegalSaving, setTrustLegalSaving] = useState(false);
  const [impactStatsForm, setImpactStatsForm] = useState({
    cases_supported: 1450, advocates_panel: 25, amount_recovered_lakhs: 45, years_serving: 12,
    rtis_filed: 340, awareness_camps: 78, citizens_supported: 5200,
  });
  const [impactStatsSaving, setImpactStatsSaving] = useState(false);
  const [founderVideoForm, setFounderVideoForm] = useState("");
  const [founderVideoSaving, setFounderVideoSaving] = useState(false);
  const emptyAnnouncement = { enabled: false, text: "", level: "info", link_label: "", link_url: "" };
  const [announcementForm, setAnnouncementForm] = useState(emptyAnnouncement);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(() => {
    try { return sessionStorage.getItem("hrcpc_ann_dismissed") || ""; } catch { return ""; }
  });
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [callbackForm, setCallbackForm] = useState({ name: "", mobile: "", note: "" });
  const [callbackSaving, setCallbackSaving] = useState(false);

  // Volunteer strict registration state
  const emptyVolunteerForm = {
    personal: { full_name: "", fathers_husbands_name: "", dob: "", gender: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" },
    professional: { qualification: "", occupation: "", help_area: "" },
    documents: { photograph: null, aadhaar_front: null, aadhaar_back: null, pan_card: null, bank_proof: null, passport: null },
    undertaking: { accepted: false, signed_name: "" },
  };
  const [volunteerForm, setVolunteerForm] = useState(emptyVolunteerForm);
  const [volunteerSaving, setVolunteerSaving] = useState(false);
  const [volunteerSubmitted, setVolunteerSubmitted] = useState(null);
  const [volunteersList, setVolunteersList] = useState([]);
  const [volunteerDetailOpen, setVolunteerDetailOpen] = useState(null);   // full detail object for admin
  const [volunteerPrintData, setVolunteerPrintData] = useState(null);      // for /volunteer-id or /volunteer-undertaking
  const [volunteerPrintCode] = useState(_volunteerCodeFromPath());
  const [approvedWall, setApprovedWall] = useState({ eligible: false, count: 0, items: [] });
  const [donorWall, setDonorWall] = useState({ count: 0, month_count: 0, items: [] });
  const [donorIndex, setDonorIndex] = useState(0);

  // Fetch initial data from backend API. Admin-only lists are fetched conditionally.
  const fetchData = async () => {
    try {
      // Public endpoints always (cases removed — leaked PII)
      const [resAdv, resTpl, resSite] = await Promise.all([
        axios.get(`${API}/advocates`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/site-settings`),
      ]);
      setAdvocatesList(resAdv.data);
      setTemplatesList(resTpl.data);
      const siteData = resSite.data || {};
      setSiteSettings(prev => ({ ...prev, ...siteData, bank_details: siteData.bank_details || prev.bank_details, contact_info: siteData.contact_info || prev.contact_info, trust_legal: siteData.trust_legal || prev.trust_legal }));
      setBankEditForm(siteData.bank_details || defaultBank);
      setContactEditForm(siteData.contact_info || defaultContact);
      if (siteData.trust_legal) setTrustLegalEditForm(siteData.trust_legal);
      if (siteData.impact_stats) setImpactStatsForm(siteData.impact_stats);
      setFounderVideoForm(siteData.founder_video_url || "");
      if (siteData.announcement) setAnnouncementForm({ ...emptyAnnouncement, ...siteData.announcement });

      // Admin-scoped endpoints — only fetch if we have a token
      if (ADMIN_TOKEN) {
        try {
          const [resClients, resTickets, resVolunteers, resCases, resAdvos] = await Promise.all([
            axios.get(`${API}/clients`),
            axios.get(`${API}/support/tickets`),
            axios.get(`${API}/volunteers`),
            axios.get(`${API}/cases`),
            axios.get(`${API}/admin/advocates`),
          ]);
          setClientsList(resClients.data);
          setSupportTicketsList(resTickets.data || []);
          setVolunteersList(resVolunteers.data || []);
          setCasesList(resCases.data || []);
          setAdvocateUsers(resAdvos.data || []);
        } catch (_) { /* token invalid — interceptor already toasted */ }
      } else if (ADVOCATE_TOKEN) {
        // Advocate — backend filters cases for us
        try {
          const resCases = await axios.get(`${API}/cases`);
          setCasesList(resCases.data || []);
        } catch (err) { console.error('[App.fetchData] advocate cases fetch failed:', err); }
        setClientsList([]);
        setSupportTicketsList([]);
        setVolunteersList([]);
        setAdvocateUsers([]);
      } else {
        setClientsList([]);
        setSupportTicketsList([]);
        setVolunteersList([]);
        setCasesList([]);
        setAdvocateUsers([]);
      }

      // Approved-volunteers wall (always public — anonymised)
      try {
        const wall = await axios.get(`${API}/volunteers-wall/approved`);
        setApprovedWall(wall.data || { eligible: false, count: 0, items: [] });
      } catch (_) { /* non-critical */ }

      // Donor Recognition Wall (public — anonymised)
      try {
        const donors = await axios.get(`${API}/donations/recent?limit=15`);
        setDonorWall(donors.data || { count: 0, month_count: 0, items: [] });
      } catch (_) { /* non-critical */ }

      // Public Wins Wall
      try {
        const wins = await axios.get(`${API}/wins?limit=20`);
        setPublicWins(wins.data || { count: 0, items: [] });
      } catch (_) { /* non-critical */ }

      // Volunteers Skill/City Map (public)
      try {
        const vmap = await axios.get(`${API}/volunteers-map`);
        setVolMap(vmap.data || { total_approved: 0, cities: [] });
      } catch (_) { /* non-critical */ }
    } catch (err) {
      console.error("Error fetching data from FastAPI backend:", err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore a still-valid admin/advocate session (24 h JWT) after a page reload.
  useEffect(() => {
    if (!(ADMIN_TOKEN || ADVOCATE_TOKEN)) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/session`);
        const s = res.data || {};
        if (s.role === "admin" && ADMIN_TOKEN) {
          setCurrentUser({ role: "admin", data: { name: s.name || "Admin", email: s.email }, token: ADMIN_TOKEN });
          setActiveTab("admin");
        } else if (s.role === "advocate" && ADVOCATE_TOKEN) {
          setCurrentUser({ role: "advocate", data: { name: s.name || "Advocate", email: s.email, is_super: !!s.is_super } });
          setActiveTab("advocate-panel");
        }
      } catch (err) {
        console.debug("[App.restoreSession] no valid session:", err?.response?.status);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Donor Wall — rotate every 4s while on home page
  useEffect(() => {
    if (activeTab !== 'public') return;
    if (!donorWall.items || donorWall.items.length <= 1) return;
    const t = setInterval(() => {
      setDonorIndex(i => (i + 1) % donorWall.items.length);
    }, 4000);
    return () => clearInterval(t);
  }, [activeTab, donorWall.items]);

  // Wins Wall — rotate every 5s while on home page
  useEffect(() => {
    if (activeTab !== 'public') return;
    if (!publicWins.items || publicWins.items.length <= 1) return;
    const t = setInterval(() => {
      setPublicWinsIdx(i => (i + 1) % publicWins.items.length);
    }, 5000);
    return () => clearInterval(t);
  }, [activeTab, publicWins.items]);

  // Fetch volunteer print data if on /volunteer-id/{code} or /volunteer-undertaking/{code}
  useEffect(() => {
    if ((activeTab === 'volunteer-id' || activeTab === 'volunteer-undertaking') && volunteerPrintCode) {
      axios.get(`${API}/volunteers/${volunteerPrintCode}/public-card`)
        .then(res => setVolunteerPrintData(res.data))
        .catch(() => setVolunteerPrintData({ error: true }));
    }
  }, [activeTab, volunteerPrintCode]);

  // Referral nudge — read ?ref=<code> from URL (any tab) once, verify it against
  // the backend, and show a warm "X invited you" banner on /register. If the
  // code is invalid we silently ignore it (the submit path just sends null).
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const r = (q.get('ref') || '').trim();
      if (!r || r === refCode) return;
      setRefCode(r);
      axios.get(`${API}/referral/lookup`, { params: { ref: r } })
        .then(res => setRefReferrerName(res.data?.referrer_first_name || ""))
        .catch(() => { /* invalid code — keep refCode so KYC still records the attempt */ });
    } catch (_) { /* SSR / no window */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch the Member's own referral card once they land on the client portal.
  useEffect(() => {
    if (currentUser?.role !== 'client') { setMemberReferral(null); setEmailNudge(null); return; }
    if ((currentUser?.data?.status || "").toLowerCase() !== "approved") { setMemberReferral(null); setEmailNudge(null); return; }
    axios.get(`${API}/client/referral`)
      .then(res => setMemberReferral(res.data))
      .catch(() => setMemberReferral(null));
    // Email-on-file Nudge — check whether we should show the one-time modal.
    axios.get(`${API}/client/profile-status`)
      .then(res => setEmailNudge(res.data))
      .catch(() => setEmailNudge(null));
  }, [currentUser]);


  // Initialize selected case when client logs in
  useEffect(() => {
    if (currentUser && currentUser.role === 'client') {
      const clientCases = casesList.filter(c => c.client_mobile === currentUser.data.mobile);
      if (clientCases.length > 0) {
        setSelectedCaseId(clientCases[0].case_number || clientCases[0].id);
      } else {
        setSelectedCaseId("");
      }
    }
  }, [currentUser, casesList]);

  // Handle Client OTP Login via API
  const handleClientLogin = async (e) => {
    e.preventDefault();
    if (otpStep === 1) {
      if (loginPhone.length !== 10) {
        toast.error("Please enter a valid 10-digit mobile number");
        return;
      }
      try {
        await axios.post(`${API}/auth/otp-send`, { mobile: loginPhone });
        setOtpStep(2);
        setOtpCode("");
        toast.success(`If ${loginPhone} is a registered Member, a 6-digit code has been sent.`);
      } catch (err) {
        const detail = err?.response?.data?.detail || "Unable to send OTP. Please try again.";
        toast.error(detail);
      }
    } else {
      if (otpCode.length !== 6) {
        toast.error("Please enter the 6-digit code you received.");
        return;
      }
      try {
        const response = await axios.post(`${API}/auth/otp-login`, { mobile: loginPhone, otp: otpCode });
        const clientData = response.data.client;
        const clientToken = response.data.token;
        // Store the client JWT so subsequent /cases/client/{mobile} calls are authorised
        if (clientToken && window.__hrcpcSetClientToken) window.__hrcpcSetClientToken(clientToken);
        setCurrentUser({ role: 'client', data: clientData });
        setActiveTab("client-portal");
        // Fetch this client's own cases (requires client JWT)
        try {
          const casesRes = await axios.get(`${API}/cases/client/${clientData.mobile}`);
          setCasesList(casesRes.data || []);
        } catch (_) { setCasesList([]); }
        toast.success("Welcome back, " + clientData.full_name + "!");
      } catch (err) {
        const detail = err?.response?.data?.detail || "Invalid or expired OTP.";
        toast.error(detail);
      }
    }
  };

  // Handle AI Chat submit
  const handleAiSubmit = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    const userMsg = aiQuery.trim();
    setAiChatHistory(prev => [...prev, { role: "user", text: userMsg }]);
    setAiQuery("");
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/legal-query`, {
        query: userMsg,
        session_id: currentUser?.data?.mobile ? `client-${currentUser.data.mobile}` : undefined,
        mobile: currentUser?.data?.mobile
      });
      const reply = res.data?.reply || "The assistant did not return a response.";
      const disclaimer = res.data?.disclaimer || MOCK_DISCLAIMER;
      setAiChatHistory(prev => [...prev, { role: "assistant", text: reply, disclaimer }]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "AI assistant is temporarily unavailable.");
      setAiChatHistory(prev => [...prev, {
        role: "assistant",
        text: "I couldn't reach the AI service just now. Please try again in a moment, or consult an HRCPC panel advocate directly.",
        disclaimer: MOCK_DISCLAIMER
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Voice grievance recording handlers
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        setVoiceBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setVoiceRecording(true);
      setVoiceResult(null);
      toast.info("Recording started. Speak clearly in any Indian language.");
    } catch (err) {
      console.error(err);
      toast.error("Microphone permission denied. Please allow mic access.");
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && voiceRecording) {
      mediaRecorderRef.current.stop();
      setVoiceRecording(false);
      toast.success("Recording captured. Click Convert to draft the complaint.");
    }
  };

  const submitVoiceGrievance = async () => {
    if (!voiceBlob) {
      toast.error("Please record your grievance first.");
      return;
    }
    setVoiceProcessing(true);
    try {
      const fd = new FormData();
      fd.append("audio", voiceBlob, "grievance.webm");
      fd.append("language_out", voiceLangOut);
      if (currentUser?.data?.mobile) fd.append("mobile", currentUser.data.mobile);
      const res = await axios.post(`${API}/ai/voice-grievance`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000
      });
      setVoiceResult(res.data);
      toast.success("Complaint drafted successfully.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Voice service failed. Try again.");
    } finally {
      setVoiceProcessing(false);
    }
  };

  const resetVoiceGrievance = () => {
    setVoiceBlob(null);
    setVoiceResult(null);
    audioChunksRef.current = [];
  };

  // Soft-guidance gate — shows the "Register first" recommendation once per session.
  // NEVER blocks — user can always continue as guest. Silently proceeds if already dismissed.
  const askRegisterGuide = (nextAction, context = "") => {
    const dismissed = typeof window !== "undefined" && sessionStorage.getItem("hrcpc_guest_dismissed") === "1";
    if (dismissed) { nextAction(); return; }
    setRegisterGuideContext(context);
    setRegisterGuidePending(() => nextAction);   // wrap so React doesn't call it as an updater
    setRegisterGuideOpen(true);
  };

  const requestAdminOtp = async () => {
    const email = adminLoginForm.email.trim();
    if (!email || !email.includes("@")) {
      setAdminLoginNotice({ type: "error", text: "Please enter a valid admin email." });
      return;
    }
    setAdminLoginLoading(true);
    setAdminLoginNotice(null);
    try {
      const res = await axios.post(`${API}/auth/admin/otp-send`, { email });
      const debugOtp = res?.data?.debug_otp;
      setAdminLoginForm(f => ({ ...f, step: "otp", otp: debugOtp || "" }));
      if (debugOtp) {
        toast.success(`[DEBUG MODE] OTP: ${debugOtp} — auto-filled. Disable DEBUG_SHOW_OTP env var in production.`, { duration: 10000 });
      } else {
        setAdminLoginNotice({ type: "ok", text: "If the email is registered, a login code has been sent. Check your inbox (and spam folder)." });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        // A code was most likely already emailed — let the admin enter it instead of getting stuck.
        setAdminLoginForm(f => ({ ...f, step: "otp" }));
        setAdminLoginNotice({ type: "warn", text: "Too many code requests (limit 5 per hour). Enter the code already sent to your inbox, or try again later." });
      } else {
        setAdminLoginNotice({ type: "error", text: detail || (err?.response ? `Server error (${status}). Please try again.` : "Cannot reach the server. Check your connection and try again.") });
      }
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const submitAdminLogin = async () => {
    const email = adminLoginForm.email.trim();
    const otp = (adminLoginForm.otp || "").trim();
    if (!otp || otp.length !== 6) {
      setAdminLoginNotice({ type: "error", text: "Please enter the 6-digit code from your email." });
      return;
    }
    setAdminLoginLoading(true);
    setAdminLoginNotice(null);
    try {
      const res = await axios.post(`${API}/auth/admin/otp-verify`, { email, otp });
      ADMIN_TOKEN = res.data.token;
      try { window.localStorage.setItem("hrcpc_admin_token", ADMIN_TOKEN); } catch (_) {}
      setCurrentUser({ role: "admin", data: { name: res.data.name || "Admin", email: String(email || "").toLowerCase() }, token: ADMIN_TOKEN });
      setAdminLoginOpen(false);
      setAdminLoginForm({ email: "", otp: "", step: "email" });
      setActiveTab("admin");
      await fetchData();
      toast.success("Welcome, " + (res.data.name || "Admin"));
    } catch (err) {
      setAdminLoginNotice({ type: "error", text: err?.response?.data?.detail || "Invalid or expired code. Use “Resend code” to get a new one." });
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const logoutAdmin = () => {
    ADMIN_TOKEN = null;
    try { window.localStorage.removeItem("hrcpc_admin_token"); } catch (_) {}
    setCurrentUser(null);
    setActiveTab("public");
    setClientsList([]); setSupportTicketsList([]); setVolunteersList([]); setCasesList([]);
    toast.info("Logged out");
  };

  const logoutClient = () => {
    if (window.__hrcpcSetClientToken) window.__hrcpcSetClientToken(null);
    setCurrentUser(null);
    setActiveTab("public");
    setCasesList([]);
    toast.info("Logged out successfully");
  };

  const logoutAdvocate = () => {
    if (window.__hrcpcSetAdvocateToken) window.__hrcpcSetAdvocateToken(null);
    setCurrentUser(null);
    setActiveTab("public");
    setCasesList([]);
    toast.info("Logged out");
  };

  // ============ Advocate case edits (status + hearing) ============
  const getAdvocateEdit = (caseObj) => {
    const cn = caseObj.case_number;
    if (advocateCaseEdits[cn]) return advocateCaseEdits[cn];
    return { status: caseObj.status || "", next_hearing_date: caseObj.next_hearing_date || "" };
  };
  const setAdvocateEditField = (caseObj, field, value) => {
    const cn = caseObj.case_number;
    const cur = getAdvocateEdit(caseObj);
    setAdvocateCaseEdits(prev => ({ ...prev, [cn]: { ...cur, [field]: value } }));
  };
  const saveAdvocateCaseEdit = async (caseObj) => {
    const cn = caseObj.case_number;
    const edit = getAdvocateEdit(caseObj);
    const changed = {};
    if (edit.status && edit.status !== caseObj.status) changed.status = edit.status;
    if (edit.next_hearing_date && edit.next_hearing_date !== caseObj.next_hearing_date) changed.next_hearing_date = edit.next_hearing_date;
    if (!Object.keys(changed).length) { toast.info("Nothing changed"); return; }
    setAdvocateSaving(prev => ({ ...prev, [cn]: true }));
    try {
      await axios.patch(`${API}/cases/${encodeURIComponent(cn)}/status`, changed);
      toast.success("Case updated");
      await fetchData();
      setAdvocateCaseEdits(prev => { const c = { ...prev }; delete c[cn]; return c; });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save case update");
    } finally {
      setAdvocateSaving(prev => ({ ...prev, [cn]: false }));
    }
  };

  // ============ Advocate / Client document utilities ============
  const MAX_UPLOAD_BYTES = 3 * 1024 * 1024; // 3 MB
  const _fileToDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });
  const _compressImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve) => {
    if (!file.type.startsWith("image/")) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (Math.max(w, h) > maxDim) {
          const s = maxDim / Math.max(w, h);
          w = Math.round(w * s); h = Math.round(h * s);
        }
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => resolve(b || file), "image/jpeg", quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
  const uploadAdvocateDocument = async (caseObj, file) => {
    if (!file) return;
    const cn = caseObj.case_number;
    setAdvocateUploading(prev => ({ ...prev, [cn]: true }));
    try {
      let payload = file;
      if (file.type.startsWith("image/") && file.size > 800 * 1024) {
        payload = await _compressImage(file, 1600, 0.82);
      }
      if (payload.size > MAX_UPLOAD_BYTES) {
        toast.error("File exceeds 3 MB even after compression. Please attach a smaller file.");
        return;
      }
      const dataURL = await _fileToDataURL(payload);
      const res = await axios.post(`${API}/cases/${encodeURIComponent(cn)}/documents`, {
        name: file.name, type: file.type || "file", data_url: dataURL,
      });
      toast.success("Document uploaded");
      // Update casesList optimistically
      setCasesList(prev => prev.map(x => x.case_number === cn ? { ...x, documents: [...(x.documents || []), res.data.document] } : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setAdvocateUploading(prev => ({ ...prev, [cn]: false }));
    }
  };
  const downloadCaseDocument = async (caseObj, doc) => {
    try {
      const res = await axios.get(`${API}/case-document-content`, {
        params: { case_id: caseObj.case_number, doc_id: doc.id },
      });
      const url = res.data?.document?.data_url;
      if (!url) { toast.error("Document content not available"); return; }
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name || "document";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };
  const deleteCaseDocument = async (caseObj, doc) => {
    if (!window.confirm(`Delete document "${doc.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/cases/${encodeURIComponent(caseObj.case_number)}/documents/${doc.id}`);
      toast.info("Document deleted");
      setCasesList(prev => prev.map(x => x.case_number === caseObj.case_number ? { ...x, documents: (x.documents || []).filter(d => d.id !== doc.id) } : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ============ Word-compatible .doc export for AI drafts ============
  const downloadAIDraftAsDoc = (text, filenameSlug) => {
    // Convert simple markdown into Word-friendly HTML (bold, headings, lists).
    const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const lines = (text || "").split(/\r?\n/);
    const html = [];
    let inList = false;
    for (let raw of lines) {
      const line = raw.trimEnd();
      const trimmed = line.trim();
      if (!trimmed) {
        if (inList) { html.push("</ul>"); inList = false; }
        html.push("<p>&nbsp;</p>");
        continue;
      }
      // Bullet list item
      if (/^[-*•]\s+/.test(trimmed)) {
        if (!inList) { html.push('<ul style="margin:0 0 10pt 24pt;padding:0;">'); inList = true; }
        html.push(`<li style="margin:0 0 4pt 0;">${inlineMd(escapeHtml(trimmed.replace(/^[-*•]\s+/, "")))}</li>`);
        continue;
      }
      if (inList) { html.push("</ul>"); inList = false; }
      // H1/H2 (# heading, ## heading)
      let m = /^#{1,3}\s+(.*)$/.exec(trimmed);
      if (m) { html.push(`<h2 style="font-size:14pt;margin:12pt 0 6pt 0;color:#0f172a;">${inlineMd(escapeHtml(m[1]))}</h2>`); continue; }
      html.push(`<p style="margin:0 0 8pt 0;">${inlineMd(escapeHtml(line))}</p>`);
    }
    if (inList) html.push("</ul>");

    function inlineMd(s) {
      // Bold **text** and italic *text*
      return s
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>');
    }

    const today = new Date().toISOString().slice(0, 10);
    const docHtml = `<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
<meta charset="utf-8">
<title>HRCPC Trust — Legal Draft</title>
<style>
  @page { size: A4; margin: 2.5cm 2.2cm; }
  body { font-family: "Times New Roman", Times, serif; font-size: 12pt; color: #111; line-height: 1.55; }
  h1 { font-size: 16pt; margin: 0 0 6pt 0; }
  .brand { color: #b45309; font-weight: bold; }
  .meta { color: #64748b; font-size: 10pt; margin-bottom: 12pt; }
  .disclaimer { border-left: 3pt solid #f59e0b; background: #fffbeb; padding: 8pt 10pt; margin-top: 18pt; font-size: 10pt; color: #78350f; }
</style>
</head>
<body>
  <h1><span class="brand">HRCPC Trust · </span>Legal Draft (Educational)</h1>
  <div class="meta">Generated ${today} · Human Rights &amp; Consumer Protection Cell Trust, Hyderabad</div>
  ${html.join("\n")}
  <div class="disclaimer">
    <strong>Disclaimer:</strong> This draft is an educational template generated with AI assistance. HRCPC Trust is a public-spirited NGO and NOT a law firm. Please consult your family advocate before filing or acting upon this document.
  </div>
</body></html>`;

    const blob = new Blob(['\ufeff', docHtml], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameSlug || 'legal-draft'}.doc`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Word document downloaded");
  };

  const fetchAuditLog = async () => {
    if (!ADMIN_TOKEN) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter.actor) params.set("actor", auditFilter.actor);
      if (auditFilter.action) params.set("action", auditFilter.action);
      if (auditFilter.target_type) params.set("target_type", auditFilter.target_type);
      params.set("limit", "200");
      const res = await axios.get(`${API}/admin/audit?${params.toString()}`);
      setAuditEvents(res.data?.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load audit log");
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchAdminDonations = async () => {
    if (!ADMIN_TOKEN) return;
    setAdminDonationLoading(true);
    try {
      const res = await axios.get(`${API}/admin/donations?limit=300`);
      setAdminDonations({ items: res.data?.items || [], total_amount: res.data?.total_amount || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load donations");
    } finally {
      setAdminDonationLoading(false);
    }
  };

  const submitAdminDonation = async () => {
    if (!adminDonationForm.donor_name.trim()) { toast.error("Donor name is required"); return; }
    const amt = parseFloat(adminDonationForm.amount);
    if (!(amt > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      await axios.post(`${API}/admin/donations`, {
        donor_name: adminDonationForm.donor_name.trim(),
        donor_city: adminDonationForm.donor_city.trim(),
        donor_email: adminDonationForm.donor_email.trim(),
        amount: amt,
        mode: adminDonationForm.mode,
        note: adminDonationForm.note.trim(),
      });
      toast.success("Donation logged to Donor Wall");
      setAdminDonationForm({ donor_name: "", donor_city: "", donor_email: "", amount: "", mode: "SBI Bank Transfer", note: "" });
      await fetchAdminDonations();
      // Refresh public wall too
      try {
        const donors = await axios.get(`${API}/donations/recent?limit=15`);
        setDonorWall(donors.data || { count: 0, month_count: 0, items: [] });
      } catch (err) { console.error('[addAdminDonation] public wall refresh failed:', err); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log donation");
    }
  };

  const toggleDonationVisibility = async (id, visible) => {
    try {
      await axios.patch(`${API}/admin/donations/${id}`, { visible });
      await fetchAdminDonations();
      const donors = await axios.get(`${API}/donations/recent?limit=15`);
      setDonorWall(donors.data || { count: 0, month_count: 0, items: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update visibility");
    }
  };

  const deleteAdminDonation = async (id) => {
    if (!window.confirm("Delete this donation entry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/donations/${id}`);
      toast.info("Donation entry removed");
      await fetchAdminDonations();
      const donors = await axios.get(`${API}/donations/recent?limit=15`);
      setDonorWall(donors.data || { count: 0, month_count: 0, items: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  // ============ Admin: Wins Wall ============
  const fetchAdminWins = async () => {
    if (!ADMIN_TOKEN) return;
    try {
      const res = await axios.get(`${API}/admin/wins`);
      setAdminWins({ items: res.data?.items || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load wins");
    }
  };
  const submitAdminWin = async () => {
    if (!adminWinForm.case_type.trim() || !adminWinForm.outcome.trim()) { toast.error("Case type and outcome are required"); return; }
    try {
      await axios.post(`${API}/admin/wins`, {
        focus_area: adminWinForm.focus_area,
        case_type: adminWinForm.case_type.trim(),
        outcome: adminWinForm.outcome.trim(),
        year: Number(adminWinForm.year) || new Date().getFullYear(),
        city: adminWinForm.city.trim(),
      });
      toast.success("Win added to public wall");
      setAdminWinForm({ focus_area: "consumer_protection", case_type: "", outcome: "", year: new Date().getFullYear(), city: "" });
      await fetchAdminWins();
      try {
        const pub = await axios.get(`${API}/wins?limit=20`);
        setPublicWins(pub.data || { count: 0, items: [] });
      } catch (err) { console.error('[addAdminWin] public wins refresh failed:', err); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add win");
    }
  };
  const toggleWinVisibility = async (id, visible) => {
    try {
      await axios.patch(`${API}/admin/wins/${id}`, { visible });
      await fetchAdminWins();
      const pub = await axios.get(`${API}/wins?limit=20`);
      setPublicWins(pub.data || { count: 0, items: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };
  const deleteAdminWin = async (id) => {
    if (!window.confirm("Delete this win entry?")) return;
    try {
      await axios.delete(`${API}/admin/wins/${id}`);
      toast.info("Win removed");
      await fetchAdminWins();
      const pub = await axios.get(`${API}/wins?limit=20`);
      setPublicWins(pub.data || { count: 0, items: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ============ Admin: Newsletter / Impact Digest ============
  const fetchDigestPreview = async () => {
    if (!ADMIN_TOKEN) return;
    try {
      const res = await axios.get(`${API}/admin/impact-digest/preview`);
      setDigestPreview(res.data?.digest || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load preview");
    }
  };
  const sendImpactDigest = async (dryRun) => {
    const list = digestRecipients.split(/[,\s]+/).map(s => s.trim()).filter(s => s.includes('@'));
    if (!dryRun && list.length === 0) { toast.error("Add at least one recipient email"); return; }
    setDigestSending(true);
    try {
      const res = await axios.post(`${API}/admin/impact-digest/send`, { recipients: list.length ? list : ['dryrun@example.com'], dry_run: !!dryRun });
      if (dryRun) toast.success(`Preview generated (HTML ${res.data.html_preview_length} chars)`);
      else toast.success(`Sent ${res.data.sent}${res.data.failed ? ` · Failed ${res.data.failed}` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setDigestSending(false);
    }
  };
  const loadDigestSegmentRecipients = async (segment) => {
    if (!ADMIN_TOKEN) return;
    try {
      const res = await axios.get(`${API}/admin/impact-digest/recipients`, { params: { segment } });
      const emails = res.data?.emails || [];
      setDigestSegmentCount(emails.length);
      setDigestRecipients(emails.join(", "));
      toast.success(`Loaded ${emails.length} recipients for segment "${segment}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load recipients");
    }
  };

  // ============ Admin: Advocate Weekly Digest ============
  const fetchAdvocateDigestList = async () => {
    if (!ADMIN_TOKEN) return;
    try {
      const res = await axios.get(`${API}/admin/advocate-digest/list`);
      setAdvocateDigestList({ advocates: res.data?.advocates || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load advocates");
    }
  };
  const previewAdvocateDigest = async (email) => {
    try {
      const res = await axios.get(`${API}/admin/advocate-digest/preview`, { params: { email } });
      setAdvocateDigestPreview(res.data?.digest || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    }
  };
  const sendAdvocateDigest = async (dryRun, targetEmails) => {
    setAdvocateDigestSending(true);
    try {
      const body = { dry_run: !!dryRun };
      if (targetEmails && targetEmails.length) body.advocate_emails = targetEmails;
      const res = await axios.post(`${API}/admin/advocate-digest/send`, body);
      const { sent, skipped, failed, dry_run } = res.data || {};
      if (dry_run) toast.success(`Dry-run: ${skipped} skipped (no cases), ${res.data.per_advocate.filter(x=>x.status==='dry_run').length} ready`);
      else toast.success(`Sent ${sent} · Skipped ${skipped}${failed ? ` · Failed ${failed}` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setAdvocateDigestSending(false);
    }
  };
  const fetchAdvocateDigestSchedule = async () => {
    if (!ADMIN_TOKEN) return;
    try {
      const res = await axios.get(`${API}/admin/advocate-digest/schedule`);
      setAdvocateDigestSchedule(res.data || null);
    } catch (err) {
      // silent — non-critical
    }
  };
  const toggleAdvocateDigestSchedule = async (enabled) => {
    try {
      await axios.patch(`${API}/admin/advocate-digest/schedule`, { enabled });
      toast.success(enabled ? "Auto-send enabled — every Friday 07:00 IST" : "Auto-send disabled");
      await fetchAdvocateDigestSchedule();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update schedule");
    }
  };

  // ============ Admin: Overdue Task Digest (Sprint 4.2) ============
  const [overdueDigestList, setOverdueDigestList] = useState({ items: [], targets_with_overdue: 0 });
  const [overdueDigestSchedule, setOverdueDigestSchedule] = useState(null);
  const [overdueDigestSending, setOverdueDigestSending] = useState(false);
  const fetchOverdueDigest = async () => {
    if (!ADMIN_TOKEN) return;
    try {
      const [listRes, schedRes] = await Promise.all([
        axios.get(`${API}/admin/overdue-digest/list`),
        axios.get(`${API}/admin/overdue-digest/schedule`),
      ]);
      setOverdueDigestList({ items: listRes.data?.items || [], targets_with_overdue: listRes.data?.targets_with_overdue || 0 });
      setOverdueDigestSchedule(schedRes.data || null);
    } catch (err) {
      // silent
    }
  };
  const toggleOverdueDigestSchedule = async (enabled) => {
    try {
      await axios.patch(`${API}/admin/overdue-digest/schedule`, { enabled });
      toast.success(enabled ? "Overdue digest enabled — every Monday 09:00 IST" : "Overdue digest disabled");
      await fetchOverdueDigest();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update schedule");
    }
  };
  const sendOverdueDigest = async (dryRun) => {
    setOverdueDigestSending(true);
    try {
      const res = await axios.post(`${API}/admin/overdue-digest/send`, { dry_run: !!dryRun });
      const { sent, skipped, failed, dry_run, previews } = res.data || {};
      if (dry_run) toast.success(`Dry-run: ${previews?.length || 0} would receive · ${skipped} skipped (no overdue)`);
      else toast.success(`Sent ${sent} · Skipped ${skipped}${failed ? ` · Failed ${failed}` : ''}`);
      await fetchOverdueDigest();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setOverdueDigestSending(false);
    }
  };

  // ============ Fee & Expense management (super-only) ============
  const EXPENSE_CATEGORY_LABELS = {
    court_fee: "Court Fee",
    certified_copies: "Certified Copies",
    service_charges: "Service Charges (per-day counsel)",
    small_inams: "Small Inams",
    commissions: "Commissions",
    filing_charges: "Filing Charges",
    incidental_travel: "Incidental — Travel",
    incidental_stationery: "Incidental — Stationery",
    misc: "Miscellaneous",
  };

  const openFinanceModal = async (c) => {
    setFinanceCase(c);
    setFeeAgreedForm({ total_fee: String(c.total_fee || ""), fee_notes: c.fee_notes || "" });
    setInstalmentForm({ due_date: "", amount: "", notes: "" });
    setExpenseForm({ date: new Date().toISOString().slice(0, 10), category: "court_fee", description: "", amount: "" });
    await refreshFinanceSummary(c.case_number);
  };

  const refreshFinanceSummary = async (caseNumber) => {
    if (!caseNumber) return;
    setFinanceLoading(true);
    try {
      const [sumRes, caseRes] = await Promise.all([
        axios.get(`${API}/admin/cases/${encodeURIComponent(caseNumber)}/finance-summary`),
        axios.get(`${API}/cases/${encodeURIComponent(caseNumber)}`),
      ]);
      setFinanceSummary(sumRes.data);
      setFinanceCase(caseRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load finance summary");
    } finally {
      setFinanceLoading(false);
    }
  };

  const saveFeeAgreed = async () => {
    if (!financeCase) return;
    const total = parseFloat(feeAgreedForm.total_fee);
    if (!(total >= 0)) { toast.error("Enter a valid total fee"); return; }
    try {
      await axios.patch(`${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/fee-agreed`, {
        total_fee: total, fee_notes: feeAgreedForm.fee_notes || "",
      });
      toast.success("Fee agreed updated");
      await refreshFinanceSummary(financeCase.case_number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const addInstalment = async () => {
    if (!financeCase) return;
    const amt = parseFloat(instalmentForm.amount);
    if (!(amt > 0)) { toast.error("Amount must be > 0"); return; }
    if (!instalmentForm.due_date) { toast.error("Due date required"); return; }
    try {
      await axios.post(`${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/fee-schedule`, {
        due_date: instalmentForm.due_date, amount: amt, notes: instalmentForm.notes || "",
      });
      setInstalmentForm({ due_date: "", amount: "", notes: "" });
      await refreshFinanceSummary(financeCase.case_number);
      toast.success("Instalment added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  const toggleInstalmentPaid = async (inst) => {
    if (!financeCase) return;
    const nextStatus = inst.status === "paid" ? "pending" : "paid";
    try {
      await axios.patch(
        `${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/fee-schedule/${inst.id}`,
        { status: nextStatus, paid_on: nextStatus === "paid" ? new Date().toISOString().slice(0, 10) : null },
      );
      await refreshFinanceSummary(financeCase.case_number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const deleteInstalment = async (inst) => {
    if (!financeCase) return;
    if (!window.confirm(`Delete instalment of ₹${inst.amount} due ${inst.due_date}?`)) return;
    try {
      await axios.delete(`${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/fee-schedule/${inst.id}`);
      await refreshFinanceSummary(financeCase.case_number);
      toast.success("Instalment removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const addExpense = async () => {
    if (!financeCase) return;
    const amt = parseFloat(expenseForm.amount);
    if (!(amt > 0)) { toast.error("Amount must be > 0"); return; }
    if (!expenseForm.description.trim()) { toast.error("Description required"); return; }
    try {
      await axios.post(`${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/expenses`, {
        date: expenseForm.date, category: expenseForm.category,
        description: expenseForm.description.trim(), amount: amt,
      });
      setExpenseForm({ date: new Date().toISOString().slice(0, 10), category: expenseForm.category, description: "", amount: "" });
      await refreshFinanceSummary(financeCase.case_number);
      toast.success("Expense added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  const deleteExpense = async (exp) => {
    if (!financeCase) return;
    if (!window.confirm(`Delete expense of ₹${exp.amount}?`)) return;
    try {
      await axios.delete(`${API}/admin/cases/${encodeURIComponent(financeCase.case_number)}/expenses/${exp.id}`);
      await refreshFinanceSummary(financeCase.case_number);
      toast.success("Expense removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- Advocate email-OTP login ----
  const requestAdvocateOtp = async () => {
    const email = advocateLoginForm.email.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid advocate email.");
      return;
    }
    setAdvocateLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/advocate/otp-send`, { email });
      const debugOtp = res?.data?.debug_otp;
      setAdvocateLoginForm(f => ({ ...f, step: "otp", otp: debugOtp || "" }));
      if (debugOtp) {
        toast.success(`[DEBUG MODE] OTP: ${debugOtp} — auto-filled. Disable DEBUG_SHOW_OTP env var in production.`, { duration: 10000 });
      } else {
        toast.success("If this is a registered advocate email, a login code has been sent.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send login code.");
    } finally {
      setAdvocateLoginLoading(false);
    }
  };

  const submitAdvocateLogin = async () => {
    const email = advocateLoginForm.email.trim().toLowerCase();
    const otp = (advocateLoginForm.otp || "").trim();
    if (!otp || otp.length !== 6) {
      toast.error("Please enter the 6-digit code from your email.");
      return;
    }
    setAdvocateLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/advocate/otp-verify`, { email, otp });
      if (window.__hrcpcSetAdvocateToken) window.__hrcpcSetAdvocateToken(res.data.token);
      setCurrentUser({
        role: "advocate",
        data: {
          name: res.data.name || "Advocate",
          email: res.data.email,
          is_super: !!res.data.is_super,
        },
      });
      setAdvocateLoginOpen(false);
      setAdvocateLoginForm({ email: "", otp: "", step: "email" });
      setActiveTab("advocate-panel");
      await fetchData();
      toast.success(`Welcome, ${res.data.name || "Advocate"}${res.data.is_super ? " (Super)" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setAdvocateLoginLoading(false);
    }
  };

  // React to 401s from the axios interceptor — clear session UI too.
  useEffect(() => {
    const handler = () => {
      setCurrentUser(null);
      setActiveTab("public");
      setClientsList([]); setSupportTicketsList([]); setVolunteersList([]); setCasesList([]);
    };
    window.addEventListener("hrcpc:session-expired", handler);
    return () => window.removeEventListener("hrcpc:session-expired", handler);
  }, []);

  const submitSupportTicket = async () => {
    if (!supportTicketForm.name.trim() || !supportTicketForm.mobile.trim() || !supportTicketForm.message.trim()) {
      toast.error("Please fill name, mobile, and message.");
      return;
    }
    if (supportTicketForm.mobile.trim().length < 10) {
      toast.error("Please enter a valid 10-digit mobile.");
      return;
    }
    // Soft guidance: recommend registering first, but don't block.
    askRegisterGuide(async () => {
      setSupportTicketSaving(true);
      try {
        const res = await axios.post(`${API}/support/tickets`, supportTicketForm);
        setSupportTicketSubmitted(res.data);
        setSupportTicketForm(emptySupportForm);
        await fetchData();
        toast.success("Query logged. Our team will respond by email/phone.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to submit query. Please try again.");
      } finally {
        setSupportTicketSaving(false);
      }
    }, "Ask HRCPC Legal Assistant");
  };

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    if (!file.type.startsWith("image/")) {
      reject(new Error("Only image files (JPG/PNG/WebP) are allowed."));
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      reject(new Error(`${file.name} exceeds 3 MB size limit.`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });

  // Client-side photo compression using Canvas — targets ~200 KB for passport photos
  const compressImageToTarget = (file, { maxDim = 800, targetKB = 200 } = {}) => new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Please choose a JPG/PNG/WebP image."));
      return;
    }
    const img = new Image();
    // Feb 14, 2026 (MEDIUM code-review fix): keep the object URL so we can
    // revoke it after the browser finishes decoding — was leaking one blob
    // buffer per uploaded volunteer / KYC document.
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => { try { URL.revokeObjectURL(objectUrl); } catch (err) { /* noop */ } };
    img.onload = () => {
      try {
        // Scale down keeping aspect ratio if needed
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);

        // Iterate quality down until size ≤ target (or floor quality reached)
        let quality = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        let approxKB = Math.round((dataUrl.length * 3) / 4 / 1024);
        while (approxKB > targetKB && quality > 0.35) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
          approxKB = Math.round((dataUrl.length * 3) / 4 / 1024);
        }
        resolve({ dataUrl, kb: approxKB, quality: Math.round(quality * 100) });
      } finally { cleanup(); }
    };
    img.onerror = () => { cleanup(); reject(new Error("Unable to read image.")); };
    img.src = objectUrl;
  });

  const handleVolunteerFileChange = async (docKey, file) => {
    if (!file) return;
    try {
      let dataUrl;
      let kb;
      // Passport photo → tight compression (small target). All ID docs → gentler compression (readability preserved).
      if (docKey === "photograph") {
        ({ dataUrl, kb } = await compressImageToTarget(file, { maxDim: 800,  targetKB: 200 }));
      } else {
        ({ dataUrl, kb } = await compressImageToTarget(file, { maxDim: 1600, targetKB: 500 }));
      }
      // Check combined total after adding this one — reject early if we'd blow past ~9 MB base64
      const others = Object.entries(volunteerForm.documents)
        .filter(([k, v]) => k !== docKey && v)
        .reduce((sum, [, v]) => sum + v.length, 0);
      const totalKB = Math.round((others + dataUrl.length) / 1024);
      if (totalKB > 12000) {
        toast.error(`Combined document size would exceed 12 MB (currently ${totalKB} KB). Please retake at lower resolution.`);
        return;
      }
      setVolunteerForm(prev => ({ ...prev, documents: { ...prev.documents, [docKey]: dataUrl } }));
      toast.success(`${docKey.replace(/_/g,' ')} compressed to ~${kb} KB · total ${totalKB} KB / 12 MB`);
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    }
  };

  const submitVolunteer = async () => {
    const p = volunteerForm.personal;
    const pr = volunteerForm.professional;
    const docs = volunteerForm.documents;
    const u = volunteerForm.undertaking;

    // Personal validation
    const personalReq = ["full_name", "fathers_husbands_name", "dob", "gender", "mobile", "email", "address", "city", "state", "pincode"];
    for (const f of personalReq) {
      if (!String(p[f] || "").trim()) { toast.error(`Please fill: ${f.replace(/_/g,' ')}`); return; }
    }
    if (String(p.mobile).length !== 10) { toast.error("Mobile must be exactly 10 digits."); return; }
    if (String(p.pincode).length !== 6) { toast.error("Pincode must be exactly 6 digits."); return; }
    if (!p.email.includes("@")) { toast.error("Please enter a valid email address."); return; }

    // Professional validation
    for (const f of ["qualification", "occupation", "help_area"]) {
      if (!String(pr[f] || "").trim()) { toast.error(`Please fill: ${f.replace(/_/g,' ')}`); return; }
    }

    // Documents validation (5 mandatory)
    const requiredDocs = ["photograph", "aadhaar_front", "aadhaar_back", "pan_card", "bank_proof"];
    for (const d of requiredDocs) {
      if (!docs[d]) { toast.error(`Please upload: ${d.replace(/_/g,' ')}`); return; }
    }

    // Undertaking validation
    if (!u.accepted) { toast.error("You must tick the checkbox to accept the Undertaking."); return; }
    if (!String(u.signed_name || "").trim()) { toast.error("Please type your Full Name as digital signature."); return; }
    if (String(u.signed_name).trim().toLowerCase() !== String(p.full_name).trim().toLowerCase()) {
      toast.error("Typed signature must EXACTLY match your Full Name above."); return;
    }

    setVolunteerSaving(true);
    try {
      const res = await axios.post(`${API}/volunteers`, volunteerForm);
      setVolunteerSubmitted(res.data);
      setVolunteerForm(emptyVolunteerForm);
      await fetchData();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setVolunteerSaving(false);
    }
  };

  const saveTrustLegal = async () => {
    setTrustLegalSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/trust-legal`, trustLegalEditForm);
      setSiteSettings(prev => ({ ...prev, trust_legal: res.data.trust_legal }));
      toast.success("Trust legal details saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save trust details.");
    } finally {
      setTrustLegalSaving(false);
    }
  };

  const saveImpactStats = async () => {
    setImpactStatsSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/impact-stats`, impactStatsForm);
      setSiteSettings(prev => ({ ...prev, impact_stats: res.data.impact_stats }));
      toast.success("Impact stats saved. Home page will refresh on next visit.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save impact stats.");
    } finally {
      setImpactStatsSaving(false);
    }
  };

  const saveFounderVideo = async () => {
    setFounderVideoSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/founder-video`, { url: founderVideoForm });
      setSiteSettings(prev => ({ ...prev, founder_video_url: res.data.founder_video_url }));
      toast.success(res.data.founder_video_url ? "Founder video URL saved." : "Founder video URL cleared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save video URL.");
    } finally {
      setFounderVideoSaving(false);
    }
  };

  const saveAnnouncement = async () => {
    setAnnouncementSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/announcement`, announcementForm);
      setSiteSettings(prev => ({ ...prev, announcement: res.data.announcement }));
      // Clear dismissed flag when the text changes so all users see the new banner
      try { sessionStorage.removeItem("hrcpc_ann_dismissed"); } catch (_) {}
      setAnnouncementDismissed("");
      toast.success(res.data.announcement.enabled ? "Announcement banner is live." : "Announcement banner turned off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save announcement.");
    } finally {
      setAnnouncementSaving(false);
    }
  };


  // Handle KYC Self-Registration Submit via API
  const handleKycSubmit = async (e) => {
    e.preventDefault();
    if (!kycForm.full_name || !kycForm.fathers_husbands_name || !kycForm.mobile || !kycForm.aadhaar_last4 || !kycForm.consent) {
      toast.error("Please fill all required fields (including Father's / Husband's Name) and provide consent.");
      return;
    }
    if (!kycForm.dpdp_consent) {
      toast.error("Please tick DPDP consent to complete self-registration (mandatory under DPDP Act).");
      return;
    }
    try {
      const payload = {
        full_name: kycForm.full_name,
        fathers_husbands_name: kycForm.fathers_husbands_name,
        mobile: kycForm.mobile,
        email: kycForm.email || "user@gmail.com",
        aadhaar_last4: kycForm.aadhaar_last4,
        address: kycForm.address,
        city: kycForm.city,
        state: kycForm.state,
        pincode: kycForm.pincode,
        purpose: kycForm.purpose || "Family Legal Advisor member registration",
        preferred_language: kycForm.preferred_language,
        // Phase-II fields (all optional except dpdp)
        dob: kycForm.dob || null,
        gender: kycForm.gender || null,
        id_type: kycForm.id_type || null,
        id_number_last4: kycForm.id_number_last4 || null,
        photo_data_url: kycForm.photo_data_url || null,
        id_front_data_url: kycForm.id_front_data_url || null,
        id_back_data_url: kycForm.id_back_data_url || null,
        bank_ac_name: kycForm.bank_ac_name || null,
        bank_ac_number_last4: kycForm.bank_ac_number_last4 || null,
        bank_ifsc: kycForm.bank_ifsc || null,
        bank_upi: kycForm.bank_upi || null,
        dpdp_consent: !!kycForm.dpdp_consent,
        registration_source: "self",
        // Phase-II Sprint 2: attach the mobile-verified ticket
        kyc_ticket: kycTicket,
        // Referral nudge (Feb 12, 2026): captured from ?ref= on the landing URL
        referred_by_code: refCode || null,
      };
      await axios.post(`${API}/clients/kyc`, payload);
      setKycSubmitted(true);
      await fetchData();
      toast.success("KYC application submitted successfully to backend!");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "KYC submission failed.");
    }
  };

  // Handle Payment simulation via backend
  const triggerRazorpayPayment = (target) => {
    setPaymentTarget(target);
    setPaymentSuccess(false);
    setPaymentMethodTab('razorpay');
    setPaymentModalOpen(true);
  };

  const confirmRazorpayPayment = async () => {
    try {
      await axios.post(`${API}/payments/razorpay`, {
        case_id: paymentTarget.case_id || null,
        amount: paymentTarget.amount,
        title: paymentTarget.title,
        mode: "Razorpay UPI"
      });
      setPaymentSuccess(true);
      await fetchData();
      toast.success("Payment of ₹" + paymentTarget.amount + " processed & recorded in MongoDB!");
      setTimeout(() => {
        setPaymentModalOpen(false);
      }, 1800);
    } catch (err) {
      console.error(err);
      toast.error("Payment processing failed.");
    }
  };

  // ===== Print-friendly standalone pages (bypass main layout) =====
  if (activeTab === 'volunteer-id' || activeTab === 'volunteer-undertaking') {
    if (!volunteerPrintData) {
      return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600 text-sm">Loading document…</div>;
    }
    if (volunteerPrintData.error || (volunteerPrintData.status !== 'Approved' && activeTab === 'volunteer-id')) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-700 p-6 text-center space-y-2">
          <AlertCircle className="w-10 h-10 text-red-600" />
          <h1 className="text-xl font-bold">Document Not Available</h1>
          <p className="text-sm max-w-md">
            {volunteerPrintData.error
              ? 'This volunteer reference could not be found. Please check the URL.'
              : `ID card is only available for Approved applications. Current status: ${volunteerPrintData.status}.`}
          </p>
          <button onClick={() => { window.location.href = '/'; }} className="mt-4 bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold">Back to Home</button>
        </div>
      );
    }
    const v = volunteerPrintData;
    const p = v.personal || {};
    const pr = v.professional || {};
    const u = v.undertaking || {};

    if (activeTab === 'volunteer-id') {
      return (
        <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center print:bg-white print:p-0" data-testid="volunteer-id-page">
          <div className="max-w-2xl w-full flex justify-between items-center mb-4 print:hidden">
            <button onClick={() => { window.location.href = '/'; }} className="text-sm text-slate-600 hover:text-slate-900">← Back to Home</button>
            <button
              onClick={() => window.print()}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-5 py-2 rounded-lg text-sm shadow flex items-center gap-2"
              data-testid="print-id-card-btn"
            >
              <Download className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>

          <div className="w-[540px] max-w-full bg-gradient-to-br from-black via-slate-900 to-black text-white rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-500 print:shadow-none print:border-4 print:break-inside-avoid" data-testid="volunteer-id-card">
            <div className="bg-orange-600 px-5 py-3 flex items-center justify-between border-b-4 border-amber-400">
              <div className="flex items-center gap-2">
                <Scale className="w-6 h-6 text-amber-300" />
                <div>
                  <div className="font-black text-sm leading-tight">HRCPC LEGAL CELL</div>
                  <div className="text-[9px] uppercase tracking-widest text-amber-100">Trust · Hyderabad</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-widest text-amber-100">Volunteer ID Card</div>
                <div className="font-mono font-bold text-xs">{v.code}</div>
              </div>
            </div>

            <div className="p-5 flex gap-5">
              <div className="w-28 h-32 border-2 border-amber-400 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0">
                {v.photograph ? (
                  <img src={v.photograph} alt={v.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">No photo</div>
                )}
              </div>
              <div className="flex-1 space-y-1 text-sm">
                <div className="text-[9px] uppercase tracking-widest text-amber-300">Volunteer Name</div>
                <div className="font-bold text-lg leading-snug">{v.name}</div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-2 text-[11px]">
                  <div><span className="text-slate-400">Role:</span> <span className="font-semibold">{pr.help_area || '—'}</span></div>
                  <div><span className="text-slate-400">Gender:</span> <span className="font-semibold">{p.gender || '—'}</span></div>
                  <div><span className="text-slate-400">City:</span> <span className="font-semibold">{p.city || '—'}</span></div>
                  <div><span className="text-slate-400">Mobile:</span> <span className="font-mono">+91 {p.mobile || '—'}</span></div>
                </div>

                <div className="mt-3 pt-2 border-t border-slate-700 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-amber-300">Approved On</div>
                    <div className="font-mono font-semibold">{(v.approved_at || '').split(' ')[0] || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-amber-300">Valid Until</div>
                    <div className="font-mono font-semibold">{v.valid_until || '—'}</div>
                  </div>
                </div>
              </div>

              {/* Verification QR — scan to open this same ID card page and confirm authenticity */}
              <div className="flex-shrink-0 flex flex-col items-center justify-start pt-1" data-testid="id-card-qr">
                <div className="bg-white p-1.5 rounded-md">
                  <QRCodeSVG
                    value={`${window.location.origin}/volunteer-id/${v.code}`}
                    size={72}
                    bgColor="#ffffff"
                    fgColor="#0a0a0a"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <div className="text-[8px] uppercase tracking-widest text-amber-300 mt-1 text-center leading-tight">Scan to<br/>Verify</div>
              </div>
            </div>

            <div className="bg-amber-500 text-slate-950 px-5 py-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
              <span>Honorary Volunteer · Not an Employee / Agent</span>
              <span>support@hrcpc.org · 08455 296263</span>
            </div>

            <div className="bg-black px-5 py-3 text-[10px] text-slate-300 leading-relaxed border-t border-slate-800">
              <strong className="text-amber-300">DECLARATION:</strong> The holder of this card is an honorary volunteer of HRCPC Trust. They are NOT authorised to collect money, sign on behalf of the Trust, or represent HRCPC as an agent / employee. Report misuse to support@hrcpc.org.
            </div>
          </div>

          <p className="text-[10px] text-slate-500 italic mt-4 print:hidden max-w-md text-center">
            Please save this page as PDF from your browser's Print dialog (Ctrl/Cmd + P → "Save as PDF").
          </p>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center print:bg-white print:p-0" data-testid="volunteer-undertaking-page">
        <div className="max-w-3xl w-full flex justify-between items-center mb-4 print:hidden">
          <button onClick={() => { window.location.href = '/'; }} className="text-sm text-slate-600 hover:text-slate-900">← Back to Home</button>
          <button onClick={() => window.print()} className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-5 py-2 rounded-lg text-sm shadow flex items-center gap-2" data-testid="print-undertaking-btn">
            <Download className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>

        <div className="max-w-3xl w-full bg-white shadow-lg print:shadow-none border border-slate-300 print:border-0 rounded-lg p-10 print:p-8 text-slate-900 space-y-4" data-testid="undertaking-doc">
          <div className="text-center border-b-2 border-black pb-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-600">Human Rights &amp; Consumer Protection Cell Trust · Hyderabad</div>
            <h1 className="text-2xl font-black mt-1">VOLUNTEER UNDERTAKING</h1>
            <div className="text-[11px] mt-1">Reference: <span className="font-mono font-bold">{v.code}</span> · Status: <span className="font-bold">{v.status}</span></div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-[13px] pt-1">
            <div><strong>Name:</strong> {p.full_name}</div>
            <div><strong>Father's/Husband's Name:</strong> {p.fathers_husbands_name}</div>
            <div><strong>Date of Birth:</strong> {p.dob}</div>
            <div><strong>Gender:</strong> {p.gender}</div>
            <div><strong>Mobile:</strong> +91 {p.mobile}</div>
            <div><strong>Email:</strong> {p.email}</div>
            <div className="col-span-2"><strong>Address:</strong> {p.address}, {p.city}, {p.state} — {p.pincode}</div>
            <div><strong>Qualification:</strong> {pr.qualification}</div>
            <div><strong>Occupation:</strong> {pr.occupation}</div>
            <div className="col-span-2"><strong>Volunteer Role:</strong> {pr.help_area}</div>
          </div>

          <div className="text-[13px] leading-relaxed pt-4 border-t border-slate-300">
            <p className="font-bold mb-2">I hereby declare and undertake that:</p>
            <ol className="list-decimal list-inside space-y-1.5 pl-1">
              <li>I have <strong>no pending criminal cases</strong> against me and I have never been convicted of any criminal offence.</li>
              <li>I shall not engage in any form of <strong>blackmailing, extortion, threat, intimidation, or illegal recovery</strong> in the name of HRCPC Trust or otherwise.</li>
              <li>I shall <strong>not collect any money or donations</strong> from any person in the name of HRCPC Trust without written authorisation.</li>
              <li>I shall not use the name, logo, or reputation of HRCPC Trust for any personal, commercial, or illegal purpose.</li>
              <li>I shall maintain complete <strong>confidentiality</strong> of all information received during my association.</li>
              <li>I understand that my association with HRCPC Trust is <strong>purely voluntary and honorary</strong>. I am not an employee, agent, or authorised representative of the Trust.</li>
              <li>HRCPC Trust reserves the right to <strong>terminate my association immediately without assigning any reason</strong> if any of the above conditions are violated.</li>
              <li>All information and documents submitted by me are <strong>true and correct</strong> to the best of my knowledge.</li>
            </ol>
            <p className="italic mt-3">I am signing this undertaking voluntarily.</p>
          </div>

          <div className="grid grid-cols-2 gap-8 pt-8 border-t border-slate-300 mt-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Digital Signature</div>
              <div className="text-xl italic font-bold border-b border-slate-800 pb-1 mb-1" style={{ fontFamily: 'cursive' }}>{u.signed_name || '—'}</div>
              <div className="text-[10px] text-slate-500">(Typed as digital signature by the applicant)</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Date &amp; Timestamp</div>
              <div className="font-mono font-bold border-b border-slate-800 pb-1 mb-1">{fmtDateTime(u.signed_at || v.created_at) || '—'}</div>
              <div className="text-[10px] text-slate-500">Auto-captured at submission</div>
            </div>
          </div>

          <div className="text-[10px] text-slate-500 italic border-t border-slate-200 pt-3">
            This is a computer-generated record submitted through the HRCPC Trust volunteer registration portal at hrcpc.org.
            For verification, contact support@hrcpc.org quoting reference <strong>{v.code}</strong>.
          </div>
        </div>

        <p className="text-[10px] text-slate-500 italic mt-4 print:hidden max-w-md text-center">
          Save this page as PDF from your browser's Print dialog (Ctrl/Cmd + P → "Save as PDF").
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col selection:bg-orange-600 selection:text-white">
      
      {/* ANNOUNCEMENT BANNER — editable from Admin, dismissible per session */}
      {(() => {
        const ann = siteSettings.announcement;
        if (!ann || !ann.enabled || !ann.text) return null;
        const dismissKey = `${ann.text}|${ann.link_url}`;
        if (announcementDismissed === dismissKey) return null;
        const palette = {
          info:    "bg-slate-900 text-white border-orange-500",
          success: "bg-emerald-800 text-white border-emerald-400",
          warning: "bg-amber-500 text-slate-950 border-amber-700",
          urgent:  "bg-red-700  text-white border-red-400",
        }[ann.level || "info"] || "bg-slate-900 text-white border-orange-500";
        return (
          <div className={`w-full ${palette} border-b-2`} data-testid="announcement-banner">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-col md:flex-row items-center gap-2 md:gap-4 text-sm">
              <div className="flex items-center gap-2 flex-1">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="font-semibold" data-testid="announcement-text">{ann.text}</span>
                {ann.link_url && ann.link_label && (
                  <a
                    href={ann.link_url}
                    target={ann.link_url.startsWith("/") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    className="underline font-bold hover:opacity-80 whitespace-nowrap"
                    data-testid="announcement-link"
                  >
                    {ann.link_label} →
                  </a>
                )}
              </div>
              <button
                onClick={() => {
                  try { sessionStorage.setItem("hrcpc_ann_dismissed", dismissKey); } catch (_) {}
                  setAnnouncementDismissed(dismissKey);
                }}
                className="text-xs font-bold opacity-80 hover:opacity-100 border border-current rounded px-2 py-0.5"
                aria-label="Dismiss announcement"
                data-testid="announcement-dismiss"
              >
                ✕ Dismiss
              </button>
            </div>
          </div>
        );
      })()}

      {/* TOP HEADER / NGO BANNER */}
      <header className="bg-black text-white border-b border-slate-800 sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          
          {/* Logo & NGO Title */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActiveTab("public")} data-testid="logo-header-btn">
            <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center shadow-inner border border-orange-400">
              <Scale className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-bold text-lg tracking-tight text-white">HRCPC Legal Cell</span>
                <span className="text-xs bg-amber-500 text-slate-950 font-bold px-1.5 py-0.5 rounded">TRUST HYDERABAD</span>
              </div>
              <p className="text-xs text-slate-400">Human Rights & Consumer Protection Cell (hrcpc.org)</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-3">
            <button 
              onClick={() => setActiveTab("public")}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'public' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-public-home"
            >
              Home & Help Hub
            </button>
            <button 
              onClick={() => setActiveTab("ai-hub")}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1.5 transition ${activeTab === 'ai-hub' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-ai-hub"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>AI Legal Assistant</span>
            </button>
            <button
              onClick={() => setActiveTab("voice-grievance")}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-1.5 transition ${activeTab === 'voice-grievance' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-voice-grievance"
            >
              <PhoneCall className="w-4 h-4 text-emerald-300" />
              <span>Voice Grievance</span>
            </button>
            <button 
              onClick={() => setActiveTab("templates")}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'templates' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-templates"
            >
              Templates &amp; Study
            </button>
            <button 
              onClick={() => setActiveTab("kyc")}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'kyc' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-kyc"
            >
              Member KYC / Register
            </button>
            <button
              onClick={() => setActiveTab("support-pay")}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'support-pay' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'}`}
              data-testid="nav-support-pay"
            >
              Support / Pay
            </button>
            <button 
              onClick={() => setActiveTab("donate")}
              className={`px-3 py-2 rounded-lg text-sm font-medium text-amber-300 hover:bg-slate-800 transition flex items-center space-x-1`}
              data-testid="nav-donate"
            >
              <Heart className="w-4 h-4 text-rose-400" />
              <span>Support NGO</span>
            </button>
            <button
              onClick={() => setActiveTab("volunteer-register")}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-700 hover:bg-emerald-600 text-white transition flex items-center space-x-1.5 shadow"
              data-testid="nav-volunteer-btn"
            >
              <Users className="w-4 h-4" />
              <span>Volunteer</span>
            </button>
          </nav>

          {/* Role Access / Portal Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {currentUser ? (
              <div className="flex items-center space-x-3 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">
                <span className="text-xs text-orange-300 font-semibold uppercase">{currentUser.role}</span>
                <span className="text-sm font-medium text-white">{currentUser.data.full_name || currentUser.data.name || "User"}</span>
                <button 
                  onClick={() => { if (currentUser?.role === 'admin') { logoutAdmin(); } else if (currentUser?.role === 'advocate') { logoutAdvocate(); } else { logoutClient(); } }}
                  className="text-slate-400 hover:text-rose-400 p-1"
                  title="Logout"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setActiveTab("client-portal")}
                  className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow transition flex items-center space-x-1.5"
                  data-testid="header-client-login-btn"
                >
                  <Lock className="w-4 h-4" />
                  <span>Member OTP Login</span>
                </button>
                <div className="flex items-center space-x-1 border-l border-slate-700 pl-2">
                  <button 
                    onClick={() => setAdminLoginOpen(true)}
                    className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2.5 py-1.5 rounded border border-slate-700 transition"
                    data-testid="quick-admin-login"
                  >
                    Admin
                  </button>
                  <button 
                    onClick={() => setAdvocateLoginOpen(true)}
                    className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2.5 py-1.5 rounded border border-slate-700 transition"
                    data-testid="quick-advocate-login"
                  >
                    Advocate
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-300 hover:text-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-slate-800 px-4 pt-3 pb-5 space-y-2">
            <button onClick={() => { setActiveTab("public"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">Home & Help Hub</button>
            <button onClick={() => { setActiveTab("ai-hub"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">AI Legal Assistant</button>
            <button onClick={() => { setActiveTab("voice-grievance"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">Voice Grievance Recorder</button>
            <button onClick={() => { setActiveTab("templates"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">Templates &amp; Study</button>
            <button onClick={() => { setActiveTab("kyc"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">Member KYC / Register</button>
            <button onClick={() => { setActiveTab("support-pay"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded text-slate-200 hover:bg-slate-800">Support / Pay HRCPC</button>
            <button onClick={() => { setActiveTab("client-portal"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded bg-orange-600 text-white font-semibold">Member Portal OTP Login</button>
            <button onClick={() => { setActiveTab("volunteer-register"); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded bg-emerald-700 text-white font-semibold" data-testid="mobile-nav-volunteer-btn">Volunteer with HRCPC</button>
            <div className="pt-2 border-t border-slate-800 flex space-x-2">
              <button onClick={() => { setCurrentUser({ role: 'admin', data: { name: 'Admin' } }); setActiveTab("admin"); setMobileMenuOpen(false); }} className="flex-1 text-xs bg-slate-800 text-slate-300 py-2 rounded">Admin Demo</button>
              <button onClick={() => { setAdvocateLoginOpen(true); setMobileMenuOpen(false); }} className="flex-1 text-xs bg-slate-800 text-slate-300 py-2 rounded" data-testid="mobile-advocate-login-btn">Advocate Login</button>
            </div>
          </div>
        )}
      </header>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1">
        
        {/* 1. PUBLIC / HOME & LEGAL HELP HUB */}
        {activeTab === "public" && (
          <div className="space-y-12 pb-16">
            
            {/* Hero Section — with rotating Golden Dharma Chakra hovering just above the "A" of Awareness */}
            <div className="relative bg-gradient-to-r from-black via-slate-900 to-black text-white py-16 px-4 sm:px-6 lg:px-8 border-b border-orange-500/40 overflow-hidden">
              {/* Chakra — mobile: small, centred above the badge, subtle */}
              <div className="lg:hidden flex justify-center mb-2 opacity-95">
                <DharmaChakra size={130} />
              </div>

              <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
                <div className="inline-flex items-center space-x-2 bg-black/60 border border-orange-500/40 px-3.5 py-1.5 rounded-full text-xs font-semibold text-amber-300">
                  <Shield className="w-4 h-4 text-amber-300" />
                  <span>Human Rights & Consumer Protection Cell Trust • Hyderabad</span>
                </div>
                {/* Desktop: wrap H1 in a relative inline-block so we can hang the
                    Sudarshana Chakra just above and to the left of the leading "A". */}
                <div className="relative inline-block max-w-full">
                  <div
                    aria-hidden="true"
                    className="hidden lg:block absolute pointer-events-none opacity-95"
                    style={{ left: "-140px", top: "-140px" }}
                  >
                    <DharmaChakra size={170} />
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
                    Awareness, Advocacy & Citizen Support
                  </h1>
                </div>
                <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-light">
                  A public-spirited NGO creating awareness on human rights, consumer protection, RTI, <strong className="text-emerald-300">environmental protection</strong> and <strong className="text-amber-300">medico-legal matters (medical negligence)</strong> — connecting citizens with independent panel advocates who take up matters on a pro-bono / concessional basis. <strong className="text-orange-300">Not a law firm.</strong>
                </p>
                
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <button 
                    onClick={() => setActiveTab("client-portal")}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-7 py-3.5 rounded-xl shadow-lg transition flex items-center space-x-2 text-base"
                    data-testid="hero-client-portal-btn"
                  >
                    <Lock className="w-5 h-5" />
                    <span>Member Portal (OTP Login)</span>
                  </button>
                  <button 
                    onClick={() => setActiveTab("ai-hub")}
                    className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-7 py-3.5 rounded-xl shadow-lg transition flex items-center space-x-2 text-base"
                    data-testid="hero-ai-hub-btn"
                  >
                    <Sparkles className="w-5 h-5 text-amber-300" />
                    <span>Ask AI Legal Assistant</span>
                  </button>
                  <button 
                    onClick={() => setActiveTab("kyc")}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold px-6 py-3.5 rounded-xl transition flex items-center space-x-2 text-base"
                    data-testid="hero-kyc-btn"
                  >
                    <UserCheck className="w-5 h-5 text-orange-400" />
                    <span>Register / Family Advisor KYC</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Donor Recognition Wall — hidden per user request (Feb 12, 2026). Backend endpoints + admin CRUD remain live. */}
            {false && donorWall.items && donorWall.items.length > 0 && (() => {
              const cur = donorWall.items[donorIndex % donorWall.items.length];
              return (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6" data-testid="donor-wall-strip">
                  <div className="bg-gradient-to-r from-rose-50 via-amber-50 to-rose-50 border border-rose-200 rounded-2xl shadow-sm px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 shrink-0 bg-rose-600 text-white rounded-full flex items-center justify-center shadow">
                        <Heart className="w-5 h-5" />
                      </div>
                      <div key={cur.id} className="min-w-0 animate-fade-in">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Recent Supporter</div>
                        <div className="text-sm sm:text-base font-semibold text-slate-900 sm:truncate leading-snug" data-testid="donor-wall-current">
                          <span className="font-mono">{cur.display_name}</span>
                          {cur.city && <span className="text-slate-600 font-normal"> from {cur.city}</span>}
                          <span className="text-slate-500 font-normal"> just contributed </span>
                          <span className="text-emerald-700 font-bold">₹{Number(cur.amount).toLocaleString()}</span>
                          {cur.when && <span className="text-slate-500 font-normal"> · {cur.when}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="hidden sm:flex flex-col items-end text-right">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">This month</span>
                        <span className="text-sm font-extrabold text-slate-900" data-testid="donor-wall-month-count">
                          {donorWall.month_count} supporter{donorWall.month_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => setActiveTab('support-pay')}
                        className="bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs px-4 py-2 rounded-lg shadow transition"
                        data-testid="donor-wall-cta"
                      >
                        Join Them
                      </button>
                    </div>
                  </div>
                  {/* Dot indicators */}
                  {donorWall.items.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-2" data-testid="donor-wall-dots">
                      {donorWall.items.slice(0, 8).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setDonorIndex(i)}
                          aria-label={`Show donor ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all ${i === (donorIndex % donorWall.items.length) ? 'w-6 bg-rose-600' : 'w-1.5 bg-rose-200 hover:bg-rose-300'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Public Wins Wall — hidden per user request (Feb 12, 2026). Backend + admin Wins CRUD remain live. */}
            {false && publicWins.items && publicWins.items.length > 0 && (() => {
              const cur = publicWins.items[publicWinsIdx % publicWins.items.length];
              const meta = FOCUS_AREA_META[cur.focus_area] || FOCUS_AREA_META.other;
              return (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="wins-wall-strip">
                  <div className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border border-emerald-200 rounded-2xl shadow-sm px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center gap-3">
                    <div className="w-10 h-10 shrink-0 bg-emerald-600 text-white rounded-full flex items-center justify-center shadow">
                      <Trophy className="w-5 h-5" />
                    </div>
                    <div key={cur.id} className="flex-1 min-w-0 animate-fade-in">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Recent Win</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                        {cur.year && <span className="text-[10px] font-semibold text-slate-500">{cur.year}</span>}
                        {cur.city && <span className="text-[10px] font-semibold text-slate-500">· {cur.city}</span>}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 leading-snug" data-testid="wins-current-outcome">
                        <span className="font-bold text-emerald-800">{cur.case_type}:</span> {cur.outcome}
                      </div>
                    </div>
                    <div className="hidden sm:block text-right shrink-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Total Wins</div>
                      <div className="text-lg font-extrabold text-slate-900" data-testid="wins-total">{publicWins.count}</div>
                    </div>
                  </div>
                  {publicWins.items.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-2">
                      {publicWins.items.slice(0, 8).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPublicWinsIdx(i)}
                          aria-label={`Show win ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all ${i === (publicWinsIdx % publicWins.items.length) ? 'w-6 bg-emerald-600' : 'w-1.5 bg-emerald-200 hover:bg-emerald-300'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Quick Feature Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                <div className="bg-[#39FF14] p-8 rounded-2xl shadow-sm border border-lime-500 hover:shadow-lg hover:shadow-lime-300/60 transition space-y-4" data-testid="home-case-portal-card">
                  <div className="w-12 h-12 bg-slate-900 text-[#39FF14] rounded-xl flex items-center justify-center font-bold">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">Track Your Case — Securely, Anytime</h3>
                  <p className="text-slate-900/80 text-sm leading-relaxed font-medium">
                    Registered members sign in with a one-time mobile OTP to see the next hearing date, fee &amp; payment history, advocate updates and every uploaded document — live, from any device.
                  </p>
                  <button 
                    onClick={() => setActiveTab("client-portal")}
                    className="text-slate-900 hover:text-slate-700 font-bold text-sm flex items-center space-x-1 pt-2 underline decoration-2 underline-offset-4"
                    data-testid="home-case-portal-btn"
                  >
                    <span>Open My Case Portal</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-[#CCFF00] p-8 rounded-2xl shadow-sm border border-yellow-500 hover:shadow-lg hover:shadow-yellow-300/60 transition space-y-4" data-testid="home-ai-assistant-card">
                  <div className="w-12 h-12 bg-slate-900 text-[#CCFF00] rounded-xl flex items-center justify-center font-bold">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">AI Legal Help Assistant</h3>
                  <p className="text-slate-900/80 text-sm leading-relaxed font-medium">
                    Get instant guidance on Indian civil, consumer, family, and criminal law queries, complete with mandatory legal disclaimers and recommended next steps.
                  </p>
                  <button 
                    onClick={() => setActiveTab("ai-hub")}
                    className="text-slate-900 hover:text-slate-700 font-bold text-sm flex items-center space-x-1 pt-2 underline decoration-2 underline-offset-4"
                    data-testid="home-ai-assistant-btn"
                  >
                    <span>Ask Legal Question</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-[#00F0FF] p-8 rounded-2xl shadow-sm border border-cyan-500 hover:shadow-lg hover:shadow-cyan-300/60 transition space-y-4" data-testid="home-templates-card">
                  <div className="w-12 h-12 bg-slate-900 text-[#00F0FF] rounded-xl flex items-center justify-center font-bold">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">Legal Document Library</h3>
                  <p className="text-slate-900/80 text-sm leading-relaxed font-medium">
                    Download verified legal document templates including Promissory Notes, Rental Agreements, RTI applications, and Consumer Notices instantly.
                  </p>
                  <button 
                    onClick={() => setActiveTab("templates")}
                    className="text-slate-900 hover:text-slate-700 font-bold text-sm flex items-center space-x-1 pt-2 underline decoration-2 underline-offset-4"
                    data-testid="home-templates-btn"
                  >
                    <span>Browse Templates</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

              </div>
            </div>

            {/* NGO IMPACT COUNTER — animated, editable from Admin → Site Settings */}
            {(() => {
              const s = siteSettings.impact_stats || {};
              const primary = [
                { key: "cases_supported", value: s.cases_supported ?? 1450, label: "Cases Supported & Coordinated", suffix: "+", color: "text-amber-400" },
                { key: "advocates_panel", value: s.advocates_panel ?? 25, label: "Panel Advocates & Advisors", suffix: "+", color: "text-orange-400" },
                { key: "amount_recovered_lakhs", value: s.amount_recovered_lakhs ?? 45, label: "Consumer Relief Secured", prefix: "₹", suffix: " L+", color: "text-amber-400" },
                { key: "years_serving", value: s.years_serving ?? 12, label: "Years of Public Service", suffix: " Yrs", color: "text-orange-400" },
              ];
              const secondary = [
                { key: "rtis_filed", value: s.rtis_filed ?? 0, label: "RTIs Filed", suffix: "+", icon: FileCheck },
                { key: "awareness_camps", value: s.awareness_camps ?? 0, label: "Awareness Camps Held", suffix: "+", icon: Users },
                { key: "citizens_supported", value: s.citizens_supported ?? 0, label: "Citizens Directly Supported", suffix: "+", icon: Heart },
              ];
              return (
                <div className="bg-black text-white py-12" data-testid="impact-counter">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
                    <div className="text-center space-y-1">
                      <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-emerald-400">Live Impact — updated regularly</span>
                      <h3 className="text-2xl font-bold text-white">Our Work in Numbers</h3>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-testid="impact-primary">
                      {primary.map((m) => (
                        <div key={m.key} className="space-y-1" data-testid={`impact-${m.key}`}>
                          <div className={`text-3xl lg:text-4xl font-extrabold ${m.color}`}>
                            <AnimatedCounter end={m.value} prefix={m.prefix || ""} suffix={m.suffix || ""} />
                          </div>
                          <div className="text-sm text-slate-400">{m.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 border-t border-slate-800" data-testid="impact-secondary">
                      {secondary.map((m) => {
                        const Icon = m.icon;
                        return (
                          <div key={m.key} className="flex items-center gap-4 bg-slate-900/60 backdrop-blur rounded-xl p-4 border border-slate-800" data-testid={`impact-${m.key}`}>
                            <div className="w-11 h-11 rounded-lg bg-emerald-700/20 text-emerald-400 flex items-center justify-center">
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-2xl font-extrabold text-white">
                                <AnimatedCounter end={m.value} suffix={m.suffix} />
                              </div>
                              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{m.label}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* APPROVED-VOLUNTEERS WALL — appears only once 5+ approved sign-ups exist */}
            {approvedWall.eligible && (
              <div className="bg-emerald-50 border-y-2 border-emerald-200 py-10" data-testid="approved-volunteers-wall">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
                  <div className="text-center space-y-1">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-emerald-700">Our Volunteers</span>
                    <h3 className="text-2xl font-bold text-slate-900">People who have joined the HRCPC mission</h3>
                    <p className="text-xs text-slate-600 max-w-xl mx-auto">Approved honorary volunteers from across India — law students, retired officers, advocates and citizens.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="flex gap-3 pb-2 min-w-max" data-testid="approved-wall-strip">
                      {approvedWall.items.map((it, idx) => (
                        <div
                          key={`${it.occupation}-${it.city}-${idx}`}
                          className="bg-white border border-emerald-200 rounded-xl px-4 py-3 shadow-sm min-w-[220px] flex items-center gap-3"
                          data-testid={`approved-wall-item-${idx}`}
                        >
                          <div className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{it.occupation}</div>
                            <div className="text-[11px] text-slate-500">from {it.city}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-center pt-1">
                    <button
                      onClick={() => setActiveTab("volunteer-register")}
                      className="text-xs font-semibold text-emerald-800 hover:text-emerald-600 underline"
                      data-testid="wall-join-cta"
                    >
                      Would you like to join them? Apply as a volunteer →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Volunteer Skill Map — approved volunteers grouped by city */}
            {volMap.cities && volMap.cities.length > 0 && (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="volunteer-skill-map">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-800 bg-sky-50 border border-sky-200 px-3 py-1 rounded-full">
                      <MapPin className="w-3 h-3" />
                      <span>Volunteer Network</span>
                    </span>
                    <h3 className="text-2xl font-extrabold text-slate-900 mt-2">Where our volunteers are</h3>
                    <p className="text-xs text-slate-500">{volMap.total_approved} approved supporters across {volMap.cities.length} cities — with the skills they bring.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {volMap.cities.slice(0, 12).map(c => (
                    <div key={c.city} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition" data-testid={`vol-map-city-${c.city}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-sky-600" />
                          <span className="font-bold text-slate-900 truncate">{c.city}</span>
                        </div>
                        <span className="text-[10px] font-bold text-sky-800 bg-sky-100 px-2 py-0.5 rounded-full">{c.count}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(c.top_skills || []).map(s => (
                          <span key={s.name} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-semibold">
                            {s.name} · {s.count}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* ABOUT / PAN-INDIA PRESENCE */}
            <div className="bg-slate-50 py-14 border-t border-slate-200">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <div className="md:col-span-2 space-y-4">
                  <span className="inline-flex items-center space-x-2 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full text-xs font-bold text-emerald-800">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Serving citizens across India</span>
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900">About HRCPC Trust</h2>
                  <div className="bg-orange-50 border-l-4 border-orange-600 p-3 rounded-r-lg" data-testid="not-a-law-firm-banner">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-orange-800 mb-1">Important Notice</p>
                    <p className="text-xs text-orange-900 font-semibold leading-relaxed">
                      HRCPC Trust is a public-spirited, non-profit organisation. <strong>It is NOT a law firm</strong> and does not provide professional legal services or legal representation.
                    </p>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    <strong>Human Rights &amp; Consumer Protection Cell (HRCPC) Trust – Hyderabad</strong> is a
                    public-spirited organisation dedicated to awareness, advocacy and pro-bono support in
                    matters of human rights, consumer protection, <strong>environmental protection</strong> and <strong>medico-legal matters (medical negligence)</strong>. It is not a law firm and does not
                    provide professional legal services or legal representation. All guidance shared is
                    <strong> educational</strong>; please consult your family advocate before acting on any information.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    We create awareness on consumer disputes, family matters, human-rights violations, RTI,
                    tenant/landlord issues, cheque-bounce recoveries, <strong>environmental &amp; NGT complaints</strong>, <strong>medico-legal / medical-negligence grievances</strong>, and basic complaint procedures — and
                    connect underprivileged citizens with independent panel advocates who take up matters on
                    a pro-bono / concessional basis.
                  </p>

                  {/* Focus Areas pills */}
                  <div className="pt-3" data-testid="about-focus-areas">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Our Focus Areas</div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: "Consumer Protection", color: "bg-orange-50 text-orange-800 border-orange-200" },
                        { label: "Human Rights", color: "bg-rose-50 text-rose-800 border-rose-200" },
                        { label: "RTI / Transparency", color: "bg-slate-100 text-slate-800 border-slate-300" },
                        { label: "Environmental Protection", color: "bg-emerald-50 text-emerald-800 border-emerald-300" },
                        { label: "Medico-Legal / Medical Negligence", color: "bg-amber-50 text-amber-900 border-amber-300" },
                        { label: "Family / Maintenance", color: "bg-violet-50 text-violet-800 border-violet-200" },
                        { label: "Tenant / Landlord", color: "bg-sky-50 text-sky-800 border-sky-200" },
                        { label: "Cheque Bounce", color: "bg-lime-50 text-lime-800 border-lime-200" },
                      ].map(f => (
                        <span key={f.label} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${f.color}`}>
                          {f.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {["Hyderabad", "Amaravati – Capital of Andhra Pradesh", "Chennai (NGT)", "Mumbai (BHC)", "Delhi (SC/DHC)", "Bengaluru", "Kolkata"].map(city => (
                      <span key={city} className="bg-white border border-slate-200 text-xs font-semibold px-3 py-1 rounded-full text-slate-700">
                        <MapPin className="w-3 h-3 inline-block mr-1 text-orange-600" />{city}
                      </span>
                    ))}
                  </div>

                  {/* Social follow row */}
                  <div className="flex items-center gap-3 pt-4" data-testid="about-social-links">
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Follow HRCPC Trust:</span>
                    <a href="https://www.facebook.com/hrcpctrust" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-orange-600 hover:text-white text-slate-700 flex items-center justify-center shadow-sm transition" data-testid="about-social-facebook">
                      <Facebook className="w-4 h-4" />
                    </a>
                    <a href="https://www.youtube.com/@hrcpctrust" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-rose-600 hover:text-white text-slate-700 flex items-center justify-center shadow-sm transition" data-testid="about-social-youtube">
                      <Youtube className="w-4 h-4" />
                    </a>
                    <a href="https://www.instagram.com/hrcpctrust" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-fuchsia-600 hover:text-white text-slate-700 flex items-center justify-center shadow-sm transition" data-testid="about-social-instagram">
                      <Instagram className="w-4 h-4" />
                    </a>
                    <a href="https://twitter.com/hrcpctrust" target="_blank" rel="noopener noreferrer" aria-label="Twitter / X" className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-black hover:text-white text-slate-700 flex items-center justify-center shadow-sm transition" data-testid="about-social-twitter">
                      <Twitter className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* FOUNDER CARD */}
                <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-6 space-y-4" data-testid="founder-card">
                  <div className="w-full aspect-square bg-gradient-to-br from-orange-100 via-amber-50 to-emerald-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-orange-200">
                    {siteSettings.founder_photo_data_url ? (
                      <img
                        src={siteSettings.founder_photo_data_url}
                        alt="Thakur Rajkumar Singh — Founder, HRCPC Trust"
                        className="w-full h-full object-cover"
                        data-testid="founder-photo-img"
                      />
                    ) : (
                      <div className="text-center space-y-1" data-testid="founder-photo-placeholder">
                        <div className="w-20 h-20 bg-black text-orange-400 rounded-full mx-auto flex items-center justify-center text-3xl font-extrabold shadow-inner">
                          TR
                        </div>
                        <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Founder photograph</div>
                        <div className="text-[10px] text-slate-400 italic">upload from admin later</div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 text-center">
                    <h3 className="font-extrabold text-lg text-slate-900">Thakur Rajkumar Singh</h3>
                    <p className="text-xs text-slate-600 -mt-1">M.A. (Public &amp; Personnel Management – Osmania University)</p>
                    <p className="text-xs text-orange-700 font-bold uppercase tracking-wider">Managing Trustee, HRCPC Trust</p>
                    <p className="text-xs text-slate-600 leading-relaxed pt-2 text-left">
                      Thakur Rajkumar Singh, M.A. (Public &amp; Personnel Management – Osmania
                      University), is the Managing Trustee of the Human Rights &amp; Consumer
                      Protection Cell Trust (HRCPC Trust). A public-interest activist working since
                      <strong> 2004</strong>, he has been actively involved in protecting human
                      rights, consumer rights, environmental justice, and public accountability.
                      He has filed numerous RTIs and public interest matters before the National
                      Green Tribunal, High Courts, and other forums, with special focus on
                      protection of lakes and public resources. He appears as party-in-person and
                      works with a network of advocates across India.
                    </p>
                  </div>

                  {/* Founder video intro (or graceful "coming soon" placeholder) */}
                  {(() => {
                    const url = (siteSettings.founder_video_url || "").trim();
                    if (!url) {
                      return (
                        <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="founder-video-placeholder">
                          <div className="w-full aspect-video bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex flex-col items-center justify-center text-white space-y-1.5 relative overflow-hidden">
                            <div className="absolute inset-0 bg-black/30" />
                            <div className="relative z-10 w-14 h-14 bg-white/10 backdrop-blur border border-white/30 rounded-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                            <div className="relative z-10 text-[11px] font-semibold uppercase tracking-widest text-amber-300">Video Coming Soon</div>
                            <div className="relative z-10 text-[10px] text-slate-300 text-center max-w-[220px]">A short welcome message from the Managing Trustee will appear here.</div>
                          </div>
                        </div>
                      );
                    }
                    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
                    const vimeo = url.match(/vimeo\.com\/(\d+)/);
                    return (
                      <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="founder-video-player">
                        <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                          {yt ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`}
                              title="Founder Video Intro"
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              data-testid="founder-video-iframe"
                            />
                          ) : vimeo ? (
                            <iframe
                              src={`https://player.vimeo.com/video/${vimeo[1]}`}
                              title="Founder Video Intro"
                              className="w-full h-full"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                              data-testid="founder-video-iframe"
                            />
                          ) : (
                            <video src={url} controls preload="metadata" className="w-full h-full" data-testid="founder-video-html" />
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 italic text-center">A short welcome from Thakur Rajkumar Singh, Managing Trustee</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* 2. SECURE CLIENT PORTAL (OTP GATED) */}
        {activeTab === "client-portal" && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            {!currentUser || currentUser.role !== 'client' ? (
              
              /* OTP Login Box */
              <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-xl border border-slate-200 my-12">
                <div className="text-center space-y-3 mb-8">
                  <div className="w-16 h-16 bg-orange-100 text-orange-700 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                    <Lock className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Member Case Portal</h2>
                  <p className="text-sm text-slate-600">Enter your registered mobile number to securely view your case status, hearings, fees and documents.</p>
                  <button
                    onClick={() => { navigator.clipboard.writeText(window.location.origin + '/client-login'); toast.success("Member login link copied — share only with existing HRCPC members."); }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:text-orange-800 border border-orange-200 bg-orange-50 px-3 py-1 rounded-full"
                    data-testid="copy-client-login-link"
                  >
                    <CopyIcon className="w-3.5 h-3.5" />
                    Copy shareable link:&nbsp;<span className="font-mono">/client-login</span>
                  </button>
                  <p className="text-[11px] text-slate-500 italic pt-1">This link is only for existing HRCPC members whose cases we handle.</p>
                </div>

                <form onSubmit={handleClientLogin} className="space-y-5">
                  {otpStep === 1 ? (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Registered Mobile Number</label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-500 font-medium">+91</span>
                        <input 
                          type="text" 
                          maxLength="10"
                          value={loginPhone}
                          onChange={(e) => setLoginPhone(e.target.value)}
                          placeholder="9876543210" 
                          className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-600 focus:outline-none text-slate-900 font-medium"
                          data-testid="client-phone-input"
                          required
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-2">Try demo numbers: <span className="font-semibold text-orange-700 cursor-pointer" onClick={() => setLoginPhone("9876543210")}>9876543210</span> (Rajesh) or <span className="font-semibold text-orange-700 cursor-pointer" onClick={() => setLoginPhone("9123456780")}>9123456780</span> (Sunita)</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg text-xs text-orange-800">
                        A 6-digit verification code has been sent to your registered contact for <strong>+91 {loginPhone}</strong>.
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Enter 6-Digit OTP</label>
                        <input 
                          type="text" 
                          maxLength="6"
                          inputMode="numeric"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                          placeholder="123456"
                          className="w-full text-center tracking-widest text-2xl font-bold px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-600 focus:outline-none text-slate-900"
                          data-testid="client-otp-input"
                          required
                        />
                        <div className="mt-2 bg-slate-50 border-l-4 border-slate-400 rounded-r-lg px-3 py-2 text-[11px] text-slate-700 leading-relaxed" data-testid="otp-hint">
                          Enter the 6-digit code sent to your registered mobile / email. The code expires in 5 minutes and works only once.
                        </div>
                      </div>
                    </div>
                  )}

                  <button 
                    type="submit"
                    className="w-full bg-orange-700 hover:bg-orange-600 text-white font-bold py-3.5 rounded-xl shadow transition flex items-center justify-center space-x-2"
                    data-testid="client-login-submit-btn"
                  >
                    <span>{otpStep === 1 ? "Send OTP Code" : "Verify & Access Cases"}</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  {otpStep === 2 && (
                    <button 
                      type="button"
                      onClick={() => setOtpStep(1)}
                      className="w-full text-center text-xs text-slate-500 hover:text-slate-800 pt-2"
                    >
                      Change mobile number
                    </button>
                  )}
                </form>
              </div>

            ) : (

              /* Client Dashboard & Case View */
              <div className="space-y-8" data-testid="client-dashboard">
                
                {/* Client Profile Header banner */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-black text-amber-300 rounded-2xl flex items-center justify-center font-bold text-2xl shadow-inner">
                      {currentUser.data.full_name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h2 className="text-2xl font-bold text-slate-900">{currentUser.data.full_name}</h2>
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">{currentUser.data.status}</span>
                      </div>
                      <p className="text-sm text-slate-600">Member Code: <span className="font-mono font-semibold text-slate-900">{currentUser.data.unique_code}</span> • Mobile: +91 {currentUser.data.mobile}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 w-full md:w-auto">
                    <div className="bg-orange-50 px-4 py-2.5 rounded-xl border border-orange-200 text-right">
                      <div className="text-xs text-orange-700 font-semibold">Active Cases</div>
                      <div className="text-xl font-extrabold text-orange-900">
                        {casesList.filter(c => c.client_mobile === currentUser.data.mobile).length}
                      </div>
                    </div>
                    <button 
                      onClick={() => { if (currentUser?.role === 'admin') { logoutAdmin(); } else if (currentUser?.role === 'advocate') { logoutAdvocate(); } else { logoutClient(); } }}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-semibold transition"
                      data-testid="client-logout-btn"
                    >
                      Logout
                    </button>
                  </div>
                </div>

                {/* Email-on-file Nudge Modal — one-time prompt to add / update
                    a real email so the Member has an OTP fallback if SMS fails.
                    Only shows for approved Members whose email is missing or
                    an auto-generated placeholder, and who haven't dismissed. */}
                {emailNudge && emailNudge.is_placeholder_email && !emailNudge.email_nudge_dismissed && (
                  <div
                    className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="email-nudge-modal"
                  >
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                      <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-5 py-4 text-white">
                        <div className="text-[10px] font-bold tracking-widest uppercase opacity-90">Add a backup contact</div>
                        <h3 className="text-lg font-bold mt-0.5">Can we reach you if you lose your phone?</h3>
                      </div>
                      <div className="p-5 space-y-3">
                        <p className="text-sm text-slate-700 leading-relaxed">
                          Right now we only have your mobile on file. Please add a personal email
                          so we can send your login code there as a backup — for example, if you
                          change SIM cards or lose your phone.
                        </p>
                        <p className="text-xs text-slate-500">Your email is never shared and only used for HRCPC verification.</p>
                        <input
                          type="email"
                          value={emailNudgeInput}
                          onChange={(e) => setEmailNudgeInput(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                          data-testid="email-nudge-input"
                          autoFocus
                        />
                        <div className="flex items-center gap-2 pt-2">
                          <button
                            onClick={async () => {
                              const email = (emailNudgeInput || "").trim().toLowerCase();
                              if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                                toast.error("Please enter a valid email address");
                                return;
                              }
                              setEmailNudgeSaving(true);
                              try {
                                await axios.patch(`${API}/client/profile/email`, { email });
                                toast.success("Thanks! We've added " + email + " as your backup.");
                                setEmailNudge({ ...emailNudge, is_placeholder_email: false, email_nudge_dismissed: true, email });
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Could not save email");
                              } finally {
                                setEmailNudgeSaving(false);
                              }
                            }}
                            disabled={emailNudgeSaving}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg"
                            data-testid="email-nudge-save-btn"
                          >
                            {emailNudgeSaving ? "Saving…" : "Save my email"}
                          </button>
                          <button
                            onClick={async () => {
                              try { await axios.post(`${API}/client/profile/nudge-dismiss`); } catch (_) { /* best-effort */ }
                              setEmailNudge({ ...emailNudge, email_nudge_dismissed: true });
                            }}
                            className="text-slate-500 hover:text-slate-800 text-sm font-semibold px-3"
                            data-testid="email-nudge-dismiss-btn"
                          >
                            Remind me later
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Member Referral Nudge — visible only to approved Members */}
                {memberReferral && memberReferral.link && (
                  <div
                    className="bg-gradient-to-br from-orange-50 via-amber-50 to-white p-5 rounded-2xl border border-orange-200 shadow-sm space-y-3"
                    data-testid="member-referral-card"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] font-bold tracking-widest text-orange-700 uppercase">Refer a family / neighbour</div>
                        <h3 className="text-base font-bold text-slate-900 mt-0.5">Know someone who needs legal guidance?</h3>
                        <p className="text-xs text-slate-600 mt-1">Share this link — they'll land on a warm sign-up page mentioning you referred them.</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-white border border-emerald-200 text-emerald-800 px-2 py-1 rounded-full font-semibold" data-testid="referral-count-approved">Approved: {memberReferral.counts?.approved ?? 0}</span>
                        <span className="bg-white border border-slate-200 text-slate-700 px-2 py-1 rounded-full font-semibold" data-testid="referral-count-pending">Pending: {memberReferral.counts?.pending ?? 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center bg-white border border-slate-300 rounded-lg overflow-hidden">
                      <input
                        type="text"
                        readOnly
                        value={memberReferral.link}
                        className="flex-1 px-3 py-2 text-xs font-mono text-slate-700 bg-white outline-none"
                        data-testid="referral-link-input"
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        onClick={() => { navigator.clipboard.writeText(memberReferral.link); toast.success("Referral link copied"); }}
                        className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2"
                        data-testid="referral-copy-btn"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={memberReferral.whatsapp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                        data-testid="referral-whatsapp-btn"
                      >
                        Share on WhatsApp
                      </a>
                      <a
                        href={memberReferral.sms_url}
                        className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                        data-testid="referral-sms-btn"
                      >
                        Share via SMS
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(memberReferral.share_message); toast.success("Message copied — paste anywhere"); }}
                        className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold px-3 py-2 rounded-lg transition"
                        data-testid="referral-copy-message-btn"
                      >
                        Copy message
                      </button>
                    </div>
                  </div>
                )}

                {/* Case Selector */}
                {(() => {
                  const myCases = casesList.filter(c => c.client_mobile === currentUser.data.mobile);
                  if (myCases.length === 0) {
                    return (
                      <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center space-y-4">
                        <HelpCircle className="w-12 h-12 text-slate-400 mx-auto" />
                        <h3 className="text-lg font-bold text-slate-900">No active cases linked to this mobile number</h3>
                        <p className="text-sm text-slate-600">If you registered recently, our admin team is reviewing your KYC and will assign your case file soon.</p>
                      </div>
                    );
                  }

                  const activeCase = myCases.find(c => (c.case_number || c.id) === selectedCaseId) || myCases[0];

                  return (
                    <div className="space-y-6">
                      
                      {/* Case Select Dropdown */}
                      {myCases.length > 1 && (
                        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Select Active Case:</span>
                          <select 
                            value={activeCase.case_number || activeCase.id}
                            onChange={(e) => setSelectedCaseId(e.target.value)}
                            className="bg-slate-50 border border-slate-300 px-4 py-2 rounded-lg text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-600"
                            data-testid="client-case-dropdown"
                          >
                            {myCases.map(c => (
                              <option key={c.case_number || c.id} value={c.case_number || c.id}>{c.case_number} - {c.title}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Main Case Details Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* Left & Center: Case Overview & Notes */}
                        <div className="lg:col-span-2 space-y-6">
                          
                          {/* Case Card */}
                          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-6">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="bg-orange-100 text-orange-800 text-xs font-bold px-2.5 py-1 rounded-md font-mono">{activeCase.case_number}</span>
                                <h3 className="text-xl font-bold text-slate-900 mt-2">{activeCase.title}</h3>
                                <p className="text-sm text-slate-600 mt-1 flex items-center space-x-1">
                                  <Building className="w-4 h-4 text-slate-400" />
                                  <span>{activeCase.court}</span>
                                </p>
                              </div>
                              <span className="bg-amber-100 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">{activeCase.status}</span>
                            </div>

                            <hr className="border-slate-100" />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
                                <div className="text-xs text-slate-500 font-semibold flex items-center space-x-1">
                                  <Clock className="w-3.5 h-3.5 text-orange-600" />
                                  <span>NEXT HEARING DATE</span>
                                </div>
                                <div className="text-lg font-bold text-slate-900">{fmtDate(activeCase.next_hearing_date)}</div>
                                <p className="text-xs text-amber-700">Please coordinate with panel advocate 1 day prior.</p>
                              </div>

                              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-1">
                                <div className="text-xs text-slate-500 font-semibold flex items-center space-x-1">
                                  <Users className="w-3.5 h-3.5 text-orange-600" />
                                  <span>ASSIGNED ADVOCATE(S)</span>
                                </div>
                                <div className="text-sm font-bold text-slate-900">{activeCase.assigned_advocates.join(", ")}</div>
                                <p className="text-xs text-slate-500">HRCPC Trust Panel Counsel</p>
                              </div>
                            </div>

                            {/* Advocate Notes / Updates */}
                            <div className="space-y-3">
                              <h4 className="font-bold text-slate-900 text-sm flex items-center space-x-2">
                                <Bell className="w-4 h-4 text-orange-600" />
                                <span>Case Hearing Notes & Advocate Updates</span>
                              </h4>
                              <div className="space-y-3">
                                {activeCase.notes.map(note => (
                                  <div key={note.id} className="bg-orange-50/50 border border-orange-100 p-4 rounded-xl space-y-1">
                                    <div className="flex justify-between items-center text-xs text-slate-500">
                                      <span className="font-semibold text-orange-900">{note.author}</span>
                                      <span>{fmtDate(note.date)}</span>
                                    </div>
                                    <p className="text-sm text-slate-700">{note.text}</p>
                                  </div>
                                ))}
                              </div>
                            </div>

                          </div>

                          {/* Documents Section */}
                          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-4">
                            <h4 className="font-bold text-slate-900 flex items-center justify-between">
                              <span className="flex items-center space-x-2">
                                <FileText className="w-5 h-5 text-orange-600" />
                                <span>Uploaded Case Documents & Petitions</span>
                              </span>
                              <span className="text-xs text-slate-500 font-normal">{activeCase.documents.length} files available</span>
                            </h4>

                            <div className="space-y-2">
                              {activeCase.documents.map(doc => (
                                <div key={doc.id} className="flex items-center justify-between p-3.5 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center font-bold text-xs">
                                      PDF
                                    </div>
                                    <div>
                                      <div className="text-sm font-bold text-slate-900">{doc.name}</div>
                                      <div className="text-xs text-slate-500">Uploaded by {doc.uploaded_by} on {fmtDate(doc.date)}</div>
                                    </div>
                                  </div>
                                  <button 
                                    onClick={() => toast.success("Downloading " + doc.name + " securely...")}
                                    className="bg-white hover:bg-orange-50 text-orange-700 border border-slate-300 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1 shadow-sm"
                                    data-testid={`download-doc-${doc.id}`}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    <span>Download</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Unified Case Timeline for the Client */}
                          <CaseTimeline caseNumber={activeCase.case_number} apiBase={API} testIdSuffix={`client-${activeCase.case_number}`} />

                          {/* Phase-II Sprint 4.3: Client-visible proceedings */}
                          <ClientCaseUpdates caseNumber={activeCase.case_number} apiBase={API} />

                        </div>

                        {/* Right Column: Fees & Payment History */}
                        <div className="space-y-6">
                          
                          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-6">
                            <h4 className="font-bold text-slate-900 flex items-center space-x-2">
                              <CreditCard className="w-5 h-5 text-orange-600" />
                              <span>Fee & Balance Summary</span>
                            </h4>

                            <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Total Legal Fee:</span>
                                <span className="font-bold text-slate-900">₹{activeCase.total_fee.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Fee Paid:</span>
                                <span className="font-bold text-emerald-700">₹{activeCase.fee_paid.toLocaleString()}</span>
                              </div>
                              <hr className="border-slate-200" />
                              <div className="flex justify-between text-base">
                                <span className="font-bold text-slate-800">Pending Balance:</span>
                                <span className="font-extrabold text-rose-600">₹{activeCase.balance.toLocaleString()}</span>
                              </div>
                            </div>

                            {activeCase.balance > 0 ? (
                              <button 
                                onClick={() => triggerRazorpayPayment({ title: activeCase.title + " (Balance)", amount: activeCase.balance, case_id: activeCase.case_number || activeCase.id })}
                                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl shadow transition flex items-center justify-center space-x-2"
                                data-testid="pay-balance-btn"
                              >
                                <DollarSign className="w-5 h-5" />
                                <span>Pay Balance ₹{activeCase.balance.toLocaleString()} via Razorpay</span>
                              </button>
                            ) : (
                              <div className="bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl text-center text-xs font-semibold text-emerald-800">
                                All case fees fully paid. Thank you!
                              </div>
                            )}

                            {/* Payment History */}
                            <div className="space-y-3 pt-2">
                              <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Payment History & Receipts</h5>
                              {activeCase.payment_history.map(pay => (
                                <div key={pay.id} className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex justify-between items-center text-xs">
                                  <div>
                                    <div className="font-bold text-slate-900">₹{pay.amount.toLocaleString()} • {pay.mode}</div>
                                    <div className="text-slate-500">{fmtDate(pay.date)} | {pay.receipt_no}</div>
                                  </div>
                                  <button 
                                    onClick={() => toast.success("Receipt " + pay.receipt_no + " downloaded successfully.")}
                                    className="text-orange-600 hover:underline font-semibold"
                                  >
                                    Receipt
                                  </button>
                                </div>
                              ))}
                            </div>

                          </div>

                          {/* Client Notifications */}
                          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-4">
                            <h4 className="font-bold text-slate-900 flex items-center space-x-2">
                              <Bell className="w-5 h-5 text-orange-600" />
                              <span>Recent Notifications</span>
                            </h4>
                            <div className="space-y-3">
                              {notificationsList.filter(n => n.client_mobile === currentUser.data.mobile).map(notif => (
                                <div key={notif.id} className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1">
                                  <div className="flex justify-between items-center">
                                    <span className="font-bold text-xs text-orange-900">{notif.title}</span>
                                    <span className="text-[10px] text-slate-400">{fmtDate(notif.date)}</span>
                                  </div>
                                  <p className="text-xs text-slate-600 leading-relaxed">{notif.message}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                        </div>

                      </div>

                    </div>
                  );
                })()}

              </div>
            )}
          </div>
        )}

        {/* 3. AI LEGAL HELP ASSISTANT */}
        {activeTab === "ai-hub" && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-xl flex items-center justify-center font-bold">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">AI Legal Help Assistant</h2>
                  <p className="text-sm text-slate-600">Ask any general legal question regarding Consumer Rights, Property, Family Law, RTI, or Employment in India.</p>
                </div>
              </div>

              {/* MANDATORY LEGAL DISCLAIMER */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl text-xs text-amber-900 leading-relaxed font-medium">
                <strong className="block font-bold mb-1">MANDATORY LEGAL DISCLAIMER:</strong>
                {MOCK_DISCLAIMER}
              </div>
            </div>

            {/* One-Click Legal Drafts — HRCPC specialities */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 space-y-3" data-testid="ai-one-click-drafts">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">One-Click Legal Drafts</h3>
                  <p className="text-[11px] text-slate-500">HRCPC specialities — click any tile to auto-fill a detailed draft prompt below.</p>
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Specialist Templates</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setAiQuery("Draft a formal complaint before the Hon'ble National Green Tribunal (Southern Zone, Chennai) under Section 14 & Section 18(1) of the National Green Tribunal Act, 2010 for environmental violation. Include: (1) Cause title with applicant and respondent details placeholders, (2) Brief statement of facts with location coordinates, source of pollution, and dates of observation placeholders, (3) Grounds citing Air (Prevention & Control of Pollution) Act 1981 / Water (Prevention & Control of Pollution) Act 1974 / Environment (Protection) Act 1986 as applicable, (4) Relief sought including immediate stop-work order, environmental compensation under polluter-pays principle, and restoration directions, (5) Verification clause and list of documents to be annexed. Use plain paragraph format suitable for Times New Roman printing, with clearly marked blank placeholders like ____________ where the citizen must fill in facts.");
                    setTimeout(() => {
                      const el = document.querySelector('[data-testid="ai-chat-input"]');
                      if (el) el.focus();
                    }, 100);
                  }}
                  className="text-left bg-emerald-50 hover:bg-emerald-100 border-2 border-emerald-300 rounded-xl p-4 transition group"
                  data-testid="ai-draft-ngt"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 shrink-0 bg-emerald-600 text-white rounded-lg flex items-center justify-center font-bold">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-emerald-900 text-sm">NGT Environmental Complaint</div>
                      <div className="text-[11px] text-emerald-800 mt-0.5 leading-snug">Auto-drafts a formal complaint under the National Green Tribunal Act, 2010 with polluter-pays relief and standard annexures.</div>
                      <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mt-2 group-hover:text-emerald-900">Click to load prompt →</div>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAiQuery("Draft a formal legal notice under Section 80 of the Code of Civil Procedure, 1908 (where applicable) or a pre-litigation notice for medical negligence against a hospital / registered medical practitioner. Include: (1) Sender and recipient placeholders (patient / legal heir vs. hospital + treating doctor), (2) Chronological statement of facts covering date of admission, treatment given, alleged breach of duty of care, and the injury / death caused, (3) Grounds citing the Bolam / Bolitho test, Indian Medical Council (Professional Conduct, Etiquette and Ethics) Regulations 2002, and applicable Supreme Court precedents including Jacob Mathew v. State of Punjab (2005) and Kusum Sharma v. Batra Hospital (2010), (4) Prayer for compensation quantified with placeholders for medical expenses, loss of income, and pain & suffering, (5) Reference to parallel complaint before the State Medical Council and District Consumer Commission under Consumer Protection Act, 2019, (6) Verification and annexure list. Use plain paragraph format suitable for Times New Roman printing with clearly marked blank placeholders like ____________.");
                    setTimeout(() => {
                      const el = document.querySelector('[data-testid="ai-chat-input"]');
                      if (el) el.focus();
                    }, 100);
                  }}
                  className="text-left bg-amber-50 hover:bg-amber-100 border-2 border-amber-300 rounded-xl p-4 transition group"
                  data-testid="ai-draft-medico"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 shrink-0 bg-amber-600 text-white rounded-lg flex items-center justify-center font-bold">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-amber-900 text-sm">Medical Negligence Notice</div>
                      <div className="text-[11px] text-amber-900 mt-0.5 leading-snug">Auto-drafts a pre-litigation notice under CPC & CPA 2019 citing Jacob Mathew, Kusum Sharma and IMC ethics regulations.</div>
                      <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mt-2 group-hover:text-amber-900">Click to load prompt →</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Chat Messages Box */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[500px]">
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
                {aiChatHistory.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xl rounded-2xl p-4 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-orange-600 text-white rounded-br-none'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-none shadow-sm space-y-3'
                    }`}
                    data-testid={`ai-msg-${msg.role}-${idx}`}
                    >
                      {msg.role === 'assistant'
                        ? <RichAIText text={msg.text} />
                        : <div className="whitespace-pre-line">{msg.text}</div>}
                      {msg.role === 'assistant' && (
                        <>
                          <div className="flex items-center gap-2 pt-1 flex-wrap">
                            <button
                              onClick={() => { navigator.clipboard.writeText(msg.text || ''); toast.success("Draft copied"); }}
                              className="text-[10px] font-bold text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                              data-testid={`ai-copy-${idx}`}
                            >
                              <CopyIcon className="w-3 h-3" /> Copy
                            </button>
                            <button
                              onClick={() => downloadAIDraftAsDoc(msg.text || '', `hrcpc-legal-draft-${idx + 1}`)}
                              className="text-[10px] font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                              data-testid={`ai-download-doc-${idx}`}
                            >
                              <Download className="w-3 h-3" /> Download .doc
                            </button>
                          </div>
                          <div className="text-[10px] leading-snug text-amber-900 bg-amber-50 border-l-2 border-amber-500 pl-2 pr-1 py-1.5 rounded-r"
                            data-testid={`ai-msg-disclaimer-${idx}`}
                          >
                            <strong className="font-bold">Disclaimer:</strong>{' '}
                            {msg.disclaimer || MOCK_DISCLAIMER}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 p-4 rounded-2xl text-sm text-slate-500 animate-pulse">
                      HRCPC AI is consulting Indian statutory provisions...
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <form onSubmit={handleAiSubmit} className="p-4 bg-white border-t border-slate-200 flex items-center space-x-3">
                <input 
                  type="text" 
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  placeholder="Ask e.g. What is the procedure for filing a consumer complaint against builder delay?" 
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-600 text-sm"
                  data-testid="ai-chat-input"
                />
                <button 
                  type="submit"
                  className="bg-orange-700 hover:bg-orange-600 text-white px-5 py-3 rounded-xl font-semibold flex items-center space-x-2 transition"
                  data-testid="ai-chat-submit"
                >
                  <Send className="w-4 h-4" />
                  <span>Ask AI</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSupportTicketForm({
                      ...emptySupportForm,
                      message: aiQuery || "",
                      subject: aiQuery ? aiQuery.slice(0, 60) : ""
                    });
                    setSupportTicketSubmitted(null);
                    setSupportTicketModalOpen(true);
                  }}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white px-5 py-3 rounded-xl font-semibold flex items-center space-x-2 transition"
                  data-testid="ask-hrcpc-legal-btn"
                  title="Human review by HRCPC advocates"
                >
                  <UserCheck className="w-4 h-4" />
                  <span>Ask HRCPC Legal Assistant</span>
                </button>
              </form>
            </div>

            {/* Two-lane explainer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3" data-testid="ai-two-lanes">
              <div className="bg-white border border-orange-200 rounded-xl p-3 text-xs text-slate-700">
                <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-orange-600" /><strong className="text-orange-800">AI Legal Assistant</strong></div>
                Instant AI-generated answers under Indian law. Mandatory disclaimer applies.
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-3 text-xs text-slate-700">
                <div className="flex items-center gap-2 mb-1"><UserCheck className="w-4 h-4 text-emerald-700" /><strong className="text-emerald-800">HRCPC Legal Assistant (Human)</strong></div>
                Your query will be reviewed by the HRCPC Legal Team. We will respond via email / phone at <strong>support@hrcpc.org</strong>.
              </div>
            </div>

            {/* Suggested Prompt Chips */}
            <div className="flex flex-wrap gap-2 pt-2">
              <span className="text-xs text-slate-500 font-semibold self-center">Suggested queries:</span>
              <button onClick={() => setAiQuery("How to file consumer complaint for defective goods?")} className="bg-white border border-slate-200 text-xs px-3 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700">Consumer Complaint</button>
              <button onClick={() => setAiQuery("What are tenant rights against arbitrary eviction in Hyderabad?")} className="bg-white border border-slate-200 text-xs px-3 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700">Tenant Rights</button>
              <button onClick={() => setAiQuery("How to file RTI application to government department?")} className="bg-white border border-slate-200 text-xs px-3 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700">RTI Application</button>
              <button onClick={() => setAiQuery("How to file a medical negligence complaint against a hospital or doctor in India?")} className="bg-amber-50 border border-amber-200 text-xs px-3 py-1.5 rounded-lg hover:bg-amber-100 text-amber-900 font-semibold">Medical Negligence</button>
              <button onClick={() => setAiQuery("How to file an environmental pollution complaint before the National Green Tribunal (NGT)?")} className="bg-emerald-50 border border-emerald-200 text-xs px-3 py-1.5 rounded-lg hover:bg-emerald-100 text-emerald-900 font-semibold">Environmental / NGT</button>
            </div>

          </div>
        )}

        {/* 3B. VOICE GRIEVANCE RECORDING */}
        {activeTab === "voice-grievance" && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="voice-grievance-page">

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center">
                  <Mic className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Voice Grievance Recorder</h2>
                  <p className="text-sm text-slate-600">Record your problem in any Indian language. Our AI will draft a formal legal complaint in the language you choose.</p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl text-xs text-amber-900 leading-relaxed font-medium">
                <strong className="block font-bold mb-1">MANDATORY LEGAL DISCLAIMER:</strong>
                {MOCK_DISCLAIMER}
              </div>
            </div>

            {/* Language + recorder */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <label className="text-sm font-semibold text-slate-700">Draft complaint in:</label>
                <select
                  value={voiceLangOut}
                  onChange={(e) => setVoiceLangOut(e.target.value)}
                  className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="voice-lang-select"
                >
                  <option value="en">English</option>
                  <option value="te">Telugu (తెలుగు)</option>
                  <option value="hi">Hindi (हिन्दी)</option>
                </select>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-center space-x-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-inner ${voiceRecording ? 'bg-rose-100 text-rose-700 animate-pulse' : 'bg-emerald-100 text-emerald-700'}`}>
                    {voiceRecording ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">
                      {voiceRecording ? "Recording... speak now" : voiceBlob ? "Recording captured" : "Ready to record"}
                    </div>
                    <div className="text-xs text-slate-500">Speak in Telugu, Hindi, Tamil, Urdu, English — any language you are comfortable with.</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!voiceRecording && !voiceBlob && (
                    <button
                      onClick={startVoiceRecording}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow flex items-center space-x-2"
                      data-testid="voice-start-btn"
                    >
                      <Mic className="w-4 h-4" />
                      <span>Start Recording</span>
                    </button>
                  )}
                  {voiceRecording && (
                    <button
                      onClick={stopVoiceRecording}
                      className="bg-rose-600 hover:bg-rose-500 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow flex items-center space-x-2"
                      data-testid="voice-stop-btn"
                    >
                      <MicOff className="w-4 h-4" />
                      <span>Stop</span>
                    </button>
                  )}
                  {!voiceRecording && voiceBlob && (
                    <>
                      <button
                        onClick={resetVoiceGrievance}
                        className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2.5 rounded-xl font-semibold text-sm"
                        data-testid="voice-reset-btn"
                      >
                        Discard
                      </button>
                      <button
                        onClick={submitVoiceGrievance}
                        disabled={voiceProcessing}
                        className="bg-orange-600 hover:bg-orange-500 disabled:bg-orange-300 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow flex items-center space-x-2"
                        data-testid="voice-submit-btn"
                      >
                        <Send className="w-4 h-4" />
                        <span>{voiceProcessing ? "Drafting…" : "Convert to Complaint"}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {voiceBlob && voiceBlobUrl && (
                <audio controls src={voiceBlobUrl} className="w-full" data-testid="voice-playback" />
              )}
            </div>

            {/* Result panel */}
            {voiceResult && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="voice-result">
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Your original words (transcript)</h4>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-800 whitespace-pre-line" data-testid="voice-transcript">
                    {voiceResult.transcript}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Structured legal complaint <span className="text-orange-700">({voiceResult.language_out_name})</span>
                    </h4>
                    <button
                      onClick={() => { navigator.clipboard.writeText(voiceResult.complaint || ''); toast.success("Copied to clipboard"); }}
                      className="text-orange-700 hover:underline text-xs font-semibold flex items-center space-x-1"
                      data-testid="voice-copy-btn"
                    >
                      <CopyIcon className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </button>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-slate-800 whitespace-pre-line leading-relaxed" data-testid="voice-complaint">
                    {voiceResult.complaint}
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg text-[12px] text-amber-900 leading-relaxed" data-testid="voice-disclaimer">
                  <strong className="block font-bold mb-1 uppercase tracking-wide text-amber-800">Mandatory Legal Disclaimer</strong>
                  {voiceResult.disclaimer || MOCK_DISCLAIMER}
                </div>

                {/* Forward this grievance to HRCPC coordinator (soft-guided) */}
                <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="voice-escalate-block">
                  <div className="flex-1">
                    <div className="font-bold text-slate-900 text-sm">Would you like a human coordinator to review this?</div>
                    <div className="text-xs text-slate-600">HRCPC's volunteer coordinator will read the drafted complaint and get back to you with next steps.</div>
                  </div>
                  <button
                    onClick={() => askRegisterGuide(() => {
                      // pre-fill support ticket with the voice grievance text
                      setSupportTicketForm({
                        ...emptySupportForm,
                        subject: "Voice grievance — needs coordinator review",
                        message: (voiceResult?.complaint || "").slice(0, 4000),
                        category: "General Legal Query",
                      });
                      setSupportTicketModalOpen(true);
                      setSupportTicketSubmitted(null);
                      // dismiss for session so support-ticket submit doesn't nag again
                      try { sessionStorage.setItem("hrcpc_guest_dismissed", "1"); } catch (_) {}
                    }, "Forward Grievance to HRCPC Coordinator")}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-5 py-2.5 rounded-xl shadow flex items-center gap-2 whitespace-nowrap"
                    data-testid="voice-escalate-btn"
                  >
                    <Users className="w-4 h-4" />
                    Forward to HRCPC Coordinator
                  </button>
                </div>
              </div>
            )}

          </div>
        )}

        {/* 4. LEGAL TEMPLATES LIBRARY */}
        {activeTab === "templates" && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
            
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Legal Templates & Study Resources</h2>
                <p className="text-sm text-slate-600">Free educational resources curated by <strong>HRCPC panel advocates</strong>. All content is for awareness &amp; guidance — not legal advice.</p>
              </div>
              <div className="bg-emerald-50 text-emerald-800 text-xs font-bold px-3.5 py-2 rounded-xl border border-emerald-200">
                100% FREE — HRCPC is a non-profit trust
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl text-xs text-amber-900 leading-relaxed font-medium">
              <strong className="block font-bold mb-1">DRAFT FORMATS FOR GUIDANCE ONLY:</strong>
              These are draft formats &amp; study material for your reference. HRCPC Trust is <strong>not a law firm</strong>. Please have any format reviewed and personalised by your family advocate before filing / signing / serving on any party.
            </div>

            {/* ============ SECTION 1: FREE DOWNLOADS ============ */}
            <section className="space-y-4" data-testid="templates-free-downloads-section">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-xl flex items-center justify-center">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900">Free Downloads</h3>
                  <p className="text-xs text-slate-600">Draft formats, bare-act extracts &amp; ready-to-personalise petition templates.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templatesList.map((tpl, idx) => (
                  <div key={tpl.title || idx} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between space-y-4 hover:shadow-md transition" data-testid={`template-card-${idx}`}>
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-1 rounded-md">{tpl.category}</span>
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded-full">FREE</span>
                      </div>
                      <h3 className="font-bold text-slate-900 text-base leading-snug">{tpl.title}</h3>
                      {tpl.drafted_by && (
                        <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Verified draft • {tpl.drafted_by}
                        </p>
                      )}
                      <p className="text-[10px] text-amber-800 font-medium bg-amber-50 border-l-2 border-amber-400 pl-2 py-1">
                        {tpl.label || "Draft format for guidance only"}
                      </p>
                      <p className="text-xs text-slate-500">{(tpl.downloads || 0).toLocaleString()} downloads by citizens</p>
                    </div>

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-400">Word / PDF Format</span>
                      <button
                        onClick={() => toast.success("Downloading " + tpl.title + " successfully!")}
                        className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 shadow"
                        data-testid={`download-tpl-${idx}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download Free</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ============ SECTION 2: STUDY MATERIAL & BOOKS ============ */}
            <section className="space-y-4 pt-4 border-t border-slate-200" data-testid="templates-study-material-section">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900">Study Material &amp; Books</h3>
                  <p className="text-xs text-slate-600">Curated links to Bare Acts, landmark judgments, official Government publications &amp; recommended reading — for self-study only.</p>
                </div>
              </div>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-xl text-xs text-emerald-900 leading-relaxed font-medium">
                <strong className="block font-bold mb-1">FOR AWARENESS &amp; SELF-STUDY:</strong>
                These are educational references only. HRCPC Trust does not sell or endorse any book. For copyrighted works, please buy authorised copies from publishers.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { category: "Bare Act", title: "The Constitution of India (Official PDF)", desc: "Full Constitution as published by the Ministry of Law & Justice, Govt. of India.", url: "https://legislative.gov.in/constitution-of-india/", type: "PDF · Official" },
                  { category: "Bare Act", title: "Consumer Protection Act, 2019", desc: "Complete text of CPA 2019 governing consumer disputes & e-commerce.", url: "https://consumeraffairs.nic.in/acts-and-rules/consumer-protection", type: "PDF · Official" },
                  { category: "Bare Act", title: "Right to Information Act, 2005", desc: "RTI Act with amendments — the citizen's transparency tool.", url: "https://rti.gov.in/rti-act.pdf", type: "PDF · Official" },
                  { category: "Bare Act", title: "Bharatiya Nyaya Sanhita, 2023 (new IPC)", desc: "The new criminal code replacing IPC 1860 — official text.", url: "https://www.mha.gov.in/en/commoncontent/three-new-criminal-laws", type: "PDF · Official" },
                  { category: "Bare Act", title: "Negotiable Instruments Act, 1881", desc: "Sec. 138 & cheque-bounce provisions — full statute.", url: "https://legislative.gov.in/sites/default/files/A1881-26.pdf", type: "PDF · Official" },
                  { category: "Research", title: "Supreme Court Judgments (indiankanoon.org)", desc: "Free full-text search of Supreme Court & High Court judgments.", url: "https://indiankanoon.org/", type: "Web · Free" },
                  { category: "Research", title: "India Code — Central Acts Repository", desc: "Official repository of every Central Act ever passed by Parliament.", url: "https://www.indiacode.nic.in/", type: "Web · Official" },
                  { category: "Book", title: "Introduction to the Constitution of India — D.D. Basu", desc: "Standard reference for constitutional law students & citizens. Buy from any authorised bookseller.", url: "https://www.amazon.in/s?k=introduction+to+the+constitution+of+india+dd+basu", type: "Book · External" },
                  { category: "Book", title: "Consumer Protection Law & Practice — Avtar Singh", desc: "Detailed commentary on CPA 2019 with case law.", url: "https://www.amazon.in/s?k=consumer+protection+avtar+singh", type: "Book · External" }
                ].map((item, idx) => (
                  <div key={item.title} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between space-y-4 hover:shadow-md transition" data-testid={`study-material-${idx}`}>
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="bg-emerald-50 text-emerald-800 text-xs font-semibold px-2.5 py-1 rounded-md">{item.category}</span>
                        <span className="bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">{item.type}</span>
                      </div>
                      <h4 className="font-bold text-slate-900 text-base leading-snug">{item.title}</h4>
                      <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-[10px] text-slate-400 italic">External source · verify authenticity</span>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 shadow"
                        data-testid={`study-link-${idx}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span>Open Resource</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-xl text-xs text-emerald-900 leading-relaxed font-medium">
              <strong className="block font-bold mb-1">100% FREE — HRCPC is an NGO:</strong>
              Every download &amp; study resource is offered free of cost. If our work has helped you, please consider donating on the <button onClick={() => setActiveTab('support-pay')} className="underline font-bold" data-testid="templates-donate-link">Support / Pay page</button> so we can continue this service for underprivileged citizens.
            </div>

          </div>
        )}

        {/* 5. CLIENT KYC SELF REGISTRATION */}
        {activeTab === "kyc" && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 space-y-6">
              
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-orange-100 text-orange-700 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                  <UserCheck className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Member Self-Registration & KYC Form</h2>
                <p className="text-sm text-slate-600">Register with HRCPC Trust for our Family Advisor &amp; citizen-support programme. Fill details exactly as printed on your Aadhaar Card.</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(window.location.origin + '/register'); toast.success("Registration link copied — share via WhatsApp or SMS."); }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:text-orange-800 border border-orange-200 bg-orange-50 px-3 py-1 rounded-full"
                  data-testid="copy-register-link"
                >
                  <CopyIcon className="w-3.5 h-3.5" />
                  Copy shareable link:&nbsp;<span className="font-mono">/register</span>
                </button>
              </div>

              {/* Referral banner — shown when a Member landed via ?ref=<code> */}
              {refCode && (
                <div
                  className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2"
                  data-testid="referral-invite-banner"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold">✓</span>
                  <span>
                    {refReferrerName
                      ? <>Warm welcome — <strong>{refReferrerName}</strong> invited you to register with HRCPC Legal Cell.</>
                      : <>You were invited by an existing HRCPC Member. Welcome!</>}
                  </span>
                </div>
              )}

              {kycSubmitted ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl text-center space-y-4" data-testid="kyc-submitted">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
                  <h3 className="text-xl font-bold text-emerald-900">KYC Application Submitted Successfully!</h3>
                  <p className="text-sm text-emerald-700 leading-relaxed">
                    Your application has been received and assigned status <strong>Under Review</strong>. Our legal admin will verify your details and generate your Unique Code (HRCPC-C-YYYYMMDD-XXX). You can then log into the Member Portal using your mobile number and OTP.
                  </p>
                  <button 
                    onClick={() => { setKycSubmitted(false); setKycStep(1); setKycOtpSent(false); setKycOtpInput(""); setKycTicket(""); setActiveTab("client-portal"); }}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm shadow transition"
                  >
                    Go to Member Portal Login
                  </button>
                </div>
              ) : kycStep === 1 ? (
                /* Phase-II Sprint 2: Step 1 — Verify Mobile via OTP */
                <div className="space-y-5" data-testid="kyc-wizard-step1">
                  <div className="flex items-center gap-2 text-xs font-bold text-orange-700 uppercase tracking-wide">
                    <span className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center">1</span>
                    Verify your mobile number
                    <span className="ml-auto text-slate-400 font-normal">Step 1 of 2</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    We'll send a 6-digit OTP to your mobile to confirm it belongs to you. This is a DPDP-Act compliance step — no identity documents are collected until your mobile is verified.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Mobile Number *</label>
                      <input
                        type="text"
                        maxLength="10"
                        value={kycForm.mobile}
                        onChange={(e) => setKycForm({...kycForm, mobile: e.target.value.replace(/\D/g,'').slice(0,10)})}
                        placeholder="10-digit mobile (e.g. 9876543210)"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                        data-testid="kyc-step1-mobile"
                        disabled={kycOtpSent}
                      />
                    </div>
                    <div className="flex items-end">
                      {!kycOtpSent ? (
                        <button
                          type="button"
                          disabled={kycOtpBusy || kycForm.mobile.length !== 10}
                          onClick={async () => {
                            setKycOtpBusy(true);
                            try {
                              const res = await axios.post(`${API}/auth/kyc/otp-send`, { mobile: kycForm.mobile });
                              setKycOtpSent(true);
                              if (res.data?.debug_otp) {
                                toast.success(`OTP sent (dev): ${res.data.debug_otp}`);
                              } else {
                                toast.success("OTP dispatched to your mobile.");
                              }
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Failed to send OTP.");
                            } finally {
                              setKycOtpBusy(false);
                            }
                          }}
                          className="w-full bg-orange-600 hover:bg-orange-500 text-white font-semibold py-2.5 rounded-xl shadow text-sm disabled:opacity-50"
                          data-testid="kyc-send-otp-btn"
                        >
                          {kycOtpBusy ? "Sending…" : "Send OTP"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setKycOtpSent(false); setKycOtpInput(""); }}
                          className="w-full bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl text-sm"
                          data-testid="kyc-change-mobile-btn"
                        >
                          Change Mobile
                        </button>
                      )}
                    </div>
                  </div>

                  {kycOtpSent && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Enter the 6-digit OTP</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength="6"
                            value={kycOtpInput}
                            onChange={(e) => setKycOtpInput(e.target.value.replace(/\D/g,'').slice(0,6))}
                            placeholder="123456"
                            className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none tracking-widest font-mono"
                            data-testid="kyc-otp-input"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">The 6-digit code was sent to your mobile. It expires in 5 minutes and works only once.</p>
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            disabled={kycOtpBusy || kycOtpInput.length < 4}
                            onClick={async () => {
                              setKycOtpBusy(true);
                              try {
                                const res = await axios.post(`${API}/auth/kyc/otp-verify`, { mobile: kycForm.mobile, otp: kycOtpInput });
                                setKycTicket(res.data?.kyc_ticket || "");
                                if (res.data?.existing_status && res.data.existing_status !== "Rejected") {
                                  toast(`A KYC application already exists for this mobile: ${res.data.existing_status}. You may still update your submission.`, { icon: "ℹ️", duration: 6000 });
                                }
                                toast.success("Mobile verified. Please fill your KYC details.");
                                setKycStep(2);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Invalid OTP.");
                              } finally {
                                setKycOtpBusy(false);
                              }
                            }}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2.5 rounded-xl shadow text-sm disabled:opacity-50"
                            data-testid="kyc-verify-otp-btn"
                          >
                            {kycOtpBusy ? "Verifying…" : "Verify & Continue"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleKycSubmit} className="space-y-5" data-testid="kyc-wizard-step2">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 uppercase tracking-wide">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center">✓</span>
                    Mobile verified: <span className="font-mono">+91 {kycForm.mobile}</span>
                    <button
                      type="button"
                      onClick={() => { setKycStep(1); setKycOtpSent(false); setKycOtpInput(""); setKycTicket(""); }}
                      className="ml-auto text-slate-500 hover:text-slate-700 font-normal underline text-[11px]"
                      data-testid="kyc-back-to-step1"
                    >
                      Change mobile
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-bold text-orange-700 uppercase tracking-wide">
                    <span className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center">2</span>
                    Fill KYC details
                    <span className="ml-auto text-slate-400 font-normal">Step 2 of 2</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Full Name (As on Aadhaar) *</label>
                      <input 
                        type="text" 
                        value={kycForm.full_name}
                        onChange={(e) => setKycForm({...kycForm, full_name: e.target.value})}
                        placeholder="e.g. Rajesh Kumar Sharma" 
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                        data-testid="kyc-fullname"
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Mobile Number *</label>
                      <input 
                        type="text" 
                        maxLength="10"
                        value={kycForm.mobile}
                        readOnly
                        placeholder="9876543210" 
                        className="w-full px-4 py-2.5 bg-slate-100 border border-slate-300 rounded-xl text-sm text-slate-700 cursor-not-allowed"
                        data-testid="kyc-mobile"
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Father's / Husband's Name *</label>
                      <input
                        type="text"
                        value={kycForm.fathers_husbands_name}
                        onChange={(e) => setKycForm({...kycForm, fathers_husbands_name: e.target.value})}
                        placeholder="e.g. Sh. Ram Kumar Sharma"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                        data-testid="kyc-fathers-husbands"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Email Address</label>
                      <input 
                        type="email" 
                        value={kycForm.email}
                        onChange={(e) => setKycForm({...kycForm, email: e.target.value})}
                        placeholder="member@gmail.com" 
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Aadhaar Last 4 Digits Only *</label>
                      <input 
                        type="text" 
                        maxLength="4"
                        value={kycForm.aadhaar_last4}
                        onChange={(e) => setKycForm({...kycForm, aadhaar_last4: e.target.value})}
                        placeholder="4321" 
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                        data-testid="kyc-aadhaar"
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Residential Address *</label>
                    <input 
                      type="text" 
                      value={kycForm.address}
                      onChange={(e) => setKycForm({...kycForm, address: e.target.value})}
                      placeholder="Door No, Street Name, Locality" 
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                      required 
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">City</label>
                      <input type="text" value={kycForm.city} onChange={(e) => setKycForm({...kycForm, city: e.target.value})} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">State</label>
                      <input type="text" value={kycForm.state} onChange={(e) => setKycForm({...kycForm, state: e.target.value})} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Pincode</label>
                      <input type="text" value={kycForm.pincode} onChange={(e) => setKycForm({...kycForm, pincode: e.target.value})} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Legal Assistance Purpose / Dispute Summary *</label>
                    <textarea 
                      rows="3"
                      value={kycForm.purpose}
                      onChange={(e) => setKycForm({...kycForm, purpose: e.target.value})}
                      placeholder="Describe your legal matter briefly (e.g. Property dispute, Consumer grievance, Medical negligence, Environmental complaint, Family counseling)"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-600 focus:outline-none"
                      required
                    ></textarea>
                  </div>

                  {/* Phase-II optional extended KYC fields */}
                  <div className="border-t border-slate-200 pt-4 space-y-3">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Additional Profile (Optional — helps us serve you better)</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Date of Birth</label>
                        <input type="date" value={kycForm.dob || ""} onChange={(e) => setKycForm({...kycForm, dob: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm" data-testid="kyc-dob" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Gender</label>
                        <select value={kycForm.gender || ""} onChange={(e) => setKycForm({...kycForm, gender: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm" data-testid="kyc-gender">
                          <option value="">-- Select --</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">ID Type</label>
                        <select value={kycForm.id_type || ""} onChange={(e) => setKycForm({...kycForm, id_type: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm" data-testid="kyc-id-type">
                          <option value="">-- Select --</option>
                          <option value="Aadhaar">Aadhaar</option>
                          <option value="PAN">PAN</option>
                          <option value="Voter ID">Voter ID</option>
                          <option value="Passport">Passport</option>
                          <option value="Driving Licence">Driving Licence</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Bank A/C Holder Name</label>
                        <input type="text" value={kycForm.bank_ac_name || ""} onChange={(e) => setKycForm({...kycForm, bank_ac_name: e.target.value})} placeholder="As per passbook" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm" data-testid="kyc-bank-name" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Bank A/C Last 4 Digits</label>
                        <input type="text" maxLength="4" value={kycForm.bank_ac_number_last4 || ""} onChange={(e) => setKycForm({...kycForm, bank_ac_number_last4: e.target.value.replace(/\D/g,'').slice(0,4)})} placeholder="1234" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm" data-testid="kyc-bank-last4" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">IFSC (optional)</label>
                        <input type="text" value={kycForm.bank_ifsc || ""} onChange={(e) => setKycForm({...kycForm, bank_ifsc: e.target.value.toUpperCase().slice(0,11)})} placeholder="SBIN0001234" className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono" data-testid="kyc-bank-ifsc" />
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500">🔒 We store only the last 4 digits of your Aadhaar and Bank A/C. Full numbers are never persisted.</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <label className="block text-sm font-semibold text-slate-700">Upload KYC Documents (Optional)</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      {[
                        { key: "photo_data_url", label: "Passport Photo" },
                        { key: "id_front_data_url", label: "ID Front" },
                        { key: "id_back_data_url", label: "ID Back" },
                      ].map(({ key, label }) => (
                        <label key={key} className="border border-dashed border-slate-300 p-3 rounded-lg text-center bg-white cursor-pointer hover:border-orange-500">
                          <span className="font-semibold text-orange-700 block">{label}</span>
                          <span className="text-slate-400 mt-0.5 block">JPG / PNG · ≤ 2 MB</span>
                          {kycForm[key] && <img src={kycForm[key]} alt={label} className="mt-2 mx-auto h-16 object-contain rounded" />}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid={`kyc-upload-${key}`}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              if (f.size > 2 * 1024 * 1024) { toast.error(`${label} must be ≤ 2 MB`); return; }
                              const reader = new FileReader();
                              reader.onload = (ev) => setKycForm(prev => ({ ...prev, [key]: ev.target.result }));
                              reader.readAsDataURL(f);
                            }}
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <input 
                      type="checkbox" 
                      id="consent"
                      checked={kycForm.consent}
                      onChange={(e) => setKycForm({...kycForm, consent: e.target.checked})}
                      className="w-4 h-4 text-orange-600 rounded border-slate-300 focus:ring-orange-500"
                      required
                    />
                    <label htmlFor="consent" className="text-xs text-slate-600">
                      I hereby consent to HRCPC Trust processing my personal data for legal consultation and case management under applicable data protection norms.
                    </label>
                  </div>

                  <div className="flex items-start space-x-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <input
                      type="checkbox"
                      id="dpdp-consent"
                      checked={!!kycForm.dpdp_consent}
                      onChange={(e) => setKycForm({...kycForm, dpdp_consent: e.target.checked})}
                      className="w-4 h-4 mt-0.5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                      data-testid="kyc-dpdp-consent"
                      required
                    />
                    <label htmlFor="dpdp-consent" className="text-xs text-slate-700 leading-relaxed">
                      <span className="font-bold text-emerald-800">DPDP Act 2023 Consent (mandatory):</span> I understand my personal data (name, contact, ID last-4, address, photo, ID images, bank last-4) will be collected, stored securely and used solely for KYC verification, legal case management and member communication by HRCPC Trust. I may withdraw consent at any time by writing to support@hrcpc.org.
                    </label>
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-orange-700 hover:bg-orange-600 text-white font-bold py-3.5 rounded-xl shadow transition"
                    data-testid="kyc-submit-btn"
                  >
                    Submit KYC Application for Admin Approval
                  </button>

                </form>
              )}

            </div>
          </div>
        )}

        {/* 6A. VOLUNTEER REGISTRATION — Strict, formal, with digital undertaking */}
        {activeTab === "volunteer-register" && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="volunteer-register-page">
            {volunteerSubmitted ? (
              <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-10 text-center space-y-4" data-testid="volunteer-success">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-700 rounded-full mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Thank you, {volunteerSubmitted.name}</h2>
                <p className="text-sm text-slate-700 max-w-xl mx-auto leading-relaxed">
                  Your volunteer application has been received and is <strong>under review</strong>. Our volunteer coordinator will contact you at <strong>+91 {volunteerSubmitted.mobile}</strong> and <strong>{volunteerSubmitted.email}</strong> within 5 working days.
                </p>
                <div className="inline-block bg-slate-50 border border-slate-200 px-4 py-2 rounded-lg">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Application Reference</div>
                  <div className="font-mono font-bold text-emerald-800 text-lg">{volunteerSubmitted.code}</div>
                </div>
                <div className="pt-3">
                  <button
                    onClick={() => { setVolunteerSubmitted(null); setActiveTab("public"); }}
                    className="bg-black hover:bg-slate-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm"
                    data-testid="volunteer-back-home"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-black via-slate-900 to-black text-white rounded-2xl p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-1">Public Registration</span>
                    <h2 className="text-2xl font-extrabold">Volunteer with HRCPC Trust</h2>
                    <p className="text-xs text-slate-300 mt-1 max-w-xl">Applications are subject to formal review. False declarations may lead to permanent disqualification and legal action.</p>
                  </div>
                  <Users className="w-10 h-10 text-emerald-400 hidden md:block" />
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 text-xs text-amber-900 leading-relaxed">
                  <strong>Please note:</strong> All fields marked with <span className="font-bold text-red-700">*</span> are mandatory. Document uploads must be clear photographs of original documents (JPG/PNG, ≤ 3 MB each). Your application will be marked <strong>Under Review</strong> upon submission and reviewed by the volunteer coordinator before onboarding.
                </div>

                {/* ================ SECTION 1: PERSONAL DETAILS ================ */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="vol-personal-section">
                  <h3 className="font-bold text-lg text-slate-900 border-b border-slate-100 pb-2">1. Personal Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { k: "full_name", label: "Full Name (as per Aadhaar)", type: "text", req: true },
                      { k: "fathers_husbands_name", label: "Father's / Husband's Name", type: "text", req: true },
                      { k: "dob", label: "Date of Birth", type: "date", req: true },
                    ].map(f => (
                      <div key={f.k}>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label} {f.req && <span className="text-red-700">*</span>}</label>
                        <input
                          type={f.type}
                          value={volunteerForm.personal[f.k]}
                          onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, [f.k]: e.target.value } }))}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                          data-testid={`vol-personal-${f.k}`}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Gender <span className="text-red-700">*</span></label>
                      <select
                        value={volunteerForm.personal.gender}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, gender: e.target.value } }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="vol-personal-gender"
                      >
                        <option value="">-- Select --</option>
                        <option>Male</option><option>Female</option><option>Other</option><option>Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Mobile Number (10-digit) <span className="text-red-700">*</span></label>
                      <input
                        type="tel" maxLength="10"
                        value={volunteerForm.personal.mobile}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, mobile: e.target.value.replace(/\D/g,'') } }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono"
                        data-testid="vol-personal-mobile"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address <span className="text-red-700">*</span></label>
                      <input
                        type="email"
                        value={volunteerForm.personal.email}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, email: e.target.value } }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="vol-personal-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Current Residential Address <span className="text-red-700">*</span></label>
                    <textarea
                      rows="2"
                      value={volunteerForm.personal.address}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, address: e.target.value } }))}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                      data-testid="vol-personal-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { k: "city", label: "City", type: "text" },
                      { k: "state", label: "State", type: "text" },
                      { k: "pincode", label: "Pincode (6-digit)", type: "text", maxLength: 6 },
                    ].map(f => (
                      <div key={f.k}>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label} <span className="text-red-700">*</span></label>
                        <input
                          type={f.type} maxLength={f.maxLength}
                          value={volunteerForm.personal[f.k]}
                          onChange={(e) => setVolunteerForm(prev => ({ ...prev, personal: { ...prev.personal, [f.k]: f.k === "pincode" ? e.target.value.replace(/\D/g,'') : e.target.value } }))}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                          data-testid={`vol-personal-${f.k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ================ SECTION 2: PROFESSIONAL DETAILS ================ */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="vol-professional-section">
                  <h3 className="font-bold text-lg text-slate-900 border-b border-slate-100 pb-2">2. Professional Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Highest Qualification <span className="text-red-700">*</span></label>
                      <input
                        type="text"
                        value={volunteerForm.professional.qualification}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, professional: { ...prev.professional, qualification: e.target.value } }))}
                        placeholder="e.g. LL.B, M.A., B.Com, Retired IAS"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="vol-prof-qualification"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Current Occupation / Profession <span className="text-red-700">*</span></label>
                      <input
                        type="text"
                        value={volunteerForm.professional.occupation}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, professional: { ...prev.professional, occupation: e.target.value } }))}
                        placeholder="e.g. Practising advocate, Law student, Retired District Judge"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="vol-prof-occupation"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700 mb-1">How do you wish to help? <span className="text-red-700">*</span></label>
                      <select
                        value={volunteerForm.professional.help_area}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, professional: { ...prev.professional, help_area: e.target.value } }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="vol-prof-help-area"
                      >
                        <option value="">-- Select --</option>
                        <option>Law Student</option><option>Advocate</option><option>Retired Officer</option>
                        <option>Research</option><option>Awareness</option><option>Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ================ SECTION 3: DOCUMENT UPLOADS ================ */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="vol-documents-section">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 border-b border-slate-100 pb-2">3. Document Uploads</h3>
                    <p className="text-[11px] text-slate-500 mt-2">Clear photographs of original documents. JPG/PNG/WebP · Max 3 MB each. All 5 are mandatory. Passport is optional.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { k: "photograph", label: "Passport-size Photograph", req: true },
                      { k: "aadhaar_front", label: "Aadhaar Card (Front)", req: true },
                      { k: "aadhaar_back", label: "Aadhaar Card (Back)", req: true },
                      { k: "pan_card", label: "PAN Card", req: true },
                      { k: "bank_proof", label: "Bank Passbook front page or Cancelled Cheque", req: true },
                      { k: "passport", label: "Passport (Optional)", req: false },
                    ].map(d => (
                      <div key={d.k} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`vol-doc-${d.k}`}>
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-slate-700">{d.label} {d.req && <span className="text-red-700">*</span>}</label>
                          {volunteerForm.documents[d.k] && (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">UPLOADED</span>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleVolunteerFileChange(d.k, e.target.files[0])}
                          className="w-full text-xs file:bg-slate-800 file:text-white file:border-0 file:px-3 file:py-1.5 file:rounded-md file:font-semibold file:cursor-pointer file:mr-2 file:text-[10px]"
                          data-testid={`vol-doc-input-${d.k}`}
                        />
                        {volunteerForm.documents[d.k] && (
                          <img src={volunteerForm.documents[d.k]} alt={d.label} className="w-full h-24 object-cover rounded border border-slate-300" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ================ SECTION 4: DIGITAL UNDERTAKING ================ */}
                <div className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6 space-y-4" data-testid="vol-undertaking-section">
                  <div>
                    <h3 className="font-bold text-lg text-red-900 border-b border-red-100 pb-2 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-700" />
                      4. Volunteer Undertaking (Read Carefully)
                    </h3>
                    <p className="text-[11px] text-red-800 mt-2 font-semibold uppercase tracking-wider">Legally binding declaration</p>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-800 leading-relaxed space-y-2 max-h-72 overflow-y-auto" data-testid="vol-undertaking-text">
                    <p className="font-semibold">I hereby declare and undertake that:</p>
                    <ol className="list-decimal list-inside space-y-1.5 pl-1">
                      <li>I have <strong>no pending criminal cases</strong> against me and I have never been convicted of any criminal offence.</li>
                      <li>I shall not engage in any form of <strong>blackmailing, extortion, threat, intimidation, or illegal recovery</strong> in the name of HRCPC Trust or otherwise.</li>
                      <li>I shall <strong>not collect any money or donations</strong> from any person in the name of HRCPC Trust without written authorisation.</li>
                      <li>I shall not use the name, logo, or reputation of HRCPC Trust for any personal, commercial, or illegal purpose.</li>
                      <li>I shall maintain complete <strong>confidentiality</strong> of all information received during my association.</li>
                      <li>I understand that my association with HRCPC Trust is <strong>purely voluntary and honorary</strong>. I am not an employee, agent, or authorised representative of the Trust.</li>
                      <li>HRCPC Trust reserves the right to <strong>terminate my association immediately without assigning any reason</strong> if any of the above conditions are violated.</li>
                      <li>All information and documents submitted by me are <strong>true and correct</strong> to the best of my knowledge.</li>
                    </ol>
                    <p className="pt-1 italic">I am signing this undertaking voluntarily.</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition" data-testid="vol-undertaking-checkbox-wrap">
                    <input
                      type="checkbox"
                      checked={volunteerForm.undertaking.accepted}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, undertaking: { ...prev.undertaking, accepted: e.target.checked } }))}
                      className="mt-0.5 w-5 h-5 accent-red-700"
                      data-testid="vol-undertaking-checkbox"
                    />
                    <span className="text-sm font-semibold text-red-900">I have read and agree to the Volunteer Undertaking.</span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Type your Full Name as Digital Signature <span className="text-red-700">*</span>
                      </label>
                      <input
                        type="text"
                        value={volunteerForm.undertaking.signed_name}
                        onChange={(e) => setVolunteerForm(prev => ({ ...prev, undertaking: { ...prev.undertaking, signed_name: e.target.value } }))}
                        placeholder="Must exactly match your Full Name above"
                        className="w-full px-3 py-2 bg-slate-50 border-2 border-red-300 rounded-lg text-sm font-semibold italic"
                        style={{ fontFamily: "cursive" }}
                        data-testid="vol-undertaking-signed-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Date (auto-captured)</label>
                      <input
                        type="text"
                        readOnly
                        value={new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
                        className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-sm font-mono text-slate-600"
                        data-testid="vol-undertaking-date"
                      />
                    </div>
                  </div>
                </div>

                {/* SUBMIT */}
                <div className="bg-black rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-white text-sm">
                    <div className="font-bold">Ready to submit your application?</div>
                    <div className="text-xs text-slate-400">Review all sections above. False declarations may lead to disqualification.</div>
                  </div>
                  <button
                    onClick={submitVolunteer}
                    disabled={volunteerSaving}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-500 text-white font-bold px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 whitespace-nowrap"
                    data-testid="vol-submit-btn"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    <span>{volunteerSaving ? "Submitting…" : "Submit Volunteer Application"}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}


        {/* 6B. SUPPORT & PAYMENTS PAGE (Direct UPI QRs on top, Razorpay below) */}
        {activeTab === "support-pay" && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="support-pay-page">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-2">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Support &amp; Payments</h2>
                  <p className="text-sm text-slate-600">Pay your case fee balance, download free study material, or contribute to HRCPC Trust's awareness &amp; citizen-support work.</p>
                </div>
              </div>
              <div className="mt-2 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg px-4 py-2 text-xs text-emerald-900 font-semibold flex items-center gap-2" data-testid="support-donation-note">
                <Heart className="w-4 h-4 text-emerald-700" />
                <span>For donations and case fee payments — every rupee funds awareness drives &amp; citizen-support programmes for underprivileged citizens.</span>
              </div>
            </div>

            {/* Direct UPI / Bank Transfer — SBI (Left) + DBS (Right), large scannable QRs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="support-direct-block">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-bold text-slate-900">Direct UPI / Bank Transfer</h3>
                <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-1 rounded">NO CONVENIENCE FEE</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                {/* SBI ACCOUNT — LEFT */}
                <div className="border border-slate-200 rounded-xl overflow-hidden flex flex-col" data-testid="bank-block-sbi">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between">
                    <span className="font-bold text-sm">SBI Bank Account</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Primary</span>
                  </div>
                  <div className="p-5 flex flex-col items-center gap-4 bg-slate-50 flex-1">
                    <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">📱 Scan with any UPI app</p>
                    <div className="bg-white border-4 border-white ring-2 ring-slate-200 rounded-2xl p-3 shadow-lg flex items-center justify-center">
                      {siteSettings.qr_sbi_data_url ? (
                        <img
                          src={siteSettings.qr_sbi_data_url}
                          alt="SBI UPI QR"
                          className="w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 object-contain rounded"
                          style={{ imageRendering: "crisp-edges" }}
                          data-testid="support-qr-sbi"
                        />
                      ) : (
                        <div className="w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-center px-4" data-testid="support-qr-sbi-placeholder">
                          <CreditCard className="w-12 h-12 text-slate-300 mb-2" />
                          <span className="text-sm text-slate-500 font-semibold">SBI QR</span>
                          <span className="text-xs text-slate-400 italic mt-1">Admin will upload</span>
                        </div>
                      )}
                    </div>
                    <div className="w-full text-sm space-y-1.5 bg-white border border-slate-200 rounded-xl p-4">
                      <div className="text-slate-800"><strong>A/C Name:</strong> {siteSettings.bank_details?.sbi?.ac_name}</div>
                      <div className="text-slate-800"><strong>A/C No:</strong> <span className="font-mono" data-testid="support-account-no">{siteSettings.bank_details?.sbi?.ac_no}</span></div>
                      <div className="text-slate-800"><strong>IFSC:</strong> <span className="font-mono" data-testid="support-ifsc">{siteSettings.bank_details?.sbi?.ifsc}</span></div>
                      <div className="text-slate-800 flex items-center gap-2 flex-wrap">
                        <strong>UPI:</strong>
                        <span className="font-mono" data-testid="support-upi-id">{siteSettings.bank_details?.sbi?.upi}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(siteSettings.bank_details?.sbi?.upi || ''); toast.success("SBI UPI ID copied"); }}
                          className="text-orange-700 text-[10px] font-bold"
                          data-testid="support-copy-upi"
                        >
                          COPY
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DBS ACCOUNT — RIGHT */}
                <div className="border border-slate-200 rounded-xl overflow-hidden flex flex-col" data-testid="bank-block-dbs">
                  <div className="bg-gradient-to-r from-rose-600 to-red-700 text-white px-4 py-2 flex items-center justify-between">
                    <span className="font-bold text-sm">DBS Bank Account</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Alternate</span>
                  </div>
                  <div className="p-5 flex flex-col items-center gap-4 bg-slate-50 flex-1">
                    <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">📱 Scan with any UPI app</p>
                    <div className="bg-white border-4 border-white ring-2 ring-slate-200 rounded-2xl p-3 shadow-lg flex items-center justify-center">
                      {siteSettings.qr_dbs_data_url ? (
                        <img
                          src={siteSettings.qr_dbs_data_url}
                          alt="DBS UPI QR"
                          className="w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 object-contain rounded"
                          style={{ imageRendering: "crisp-edges" }}
                          data-testid="support-qr-dbs"
                        />
                      ) : (
                        <div className="w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-center px-4" data-testid="support-qr-dbs-placeholder">
                          <CreditCard className="w-12 h-12 text-slate-300 mb-2" />
                          <span className="text-sm text-slate-500 font-semibold">DBS QR</span>
                          <span className="text-xs text-slate-400 italic mt-1">Admin will upload</span>
                        </div>
                      )}
                    </div>
                    <div className="w-full text-sm space-y-1.5 bg-white border border-slate-200 rounded-xl p-4">
                      <div className="text-slate-800"><strong>A/C Name:</strong> {siteSettings.bank_details?.dbs?.ac_name}</div>
                      <div className="text-slate-800"><strong>A/C No:</strong> <span className="font-mono" data-testid="support-account-no-dbs">{siteSettings.bank_details?.dbs?.ac_no}</span></div>
                      <div className="text-slate-800"><strong>IFSC:</strong> <span className="font-mono" data-testid="support-ifsc-dbs">{siteSettings.bank_details?.dbs?.ifsc}</span></div>
                      <div className="text-slate-800 flex items-center gap-2 flex-wrap">
                        <strong>UPI:</strong>
                        <span className="font-mono" data-testid="support-upi-id-dbs">{siteSettings.bank_details?.dbs?.upi}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(siteSettings.bank_details?.dbs?.upi || ''); toast.success("DBS UPI ID copied"); }}
                          className="text-orange-700 text-[10px] font-bold"
                        >
                          COPY
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 text-[11px] text-amber-900 leading-relaxed">
                After transfer, please email the transaction reference along with your <strong>name / case number / unique code</strong> to <strong>support@hrcpc.org</strong> or WhatsApp our helpline. All payments are treated as contributions to HRCPC Trust. A signed receipt will be shared within 1 working day.
              </div>
            </div>

            {/* Razorpay Secure Checkout — moved BELOW the QR blocks */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="support-razorpay-block">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-bold text-slate-900">Razorpay Secure Checkout</h3>
                <span className="text-[10px] font-bold text-orange-800 bg-orange-100 px-2 py-1 rounded">SIMULATED (demo)</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Cards, UPI apps (Google Pay, PhonePe, Paytm, BHIM), Net Banking, and Wallets. Instant receipt sent to your registered mobile.
              </p>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-md">
                {[500, 1100, 2500].map(v => (
                  <button
                    key={v}
                    onClick={() => triggerRazorpayPayment({ title: "HRCPC Trust — Citizen Support Fund", amount: v, case_id: null })}
                    className="p-3 bg-slate-50 hover:bg-orange-50 border border-slate-200 rounded-xl font-bold text-slate-800 transition text-sm"
                    data-testid={`support-razorpay-${v}`}
                  >
                    ₹{v.toLocaleString()}
                  </button>
                ))}
              </div>
              <button
                onClick={() => triggerRazorpayPayment({ title: "HRCPC Trust — Citizen Support Fund", amount: 1100, case_id: null })}
                className="w-full sm:w-auto sm:min-w-[240px] bg-orange-600 hover:bg-orange-500 text-white font-bold px-6 py-3 rounded-xl shadow flex items-center justify-center space-x-2"
                data-testid="support-razorpay-open"
              >
                <CreditCard className="w-4 h-4" />
                <span>Pay via Razorpay</span>
              </button>
            </div>

            {/* ============ TRUST DETAILS BLOCK (Reg No / PAN / 80G) ============ */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="trust-details-block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-800 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Trust Details &amp; Legal Compliance</h3>
                  <p className="text-xs text-slate-600">HRCPC Trust is a registered public charitable trust — dedicated to citizen awareness, advocacy and pro-bono support across consumer rights, human rights, RTI, environmental protection and medico-legal matters.</p>
                </div>
              </div>

              {(() => {
                const tl = siteSettings.trust_legal || {};
                const rows = [
                  { label: "Trust Registration No.", value: tl.reg_no, fallback: "Available on request", authority: tl.reg_authority, testid: "trust-reg-no" },
                  { label: "PAN (Income Tax)", value: tl.pan, fallback: "Available on request", testid: "trust-pan" },
                  { label: "TAN", value: tl.tan, fallback: "Not applicable", testid: "trust-tan", hideIfEmpty: true },
                  { label: "12A Registration (Income Tax Exempt Status)", value: tl.s12a_reference, fallback: tl.s12a_status || "Applied — awaiting certificate", statusOnly: !tl.s12a_reference, testid: "trust-12a" },
                  { label: "80G Exemption (Donor Tax Benefit)", value: tl.s80g_reference, fallback: tl.s80g_status || "Applied — awaiting certificate", statusOnly: !tl.s80g_reference, testid: "trust-80g" },
                ];
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {rows.filter(r => !(r.hideIfEmpty && !r.value)).map(r => (
                      <div key={r.label} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1" data-testid={r.testid}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{r.label}</div>
                        <div className="font-mono text-sm font-semibold text-slate-900 break-all">
                          {r.value ? r.value : <span className="text-slate-500 italic font-sans font-normal">{r.fallback}</span>}
                        </div>
                        {r.authority && (
                          <div className="text-[10px] text-slate-500 italic">Registered with: {r.authority}</div>
                        )}
                        {r.statusOnly && r.value && (
                          <div className="text-[10px] text-emerald-700 font-semibold">Status: Certified</div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg px-3 py-2 text-[11px] text-emerald-900 leading-relaxed">
                <strong>{(siteSettings.trust_legal?.s80g_status === "Approved") ? "80G tax exemption ACTIVE — " : "80G tax exemption "}</strong>
                {siteSettings.trust_legal?.note || "HRCPC Trust is a registered public charitable trust dedicated to citizen awareness, advocacy and pro-bono support."}
              </div>
            </div>

            <div className="bg-black text-white rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-rose-400" />
                <div>
                  <div className="font-bold">Every contribution keeps HRCPC's citizen-support work free</div>
                  <div className="text-xs text-slate-300">Donations are utilised for court-fee assistance, notice serving costs, awareness camps &amp; pro-bono advocate coordination for underprivileged citizens.</div>
                </div>
              </div>
              <button
                onClick={() => setActiveTab("donate")}
                className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm"
                data-testid="support-goto-donate"
              >
                Make a Donation
              </button>
            </div>
          </div>
        )}

        {/* 6. SUPPORT NGO / DONATION */}
        {activeTab === "donate" && (
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 text-center space-y-6">
              <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                <Heart className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-900">Support Awareness & Citizen Advocacy Work</h2>
                <p className="text-sm text-slate-600">HRCPC Trust is a non-profit organisation (not a law firm) that runs awareness drives, RTI campaigns, and connects underprivileged citizens with independent panel advocates who take up matters on a pro-bono / concessional basis.</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button onClick={() => triggerRazorpayPayment({ title: "HRCPC Citizen Support Donation", amount: 500, case_id: null })} className="p-4 bg-slate-50 hover:bg-orange-50 border border-slate-200 rounded-xl font-bold text-slate-800 transition">₹500</button>
                <button onClick={() => triggerRazorpayPayment({ title: "HRCPC Citizen Support Donation", amount: 1000, case_id: null })} className="p-4 bg-orange-50 border border-orange-300 rounded-xl font-bold text-orange-900 shadow-sm">₹1,000</button>
                <button onClick={() => triggerRazorpayPayment({ title: "HRCPC Citizen Support Donation", amount: 5000, case_id: null })} className="p-4 bg-slate-50 hover:bg-orange-50 border border-slate-200 rounded-xl font-bold text-slate-800 transition">₹5,000</button>
              </div>

              <button 
                onClick={() => triggerRazorpayPayment({ title: "General NGO Support Donation", amount: 1000, case_id: null })}
                className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-3.5 rounded-xl shadow transition flex items-center justify-center space-x-2"
                data-testid="donate-now-btn"
              >
                <DollarSign className="w-5 h-5" />
                <span>Contribute Securely via Razorpay</span>
              </button>
            </div>
          </div>
        )}

        {/* 7. ADMIN PANEL */}
        {activeTab === "admin" && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="admin-panel">
            
            {/* Admin Header */}
            <div className="bg-black text-white p-6 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-amber-500 text-slate-950 rounded-xl flex items-center justify-center font-extrabold text-xl">
                  AD
                </div>
                <div>
                  <h2 className="text-xl font-bold">HRCPC Admin & Master Panel</h2>
                  <p className="text-xs text-slate-400">Manage members, cases, advocates, fee receipts, and KYC approvals</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-slate-800 p-1.5 rounded-xl border border-slate-700 text-xs">
                <button onClick={() => setAdminTab("cases")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'cases' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-cases">Cases ({casesList.length})</button>
                <button onClick={() => setAdminTab("clients")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'clients' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-clients">Members ({clientsList.length})</button>
                <button onClick={() => setAdminTab("advocates")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'advocates' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-advocates">Advocates</button>
                <button onClick={() => setAdminTab("kyc")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'kyc' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-kyc">KYC Review</button>
                <button onClick={() => setAdminTab("tickets")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'tickets' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-tickets">Support Tickets ({supportTicketsList.filter(t => t.status === 'Open').length})</button>
                <button onClick={() => setAdminTab("volunteers")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'volunteers' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-volunteers">Volunteers ({volunteersList.length})</button>
                <button onClick={() => { setAdminTab("donors"); fetchAdminDonations(); }} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'donors' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-donors">Donor Wall</button>
                <button onClick={() => { setAdminTab("wins"); fetchAdminWins(); }} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'wins' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-wins">Wins Wall</button>
                <button onClick={() => { setAdminTab("newsletter"); fetchDigestPreview(); fetchAdvocateDigestList(); fetchAdvocateDigestSchedule(); fetchOverdueDigest(); }} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'newsletter' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-newsletter">Newsletter</button>
                <button onClick={async () => {
                  setAdminTab("referrals");
                  setLeaderboardLoading(true);
                  try {
                    const params = leaderboardMonth ? { month: leaderboardMonth } : {};
                    const res = await axios.get(`${API}/admin/referral-leaderboard`, { params });
                    setReferralLeaderboard(res.data);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not load referral leaderboard");
                    setReferralLeaderboard(null);
                  } finally { setLeaderboardLoading(false); }
                }} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'referrals' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-referrals">Referrals</button>
                <button onClick={() => setAdminTab("settings")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'settings' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-settings">Site Settings</button>
                <button onClick={() => { setAdminTab("audit"); fetchAuditLog(); }} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'audit' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-audit">Audit Log</button>
                {SUPER_FINANCE_EMAILS_FRONT.has(String(currentUser?.data?.email || "").toLowerCase()) && (
                  <button onClick={() => setAdminTab("dataops")} className={`px-3 py-2 rounded-lg font-semibold transition ${adminTab === 'dataops' ? 'bg-rose-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`} data-testid="admin-tab-dataops">Data Ops</button>
                )}
              </div>
            </div>

            {/* Admin Tab: Cases Management */}
            {adminTab === "cases" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-4 p-6">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-900">All Active Legal Cases</h3>
                  <button 
                    onClick={() => {
                      const firstApproved = clientsList.find(c => c.status === 'Approved') || {};
                      setEditingCase({
                        id: "case_" + Date.now(),
                        case_number: "CC/" + Math.floor(100+Math.random()*900) + "/2026",
                        client_id: firstApproved.unique_code || "",
                        client_name: firstApproved.full_name || "",
                        client_mobile: firstApproved.mobile || "",
                        court: "City Civil Court, Hyderabad",
                        title: "New Legal Case Registration",
                        status: "Fresh Filing",
                        next_hearing_date: "2026-04-30",
                        assigned_advocates: ["Adv. K. Srinivasa Rao"],
                        total_fee: 0,
                        fee_paid: 0,
                        balance: 0,
                        payment_history: [],
                        documents: [],
                        notes: [{ id: "n_1", date: "2026-03-28", author: "Admin", text: "Case file created." }],
                        focus_area: "other",
                        // Phase-II fields (defaults)
                        client_role: "",
                        forum_type: "",
                        cnr_number: "",
                        case_nature: "",
                        priority: "Normal",
                        next_action: "",
                        opposing_party: "",
                        conflict_checked: false,
                        conflict_check_result: "clear"
                      });
                      setShowCaseModal(true);
                    }}
                    className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow transition"
                    data-testid="admin-add-case-btn"
                  >
                    + Register New Case
                  </button>
                </div>

                {/* Focus Area filter + reporting bar */}
                {(() => {
                  const counts = casesList.reduce((acc, c) => {
                    const k = c.focus_area || "other";
                    acc[k] = (acc[k] || 0) + 1;
                    return acc;
                  }, {});
                  return (
                    <div className="border border-slate-200 rounded-xl bg-slate-50 p-3 space-y-3" data-testid="case-focus-filter-bar">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Filter by focus area</div>
                        <div className="text-xs text-slate-600">
                          Showing <strong className="text-slate-900">{caseFocusFilter === 'all' ? casesList.length : (counts[caseFocusFilter] || 0)}</strong> of <strong>{casesList.length}</strong> cases
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setCaseFocusFilter('all')}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${caseFocusFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                          data-testid="case-focus-filter-all"
                        >
                          All ({casesList.length})
                        </button>
                        {FOCUS_AREA_KEYS.map(k => (
                          <button
                            key={k}
                            onClick={() => setCaseFocusFilter(k)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${caseFocusFilter === k ? FOCUS_AREA_META[k].cls + ' ring-2 ring-offset-1 ring-slate-400' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                            data-testid={`case-focus-filter-${k}`}
                          >
                            {FOCUS_AREA_META[k].label} ({counts[k] || 0})
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase font-semibold">
                      <tr>
                        <th className="p-3.5">Case Number</th>
                        <th className="p-3.5">Member & Mobile</th>
                        <th className="p-3.5">Title & Court</th>
                        <th className="p-3.5">Focus Area</th>
                        <th className="p-3.5">Status & Next Hearing</th>
                        <th className="p-3.5">Fee & Balance</th>
                        <th className="p-3.5">Advocates</th>
                        <th className="p-3.5">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {casesList.filter(c => caseFocusFilter === 'all' || (c.focus_area || 'other') === caseFocusFilter).map(c => (
                        <tr key={c.case_number} className="hover:bg-slate-50/60">
                          <td className="p-3.5 font-mono font-bold text-orange-900">{c.case_number}</td>
                          <td className="p-3.5 font-semibold text-slate-900">{c.client_name}<br/><span className="text-xs text-slate-500 font-normal">+91 {c.client_mobile}</span></td>
                          <td className="p-3.5 max-w-xs truncate text-slate-700">{c.title}<br/><span className="text-xs text-slate-400">{c.court}</span></td>
                          <td className="p-3.5" data-testid={`case-focus-${c.case_number}`}>
                            {(() => {
                              const fa = c.focus_area || 'other';
                              const meta = FOCUS_AREA_META[fa] || FOCUS_AREA_META.other;
                              return (
                                <select
                                  value={fa}
                                  onChange={async (e) => {
                                    const newFa = e.target.value;
                                    try {
                                      await axios.patch(`${API}/admin/cases/${c.case_number}/focus-area`, { focus_area: newFa });
                                      toast.success(`Focus area updated to ${FOCUS_AREA_META[newFa].label}`);
                                      await fetchData();
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Failed to update focus area");
                                    }
                                  }}
                                  className={`text-[11px] font-semibold px-2 py-1 rounded-full border cursor-pointer ${meta.cls}`}
                                  data-testid={`case-focus-select-${c.case_number}`}
                                >
                                  {FOCUS_AREA_KEYS.map(k => (
                                    <option key={k} value={k}>{FOCUS_AREA_META[k].label}</option>
                                  ))}
                                </select>
                              );
                            })()}
                          </td>
                          <td className="p-3.5">
                            <span className="bg-amber-100 text-amber-900 text-[11px] font-bold px-2 py-0.5 rounded">{c.status}</span>
                            <div className="text-xs text-slate-500 mt-1">Hearing: {fmtDate(c.next_hearing_date)}</div>
                          </td>
                          <td className="p-3.5 font-mono">
                            <div className="text-emerald-700">Paid: ₹{c.fee_paid}</div>
                            <div className="text-rose-600 font-bold">Bal: ₹{c.balance}</div>
                          </td>
                          <td className="p-3.5 text-xs" data-testid={`case-advocates-${c.case_number}`}>
                            <div className="flex flex-wrap gap-1 max-w-[220px]">
                              {(c.assigned_advocate_emails || []).map(em => {
                                const isSuper = SUPER_ADVOCATE_EMAILS_FRONT.has(em);
                                return (
                                  <span key={em} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isSuper ? 'bg-amber-100 text-amber-900' : 'bg-emerald-50 text-emerald-800'}`}>
                                    {em.split("@")[0]}{isSuper && "★"}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="p-3.5 space-x-2 whitespace-nowrap">
                            <button
                              onClick={() => { setAssignCase(c); setAssignSelected(new Set((c.assigned_advocate_emails || []).filter(e => !SUPER_ADVOCATE_EMAILS_FRONT.has(e)))); }}
                              className="text-emerald-700 hover:underline font-semibold text-xs"
                              data-testid={`admin-assign-advocates-${c.case_number}`}
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => openFinanceModal(c)}
                              className="text-amber-700 hover:underline font-semibold text-xs"
                              data-testid={`admin-finance-${c.case_number}`}
                              title="Fee schedule & expense tracking (super only)"
                            >
                              💰 Finance
                            </button>
                            <button
                              onClick={() => setProceedingsCase(c)}
                              className="text-sky-700 hover:underline font-semibold text-xs"
                              data-testid={`admin-proceedings-${c.case_number}`}
                              title="Structured proceedings / timeline log"
                            >
                              🗂 Proceedings
                            </button>
                            <button
                              onClick={() => {
                                setEditingCase(c);
                                setShowCaseModal(true);
                              }}
                              className="text-orange-600 hover:underline font-semibold text-xs"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Admin Tab: Clients Management */}
            {adminTab === "clients" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <h3 className="font-bold text-lg text-slate-900">Registered Members & Unique Codes</h3>
                  <button
                    onClick={() => {
                      setAddClientForm({
                        full_name: "", mobile: "", email: "", aadhaar_last4: "",
                        address: "", city: "Hyderabad", state: "Telangana", pincode: "500001",
                        purpose: "", preferred_language: "Telugu / English"
                      });
                      setShowAddClientModal(true);
                    }}
                    className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow transition"
                    data-testid="admin-add-client-btn"
                  >
                    + Add New Member
                  </button>
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="relative md:w-96">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      placeholder="Search members by name, mobile, unique code, purpose..."
                      className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                      data-testid="client-search-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500">Case link:</span>
                    <span className="text-slate-700">Click "Register New Case" in the Cases tab to link a case to any Approved member.</span>
                  </div>
                </div>

                {(() => {
                  const q = clientSearchQuery.trim().toLowerCase();
                  const visible = clientsList.filter(cli => !q ||
                    (cli.full_name || '').toLowerCase().includes(q) ||
                    (cli.mobile || '').includes(q) ||
                    (cli.unique_code || '').toLowerCase().includes(q) ||
                    (cli.purpose || '').toLowerCase().includes(q) ||
                    (cli.email || '').toLowerCase().includes(q)
                  );
                  if (visible.length === 0) {
                    return (
                      <div className="text-center text-sm text-slate-500 py-6" data-testid="client-empty-state">
                        No members match your search. Click "Add New Member" to onboard one manually.
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {visible.map(cli => {
                        const caseCount = casesList.filter(c => c.client_mobile === cli.mobile).length;
                        return (
                          <div key={cli.unique_code || cli.mobile} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2" data-testid={`client-card-${cli.mobile}`}>
                            <div className="flex justify-between items-start gap-2">
                              <span className="font-bold text-sm text-slate-900 truncate">{cli.full_name}</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                cli.status === 'Approved' ? 'bg-emerald-100 text-emerald-800' :
                                cli.status === 'Rejected' ? 'bg-rose-100 text-rose-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>{cli.status}</span>
                            </div>
                            <p className="text-xs text-slate-500 font-mono">Code: {cli.unique_code}</p>
                            <p className="text-xs text-slate-600">Mobile: +91 {cli.mobile}</p>
                            <p className="text-xs text-slate-600 truncate">Purpose: {cli.purpose}</p>
                            <div className="flex items-center justify-between pt-1 border-t border-slate-200 text-[10px] text-slate-500">
                              <span>Registered: {fmtDateTime(cli.created_at)}</span>
                              <span className="font-bold text-orange-700">{caseCount} case{caseCount === 1 ? '' : 's'}</span>
                            </div>
                            {cli.status === 'Approved' && (
                              <button
                                onClick={() => {
                                  setEditingCase({
                                    id: "case_" + Date.now(),
                                    case_number: "CC/" + Math.floor(100 + Math.random() * 900) + "/2026",
                                    client_id: cli.unique_code,
                                    client_name: cli.full_name,
                                    client_mobile: cli.mobile,
                                    court: "City Civil Court, Hyderabad",
                                    title: "New Case for " + cli.full_name,
                                    status: "Fresh Filing",
                                    next_hearing_date: "2026-04-30",
                                    assigned_advocates: ["Adv. K. Srinivasa Rao"],
                                    total_fee: 0,
                                    fee_paid: 0,
                                    balance: 0,
                                    payment_history: [],
                                    documents: [],
                                    notes: [{ id: "n_1", date: new Date().toISOString().slice(0,10), author: "Admin", text: "Case linked to " + cli.full_name }],
                                    focus_area: "other",
                                    // Phase-II fields (defaults)
                                    client_role: "",
                                    forum_type: "",
                                    cnr_number: "",
                                    case_nature: "",
                                    priority: "Normal",
                                    next_action: "",
                                    opposing_party: "",
                                    conflict_checked: false,
                                    conflict_check_result: "clear"
                                  });
                                  setAdminTab("cases");
                                  setShowCaseModal(true);
                                }}
                                className="w-full mt-1 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-semibold py-1.5 rounded"
                                data-testid={`link-case-${cli.mobile}`}
                              >
                                + Link New Case
                              </button>
                            )}
                            {cli.status === 'Approved' && (
                              <button
                                data-testid={`export-client-pdf-${cli.mobile}`}
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/clients/${cli.mobile}/export-pdf`, { responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `hrcpc-client-${cli.mobile}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success("Member PDF downloaded");
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "PDF export failed");
                                  }
                                }}
                                className="w-full mt-1 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold py-1.5 rounded"
                              >
                                📄 Export Member PDF (A4)
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Admin Tab: KYC Review */}
            {adminTab === "kyc" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <h3 className="font-bold text-lg text-slate-900">KYC Applications & Approvals</h3>
                  <div className="text-xs text-slate-500 font-medium">Total: {clientsList.length} applications</div>
                </div>

                {/* Search + Status filter chips */}
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="relative md:w-72">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={kycSearchQuery}
                      onChange={(e) => setKycSearchQuery(e.target.value)}
                      placeholder="Search by name, mobile, code, purpose..."
                      className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                      data-testid="kyc-search-input"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="kyc-filter-chips">
                    {[
                      { key: "all", label: "All" },
                      { key: "Under Review", label: "Under Review" },
                      { key: "Approved", label: "Approved" },
                      { key: "Rejected", label: "Rejected" }
                    ].map(chip => {
                      const count = chip.key === "all" ? clientsList.length : clientsList.filter(c => c.status === chip.key).length;
                      const active = kycStatusFilter === chip.key;
                      return (
                        <button
                          key={chip.key}
                          onClick={() => setKycStatusFilter(chip.key)}
                          data-testid={`kyc-filter-${chip.key.replace(/ /g, '-').toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            active
                              ? 'bg-orange-600 text-white border-orange-600 shadow'
                              : 'bg-white text-slate-600 border-slate-300 hover:border-orange-400'
                          }`}
                        >
                          {chip.label} <span className="ml-1 opacity-80">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Bulk action bar */}
                  {(() => {
                    const pendingIds = clientsList.filter(c => c.status !== 'Approved' && c.status !== 'Rejected').map(c => c.mobile);
                    return (
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="kyc-bulk-bar">
                        <label className="inline-flex items-center gap-2 text-xs font-semibold text-orange-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pendingIds.length > 0 && pendingIds.every(id => selectedKycIds.has(id))}
                            onChange={(e) => {
                              const next = new Set(selectedKycIds);
                              if (e.target.checked) pendingIds.forEach(id => next.add(id));
                              else pendingIds.forEach(id => next.delete(id));
                              setSelectedKycIds(next);
                            }}
                            className="w-4 h-4"
                            data-testid="kyc-bulk-select-all"
                          />
                          Select all pending ({pendingIds.length})
                        </label>
                        <span className="text-xs text-slate-700 font-semibold" data-testid="kyc-bulk-selected-count">
                          {selectedKycIds.size} selected
                        </span>
                        <input
                          type="text"
                          value={bulkRemarks}
                          onChange={(e) => setBulkRemarks(e.target.value)}
                          placeholder="Optional remarks (applied to all)"
                          className="flex-1 min-w-[200px] px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs"
                          data-testid="kyc-bulk-remarks"
                        />
                        <button
                          disabled={selectedKycIds.size === 0}
                          onClick={async () => {
                            if (selectedKycIds.size === 0) return;
                            if (!window.confirm(`Approve ${selectedKycIds.size} KYC applications?`)) return;
                            try {
                              const res = await axios.post(`${API}/admin/kyc/bulk`, { ids: Array.from(selectedKycIds), action: 'approve', remarks: bulkRemarks });
                              toast.success(`Approved ${res.data.processed}${res.data.failed ? ` · Failed ${res.data.failed}` : ''}`);
                              setSelectedKycIds(new Set()); setBulkRemarks('');
                              await fetchData();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Bulk approve failed");
                            }
                          }}
                          className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                          data-testid="kyc-bulk-approve"
                        >
                          Bulk Approve
                        </button>
                        <button
                          disabled={selectedKycIds.size === 0}
                          onClick={async () => {
                            if (selectedKycIds.size === 0) return;
                            if (!window.confirm(`Reject ${selectedKycIds.size} KYC applications?`)) return;
                            try {
                              const res = await axios.post(`${API}/admin/kyc/bulk`, { ids: Array.from(selectedKycIds), action: 'reject', remarks: bulkRemarks });
                              toast.info(`Rejected ${res.data.processed}${res.data.failed ? ` · Failed ${res.data.failed}` : ''}`);
                              setSelectedKycIds(new Set()); setBulkRemarks('');
                              await fetchData();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Bulk reject failed");
                            }
                          }}
                          className="bg-rose-600 hover:bg-rose-500 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                          data-testid="kyc-bulk-reject"
                        >
                          Bulk Reject
                        </button>
                      </div>
                    );
                  })()}

                  {clientsList
                    .filter(cli => kycStatusFilter === "all" ? true : cli.status === kycStatusFilter)
                    .filter(cli => {
                      const q = kycSearchQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (cli.full_name || '').toLowerCase().includes(q) ||
                        (cli.mobile || '').includes(q) ||
                        (cli.unique_code || '').toLowerCase().includes(q) ||
                        (cli.purpose || '').toLowerCase().includes(q) ||
                        (cli.email || '').toLowerCase().includes(q)
                      );
                    })
                    .map(cli => (
                    <div key={cli.id || cli.mobile} className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-3" data-testid={`kyc-row-${cli.mobile}`}>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                        <div className="space-y-1 flex items-start gap-3">
                          {cli.status !== 'Approved' && cli.status !== 'Rejected' && (
                            <input
                              type="checkbox"
                              checked={selectedKycIds.has(cli.mobile)}
                              onChange={(e) => {
                                const next = new Set(selectedKycIds);
                                if (e.target.checked) next.add(cli.mobile);
                                else next.delete(cli.mobile);
                                setSelectedKycIds(next);
                              }}
                              className="w-4 h-4 mt-1"
                              data-testid={`kyc-select-${cli.mobile}`}
                            />
                          )}
                          <div>
                          <div className="flex items-center space-x-2 flex-wrap">
                            <h4 className="font-bold text-slate-900 text-base">{cli.full_name}</h4>
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-mono">{cli.aadhaar_last4 ? `Aadhaar XX${cli.aadhaar_last4}` : 'Aadhaar: Not provided'}</span>
                            <span className="text-xs text-slate-500 font-mono">({cli.unique_code})</span>
                          </div>
                          <p className="text-xs text-slate-600">Mobile: <strong className="text-slate-900">+91 {cli.mobile}</strong> | Email: {cli.email} | City: {cli.city}</p>
                          <p className="text-xs text-slate-700 bg-orange-50/50 p-2 rounded border border-orange-100"><strong>Purpose:</strong> {cli.purpose}</p>
                          {/* Phase-II Sprint 2: extended profile summary + document thumbnails */}
                          {(cli.dob || cli.gender || cli.id_type || cli.bank_ac_number_last4 || cli.registration_source === 'self' || cli.dpdp_consent) && (
                            <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                              {cli.registration_source === 'self' && <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">Self-registered {cli.mobile_verified ? '· mobile ✓' : ''}</span>}
                              {cli.dpdp_consent && <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">DPDP ✓</span>}
                              {cli.dob && <span>DOB: <strong>{cli.dob}</strong></span>}
                              {cli.gender && <span>Gender: <strong>{cli.gender}</strong></span>}
                              {cli.id_type && <span>ID: <strong>{cli.id_type}</strong>{cli.id_number_last4 ? ` ····${cli.id_number_last4}` : ''}</span>}
                              {cli.bank_ac_number_last4 && <span>Bank: <strong>{cli.bank_ac_name || 'A/C'}</strong> ····{cli.bank_ac_number_last4} {cli.bank_ifsc ? `· ${cli.bank_ifsc}` : ''}</span>}
                            </div>
                          )}
                          {(cli.photo_data_url || cli.id_front_data_url || cli.id_back_data_url) && (
                            <div className="flex gap-2 mt-2" data-testid={`kyc-docs-${cli.mobile}`}>
                              {[
                                { key: 'photo_data_url', label: 'Photo' },
                                { key: 'id_front_data_url', label: 'ID Front' },
                                { key: 'id_back_data_url', label: 'ID Back' },
                              ].filter(d => cli[d.key]).map(d => (
                                <button
                                  key={d.key}
                                  type="button"
                                  onClick={() => window.open(cli[d.key], '_blank')}
                                  className="w-14 h-14 rounded border border-slate-300 overflow-hidden bg-white hover:ring-2 hover:ring-orange-500"
                                  title={`View ${d.label} full size`}
                                  data-testid={`kyc-doc-${d.key}-${cli.mobile}`}
                                >
                                  <img src={cli[d.key]} alt={d.label} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                          {cli.remarks && (
                            <p className="text-xs text-slate-600 italic border-l-2 border-slate-300 pl-2">
                              <strong>Admin remarks:</strong> {cli.remarks}
                              {cli.reviewed_by && <span className="ml-1 text-slate-400">— {cli.reviewed_by}, {cli.reviewed_at}</span>}
                            </p>
                          )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                            cli.status === 'Approved' ? 'bg-emerald-100 text-emerald-800' :
                            cli.status === 'Rejected' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {cli.status}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between pt-2 border-t border-slate-200 gap-2">
                        <div className="text-xs text-slate-500">Registered on: {fmtDateTime(cli.created_at) || '2026-03-28'}</div>
                        <div className="flex items-center space-x-2">
                          {cli.status !== 'Approved' && (
                            <button
                              onClick={() => setKycActionModal({ client: cli, action: 'approve', remarks: '' })}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow"
                              data-testid={`approve-kyc-${cli.mobile}`}
                            >
                              Approve & Create Case
                            </button>
                          )}
                          {cli.status !== 'Rejected' && (
                            <button
                              onClick={() => setKycActionModal({ client: cli, action: 'reject', remarks: '' })}
                              className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold"
                              data-testid={`reject-kyc-${cli.mobile}`}
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(() => {
                    const q = kycSearchQuery.trim().toLowerCase();
                    const visible = clientsList
                      .filter(cli => kycStatusFilter === "all" ? true : cli.status === kycStatusFilter)
                      .filter(cli => !q ||
                        (cli.full_name || '').toLowerCase().includes(q) ||
                        (cli.mobile || '').includes(q) ||
                        (cli.unique_code || '').toLowerCase().includes(q) ||
                        (cli.purpose || '').toLowerCase().includes(q) ||
                        (cli.email || '').toLowerCase().includes(q)
                      );
                    return visible.length === 0 ? (
                      <div className="text-center text-sm text-slate-500 py-6" data-testid="kyc-empty-state">
                        No KYC applications match your filter/search.
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            )}

            {/* Admin Tab: Advocates (super-only CRUD on the allowlist) */}
            {adminTab === "advocates" && (
              <AdminAdvocatesPanel currentUser={currentUser} />
            )}

            {/* Admin Tab: Data Ops (super-only — Wipe test data + CSV Import) */}
            {adminTab === "dataops" && (
              <AdminDataOpsPanel currentUser={currentUser} />
            )}

            {/* Admin Tab: Support Tickets (Ask HRCPC Legal Assistant) */}
            {adminTab === "tickets" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="admin-tickets-panel">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Support Tickets — Human Legal Queries</h3>
                    <p className="text-xs text-slate-500">Citizen queries submitted via "Ask HRCPC Legal Assistant". Reply via email/phone at <strong>support@hrcpc.org</strong>.</p>
                  </div>
                  <div className="text-xs text-slate-500">Total: {supportTicketsList.length} | Open: {supportTicketsList.filter(t => t.status === 'Open').length}</div>
                </div>

                <div className="space-y-3">
                  {supportTicketsList.length === 0 ? (
                    <div className="text-center text-sm text-slate-500 py-8" data-testid="tickets-empty">No support tickets yet.</div>
                  ) : supportTicketsList.map(t => (
                    <div key={t.ticket_code} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-2" data-testid={`ticket-row-${t.ticket_code}`}>
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[11px] font-bold text-orange-800 bg-orange-100 px-2 py-0.5 rounded">{t.ticket_code}</span>
                            <span className="font-bold text-sm text-slate-900">{t.subject}</span>
                          </div>
                          <div className="text-xs text-slate-600">
                            <strong>{t.name}</strong> • +91 {t.mobile}{t.email ? ` • ${t.email}` : ''} • {t.category}
                          </div>
                          <div className="text-[10px] text-slate-400">Received: {fmtDateTime(t.created_at)}</div>
                        </div>
                        <span className={`text-[10px] font-bold px-3 py-1 rounded-full ${
                          t.status === 'Open' ? 'bg-amber-100 text-amber-800' :
                          t.status === 'In Progress' ? 'bg-orange-100 text-orange-800' :
                          t.status === 'Resolved' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-slate-200 text-slate-700'
                        }`}>{t.status}</span>
                      </div>
                      <div className="text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-3 whitespace-pre-line">{t.message}</div>
                      {t.response && (
                        <div className="text-xs text-emerald-900 bg-emerald-50 border-l-2 border-emerald-500 pl-3 py-2">
                          <strong>Response from {t.assigned_to || 'HRCPC Legal Team'}:</strong>
                          <div className="whitespace-pre-line mt-1">{t.response}</div>
                          <div className="text-[10px] text-emerald-700 mt-1">Replied on {t.responded_at}</div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                        <button
                          onClick={() => setTicketRespondModal({ ...t, _draftResponse: t.response || '', _draftStatus: t.status || 'Open' })}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                          data-testid={`ticket-respond-${t.ticket_code}`}
                        >
                          {t.response ? "Update Response" : "Respond / Assign"}
                        </button>
                        {t.status !== 'Resolved' && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.patch(`${API}/support/tickets/${t.ticket_code}`, { status: 'Resolved' });
                                await fetchData();
                                toast.success("Ticket marked resolved.");
                              } catch { toast.error("Update failed."); }
                            }}
                            className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                            data-testid={`ticket-resolve-${t.ticket_code}`}
                          >
                            Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Tab: Volunteers */}
            {adminTab === "volunteers" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4" data-testid="admin-volunteers-panel">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Volunteer Sign-ups</h3>
                    <p className="text-xs text-slate-500">Citizens, law students, retired officers &amp; advocates who volunteered via the header "Volunteer" button.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (!volunteersList.length) { toast.error("No volunteers to export yet."); return; }
                        const headers = ["Reference","Name","Mobile","Email","Role","City","How they want to help","Status","Signed up"];
                        const esc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
                        const rows = volunteersList.map(v => [v.code, v.name, `+91 ${v.mobile}`, v.email || "", v.role, v.city || "", v.help_area || "", v.status, v.created_at].map(esc).join(","));
                        const csv = [headers.map(esc).join(","), ...rows].join("\n");
                        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `hrcpc-volunteers-${new Date().toISOString().slice(0,10)}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success(`Exported ${volunteersList.length} volunteer sign-ups.`);
                      }}
                      className="text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3.5 py-2 rounded-lg flex items-center gap-1.5 shadow"
                      data-testid="volunteers-csv-export"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Export CSV</span>
                    </button>
                    <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold px-3 py-1.5 rounded-full">
                      Total: <span className="font-bold">{volunteersList.length}</span>
                    </div>
                  </div>
                </div>

                {volunteersList.length === 0 ? (
                  <div className="text-center py-10 text-sm text-slate-500 italic">No volunteers signed up yet. Share the site link to gather more helpers.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="volunteers-table">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="py-2 pr-3">Reference</th>
                          <th className="py-2 pr-3">Name</th>
                          <th className="py-2 pr-3">Mobile</th>
                          <th className="py-2 pr-3">Role</th>
                          <th className="py-2 pr-3">City</th>
                          <th className="py-2 pr-3">Occupation</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Signed up</th>
                          <th className="py-2 pr-3">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {volunteersList.map((v, idx) => (
                          <tr key={v.code} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`volunteer-row-${idx}`}>
                            <td className="py-2 pr-3 font-mono text-[11px] text-emerald-800">{v.code}</td>
                            <td className="py-2 pr-3 font-semibold text-slate-900">{v.name}</td>
                            <td className="py-2 pr-3 font-mono">+91 {v.mobile}</td>
                            <td className="py-2 pr-3"><span className="bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">{v.role}</span></td>
                            <td className="py-2 pr-3 text-slate-700">{v.city || v.personal?.city || "—"}</td>
                            <td className="py-2 pr-3 text-slate-600 text-xs max-w-xs truncate" title={v.professional?.occupation || v.help_area || ""}>{v.professional?.occupation || v.help_area || "—"}</td>
                            <td className="py-2 pr-3">
                              <select
                                value={v.status}
                                onChange={async (e) => {
                                  try {
                                    await axios.patch(`${API}/volunteers/${v.code}`, { status: e.target.value });
                                    await fetchData();
                                    toast.success("Status updated");
                                  } catch (err) {
                                    toast.error("Failed to update status");
                                  }
                                }}
                                className="text-xs bg-white border border-slate-300 rounded px-2 py-1"
                                data-testid={`volunteer-status-${idx}`}
                              >
                                <option>New</option>
                                <option>Under Review</option>
                                <option>Approved</option>
                                <option>Rejected</option>
                              </select>
                            </td>
                            <td className="py-2 pr-3 text-[11px] text-slate-500">{fmtDateTime(v.created_at)}</td>
                            <td className="py-2 pr-3">
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/volunteers/${v.code}`);
                                    setVolunteerDetailOpen(res.data);
                                  } catch (err) {
                                    toast.error("Failed to load details.");
                                  }
                                }}
                                className="text-[11px] bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-2.5 py-1 rounded"
                                data-testid={`volunteer-view-${idx}`}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}


            {/* Admin Tab: Site Settings (Founder Photo, QR Code uploads) */}
            {adminTab === "settings" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6" data-testid="admin-settings-panel">
                <div>
                  <h3 className="font-bold text-lg text-slate-900">Site Settings — Uploads</h3>
                  <p className="text-xs text-slate-500">Upload the founder's photograph and the Trust's UPI QR code. Changes appear on the home &amp; Support pages immediately for all visitors. PNG / JPG / WEBP up to 5 MB.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Founder photo uploader */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-900 text-sm">Founder Photograph</h4>
                      {siteSettings.founder_photo_data_url && (
                        <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">LIVE ON HOME</span>
                      )}
                    </div>
                    <div className="aspect-square bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden max-w-[240px]">
                      {siteSettings.founder_photo_data_url ? (
                        <img src={siteSettings.founder_photo_data_url} alt="Founder" className="w-full h-full object-cover" data-testid="admin-founder-preview" />
                      ) : (
                        <div className="text-xs text-slate-400 italic text-center px-2" data-testid="admin-founder-empty">No photo uploaded yet</div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      data-testid="admin-founder-file-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5 MB."); return; }
                        const fd = new FormData();
                        fd.append("image", file);
                        try {
                          await axios.post(`${API}/admin/upload-founder-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                          await fetchData();
                          toast.success("Founder photograph uploaded.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Upload failed.");
                        } finally {
                          e.target.value = "";
                        }
                      }}
                      className="block w-full text-xs text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-600 file:text-white hover:file:bg-orange-500"
                    />
                    {siteSettings.founder_photo_data_url && (
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/admin/founder-photo`);
                            await fetchData();
                            toast.info("Founder photograph removed.");
                          } catch (err) {
                            toast.error("Delete failed.");
                          }
                        }}
                        className="text-xs text-rose-600 hover:underline font-semibold"
                        data-testid="admin-founder-remove"
                      >
                        Remove current photo
                      </button>
                    )}
                  </div>

                  {/* QR code uploaders (SBI + DBS) */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-900 text-sm">UPI Payment QR Codes</h4>
                    </div>

                    {/* SBI */}
                    <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-2" data-testid="admin-qr-sbi-card">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-orange-800">SBI Bank QR</span>
                        {siteSettings.qr_sbi_data_url && (
                          <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">LIVE</span>
                        )}
                      </div>
                      <div className="aspect-square bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden max-w-[180px] mx-auto">
                        {siteSettings.qr_sbi_data_url ? (
                          <img src={siteSettings.qr_sbi_data_url} alt="SBI QR" className="w-full h-full object-contain" data-testid="admin-qr-sbi-preview" />
                        ) : (
                          <div className="text-[10px] text-slate-400 italic text-center px-2" data-testid="admin-qr-sbi-empty">No SBI QR uploaded</div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        data-testid="admin-qr-sbi-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5 MB."); return; }
                          const fd = new FormData();
                          fd.append("image", file);
                          try {
                            await axios.post(`${API}/admin/upload-qr-code?bank=sbi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                            await fetchData();
                            toast.success("SBI QR uploaded.");
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Upload failed.");
                          } finally {
                            e.target.value = "";
                          }
                        }}
                        className="block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[11px] file:font-semibold file:bg-orange-600 file:text-white hover:file:bg-orange-500"
                      />
                      {siteSettings.qr_sbi_data_url && (
                        <button
                          onClick={async () => {
                            try { await axios.delete(`${API}/admin/qr-code?bank=sbi`); await fetchData(); toast.info("SBI QR removed."); }
                            catch { toast.error("Delete failed."); }
                          }}
                          className="text-[11px] text-rose-600 hover:underline font-semibold"
                          data-testid="admin-qr-sbi-remove"
                        >
                          Remove SBI QR
                        </button>
                      )}
                    </div>

                    {/* DBS */}
                    <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-2" data-testid="admin-qr-dbs-card">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-rose-800">DBS Bank QR</span>
                        {siteSettings.qr_dbs_data_url && (
                          <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">LIVE</span>
                        )}
                      </div>
                      <div className="aspect-square bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden max-w-[180px] mx-auto">
                        {siteSettings.qr_dbs_data_url ? (
                          <img src={siteSettings.qr_dbs_data_url} alt="DBS QR" className="w-full h-full object-contain" data-testid="admin-qr-dbs-preview" />
                        ) : (
                          <div className="text-[10px] text-slate-400 italic text-center px-2" data-testid="admin-qr-dbs-empty">No DBS QR uploaded</div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        data-testid="admin-qr-dbs-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5 MB."); return; }
                          const fd = new FormData();
                          fd.append("image", file);
                          try {
                            await axios.post(`${API}/admin/upload-qr-code?bank=dbs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                            await fetchData();
                            toast.success("DBS QR uploaded.");
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Upload failed.");
                          } finally {
                            e.target.value = "";
                          }
                        }}
                        className="block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[11px] file:font-semibold file:bg-orange-600 file:text-white hover:file:bg-orange-500"
                      />
                      {siteSettings.qr_dbs_data_url && (
                        <button
                          onClick={async () => {
                            try { await axios.delete(`${API}/admin/qr-code?bank=dbs`); await fetchData(); toast.info("DBS QR removed."); }
                            catch { toast.error("Delete failed."); }
                          }}
                          className="text-[11px] text-rose-600 hover:underline font-semibold"
                          data-testid="admin-qr-dbs-remove"
                        >
                          Remove DBS QR
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* CONTACT INFO EDITOR */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4" data-testid="admin-contact-editor">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">Editable Trust Office Contact</h4>
                      <p className="text-[11px] text-slate-500">Address, phones, email, and Google Maps link that appear in the footer everywhere.</p>
                    </div>
                    <button
                      onClick={async () => {
                        setContactEditSaving(true);
                        try {
                          const res = await axios.patch(`${API}/admin/contact-info`, contactEditForm);
                          setSiteSettings(prev => ({ ...prev, contact_info: res.data.contact_info }));
                          setContactEditForm(res.data.contact_info);
                          toast.success("Contact details saved.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Save failed.");
                        } finally {
                          setContactEditSaving(false);
                        }
                      }}
                      disabled={contactEditSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-contact-save-btn"
                    >
                      {contactEditSaving ? "Saving…" : "Save Contact"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {[
                      ['address_line1','Address Line 1'],
                      ['address_line2','Address Line 2'],
                      ['phone_primary','Primary Phone'],
                      ['phone_primary_label','Primary Label (e.g. Landline)'],
                      ['phone_secondary','Secondary Phone'],
                      ['phone_secondary_label','Secondary Label (e.g. Mobile)'],
                      ['email','Support Email'],
                      ['map_url','Google Maps URL']
                    ].map(([k, lbl]) => (
                      <div key={k}>
                        <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">{lbl}</label>
                        <input
                          type="text"
                          value={contactEditForm[k] || ''}
                          onChange={(e) => setContactEditForm(prev => ({ ...prev, [k]: e.target.value }))}
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                          data-testid={`admin-contact-${k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* BANK DETAILS EDITOR */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4" data-testid="admin-bank-editor">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">Editable Bank Details (SBI &amp; DBS)</h4>
                      <p className="text-[11px] text-slate-500">These values appear live on Support / Pay page and inside the Payment modal.</p>
                    </div>
                    <button
                      onClick={async () => {
                        setBankEditSaving(true);
                        try {
                          const res = await axios.patch(`${API}/admin/bank-details`, bankEditForm);
                          setSiteSettings(prev => ({ ...prev, bank_details: res.data.bank_details }));
                          setBankEditForm(res.data.bank_details);
                          toast.success("Bank details saved.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Save failed.");
                        } finally {
                          setBankEditSaving(false);
                        }
                      }}
                      disabled={bankEditSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-bank-save-btn"
                    >
                      {bankEditSaving ? "Saving…" : "Save Bank Details"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SBI editor */}
                    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2" data-testid="admin-bank-sbi-editor">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-orange-800">SBI Account</h5>
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">Primary</span>
                      </div>
                      {['ac_name', 'ac_no', 'ifsc', 'upi'].map(field => (
                        <div key={field}>
                          <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                            {field === 'ac_name' ? 'Account Name' : field === 'ac_no' ? 'Account Number' : field.toUpperCase()}
                          </label>
                          <input
                            type="text"
                            value={bankEditForm.sbi?.[field] || ''}
                            onChange={(e) => setBankEditForm(prev => ({ ...prev, sbi: { ...prev.sbi, [field]: e.target.value } }))}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-600"
                            data-testid={`admin-bank-sbi-${field}`}
                          />
                        </div>
                      ))}
                    </div>

                    {/* DBS editor */}
                    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2" data-testid="admin-bank-dbs-editor">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-rose-800">DBS Account</h5>
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider">Alternate</span>
                      </div>
                      {['ac_name', 'ac_no', 'ifsc', 'upi'].map(field => (
                        <div key={field}>
                          <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                            {field === 'ac_name' ? 'Account Name' : field === 'ac_no' ? 'Account Number' : field.toUpperCase()}
                          </label>
                          <input
                            type="text"
                            value={bankEditForm.dbs?.[field] || ''}
                            onChange={(e) => setBankEditForm(prev => ({ ...prev, dbs: { ...prev.dbs, [field]: e.target.value } }))}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-600"
                            data-testid={`admin-bank-dbs-${field}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ---- Trust Legal (Reg No / PAN / 80G / 12A) Editor ---- */}
                <div className="border-t border-slate-200 pt-6 space-y-4" data-testid="admin-trust-legal-editor">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-slate-900">Trust Legal Details (Reg No / PAN / 80G)</h4>
                      <p className="text-xs text-slate-500">Shown publicly on the Support / Pay page as the "Trust Details" block. Leave blank if not yet issued.</p>
                    </div>
                    <button
                      onClick={saveTrustLegal}
                      disabled={trustLegalSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-trust-legal-save-btn"
                    >
                      {trustLegalSaving ? "Saving…" : "Save Trust Details"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'reg_no', label: 'Trust Registration Number', placeholder: 'e.g. 1234/2013' },
                      { key: 'reg_authority', label: 'Registration Authority', placeholder: 'Sub-Registrar Office, Hyderabad' },
                      { key: 'pan', label: 'PAN', placeholder: 'e.g. AABTH1234K' },
                      { key: 'tan', label: 'TAN (optional)', placeholder: 'e.g. HYDA12345B' },
                      { key: 's12a_status', label: '12A Status', placeholder: 'Approved / Applied / Not Applied' },
                      { key: 's12a_reference', label: '12A Reference / URN', placeholder: 'e.g. AAETH1234K/12A/2024' },
                      { key: 's80g_status', label: '80G Status', placeholder: 'Approved / Applied / Not Applied' },
                      { key: 's80g_reference', label: '80G Reference / URN', placeholder: 'e.g. AAETH1234K/80G/2024' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">{f.label}</label>
                        <input
                          type="text"
                          value={trustLegalEditForm[f.key] || ''}
                          onChange={(e) => setTrustLegalEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-600"
                          data-testid={`admin-trust-legal-${f.key}`}
                        />
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Public Note</label>
                      <textarea
                        rows="2"
                        value={trustLegalEditForm.note || ''}
                        onChange={(e) => setTrustLegalEditForm(prev => ({ ...prev, note: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                        data-testid="admin-trust-legal-note"
                      />
                    </div>
                  </div>
                </div>

                {/* ---- Impact Counter Editor ---- */}
                <div className="border-t border-slate-200 pt-6 space-y-4" data-testid="admin-impact-stats-editor">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-slate-900">Impact Counter (Home page live numbers)</h4>
                      <p className="text-xs text-slate-500">Update these numbers regularly so the animated counter on the home page reflects HRCPC's current work.</p>
                    </div>
                    <button
                      onClick={saveImpactStats}
                      disabled={impactStatsSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-impact-stats-save-btn"
                    >
                      {impactStatsSaving ? "Saving…" : "Save Impact Stats"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { k: "cases_supported", label: "Cases Supported" },
                      { k: "advocates_panel", label: "Panel Advocates" },
                      { k: "amount_recovered_lakhs", label: "Relief (₹ Lakhs)" },
                      { k: "years_serving", label: "Years Serving" },
                      { k: "rtis_filed", label: "RTIs Filed" },
                      { k: "awareness_camps", label: "Awareness Camps" },
                      { k: "citizens_supported", label: "Citizens Supported" },
                    ].map(f => (
                      <div key={f.k}>
                        <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">{f.label}</label>
                        <input
                          type="number"
                          min="0"
                          value={impactStatsForm[f.k] ?? 0}
                          onChange={(e) => setImpactStatsForm(prev => ({ ...prev, [f.k]: Number(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-600"
                          data-testid={`admin-impact-${f.k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ---- Founder Video URL Editor ---- */}
                <div className="border-t border-slate-200 pt-6 space-y-3" data-testid="admin-founder-video-editor">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-slate-900">Founder Video Intro URL</h4>
                      <p className="text-xs text-slate-500">Paste a YouTube link (e.g. https://youtu.be/XXXXX), Vimeo link, or a direct .mp4 URL. Leave blank to show the "Coming soon" placeholder on the home page.</p>
                    </div>
                    <button
                      onClick={saveFounderVideo}
                      disabled={founderVideoSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-founder-video-save-btn"
                    >
                      {founderVideoSaving ? "Saving…" : (founderVideoForm ? "Save Video URL" : "Clear Video")}
                    </button>
                  </div>
                  <input
                    type="url"
                    value={founderVideoForm}
                    onChange={(e) => setFounderVideoForm(e.target.value)}
                    placeholder="https://youtu.be/XXXXXXXXXXX  or  https://your-cdn.com/founder.mp4"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-600"
                    data-testid="admin-founder-video-url"
                  />
                  {siteSettings.founder_video_url && (
                    <div className="text-[11px] text-emerald-700 font-semibold">
                      Currently live: <a href={siteSettings.founder_video_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{siteSettings.founder_video_url}</a>
                    </div>
                  )}
                </div>

                {/* ---- Announcement Banner Editor ---- */}
                <div className="border-t border-slate-200 pt-6 space-y-3" data-testid="admin-announcement-editor">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-slate-900">Site-Wide Announcement Banner</h4>
                      <p className="text-xs text-slate-500">Time-sensitive notice that appears at the top of every page (dismissible per session). Turn off when no longer relevant.</p>
                    </div>
                    <button
                      onClick={saveAnnouncement}
                      disabled={announcementSaving}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-300 text-white font-semibold text-xs px-4 py-2 rounded-lg shadow"
                      data-testid="admin-announcement-save-btn"
                    >
                      {announcementSaving ? "Saving…" : "Save Announcement"}
                    </button>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={announcementForm.enabled}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-4 h-4 accent-emerald-600"
                      data-testid="admin-announcement-enabled"
                    />
                    <span className="font-semibold">Show banner on every page</span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Announcement Text</label>
                      <input
                        type="text"
                        maxLength="200"
                        value={announcementForm.text}
                        onChange={(e) => setAnnouncementForm(prev => ({ ...prev, text: e.target.value }))}
                        placeholder="e.g. Free legal awareness camp on 15 Aug — register now"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="admin-announcement-text"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Style</label>
                      <select
                        value={announcementForm.level}
                        onChange={(e) => setAnnouncementForm(prev => ({ ...prev, level: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="admin-announcement-level"
                      >
                        <option value="info">Info (black + orange)</option>
                        <option value="success">Success (emerald)</option>
                        <option value="warning">Warning (amber)</option>
                        <option value="urgent">Urgent (red)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Link Label (optional)</label>
                      <input
                        type="text"
                        maxLength="40"
                        value={announcementForm.link_label}
                        onChange={(e) => setAnnouncementForm(prev => ({ ...prev, link_label: e.target.value }))}
                        placeholder="e.g. Register now"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                        data-testid="admin-announcement-link-label"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Link URL (optional)</label>
                      <input
                        type="text"
                        value={announcementForm.link_url}
                        onChange={(e) => setAnnouncementForm(prev => ({ ...prev, link_url: e.target.value }))}
                        placeholder="/register  or  https://…"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono"
                        data-testid="admin-announcement-link-url"
                      />
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-500 italic">
                    Tip: Use <span className="font-mono bg-slate-100 px-1 rounded">/register</span> for internal routes, or a full <span className="font-mono bg-slate-100 px-1 rounded">https://…</span> URL for external links.
                  </p>
                </div>



              </div>
            )}

            {/* ADMIN - DONOR WALL MANAGEMENT */}
            {adminTab === "donors" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="admin-donors-panel">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Donor Recognition Wall</h3>
                    <p className="text-xs text-slate-500">Log offline bank transfers so supporters appear on the home-page thank-you strip. Names are auto-anonymised publicly (e.g. <span className="font-mono">R****h I.</span>).</p>
                  </div>
                  <button onClick={fetchAdminDonations} disabled={adminDonationLoading} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg" data-testid="donors-refresh">
                    {adminDonationLoading ? "Loading…" : "Refresh"}
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Total Supporters</div>
                    <div className="text-2xl font-extrabold text-slate-900" data-testid="donors-total-count">{adminDonations.items.length}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Total Contributed</div>
                    <div className="text-2xl font-extrabold text-slate-900">₹{Number(adminDonations.total_amount || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Visible on Wall</div>
                    <div className="text-2xl font-extrabold text-slate-900">{adminDonations.items.filter(x => x.visible).length}</div>
                  </div>
                </div>

                {/* Add form */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-sm text-slate-900">Log an Offline Donation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <input
                      value={adminDonationForm.donor_name}
                      onChange={e => setAdminDonationForm({ ...adminDonationForm, donor_name: e.target.value })}
                      placeholder="Donor full name"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm md:col-span-2"
                      data-testid="donor-form-name"
                    />
                    <input
                      value={adminDonationForm.donor_city}
                      onChange={e => setAdminDonationForm({ ...adminDonationForm, donor_city: e.target.value })}
                      placeholder="City (optional)"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      data-testid="donor-form-city"
                    />
                    <input
                      value={adminDonationForm.amount}
                      onChange={e => setAdminDonationForm({ ...adminDonationForm, amount: e.target.value })}
                      placeholder="Amount ₹"
                      type="number"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      data-testid="donor-form-amount"
                    />
                    <select
                      value={adminDonationForm.mode}
                      onChange={e => setAdminDonationForm({ ...adminDonationForm, mode: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      data-testid="donor-form-mode"
                    >
                      <option>SBI Bank Transfer</option>
                      <option>DBS Bank Transfer</option>
                      <option>UPI</option>
                      <option>Cheque</option>
                      <option>Cash</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <input
                    value={adminDonationForm.note}
                    onChange={e => setAdminDonationForm({ ...adminDonationForm, note: e.target.value })}
                    placeholder="Note / reference (optional, private)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="donor-form-note"
                  />
                  <input
                    value={adminDonationForm.donor_email}
                    onChange={e => setAdminDonationForm({ ...adminDonationForm, donor_email: e.target.value })}
                    placeholder="Donor email (optional — used for Newsletter segment)"
                    type="email"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="donor-form-email"
                  />
                  <button onClick={submitAdminDonation} className="bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="donor-form-submit">
                    Add to Donor Wall
                  </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-200 rounded-xl">
                  <table className="min-w-full text-xs" data-testid="donors-table">
                    <thead className="bg-slate-50 text-left uppercase text-[10px] tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="p-3">When (UTC)</th>
                        <th className="p-3">Donor</th>
                        <th className="p-3">City</th>
                        <th className="p-3">Amount</th>
                        <th className="p-3">Mode</th>
                        <th className="p-3">Source</th>
                        <th className="p-3">Visible</th>
                        <th className="p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {adminDonations.items.map(d => (
                        <tr key={d.id} className="hover:bg-slate-50">
                          <td className="p-3 font-mono text-slate-600">{(d.ts || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="p-3 text-slate-900 font-semibold">{d.donor_name}</td>
                          <td className="p-3 text-slate-700">{d.donor_city || "—"}</td>
                          <td className="p-3 font-bold text-emerald-700">₹{Number(d.amount || 0).toLocaleString()}</td>
                          <td className="p-3 text-slate-700">{d.mode}</td>
                          <td className="p-3">
                            <span className={`px-1.5 py-0.5 rounded font-semibold ${d.source === 'razorpay' ? 'bg-orange-100 text-orange-800' : d.source === 'offline_admin' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                              {d.source || "—"}
                            </span>
                          </td>
                          <td className="p-3">
                            <label className="inline-flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!d.visible}
                                onChange={e => toggleDonationVisibility(d.id, e.target.checked)}
                                className="w-4 h-4"
                                data-testid={`donor-toggle-${d.id}`}
                              />
                              <span className="text-slate-600">{d.visible ? "Public" : "Hidden"}</span>
                            </label>
                          </td>
                          <td className="p-3">
                            <button onClick={() => deleteAdminDonation(d.id)} className="text-rose-600 hover:text-rose-800 font-semibold text-[11px]" data-testid={`donor-delete-${d.id}`}>Delete</button>
                          </td>
                        </tr>
                      ))}
                      {adminDonations.items.length === 0 && !adminDonationLoading && (
                        <tr><td colSpan="8" className="p-6 text-center text-slate-500 italic">No donations yet. Log offline bank transfers above so supporters appear on the wall.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ADMIN - AUDIT LOG */}
            {/* ADMIN - WINS WALL MANAGEMENT */}
            {adminTab === "wins" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="admin-wins-panel">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Public Wins Wall</h3>
                    <p className="text-xs text-slate-500">Anonymised outcomes shown on the home-page strip. Never include member names or identifying details.</p>
                  </div>
                  <button onClick={fetchAdminWins} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg">Refresh</button>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-sm text-slate-900">Add a Win</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <select value={adminWinForm.focus_area} onChange={e => setAdminWinForm({...adminWinForm, focus_area: e.target.value})} className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="win-form-focus">
                      {FOCUS_AREA_KEYS.map(k => <option key={k} value={k}>{FOCUS_AREA_META[k].label}</option>)}
                    </select>
                    <input value={adminWinForm.case_type} onChange={e => setAdminWinForm({...adminWinForm, case_type: e.target.value})} placeholder="Case type (e.g. Consumer Complaint)" className="px-3 py-2 border border-slate-300 rounded-lg text-sm md:col-span-2" data-testid="win-form-type" />
                    <input value={adminWinForm.year} onChange={e => setAdminWinForm({...adminWinForm, year: e.target.value})} placeholder="Year" type="number" className="px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="win-form-year" />
                  </div>
                  <input value={adminWinForm.outcome} onChange={e => setAdminWinForm({...adminWinForm, outcome: e.target.value})} placeholder="Outcome (anonymised, max 280 chars)" maxLength={280} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="win-form-outcome" />
                  <input value={adminWinForm.city} onChange={e => setAdminWinForm({...adminWinForm, city: e.target.value})} placeholder="City (optional)" className="w-full md:w-1/3 px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="win-form-city" />
                  <button onClick={submitAdminWin} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="win-form-submit">Publish to Wall</button>
                </div>

                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="min-w-full text-xs" data-testid="wins-admin-table">
                    <thead className="bg-slate-50 text-left uppercase text-[10px] tracking-wider text-slate-500">
                      <tr>
                        <th className="p-3">When</th>
                        <th className="p-3">Focus</th>
                        <th className="p-3">Case Type</th>
                        <th className="p-3">Outcome</th>
                        <th className="p-3">Year · City</th>
                        <th className="p-3">Visible</th>
                        <th className="p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {adminWins.items.map(w => {
                        const meta = FOCUS_AREA_META[w.focus_area] || FOCUS_AREA_META.other;
                        return (
                          <tr key={w.id} className="hover:bg-slate-50">
                            <td className="p-3 font-mono text-slate-600">{fmtDate((w.created_at || "").slice(0, 10))}</td>
                            <td className="p-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span></td>
                            <td className="p-3 font-semibold text-slate-900">{w.case_type}</td>
                            <td className="p-3 text-slate-700 max-w-md">{w.outcome}</td>
                            <td className="p-3 text-slate-600">{w.year}{w.city ? ` · ${w.city}` : ''}</td>
                            <td className="p-3">
                              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={!!w.visible} onChange={e => toggleWinVisibility(w.id, e.target.checked)} className="w-4 h-4" data-testid={`win-toggle-${w.id}`} />
                                <span className="text-slate-600">{w.visible ? "Public" : "Hidden"}</span>
                              </label>
                            </td>
                            <td className="p-3">
                              <button onClick={() => deleteAdminWin(w.id)} className="text-rose-600 hover:text-rose-800 font-semibold text-[11px]" data-testid={`win-delete-${w.id}`}>Delete</button>
                            </td>
                          </tr>
                        );
                      })}
                      {adminWins.items.length === 0 && (
                        <tr><td colSpan="7" className="p-6 text-center text-slate-500 italic">No wins yet. Add an anonymised outcome above.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ADMIN - NEWSLETTER / IMPACT DIGEST */}
            {adminTab === "newsletter" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="admin-newsletter-panel">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Monthly Impact Digest</h3>
                    <p className="text-xs text-slate-500">Preview and email a monthly summary of contributions, resolved matters and volunteer growth to your supporters. Sent via Resend.</p>
                  </div>
                  <button onClick={fetchDigestPreview} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg" data-testid="digest-refresh">Refresh Preview</button>
                </div>

                {digestPreview && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 space-y-3" data-testid="digest-preview">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="font-bold text-lg text-slate-900">{digestPreview.month}</h4>
                      <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded uppercase tracking-wider">Preview</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white border border-amber-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Supporters</div>
                        <div className="text-xl font-extrabold text-slate-900">{digestPreview.donor_count}</div>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Contributions</div>
                        <div className="text-xl font-extrabold text-slate-900">₹{Number(digestPreview.donor_total || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Matters Resolved</div>
                        <div className="text-xl font-extrabold text-slate-900">{digestPreview.resolved_cases}</div>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-lg p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Volunteers</div>
                        <div className="text-xl font-extrabold text-slate-900">{digestPreview.approved_volunteers}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                    <div>
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Recipient Segment</label>
                      <div className="flex flex-wrap gap-2 mt-1.5" data-testid="digest-segments">
                        {[
                          { key: "donors", label: "Donors only" },
                          { key: "volunteers", label: "Volunteers only" },
                          { key: "clients", label: "Members only" },
                          { key: "everyone", label: "Everyone" },
                        ].map(s => (
                          <button
                            key={s.key}
                            onClick={() => setDigestSegment(s.key)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${digestSegment === s.key ? 'bg-orange-600 border-orange-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-orange-400'}`}
                            data-testid={`digest-segment-${s.key}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => loadDigestSegmentRecipients(digestSegment)}
                      className="bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold px-4 py-2 rounded-lg"
                      data-testid="digest-load-recipients"
                    >
                      Load recipients
                    </button>
                  </div>
                  {digestSegmentCount !== null && (
                    <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block font-semibold" data-testid="digest-segment-count">
                      {digestSegmentCount} email{digestSegmentCount === 1 ? '' : 's'} loaded from "{digestSegment}" segment
                    </div>
                  )}
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Recipients (comma or newline separated)</label>
                  <textarea
                    value={digestRecipients}
                    onChange={e => setDigestRecipients(e.target.value)}
                    rows={4}
                    placeholder="donor1@example.com, donor2@example.com, ..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono"
                    data-testid="digest-recipients"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => sendImpactDigest(true)}
                      disabled={digestSending}
                      className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      data-testid="digest-dryrun"
                    >
                      {digestSending ? "…" : "Dry-run preview"}
                    </button>
                    <button
                      onClick={() => sendImpactDigest(false)}
                      disabled={digestSending}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      data-testid="digest-send"
                    >
                      {digestSending ? "Sending…" : `Send Digest`}
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500">Uses your Resend API key. Max 200 recipients per send. All emails are personalised per recipient.</p>
                </div>

                {/* Advocate Weekly Digest — per-advocate personalised */}
                <div className="border-t border-slate-200 pt-5 mt-4 space-y-4" data-testid="advocate-digest-block">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 className="font-bold text-slate-900">Advocate Weekly Digest</h4>
                      <p className="text-[11px] text-slate-500">Fri-morning email to each advocate — their upcoming hearings, cases missing docs, and stale-status cases. Personalised per advocate.</p>
                    </div>
                    <button onClick={fetchAdvocateDigestList} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg" data-testid="advocate-digest-refresh">
                      Refresh list
                    </button>
                  </div>

                  {/* Auto-schedule toggle */}
                  {advocateDigestSchedule && (
                    <div className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${advocateDigestSchedule.enabled ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`} data-testid="advocate-digest-auto-block">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${advocateDigestSchedule.enabled ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'}`}>
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 text-sm">
                            Auto-send · <span className={advocateDigestSchedule.enabled ? 'text-emerald-800' : 'text-slate-600'}>{advocateDigestSchedule.enabled ? 'ENABLED' : 'DISABLED'}</span>
                          </div>
                          <div className="text-[11px] text-slate-600">
                            {advocateDigestSchedule.cron}
                            {advocateDigestSchedule.next_run && (
                              <> · Next run: <span className="font-mono">{new Date(advocateDigestSchedule.next_run).toISOString().slice(0, 16).replace('T', ' ')} UTC</span></>
                            )}
                          </div>
                          {advocateDigestSchedule.last_run && (
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              Last auto-run: {String(advocateDigestSchedule.last_run).slice(0, 16).replace('T', ' ')} UTC
                              {advocateDigestSchedule.last_stats && (
                                <> · Sent {advocateDigestSchedule.last_stats.sent} · Skipped {advocateDigestSchedule.last_stats.skipped}{advocateDigestSchedule.last_stats.failed ? ` · Failed ${advocateDigestSchedule.last_stats.failed}` : ''}</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleAdvocateDigestSchedule(!advocateDigestSchedule.enabled)}
                        className={`text-xs font-bold px-4 py-2 rounded-lg text-white shadow ${advocateDigestSchedule.enabled ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                        data-testid="advocate-digest-auto-toggle"
                      >
                        {advocateDigestSchedule.enabled ? "Disable auto-send" : "Enable auto-send"}
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-[360px]">
                    <table className="min-w-full text-xs" data-testid="advocate-digest-table">
                      <thead className="bg-slate-50 text-left uppercase text-[10px] tracking-wider text-slate-500 sticky top-0">
                        <tr>
                          <th className="p-3">
                            <input
                              type="checkbox"
                              checked={advocateDigestList.advocates.length > 0 && advocateDigestList.advocates.every(a => selectedAdvocateEmails.has(a.email))}
                              onChange={(e) => {
                                const next = new Set(selectedAdvocateEmails);
                                if (e.target.checked) advocateDigestList.advocates.forEach(a => next.add(a.email));
                                else advocateDigestList.advocates.forEach(a => next.delete(a.email));
                                setSelectedAdvocateEmails(next);
                              }}
                              data-testid="advocate-digest-select-all"
                            />
                          </th>
                          <th className="p-3">Advocate</th>
                          <th className="p-3">Assigned</th>
                          <th className="p-3">Hearings (7d)</th>
                          <th className="p-3">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {advocateDigestList.advocates.map(a => (
                          <tr key={a.email} className="hover:bg-slate-50">
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedAdvocateEmails.has(a.email)}
                                onChange={(e) => {
                                  const next = new Set(selectedAdvocateEmails);
                                  if (e.target.checked) next.add(a.email); else next.delete(a.email);
                                  setSelectedAdvocateEmails(next);
                                }}
                                data-testid={`advocate-digest-select-${a.email}`}
                              />
                            </td>
                            <td className="p-3">
                              <div className="font-semibold text-slate-900">{a.name}</div>
                              <div className="text-[10px] font-mono text-slate-500">{a.email}{a.is_super && <span className="ml-1 text-emerald-700 font-bold">· SUPER</span>}</div>
                            </td>
                            <td className="p-3 font-bold text-slate-900">{a.total_cases}</td>
                            <td className="p-3">
                              <span className={`font-bold ${a.upcoming_hearings_this_week > 0 ? 'text-orange-700' : 'text-slate-400'}`}>{a.upcoming_hearings_this_week}</span>
                            </td>
                            <td className="p-3">
                              <button
                                onClick={() => previewAdvocateDigest(a.email)}
                                className="text-emerald-700 hover:underline font-semibold text-[11px]"
                                data-testid={`advocate-digest-preview-${a.email}`}
                                disabled={a.total_cases === 0}
                              >
                                {a.total_cases === 0 ? 'No cases' : 'Preview'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {advocateDigestList.advocates.length === 0 && (
                          <tr><td colSpan="5" className="p-6 text-center text-slate-500 italic">Click "Refresh list" to load advocates.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {advocateDigestPreview && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2" data-testid="advocate-digest-preview">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-bold text-emerald-900">{advocateDigestPreview.name}</div>
                          <div className="text-[10px] font-mono text-emerald-800">{advocateDigestPreview.email}</div>
                        </div>
                        <button onClick={() => setAdvocateDigestPreview(null)} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-white p-2 rounded border border-emerald-200"><div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Total cases</div><div className="font-extrabold text-slate-900">{advocateDigestPreview.total_cases}</div></div>
                        <div className="bg-white p-2 rounded border border-emerald-200"><div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Hearings (7d)</div><div className="font-extrabold text-slate-900">{advocateDigestPreview.upcoming_hearings?.length || 0}</div></div>
                        <div className="bg-white p-2 rounded border border-emerald-200"><div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Docs pending</div><div className="font-extrabold text-slate-900">{advocateDigestPreview.docs_pending?.length || 0}</div></div>
                        <div className="bg-white p-2 rounded border border-emerald-200"><div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Stale (14d+)</div><div className="font-extrabold text-slate-900">{advocateDigestPreview.stale_status?.length || 0}</div></div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-600 font-semibold" data-testid="advocate-digest-selected-count">
                      {selectedAdvocateEmails.size} selected · {advocateDigestList.advocates.length} total
                    </span>
                    <button
                      onClick={() => sendAdvocateDigest(true, Array.from(selectedAdvocateEmails))}
                      disabled={advocateDigestSending}
                      className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      data-testid="advocate-digest-dryrun"
                    >
                      {advocateDigestSending ? "…" : "Dry-run"}
                    </button>
                    <button
                      onClick={() => sendAdvocateDigest(false, Array.from(selectedAdvocateEmails))}
                      disabled={advocateDigestSending}
                      className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      data-testid="advocate-digest-send-selected"
                    >
                      {advocateDigestSending ? "…" : `Send to ${selectedAdvocateEmails.size || 'ALL'}`}
                    </button>
                  </div>
                </div>

                {/* Overdue Task Digest — Sprint 4.2 */}
                <div className="border-t border-slate-200 pt-5 mt-4 space-y-4" data-testid="overdue-digest-block">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 className="font-bold text-slate-900">📅 Overdue Task Digest</h4>
                      <p className="text-[11px] text-slate-500">Monday-morning email to each advocate listing tasks whose due date has passed and are not yet Completed/Cancelled. Skips advocates with zero overdue tasks.</p>
                    </div>
                    <button onClick={fetchOverdueDigest} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg" data-testid="overdue-digest-refresh">
                      Refresh
                    </button>
                  </div>

                  {overdueDigestSchedule && (
                    <div className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${overdueDigestSchedule.enabled ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`} data-testid="overdue-digest-auto-block">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${overdueDigestSchedule.enabled ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'}`}>
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 text-sm">
                            Auto-send · <span className={overdueDigestSchedule.enabled ? 'text-emerald-800' : 'text-slate-600'}>{overdueDigestSchedule.enabled ? 'ENABLED' : 'DISABLED'}</span>
                          </div>
                          <div className="text-[11px] text-slate-600">
                            {overdueDigestSchedule.cron}
                            {overdueDigestSchedule.next_run_at && (
                              <> · Next run: <span className="font-mono">{new Date(overdueDigestSchedule.next_run_at).toISOString().slice(0, 16).replace('T', ' ')} UTC</span></>
                            )}
                          </div>
                          {overdueDigestSchedule.last_auto_run && (
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              Last auto-run: {String(overdueDigestSchedule.last_auto_run).slice(0, 16).replace('T', ' ')} UTC
                              {overdueDigestSchedule.last_auto_stats && (
                                <> · Sent {overdueDigestSchedule.last_auto_stats.sent} · Skipped {overdueDigestSchedule.last_auto_stats.skipped}{overdueDigestSchedule.last_auto_stats.failed ? ` · Failed ${overdueDigestSchedule.last_auto_stats.failed}` : ''}</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleOverdueDigestSchedule(!overdueDigestSchedule.enabled)}
                        className={`text-xs font-bold px-4 py-2 rounded-lg text-white shadow ${overdueDigestSchedule.enabled ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                        data-testid="overdue-digest-auto-toggle"
                      >
                        {overdueDigestSchedule.enabled ? "Disable auto-send" : "Enable auto-send"}
                      </button>
                    </div>
                  )}

                  {overdueDigestList.items.length > 0 && (
                    <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-[280px]">
                      <table className="min-w-full text-xs" data-testid="overdue-digest-table">
                        <thead className="bg-slate-50 text-left uppercase text-[10px] tracking-wider text-slate-500 sticky top-0">
                          <tr>
                            <th className="p-3">Advocate</th>
                            <th className="p-3">Overdue Tasks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {overdueDigestList.items.map(a => (
                            <tr key={a.email} className="hover:bg-slate-50" data-testid={`overdue-row-${a.email}`}>
                              <td className="p-3">
                                <div className="font-semibold text-slate-900">{a.name}</div>
                                <div className="text-[10px] font-mono text-slate-500">{a.email}</div>
                              </td>
                              <td className="p-3">
                                <span className={`font-bold ${a.overdue_count > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{a.overdue_count}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-[11px] text-slate-500">
                      {overdueDigestList.targets_with_overdue} advocate(s) currently have overdue tasks.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => sendOverdueDigest(true)}
                        disabled={overdueDigestSending}
                        className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
                        data-testid="overdue-digest-dry-run"
                      >
                        {overdueDigestSending ? "…" : "Dry-run"}
                      </button>
                      <button
                        onClick={() => sendOverdueDigest(false)}
                        disabled={overdueDigestSending}
                        className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-4 py-1.5 rounded-lg shadow disabled:opacity-50"
                        data-testid="overdue-digest-send-now"
                      >
                        {overdueDigestSending ? "Sending…" : "Send now"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {adminTab === "referrals" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="admin-referrals-panel">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Referral Leaderboard</h3>
                    <p className="text-xs text-slate-500">Top Members who invited neighbours this month. Celebrate them at your next event 🎉</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="month"
                      value={leaderboardMonth}
                      onChange={(e) => setLeaderboardMonth(e.target.value)}
                      className="text-xs px-2 py-1.5 border border-slate-300 rounded-lg"
                      data-testid="leaderboard-month-input"
                    />
                    <button
                      onClick={async () => {
                        setLeaderboardLoading(true);
                        try {
                          const params = leaderboardMonth ? { month: leaderboardMonth } : {};
                          const res = await axios.get(`${API}/admin/referral-leaderboard`, { params });
                          setReferralLeaderboard(res.data);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Could not load leaderboard");
                        } finally { setLeaderboardLoading(false); }
                      }}
                      className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg"
                      data-testid="leaderboard-refresh-btn"
                    >Refresh</button>
                  </div>
                </div>

                {leaderboardLoading && (
                  <div className="text-center text-slate-500 py-10 text-sm">Loading…</div>
                )}
                {!leaderboardLoading && referralLeaderboard && (
                  <>
                    <div className="flex items-center gap-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                      <div>
                        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Month</div>
                        <div className="text-slate-900 font-bold text-base" data-testid="leaderboard-month-label">{referralLeaderboard.month_label}</div>
                      </div>
                      <div className="border-l border-slate-200 pl-4">
                        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Approved this month</div>
                        <div className="text-emerald-700 font-bold text-base" data-testid="leaderboard-total">{referralLeaderboard.total_approved_in_month}</div>
                      </div>
                    </div>

                    {(referralLeaderboard.entries || []).length === 0 ? (
                      <div className="text-center text-slate-500 py-10 text-sm italic" data-testid="leaderboard-empty">
                        No approved referrals recorded in this window yet.
                        <div className="text-xs mt-2 text-slate-400">Members' referrals show up here once their invitees complete KYC and get approved.</div>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="leaderboard-table">
                          <thead>
                            <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                              <th className="text-left px-3 py-2">Rank</th>
                              <th className="text-left px-3 py-2">Member</th>
                              <th className="text-left px-3 py-2">Location</th>
                              <th className="text-left px-3 py-2">Unique Code</th>
                              <th className="text-right px-3 py-2">This Month</th>
                              <th className="text-right px-3 py-2">All-Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referralLeaderboard.entries.map((e, i) => (
                              <tr key={e.unique_code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`leaderboard-row-${i}`}>
                                <td className="px-3 py-2 text-xs font-bold text-slate-500">
                                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : (i + 1)}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-slate-900">{e.full_name}</div>
                                  <div className="text-[11px] text-slate-500 font-mono">+91 {e.mobile_masked}</div>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{[e.city, e.state].filter(Boolean).join(", ") || "—"}</td>
                                <td className="px-3 py-2 text-xs font-mono text-slate-500">{e.unique_code}</td>
                                <td className="px-3 py-2 text-right"><span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs font-bold">{e.month_count}</span></td>
                                <td className="px-3 py-2 text-right text-xs text-slate-700 font-semibold">{e.all_time_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {adminTab === "audit" && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5" data-testid="admin-audit-panel">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Audit Log</h3>
                    <p className="text-xs text-slate-500">Every admin & advocate action is recorded here for accountability.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const params = new URLSearchParams();
                          if (auditFilter.actor) params.set("actor", auditFilter.actor);
                          if (auditFilter.action) params.set("action", auditFilter.action);
                          if (auditFilter.target_type) params.set("target_type", auditFilter.target_type);
                          const res = await axios.get(`${API}/admin/audit/export?${params.toString()}`, { responseType: 'blob' });
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `hrcpc-audit-${new Date().toISOString().slice(0,10)}.csv`;
                          document.body.appendChild(a); a.click(); a.remove();
                          URL.revokeObjectURL(url);
                          toast.success("Audit CSV downloaded");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Export failed");
                        }
                      }}
                      className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-3 py-1.5 rounded-lg"
                      data-testid="audit-export-csv"
                    >
                      Export CSV
                    </button>
                    <button onClick={fetchAuditLog} disabled={auditLoading} className="text-xs bg-orange-600 hover:bg-orange-500 text-white font-semibold px-3 py-1.5 rounded-lg" data-testid="audit-refresh">
                      {auditLoading ? "Loading…" : "Refresh"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input
                    value={auditFilter.actor}
                    onChange={(e) => setAuditFilter({ ...auditFilter, actor: e.target.value })}
                    placeholder="Filter by actor (email)"
                    className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
                    data-testid="audit-filter-actor"
                  />
                  <select
                    value={auditFilter.action}
                    onChange={(e) => setAuditFilter({ ...auditFilter, action: e.target.value })}
                    className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
                    data-testid="audit-filter-action"
                  >
                    <option value="">All actions</option>
                    <option value="admin_login">admin_login</option>
                    <option value="advocate_login">advocate_login</option>
                    <option value="kyc_approve">kyc_approve</option>
                    <option value="kyc_reject">kyc_reject</option>
                    <option value="case_create">case_create</option>
                    <option value="case_status_update">case_status_update</option>
                    <option value="case_note_add">case_note_add</option>
                    <option value="case_advocates_update">case_advocates_update</option>
                    <option value="payment">payment</option>
                    <option value="volunteer_update">volunteer_update</option>
                    <option value="ticket_update">ticket_update</option>
                  </select>
                  <select
                    value={auditFilter.target_type}
                    onChange={(e) => setAuditFilter({ ...auditFilter, target_type: e.target.value })}
                    className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs"
                    data-testid="audit-filter-target"
                  >
                    <option value="">All targets</option>
                    <option value="auth">auth</option>
                    <option value="client">client</option>
                    <option value="case">case</option>
                    <option value="volunteer">volunteer</option>
                    <option value="support_ticket">support_ticket</option>
                  </select>
                  <button onClick={fetchAuditLog} className="bg-slate-800 text-white text-xs font-semibold px-3 py-2 rounded-lg" data-testid="audit-apply">
                    Apply Filters
                  </button>
                </div>

                <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-slate-200 rounded-xl">
                  <table className="min-w-full text-xs" data-testid="audit-table">
                    <thead className="bg-slate-50 text-left uppercase text-[10px] tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="p-3">When (UTC)</th>
                        <th className="p-3">Actor</th>
                        <th className="p-3">Role</th>
                        <th className="p-3">Action</th>
                        <th className="p-3">Target</th>
                        <th className="p-3">Details</th>
                        <th className="p-3">IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {auditEvents.map(e => (
                        <tr key={e.id} className="hover:bg-slate-50">
                          <td className="p-3 font-mono text-slate-600">{e.ts}</td>
                          <td className="p-3 font-mono text-slate-900">{e.actor}</td>
                          <td className="p-3">
                            <span className={`px-1.5 py-0.5 rounded font-semibold ${e.actor_role === 'admin' ? 'bg-orange-100 text-orange-800' : e.actor_role === 'advocate' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                              {e.actor_role}
                            </span>
                          </td>
                          <td className="p-3 font-mono text-slate-700">{e.action}</td>
                          <td className="p-3 font-mono text-slate-600">{e.target_type}:{e.target_id}</td>
                          <td className="p-3 text-slate-500 max-w-xs truncate" title={JSON.stringify(e.details)}>
                            {e.details && Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : "—"}
                          </td>
                          <td className="p-3 font-mono text-slate-400">{e.ip || "—"}</td>
                        </tr>
                      ))}
                      {auditEvents.length === 0 && !auditLoading && (
                        <tr><td colSpan="7" className="p-6 text-center text-slate-500 italic">No audit events match the current filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


          </div>
        )}

        {/* 8. ADVOCATE PANEL — real email-OTP auth, backend-filtered case list */}
        {activeTab === "advocate-panel" && currentUser?.role === 'advocate' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="advocate-panel">
            <div className="bg-black text-white p-6 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-emerald-600 text-white rounded-xl flex items-center justify-center font-bold text-xl">
                  ADV
                </div>
                <div>
                  <h2 className="text-xl font-bold" data-testid="advocate-panel-heading">Advocate Portal</h2>
                  <p className="text-xs text-slate-400">
                    {currentUser.data.name} · <span className="text-emerald-300">{currentUser.data.email}</span>
                    {currentUser.data.is_super && <span className="ml-2 bg-amber-500 text-slate-950 px-2 py-0.5 rounded font-bold">Super — All Cases</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    setRecapPreviewOpen(true);
                    setRecapPreviewLoading(true);
                    try {
                      const res = await axios.get(`${API}/advocate/my-recap-preview`);
                      setRecapPreviewHtml(res.data?.html || "");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Could not load your recap preview");
                      setRecapPreviewHtml("");
                    } finally {
                      setRecapPreviewLoading(false);
                    }
                  }}
                  className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded font-semibold"
                  data-testid="advocate-recap-preview-btn"
                  title="See exactly what your Friday email digest will look like"
                >
                  Preview my Friday recap
                </button>
                <button onClick={logoutAdvocate} className="text-xs bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded" data-testid="advocate-logout">Log out</button>
              </div>
            </div>

            {/* Recap Preview Modal (Feb 12, 2026) */}
            {recapPreviewOpen && (
              <div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => setRecapPreviewOpen(false)}
                data-testid="advocate-recap-preview-modal"
              >
                <div
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
                    <div>
                      <div className="text-[10px] font-bold tracking-widest text-emerald-700 uppercase">Preview</div>
                      <h3 className="text-base font-bold text-slate-900">My Friday recap — as you'll receive it in email</h3>
                    </div>
                    <button
                      onClick={() => setRecapPreviewOpen(false)}
                      className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2"
                      data-testid="advocate-recap-preview-close"
                    >×</button>
                  </div>
                  <div className="flex-1 overflow-y-auto bg-slate-100 p-4">
                    {recapPreviewLoading ? (
                      <div className="text-center text-slate-500 py-16 text-sm">Rendering your recap…</div>
                    ) : recapPreviewHtml ? (
                      <iframe
                        title="Weekly Recap Preview"
                        srcDoc={recapPreviewHtml}
                        className="w-full h-[70vh] bg-white rounded-lg border border-slate-200"
                        sandbox=""
                        data-testid="advocate-recap-preview-iframe"
                      />
                    ) : (
                      <div className="text-center text-slate-500 py-16 text-sm italic">No preview available right now.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <MyTasksDashboard apiBase={API} myEmail={currentUser.data.email} refreshKey={casesList.length} />

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg text-slate-900" data-testid="advocate-cases-heading">
                  {currentUser.data.is_super ? "All HRCPC Cases" : "Cases Assigned To You"} ({casesList.length})
                </h3>
                {casesList.length === 0 && !currentUser.data.is_super && (
                  <span className="text-xs text-slate-500 italic">No cases assigned yet — please contact the admin.</span>
                )}
              </div>

              <div className="space-y-4">
                {casesList.map(c => (
                  <div key={c.case_number} className="bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4" data-testid={`adv-case-${c.case_number}`}>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                      <div>
                        <span className="font-mono text-xs font-bold text-orange-700">{c.case_number}</span>
                        <h4 className="font-bold text-slate-900 text-lg">{c.title}</h4>
                        <p className="text-xs text-slate-600">Member: {c.client_name} (+91 {c.client_mobile}) · Court: {c.court}</p>
                      </div>
                      <span className="bg-amber-100 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">{c.status}</span>
                    </div>

                    <div className={`grid grid-cols-1 ${currentUser.data.is_super ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3 bg-white p-4 rounded-xl border border-slate-200 text-xs`}>
                      <div>
                        <span className="text-slate-500 font-semibold">Next Hearing</span>
                        <div className="font-bold text-slate-900 text-sm mt-0.5">{fmtDate(c.next_hearing_date)}</div>
                      </div>
                      {currentUser.data.is_super && (
                        <div data-testid={`adv-fee-${c.case_number}`}>
                          <span className="text-slate-500 font-semibold">Fee Balance</span>
                          <div className="font-bold text-rose-600 text-sm mt-0.5">₹{Number(c.balance || 0).toLocaleString()}</div>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-500 font-semibold">Member</span>
                        <div className="font-medium text-slate-900 text-xs mt-0.5 truncate">{c.client_name}</div>
                      </div>
                      <div>
                        <span className="text-slate-500 font-semibold">Assigned Advocates</span>
                        <div className="font-medium text-slate-900 text-xs mt-0.5 truncate">
                          {(c.assigned_advocate_emails || []).join(", ") || <em className="text-slate-400">—</em>}
                        </div>
                      </div>
                    </div>

                    {/* Advocate-editable Status + Next Hearing */}
                    <div className="bg-white p-4 rounded-xl border border-emerald-200 space-y-2" data-testid={`adv-edit-${c.case_number}`}>
                      <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Update Case Status</div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Status</label>
                          <select
                            value={getAdvocateEdit(c).status}
                            onChange={e => setAdvocateEditField(c, 'status', e.target.value)}
                            className="w-full mt-0.5 px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs"
                            data-testid={`adv-status-select-${c.case_number}`}
                          >
                            {["Fresh Filing - Registered","Notice Issued","Written Statement Filed","Evidence Stage","Arguments Heard","Judgement Reserved","Disposed","Adjourned","Mediation In Progress","Appeal Filed"].concat(
                              c.status && !["Fresh Filing - Registered","Notice Issued","Written Statement Filed","Evidence Stage","Arguments Heard","Judgement Reserved","Disposed","Adjourned","Mediation In Progress","Appeal Filed"].includes(c.status) ? [c.status] : []
                            ).map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Next Hearing Date</label>
                          <input
                            type="date"
                            value={getAdvocateEdit(c).next_hearing_date}
                            onChange={e => setAdvocateEditField(c, 'next_hearing_date', e.target.value)}
                            className="w-full mt-0.5 px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs"
                            data-testid={`adv-hearing-input-${c.case_number}`}
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => saveAdvocateCaseEdit(c)}
                            disabled={!!advocateSaving[c.case_number]}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-xs font-bold py-1.5 rounded-lg"
                            data-testid={`adv-save-${c.case_number}`}
                          >
                            {advocateSaving[c.case_number] ? "Saving…" : "Save Update"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Documents — assigned advocates can upload / download / delete */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3" data-testid={`adv-docs-${c.case_number}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Case Documents ({(c.documents || []).length})</div>
                        <label className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg cursor-pointer shadow">
                          {advocateUploading[c.case_number] ? "Uploading…" : "+ Attach file"}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,application/pdf,.doc,.docx,.txt"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (f) uploadAdvocateDocument(c, f);
                            }}
                            data-testid={`adv-doc-upload-${c.case_number}`}
                          />
                        </label>
                      </div>
                      <p className="text-[10px] text-slate-500">PDF / images / DOC / TXT. Images auto-compressed. Max 3 MB.</p>
                      {(c.documents || []).length === 0 && (
                        <div className="text-xs text-slate-400 italic">No documents attached yet.</div>
                      )}
                      {(c.documents || []).length > 0 && (
                        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                          {(c.documents || []).map(d => (
                            <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`adv-doc-row-${d.id}`}>
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-900 truncate">{d.name}</div>
                                <div className="text-[10px] text-slate-500">{fmtDate(d.date)} · {d.uploaded_by || 'Member'}{d.uploaded_role ? ` (${d.uploaded_role})` : ''} · {d.type}</div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {d.data_url === undefined && (
                                  <button onClick={() => downloadCaseDocument(c, d)} className="text-emerald-700 hover:underline font-semibold" data-testid={`adv-doc-download-${d.id}`}>Download</button>
                                )}
                                <button onClick={() => deleteCaseDocument(c, d)} className="text-rose-600 hover:underline font-semibold" data-testid={`adv-doc-delete-${d.id}`}>Delete</button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {(c.notes || []).length > 0 && (
                      <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 mb-2">Case Notes / Hearing Updates</div>
                        <ul className="space-y-2 max-h-40 overflow-y-auto">
                          {[...(c.notes || [])].reverse().map(n => (
                            <li key={n.id} className="text-xs border-l-2 border-emerald-500 pl-2">
                              <div className="font-mono text-slate-500">{fmtDate(n.date)} · <span className="text-slate-700">{n.author}</span> {n.author_role && <em className="text-slate-400">({n.author_role})</em>}</div>
                              <div className="text-slate-800">{n.text}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <CaseTimeline caseNumber={c.case_number} apiBase={API} testIdSuffix={`adv-${c.case_number}`} />

                    <CaseProceedings caseNumber={c.case_number} apiBase={API} testIdSuffix={`adv-${c.case_number}`} />

                    <CasePartiesPanel caseNumber={c.case_number} apiBase={API} testIdSuffix={`adv-${c.case_number}`} />

                    <CaseTasks caseNumber={c.case_number} apiBase={API} testIdSuffix={`adv-${c.case_number}`} />

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={async () => {
                          const text = prompt("Enter hearing note / update:");
                          if (!text || !text.trim()) return;
                          try {
                            const res = await axios.post(`${API}/cases/${encodeURIComponent(c.case_number)}/notes`, { text: text.trim() });
                            setCasesList(prev => prev.map(x => x.case_number === c.case_number ? { ...x, notes: [...(x.notes || []), res.data.note] } : x));
                            toast.success("Note added");
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Could not add note");
                          }
                        }}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-xs font-semibold shadow"
                        data-testid={`adv-add-note-${c.case_number}`}
                      >
                        + Add Hearing Note
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* If someone lands on /advocate but is not logged in, open the login modal */}
        {activeTab === "advocate-panel" && currentUser?.role !== 'advocate' && (
          <div className="max-w-md mx-auto px-4 py-16 text-center">
            <Shield className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Advocate Panel</h2>
            <p className="text-sm text-slate-600 mb-5">Please sign in with your HRCPC advocate email to access assigned cases.</p>
            <button onClick={() => setAdvocateLoginOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-6 py-2.5 rounded-lg shadow" data-testid="advocate-panel-login-cta">Sign in as Advocate</button>
          </div>
        )}

      </main>

      {/* RAZORPAY PAYMENT SIMULATION MODAL */}
      {paymentModalOpen && paymentTarget && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-100 pb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-black text-amber-300 rounded-lg flex items-center justify-center font-bold text-xs">
                  ₹
                </div>
                <h3 className="font-bold text-slate-900">Pay to HRCPC Legal Cell Trust</h3>
              </div>
              <button onClick={() => { setPaymentModalOpen(false); setPaymentMethodTab('razorpay'); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            {!paymentSuccess ? (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                  <div className="text-xs text-slate-500">Payable For</div>
                  <div className="font-bold text-sm text-slate-900 truncate">{paymentTarget.title}</div>
                  <hr className="border-slate-200" />
                  <div className="flex justify-between items-center text-base font-extrabold text-orange-900">
                    <span>Total Amount:</span>
                    <span>₹{paymentTarget.amount.toLocaleString()}</span>
                  </div>
                </div>

                {/* Payment method tabs */}
                <div className="flex border border-slate-200 rounded-xl overflow-hidden text-xs font-semibold" data-testid="payment-method-tabs">
                  <button
                    onClick={() => setPaymentMethodTab('razorpay')}
                    className={`flex-1 py-2.5 transition ${paymentMethodTab === 'razorpay' ? 'bg-orange-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="pay-tab-razorpay"
                  >
                    Razorpay Card / UPI
                  </button>
                  <button
                    onClick={() => setPaymentMethodTab('direct')}
                    className={`flex-1 py-2.5 transition ${paymentMethodTab === 'direct' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="pay-tab-direct"
                  >
                    Direct UPI / Bank Transfer
                  </button>
                </div>

                {paymentMethodTab === 'razorpay' && (
                  <>
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">Select Payment Method (Simulation)</label>
                      <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                        <button className="p-3 bg-orange-50 border border-orange-300 text-orange-900 rounded-xl text-center">UPI / GooglePay</button>
                        <button className="p-3 bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-center">Credit / Debit Card</button>
                      </div>
                    </div>

                    <button
                      onClick={confirmRazorpayPayment}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl shadow transition"
                      data-testid="razorpay-pay-confirm"
                    >
                      Pay ₹{paymentTarget.amount.toLocaleString()} via Razorpay
                    </button>
                  </>
                )}

                {paymentMethodTab === 'direct' && (
                  <div className="space-y-3" data-testid="direct-payment-panel">
                    <div className="text-xs text-slate-600">
                      Pay to any of HRCPC Trust's official bank accounts. Prefer SBI (primary) or DBS (alternate).
                    </div>

                    {/* SBI */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="modal-bank-block-sbi">
                      <div className="bg-orange-100 text-orange-900 px-3 py-1.5 font-bold text-xs flex items-center justify-between">
                        <span>SBI Account (Primary)</span>
                        {siteSettings.qr_sbi_data_url && (
                          <span className="text-[9px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">QR available</span>
                        )}
                      </div>
                      <div className="p-3 grid grid-cols-2 gap-3 text-[11px] bg-slate-50">
                        <div className="space-y-1">
                          <div><strong>A/C Name:</strong> {siteSettings.bank_details?.sbi?.ac_name}</div>
                          <div><strong>A/C No:</strong> <span className="font-mono" data-testid="modal-account-no-sbi">{siteSettings.bank_details?.sbi?.ac_no}</span></div>
                          <div><strong>IFSC:</strong> <span className="font-mono" data-testid="modal-ifsc-sbi">{siteSettings.bank_details?.sbi?.ifsc}</span></div>
                          <div><strong>UPI:</strong> <span className="font-mono" data-testid="modal-upi-sbi">{siteSettings.bank_details?.sbi?.upi}</span></div>
                        </div>
                        <div className="flex items-center justify-center">
                          {siteSettings.qr_sbi_data_url ? (
                            <img src={siteSettings.qr_sbi_data_url} alt="SBI QR" className="w-24 h-24 object-contain border border-slate-200 rounded" data-testid="modal-qr-sbi" />
                          ) : (
                            <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-[9px] text-slate-400 italic text-center px-1">Admin will upload QR</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* DBS */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="modal-bank-block-dbs">
                      <div className="bg-rose-100 text-rose-900 px-3 py-1.5 font-bold text-xs flex items-center justify-between">
                        <span>DBS Account (Alternate)</span>
                        {siteSettings.qr_dbs_data_url && (
                          <span className="text-[9px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">QR available</span>
                        )}
                      </div>
                      <div className="p-3 grid grid-cols-2 gap-3 text-[11px] bg-slate-50">
                        <div className="space-y-1">
                          <div><strong>A/C Name:</strong> {siteSettings.bank_details?.dbs?.ac_name}</div>
                          <div><strong>A/C No:</strong> <span className="font-mono" data-testid="modal-account-no-dbs">{siteSettings.bank_details?.dbs?.ac_no}</span></div>
                          <div><strong>IFSC:</strong> <span className="font-mono" data-testid="modal-ifsc-dbs">{siteSettings.bank_details?.dbs?.ifsc}</span></div>
                          <div><strong>UPI:</strong> <span className="font-mono" data-testid="modal-upi-dbs">{siteSettings.bank_details?.dbs?.upi}</span></div>
                        </div>
                        <div className="flex items-center justify-center">
                          {siteSettings.qr_dbs_data_url ? (
                            <img src={siteSettings.qr_dbs_data_url} alt="DBS QR" className="w-24 h-24 object-contain border border-slate-200 rounded" data-testid="modal-qr-dbs" />
                          ) : (
                            <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-[9px] text-slate-400 italic text-center px-1">Admin will upload QR</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 text-[11px] text-amber-900 leading-relaxed">
                      After transfer, email the transaction reference and your case / unique code to <strong>support@hrcpc.org</strong>. Your fee balance will be reconciled and a signed receipt shared within 1 working day.
                    </div>

                    <button
                      onClick={() => {
                        setPaymentSuccess(true);
                        toast.success("Please complete the transfer on your UPI/Bank app.");
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl shadow transition text-sm"
                      data-testid="direct-payment-done"
                    >
                      I have transferred ₹{paymentTarget.amount.toLocaleString()}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center space-y-4 py-4">
                <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
                <h4 className="text-xl font-bold text-slate-900">Payment Recorded!</h4>
                <p className="text-xs text-slate-600">Transaction reference will be verified by our accounts team. A receipt (PDF) will be shared to your registered mobile within 1 working day.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EDIT / REGISTER CASE MODAL */}
      {showCaseModal && editingCase && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-lg text-slate-900">Register / Edit Case File</h3>
              <button onClick={() => setShowCaseModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Case Number</label>
                <input 
                  type="text" 
                  value={editingCase.case_number}
                  onChange={(e) => setEditingCase({...editingCase, case_number: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Case Title</label>
                <input 
                  type="text" 
                  value={editingCase.title}
                  onChange={(e) => setEditingCase({...editingCase, title: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Member (from Approved list)</label>
                  <select
                    value={editingCase.client_mobile}
                    onChange={(e) => {
                      const picked = clientsList.find(x => x.mobile === e.target.value);
                      setEditingCase({
                        ...editingCase,
                        client_mobile: e.target.value,
                        client_name: picked ? picked.full_name : editingCase.client_name,
                        client_id: picked ? picked.unique_code : editingCase.client_id
                      });
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-client-select"
                  >
                    <option value="">-- Select member --</option>
                    {clientsList.filter(c => c.status === 'Approved').map(c => (
                      <option key={c.mobile} value={c.mobile}>{c.full_name} (+91 {c.mobile})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Member Mobile (auto)</label>
                  <input
                    type="text"
                    value={editingCase.client_mobile}
                    onChange={(e) => setEditingCase({...editingCase, client_mobile: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Court / Forum</label>
                <input 
                  type="text" 
                  value={editingCase.court}
                  onChange={(e) => setEditingCase({...editingCase, court: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Status</label>
                  <input 
                    type="text" 
                    value={editingCase.status}
                    onChange={(e) => setEditingCase({...editingCase, status: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Next Hearing Date</label>
                  <input 
                    type="text" 
                    value={editingCase.next_hearing_date}
                    onChange={(e) => setEditingCase({...editingCase, next_hearing_date: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Focus Area (for reporting)</label>
                <select
                  value={editingCase.focus_area || "other"}
                  onChange={(e) => setEditingCase({...editingCase, focus_area: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                  data-testid="case-modal-focus-area"
                >
                  {FOCUS_AREA_KEYS.map(k => (
                    <option key={k} value={k}>{FOCUS_AREA_META[k].label}</option>
                  ))}
                </select>
              </div>

              {/* Phase-II: Client Role + Forum Type (both mandatory) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Member Role <span className="text-rose-600">*</span>
                  </label>
                  <select
                    value={editingCase.client_role || ""}
                    onChange={(e) => setEditingCase({...editingCase, client_role: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-client-role"
                  >
                    <option value="">-- Select role --</option>
                    {CLIENT_ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Forum Type <span className="text-rose-600">*</span>
                  </label>
                  <select
                    value={editingCase.forum_type || ""}
                    onChange={(e) => setEditingCase({...editingCase, forum_type: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-forum-type"
                  >
                    <option value="">-- Select forum --</option>
                    {FORUM_TYPES.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phase-II: CNR + Case Nature + Priority */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">CNR Number</label>
                  <input
                    type="text"
                    value={editingCase.cnr_number || ""}
                    onChange={(e) => setEditingCase({...editingCase, cnr_number: e.target.value.toUpperCase().slice(0,16)})}
                    placeholder="16-char CNR"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm font-mono"
                    data-testid="case-modal-cnr"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Case Nature</label>
                  <input
                    type="text"
                    value={editingCase.case_nature || ""}
                    onChange={(e) => setEditingCase({...editingCase, case_nature: e.target.value})}
                    placeholder="Civil / Criminal / Consumer / RTI …"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-nature"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Priority</label>
                  <select
                    value={editingCase.priority || "Normal"}
                    onChange={(e) => setEditingCase({...editingCase, priority: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-priority"
                  >
                    {CASE_PRIORITY_LEVELS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phase-II: Opposing Party + Next Action */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Opposing Party <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingCase.opposing_party || ""}
                    onChange={(e) => setEditingCase({...editingCase, opposing_party: e.target.value, conflict_checked: false})}
                    placeholder="e.g. XYZ Constructions Pvt Ltd"
                    className={`w-full px-3 py-2 bg-slate-50 border rounded-lg text-sm ${!editingCase.opposing_party ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`}
                    data-testid="case-modal-opposing"
                  />
                  {!editingCase.opposing_party && (
                    <div className="text-[10px] text-amber-700 mt-1" data-testid="case-modal-opposing-warn">
                      ⚠ Opposing Party is expected for every case type. Save anyway if not yet finalised.
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Next Action</label>
                  <input
                    type="text"
                    value={editingCase.next_action || ""}
                    onChange={(e) => setEditingCase({...editingCase, next_action: e.target.value})}
                    placeholder="e.g. File complaint copy by 25 Feb"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-next-action"
                  />
                </div>
              </div>

              {/* Feb 14, 2026 — Parties & Counsels summary fields (optional) */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="case-modal-counsel-section">
                <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">Parties & Counsels (optional summary)</div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">Opposing Party Address</label>
                  <input type="text" value={editingCase.opposing_party_address || ""}
                    onChange={(e) => setEditingCase({...editingCase, opposing_party_address: e.target.value})}
                    placeholder="Registered / service address"
                    className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                    data-testid="case-modal-opp-addr" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Opposite Counsel — Name</label>
                    <input type="text" value={editingCase.opposite_counsel_name || ""}
                      onChange={(e) => setEditingCase({...editingCase, opposite_counsel_name: e.target.value})}
                      placeholder="Adv. R. Rao"
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                      data-testid="case-modal-oc-name" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Firm / Chambers</label>
                    <input type="text" value={editingCase.opposite_counsel_firm || ""}
                      onChange={(e) => setEditingCase({...editingCase, opposite_counsel_firm: e.target.value})}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                      data-testid="case-modal-oc-firm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Mobile</label>
                    <input type="tel" value={editingCase.opposite_counsel_mobile || ""} maxLength={10}
                      onChange={(e) => setEditingCase({...editingCase, opposite_counsel_mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                      placeholder="10-digit"
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                      data-testid="case-modal-oc-mobile" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Email</label>
                    <input type="email" value={editingCase.opposite_counsel_email || ""}
                      onChange={(e) => setEditingCase({...editingCase, opposite_counsel_email: e.target.value})}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                      data-testid="case-modal-oc-email" />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200 space-y-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Co-Parties on our side (optional)</label>
                    <input type="text" value={editingCase.co_parties_our_side || ""}
                      onChange={(e) => setEditingCase({...editingCase, co_parties_our_side: e.target.value})}
                      placeholder="e.g. Rajesh (co-petitioner), Sunita (mother)"
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                      data-testid="case-modal-copart" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Member's Private Counsel — Name</label>
                      <input type="text" value={editingCase.our_side_private_counsel_name || ""}
                        onChange={(e) => setEditingCase({...editingCase, our_side_private_counsel_name: e.target.value})}
                        placeholder="If Member engaged a private lawyer alongside HRCPC"
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                        data-testid="case-modal-pc-name" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Firm / Chambers</label>
                      <input type="text" value={editingCase.our_side_private_counsel_firm || ""}
                        onChange={(e) => setEditingCase({...editingCase, our_side_private_counsel_firm: e.target.value})}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                        data-testid="case-modal-pc-firm" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Mobile</label>
                      <input type="tel" value={editingCase.our_side_private_counsel_mobile || ""} maxLength={10}
                        onChange={(e) => setEditingCase({...editingCase, our_side_private_counsel_mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                        data-testid="case-modal-pc-mobile" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Email</label>
                      <input type="email" value={editingCase.our_side_private_counsel_email || ""}
                        onChange={(e) => setEditingCase({...editingCase, our_side_private_counsel_email: e.target.value})}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm"
                        data-testid="case-modal-pc-email" />
                    </div>
                  </div>
                </div>
                {editingCase.case_number && (
                  <div className="text-[10px] text-slate-500 pt-1">
                    💡 Add / edit multiple parties &amp; counsels (Applicants, Respondents, Intervenors…) from the case detail page.
                  </div>
                )}
              </div>

              {/* Phase-II: Conflict of Interest check (mandatory before save on new cases) */}
              <div className={`rounded-lg border-2 p-3 ${editingCase.conflict_checked ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-bold text-slate-800">
                    Conflict of Interest Check
                    {editingCase.conflict_checked && <span className="ml-2 text-emerald-700">✓ Verified ({editingCase.conflict_check_result || 'clear'})</span>}
                  </div>
                  <button
                    type="button"
                    data-testid="case-conflict-check-btn"
                    onClick={async () => {
                      const q = (editingCase.opposing_party || editingCase.client_name || "").trim();
                      if (q.length < 2) {
                        toast.error("Enter Opposing Party or Member Name (min 2 chars) to run conflict check.");
                        return;
                      }
                      try {
                        const res = await axios.get(`${API}/admin/conflict-search`, { params: { q } });
                        const anyMatch = res.data?.any_match;
                        const result = anyMatch ? "conflict_noted" : "clear";
                        setEditingCase({...editingCase, conflict_checked: true, conflict_check_result: result, _conflict_details: res.data});
                        if (anyMatch) {
                          toast.warning(`Possible conflict: ${res.data?.clients?.length || 0} member(s), ${res.data?.cases?.length || 0} case(s) matched "${q}". Proceed with caution.`, { duration: 8000 });
                        } else {
                          toast.success(`Conflict check clear for "${q}".`);
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Conflict search failed.");
                      }
                    }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {editingCase.conflict_checked ? "Re-run Check" : "Run Conflict Check"}
                  </button>
                </div>
                {editingCase._conflict_details && (editingCase._conflict_details.clients?.length > 0 || editingCase._conflict_details.cases?.length > 0) && (
                  <div className="mt-2 text-[11px] text-slate-700 max-h-24 overflow-y-auto space-y-1">
                    {editingCase._conflict_details.clients?.map(c => (
                      <div key={c.unique_code}>· Member: <b>{c.full_name}</b> ({c.mobile}) — {c.status}</div>
                    ))}
                    {editingCase._conflict_details.cases?.map(c => (
                      <div key={c.case_number}>· Case: <b>{c.case_number}</b> — {c.title || '—'} — Opp: {c.opposing_party || '—'}</div>
                    ))}
                  </div>
                )}
                {!editingCase.conflict_checked && (
                  <p className="text-[11px] text-amber-800 mt-1">A conflict-of-interest search must be run before saving a new case.</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Total Fee (₹) — optional, 0 allowed</label>
                  <input 
                    type="number" 
                    value={editingCase.total_fee}
                    onChange={(e) => setEditingCase({...editingCase, total_fee: Number(e.target.value), balance: Number(e.target.value) - editingCase.fee_paid})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Fee Paid (₹)</label>
                  <input 
                    type="number" 
                    value={editingCase.fee_paid}
                    onChange={(e) => setEditingCase({...editingCase, fee_paid: Number(e.target.value), balance: editingCase.total_fee - Number(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Balance (₹)</label>
                  <input 
                    type="number" 
                    value={editingCase.balance}
                    readOnly
                    className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-sm font-bold text-rose-600"
                  />
                </div>
              </div>

              <button 
                onClick={async () => {
                  if (!editingCase.client_mobile) {
                    toast.error("Please select a member for this case.");
                    return;
                  }
                  const isExisting = casesList.some(x => x.case_number === editingCase.case_number);
                  // Phase-II mandatory checks on NEW cases
                  if (!isExisting) {
                    if (!editingCase.client_role) {
                      toast.error("Member Role is required (Phase-II).");
                      return;
                    }
                    if (!editingCase.forum_type) {
                      toast.error("Forum Type is required (Phase-II).");
                      return;
                    }
                    if (!editingCase.conflict_checked) {
                      toast.error("Please run the Conflict-of-Interest check before saving.");
                      return;
                    }
                  }
                  try {
                    if (!isExisting) {
                      const payload = {
                        ...editingCase,
                        conflict_checked: true,
                        conflict_check_result: editingCase.conflict_check_result || "clear",
                      };
                      // strip UI-only detail blob before POST
                      delete payload._conflict_details;
                      await axios.post(`${API}/cases`, payload);
                      toast.success("New case registered in database!");
                    } else {
                      // Update status + focus area for existing cases
                      await axios.patch(`${API}/cases/${editingCase.case_number}/status`, { status: editingCase.status });
                      if (editingCase.focus_area) {
                        try {
                          await axios.patch(`${API}/admin/cases/${editingCase.case_number}/focus-area`, { focus_area: editingCase.focus_area });
                        } catch (_) { /* focus_area is optional */ }
                      }
                      // Feb 14, 2026 — persist parties-summary fields (opposing party + counsels)
                      try {
                        await axios.patch(`${API}/admin/cases/${encodeURIComponent(editingCase.case_number)}/parties-summary`, {
                          opposing_party: editingCase.opposing_party || "",
                          opposing_party_address: editingCase.opposing_party_address || "",
                          opposite_counsel_name: editingCase.opposite_counsel_name || "",
                          opposite_counsel_mobile: editingCase.opposite_counsel_mobile || "",
                          opposite_counsel_email: editingCase.opposite_counsel_email || "",
                          opposite_counsel_firm: editingCase.opposite_counsel_firm || "",
                          co_parties_our_side: editingCase.co_parties_our_side || "",
                          our_side_private_counsel_name: editingCase.our_side_private_counsel_name || "",
                          our_side_private_counsel_mobile: editingCase.our_side_private_counsel_mobile || "",
                          our_side_private_counsel_email: editingCase.our_side_private_counsel_email || "",
                          our_side_private_counsel_firm: editingCase.our_side_private_counsel_firm || "",
                        });
                      } catch (err) { console.error('[case-edit] parties-summary save failed:', err); }
                      toast.success("Case updated!");
                    }
                    await fetchData();
                    setShowCaseModal(false);
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.response?.data?.detail || "Failed to save case.");
                  }
                }}
                className="w-full bg-orange-700 hover:bg-orange-600 text-white font-bold py-3 rounded-xl shadow mt-2"
                data-testid="save-case-btn"
              >
                Save Case File
              </button>
              {/* Super-Admin only: Send Cover Brief to a temporary advocate */}
              {SUPER_FINANCE_EMAILS_FRONT.has(String(currentUser?.data?.email || "").toLowerCase()) && (
                <button
                  type="button"
                  onClick={() => setCoverBriefCase(editingCase)}
                  data-testid="btn-open-cover-brief"
                  className="w-full mt-2 bg-teal-700 hover:bg-teal-600 text-white font-bold py-3 rounded-xl shadow flex items-center justify-center gap-2"
                >
                  <span>📨</span>
                  <span>Send Cover Brief to Temporary Advocate</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SUPER-ADMIN: Cover Brief Modal */}
      {coverBriefCase && (
        <CoverBriefModal
          caseObj={coverBriefCase}
          onClose={() => setCoverBriefCase(null)}
          onSent={() => { setCoverBriefCase(null); fetchData(); }}
        />
      )}


      {/* PHASE-II SPRINT 3+4 — CASE PROCEEDINGS + TASKS MODAL (Admin) */}
      {proceedingsCase && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-proceedings-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Case Workspace — {proceedingsCase.case_number}</h3>
                <p className="text-xs text-slate-600 mt-0.5">{proceedingsCase.client_name} · {proceedingsCase.forum_type || proceedingsCase.court || '—'} · {proceedingsCase.client_role || '—'}</p>
              </div>
              <button onClick={() => setProceedingsCase(null)} className="text-slate-400 hover:text-slate-600 text-xl" data-testid="admin-proceedings-close">✕</button>
            </div>
            <CaseProceedings caseNumber={proceedingsCase.case_number} apiBase={API} testIdSuffix={`admin-${proceedingsCase.case_number}`} autoOpen={true} />
            <CasePartiesPanel caseNumber={proceedingsCase.case_number} apiBase={API} testIdSuffix={`admin-${proceedingsCase.case_number}`} />
            <CaseTasks caseNumber={proceedingsCase.case_number} apiBase={API} testIdSuffix={`admin-${proceedingsCase.case_number}`} autoOpen={true} isAdmin={true} />
          </div>
        </div>
      )}


      {/* CALL-BACK REQUEST MODAL */}
      {callbackModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4" data-testid="callback-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-orange-700" />
                <h3 className="font-bold text-slate-900">Request a Call-Back</h3>
              </div>
              <button onClick={() => setCallbackModalOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <p className="text-xs text-slate-600">
              Prefer to speak with someone? Leave your name and mobile — an HRCPC volunteer will call you back on <strong>{siteSettings.contact_info?.phone_primary}</strong> business hours.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name *</label>
              <input
                type="text"
                value={callbackForm.name}
                onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="callback-name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Mobile (10-digit) *</label>
              <input
                type="text"
                maxLength="10"
                value={callbackForm.mobile}
                onChange={(e) => setCallbackForm({ ...callbackForm, mobile: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="callback-mobile"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Preferred time / brief note</label>
              <textarea
                rows="3"
                value={callbackForm.note}
                onChange={(e) => setCallbackForm({ ...callbackForm, note: e.target.value })}
                placeholder="e.g. Please call after 4pm about builder delay case"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="callback-note"
              />
            </div>
            <button
              onClick={async () => {
                if (!callbackForm.name.trim() || callbackForm.mobile.length !== 10) {
                  toast.error("Please enter your name and a 10-digit mobile.");
                  return;
                }
                setCallbackSaving(true);
                try {
                  await axios.post(`${API}/support/tickets`, {
                    name: callbackForm.name,
                    mobile: callbackForm.mobile,
                    email: "",
                    subject: "Call-back requested",
                    message: callbackForm.note || "Please call me back.",
                    category: "Callback Request"
                  });
                  await fetchData();
                  toast.success("Call-back request logged. Our volunteer will ring you soon.");
                  setCallbackModalOpen(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not save. Please retry.");
                } finally {
                  setCallbackSaving(false);
                }
              }}
              disabled={callbackSaving}
              className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-orange-300 text-white font-bold py-3 rounded-xl shadow"
              data-testid="callback-submit"
            >
              {callbackSaving ? "Submitting…" : "Request Call-Back"}
            </button>
          </div>
        </div>
      )}



      {/* ADMIN LOGIN MODAL — email + OTP (two-step) */}
      {adminLoginOpen && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4" data-testid="admin-login-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" />
                <h3 className="font-bold text-slate-900">Admin Login</h3>
              </div>
              <button onClick={() => { setAdminLoginOpen(false); setAdminLoginNotice(null); setAdminLoginForm({ email:"", otp:"", step:"email" }); }} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="admin-login-close">✕</button>
            </div>

            {adminLoginNotice && (
              <div
                role="alert"
                data-testid="admin-login-notice"
                className={`text-xs rounded-lg px-3 py-2 border ${
                  adminLoginNotice.type === "error" ? "bg-red-50 border-red-200 text-red-700"
                  : adminLoginNotice.type === "warn" ? "bg-amber-50 border-amber-200 text-amber-800"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}
              >
                {adminLoginNotice.text}
              </div>
            )}

            {adminLoginForm.step === "email" && (
              <>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Enter your admin email. We&rsquo;ll send a one-time login code to your inbox.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Admin Email</label>
                  <input
                    type="email"
                    value={adminLoginForm.email}
                    onChange={(e) => setAdminLoginForm({ ...adminLoginForm, email: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') requestAdminOtp(); }}
                    placeholder="master@hrcpc.org"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                    data-testid="admin-login-email"
                    autoFocus
                  />
                </div>
                <button
                  onClick={requestAdminOtp}
                  disabled={adminLoginLoading}
                  className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-slate-400 text-white font-bold py-2.5 rounded-xl shadow"
                  data-testid="admin-login-send-otp"
                >
                  {adminLoginLoading ? "Sending code…" : "Send Login Code"}
                </button>
              </>
            )}

            {adminLoginForm.step === "otp" && (
              <>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {adminLoginNotice?.type === "warn"
                    ? <>Enter the most recent 6-digit code emailed to <strong>{adminLoginForm.email}</strong>. Codes stay valid for 5 minutes.</>
                    : <>A 6-digit login code was sent to <strong>{adminLoginForm.email}</strong>. It expires in 5 minutes — if you received several emails, use the newest one.</>}
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">6-Digit Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength="6"
                    value={adminLoginForm.otp}
                    onChange={(e) => setAdminLoginForm({ ...adminLoginForm, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitAdminLogin(); }}
                    placeholder="123456"
                    className="w-full px-3 py-3 bg-slate-50 border border-slate-300 rounded-lg text-2xl font-mono tracking-[0.6em] text-center focus:outline-none focus:ring-2 focus:ring-orange-600"
                    data-testid="admin-login-otp"
                    autoFocus
                  />
                </div>
                <button
                  onClick={submitAdminLogin}
                  disabled={adminLoginLoading || adminLoginForm.otp.length !== 6}
                  className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-slate-400 text-white font-bold py-2.5 rounded-xl shadow"
                  data-testid="admin-login-submit"
                >
                  {adminLoginLoading ? "Verifying…" : "Verify & Sign In"}
                </button>
                <div className="flex justify-between text-[11px]">
                  <button
                    onClick={() => { setAdminLoginNotice(null); setAdminLoginForm({ email:"", otp:"", step:"email" }); }}
                    className="text-slate-500 hover:text-slate-700 underline"
                    data-testid="admin-login-change-email"
                  >
                    ← Use a different email
                  </button>
                  <button
                    onClick={requestAdminOtp}
                    disabled={adminLoginLoading}
                    className="text-orange-600 hover:text-orange-700 underline disabled:opacity-50"
                    data-testid="admin-login-resend-otp"
                  >
                    Resend code
                  </button>
                </div>
              </>
            )}

            <p className="text-[10px] text-slate-500 italic text-center pt-1">
              Admin access is protected by email-OTP + JWT. Sessions expire after 24 hours.
            </p>
          </div>
        </div>
      )}

      {/* ADVOCATE LOGIN MODAL — email + OTP (two-step) */}
      {advocateLoginOpen && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4" data-testid="advocate-login-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-slate-900">Advocate Login</h3>
              </div>
              <button onClick={() => { setAdvocateLoginOpen(false); setAdvocateLoginForm({ email:"", otp:"", step:"email" }); }} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="advocate-login-close">✕</button>
            </div>

            {advocateLoginForm.step === "email" && (
              <>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Enter your official HRCPC advocate email. We&rsquo;ll send a one-time login code to your inbox.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Advocate Email</label>
                  <input
                    type="email"
                    value={advocateLoginForm.email}
                    onChange={(e) => setAdvocateLoginForm({ ...advocateLoginForm, email: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') requestAdvocateOtp(); }}
                    placeholder="yourname@hrcpc.org"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    data-testid="advocate-login-email"
                    autoFocus
                  />
                </div>
                <button
                  onClick={requestAdvocateOtp}
                  disabled={advocateLoginLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white font-bold py-2.5 rounded-xl shadow"
                  data-testid="advocate-login-send-otp"
                >
                  {advocateLoginLoading ? "Sending code…" : "Send Login Code"}
                </button>
              </>
            )}

            {advocateLoginForm.step === "otp" && (
              <>
                <p className="text-xs text-slate-600 leading-relaxed">
                  A 6-digit login code was sent to <strong>{advocateLoginForm.email}</strong>. It expires in 5 minutes.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">6-Digit Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength="6"
                    value={advocateLoginForm.otp}
                    onChange={(e) => setAdvocateLoginForm({ ...advocateLoginForm, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitAdvocateLogin(); }}
                    placeholder="123456"
                    className="w-full px-3 py-3 bg-slate-50 border border-slate-300 rounded-lg text-2xl font-mono tracking-[0.6em] text-center focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    data-testid="advocate-login-otp"
                    autoFocus
                  />
                </div>
                <button
                  onClick={submitAdvocateLogin}
                  disabled={advocateLoginLoading || advocateLoginForm.otp.length !== 6}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white font-bold py-2.5 rounded-xl shadow"
                  data-testid="advocate-login-submit"
                >
                  {advocateLoginLoading ? "Verifying…" : "Verify & Sign In"}
                </button>
                <div className="flex justify-between text-[11px]">
                  <button
                    onClick={() => setAdvocateLoginForm({ email:"", otp:"", step:"email" })}
                    className="text-slate-500 hover:text-slate-700 underline"
                    data-testid="advocate-login-change-email"
                  >
                    ← Use a different email
                  </button>
                  <button
                    onClick={requestAdvocateOtp}
                    disabled={advocateLoginLoading}
                    className="text-emerald-600 hover:text-emerald-700 underline disabled:opacity-50"
                    data-testid="advocate-login-resend-otp"
                  >
                    Resend code
                  </button>
                </div>
              </>
            )}

            <p className="text-[10px] text-slate-500 italic text-center pt-1">
              Advocate access is protected by email-OTP + JWT. Sessions expire after 24 hours.
            </p>
          </div>
        </div>
      )}

      {/* ADMIN — ASSIGN ADVOCATES TO A CASE */}
      {assignCase && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="assign-advocates-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900">Assign Advocates</h3>
                <p className="text-xs text-slate-500 font-mono">{assignCase.case_number}</p>
              </div>
              <button onClick={() => setAssignCase(null)} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="assign-close">✕</button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <strong>master@hrcpc.org</strong> and <strong>poornima@hrcpc.org</strong> automatically see this case and cannot be removed.
            </div>

            <div className="space-y-2">
              {advocateUsers.filter(a => !SUPER_ADVOCATE_EMAILS_FRONT.has(a.email)).map(a => {
                const checked = assignSelected.has(a.email);
                return (
                  <label key={a.email} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${checked ? 'bg-emerald-50 border-emerald-300' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`assign-row-${a.email}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setAssignSelected(prev => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(a.email); else next.delete(a.email);
                          return next;
                        });
                      }}
                      className="w-4 h-4 text-emerald-600"
                      data-testid={`assign-cb-${a.email}`}
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-slate-900">{a.name}</div>
                      <div className="font-mono text-xs text-slate-500">{a.email}</div>
                    </div>
                  </label>
                );
              })}
              {/* Show super advocates as locked chips */}
              {advocateUsers.filter(a => SUPER_ADVOCATE_EMAILS_FRONT.has(a.email)).map(a => (
                <div key={a.email} className="flex items-center gap-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50/50" data-testid={`assign-super-${a.email}`}>
                  <input type="checkbox" checked disabled className="w-4 h-4 text-amber-600 opacity-70" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{a.name} <span className="text-amber-700 text-xs">★ Super — always assigned</span></div>
                    <div className="font-mono text-xs text-slate-500">{a.email}</div>
                  </div>
                </div>
              ))}
              {advocateUsers.length === 0 && (
                <div className="text-xs text-slate-500 italic text-center py-3">No advocates found. Check backend seeding.</div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={() => setAssignCase(null)} className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="assign-cancel">Cancel</button>
              <button
                disabled={assignSaving}
                onClick={async () => {
                  setAssignSaving(true);
                  try {
                    const emails = Array.from(assignSelected);
                    const res = await axios.patch(`${API}/admin/cases/${encodeURIComponent(assignCase.case_number)}/advocates`, { emails });
                    setCasesList(prev => prev.map(x => x.case_number === assignCase.case_number ? { ...x, assigned_advocate_emails: res.data.assigned_advocate_emails } : x));
                    toast.success("Assignment updated");
                    setAssignCase(null);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not update assignment");
                  } finally {
                    setAssignSaving(false);
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-xs font-bold px-4 py-2 rounded-lg"
                data-testid="assign-save"
              >
                {assignSaving ? "Saving…" : "Save Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FINANCE MODAL — Fee schedule + Expense tracking (super only) */}
      {financeCase && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8 p-6 shadow-2xl space-y-5" data-testid="finance-modal">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Fee & Expense Management</h3>
                <p className="text-xs text-slate-500 font-mono">{financeCase.case_number} · {financeCase.client_name}</p>
                <p className="text-[10px] text-amber-700 italic mt-0.5">Confidential — visible only to master@ and poornima@hrcpc.org</p>
              </div>
              <button onClick={() => { setFinanceCase(null); setFinanceSummary(null); }} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="finance-close">✕</button>
            </div>

            {/* SUMMARY */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="text-[10px] font-bold text-emerald-700 uppercase">Fee Agreed</div>
                <div className="text-xl font-black text-emerald-900" data-testid="fin-fee-agreed">₹{Number(financeSummary?.fee_agreed || 0).toLocaleString()}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-[10px] font-bold text-blue-700 uppercase">Fee Received</div>
                <div className="text-xl font-black text-blue-900" data-testid="fin-fee-received">₹{Number(financeSummary?.fee_received || 0).toLocaleString()}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                <div className="text-[10px] font-bold text-rose-700 uppercase">Total Expenses</div>
                <div className="text-xl font-black text-rose-900" data-testid="fin-total-expenses">₹{Number(financeSummary?.total_expenses || 0).toLocaleString()}</div>
              </div>
              <div className={`border rounded-lg p-3 ${(financeSummary?.net_position || 0) >= 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-300'}`}>
                <div className="text-[10px] font-bold text-slate-700 uppercase">Net Position</div>
                <div className={`text-xl font-black ${(financeSummary?.net_position || 0) >= 0 ? 'text-amber-900' : 'text-red-800'}`} data-testid="fin-net-position">₹{Number(financeSummary?.net_position || 0).toLocaleString()}</div>
              </div>
            </div>

            {/* AGREED FEE EDIT */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Agreed Total Fee</div>
              <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500">Total Fee (₹)</label>
                  <input type="number" value={feeAgreedForm.total_fee}
                    onChange={(e) => setFeeAgreedForm({ ...feeAgreedForm, total_fee: e.target.value })}
                    className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm font-mono"
                    data-testid="fin-fee-agreed-input" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500">Fee Notes (optional)</label>
                  <input type="text" value={feeAgreedForm.fee_notes}
                    onChange={(e) => setFeeAgreedForm({ ...feeAgreedForm, fee_notes: e.target.value })}
                    className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm"
                    placeholder="e.g. Agreed in first consultation"
                    data-testid="fin-fee-notes-input" />
                </div>
                <button onClick={saveFeeAgreed} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-3 py-2 rounded" data-testid="fin-fee-agreed-save">Save</button>
              </div>
            </div>

            {/* FEE SCHEDULE / INSTALMENTS */}
            <div className="border border-slate-200 rounded-lg">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Payment Schedule / Instalments</div>
              </div>
              <div className="p-4 space-y-3">
                {(financeCase.fee_schedule || []).length === 0 && (
                  <p className="text-xs text-slate-500 italic">No instalments yet.</p>
                )}
                {(financeCase.fee_schedule || []).map(inst => (
                  <div key={inst.id} className={`flex items-center gap-2 p-2 rounded border ${inst.status === 'paid' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`fin-inst-${inst.id}`}>
                    <input type="checkbox" checked={inst.status === 'paid'} onChange={() => toggleInstalmentPaid(inst)} className="w-4 h-4" data-testid={`fin-inst-toggle-${inst.id}`} />
                    <span className="font-mono text-xs w-24">{fmtDate(inst.due_date)}</span>
                    <span className="font-bold text-sm text-slate-900 w-24">₹{Number(inst.amount).toLocaleString()}</span>
                    <span className="text-xs text-slate-600 flex-1 truncate">{inst.notes || <em className="text-slate-400">—</em>}</span>
                    {inst.paid_on && <span className="text-[10px] text-emerald-700 font-semibold">Paid {inst.paid_on}</span>}
                    <button onClick={() => deleteInstalment(inst)} className="text-rose-600 text-[10px] font-bold" data-testid={`fin-inst-delete-${inst.id}`}>✕</button>
                  </div>
                ))}
                <div className="grid grid-cols-1 md:grid-cols-[140px_140px_1fr_auto] gap-2 items-end pt-2 border-t border-slate-100">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Due Date</label>
                    <input type="date" value={instalmentForm.due_date} onChange={(e) => setInstalmentForm({ ...instalmentForm, due_date: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm" data-testid="fin-inst-due" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Amount (₹)</label>
                    <input type="number" value={instalmentForm.amount} onChange={(e) => setInstalmentForm({ ...instalmentForm, amount: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm font-mono" data-testid="fin-inst-amount" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Notes (optional)</label>
                    <input type="text" value={instalmentForm.notes} onChange={(e) => setInstalmentForm({ ...instalmentForm, notes: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm" placeholder="e.g. On first hearing" data-testid="fin-inst-notes" />
                  </div>
                  <button onClick={addInstalment} className="bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded" data-testid="fin-inst-add">+ Add</button>
                </div>
              </div>
            </div>

            {/* EXPENSES */}
            <div className="border border-slate-200 rounded-lg">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Case Expenses</div>
                {financeSummary?.expenses_by_category && Object.keys(financeSummary.expenses_by_category).length > 0 && (
                  <div className="text-[10px] text-slate-500">
                    {Object.entries(financeSummary.expenses_by_category).map(([k, v]) => (
                      <span key={k} className="ml-2">{EXPENSE_CATEGORY_LABELS[k] || k}: ₹{Number(v).toLocaleString()}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 space-y-2">
                {(financeCase.expenses || []).length === 0 && <p className="text-xs text-slate-500 italic">No expenses recorded.</p>}
                <div className="max-h-56 overflow-y-auto space-y-1.5">
                  {(financeCase.expenses || []).slice().reverse().map(exp => (
                    <div key={exp.id} className="flex items-center gap-2 p-2 rounded bg-white border border-slate-200 text-xs" data-testid={`fin-exp-${exp.id}`}>
                      <span className="font-mono text-slate-500 w-20">{fmtDate(exp.date)}</span>
                      <span className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-semibold w-40 truncate">{EXPENSE_CATEGORY_LABELS[exp.category] || exp.category}</span>
                      <span className="flex-1 text-slate-700 truncate" title={exp.description}>{exp.description}</span>
                      <span className="font-bold text-slate-900 font-mono w-20 text-right">₹{Number(exp.amount).toLocaleString()}</span>
                      <button onClick={() => deleteExpense(exp)} className="text-rose-600 text-[10px] font-bold" data-testid={`fin-exp-delete-${exp.id}`}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[130px_180px_1fr_120px_auto] gap-2 items-end pt-3 border-t border-slate-100">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Date</label>
                    <input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm" data-testid="fin-exp-date" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Category</label>
                    <select value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm" data-testid="fin-exp-category">
                      {Object.entries(EXPENSE_CATEGORY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Description</label>
                    <input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm" placeholder="e.g. Filing fees for main plaint" data-testid="fin-exp-desc" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500">Amount (₹)</label>
                    <input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-sm font-mono" data-testid="fin-exp-amount" />
                  </div>
                  <button onClick={addExpense} className="bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded" data-testid="fin-exp-add">+ Add</button>
                </div>
              </div>
            </div>

            {financeLoading && <p className="text-xs text-slate-500 italic text-center">Loading…</p>}
          </div>
        </div>
      )}




      {/* SOFT REGISTER-FIRST GUIDANCE — polite, never blocks */}
      {registerGuideOpen && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5" data-testid="register-guide-modal">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg leading-tight">A gentle recommendation</h3>
                {registerGuideContext && (
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mt-0.5">Before you continue with {registerGuideContext}</div>
                )}
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 text-sm text-slate-800 leading-relaxed" data-testid="register-guide-message">
              For better support and proper follow-up, we recommend you <strong>Register first</strong> through the Family Advisor KYC form.
              <br/><br/>
              <span className="text-slate-600">You can still continue as a guest — HRCPC Trust is a public-spirited organisation and no citizen is ever turned away.</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                onClick={() => {
                  setRegisterGuideOpen(false);
                  setRegisterGuidePending(null);
                  setActiveTab("kyc");
                }}
                className="flex-1 bg-orange-600 hover:bg-orange-500 text-white font-bold px-4 py-2.5 rounded-xl shadow flex items-center justify-center gap-2"
                data-testid="register-guide-register-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                Register Now
              </button>
              <button
                onClick={() => {
                  // remember for this session so we don't nag again
                  try { sessionStorage.setItem("hrcpc_guest_dismissed", "1"); } catch (_) {}
                  setRegisterGuideOpen(false);
                  const next = registerGuidePending;
                  setRegisterGuidePending(null);
                  if (typeof next === "function") { next(); }
                }}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-4 py-2.5 rounded-xl border border-slate-300"
                data-testid="register-guide-guest-btn"
              >
                Continue as Guest
              </button>
            </div>

            <p className="text-[10px] text-slate-500 italic text-center">
              Registering helps our coordinator link your query to a case file and follow up in a structured way.
            </p>
          </div>
        </div>
      )}


      {/* VOLUNTEER DETAIL MODAL (admin — shows documents + undertaking) */}
      {volunteerDetailOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto" data-testid="volunteer-detail-modal">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest">Volunteer Application · {volunteerDetailOpen.code}</div>
                <h3 className="font-bold text-lg text-slate-900">{volunteerDetailOpen.name}</h3>
                <p className="text-xs text-slate-600">+91 {volunteerDetailOpen.mobile} · {volunteerDetailOpen.email} · <span className="font-semibold">{volunteerDetailOpen.status}</span></p>
              </div>
              <button onClick={() => setVolunteerDetailOpen(null)} className="text-slate-400 hover:text-slate-700 text-xl font-bold" data-testid="volunteer-detail-close">✕</button>
            </div>

            {/* Personal + Professional two-col */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1">
                <div className="font-bold text-slate-900 mb-1 text-[13px]">Personal Details</div>
                {[
                  ["Full Name", volunteerDetailOpen.personal?.full_name],
                  ["Father's/Husband's Name", volunteerDetailOpen.personal?.fathers_husbands_name],
                  ["Date of Birth", volunteerDetailOpen.personal?.dob],
                  ["Gender", volunteerDetailOpen.personal?.gender],
                  ["Mobile", `+91 ${volunteerDetailOpen.personal?.mobile}`],
                  ["Email", volunteerDetailOpen.personal?.email],
                  ["Address", volunteerDetailOpen.personal?.address],
                  ["City / State / Pincode", `${volunteerDetailOpen.personal?.city || '—'}, ${volunteerDetailOpen.personal?.state || '—'} — ${volunteerDetailOpen.personal?.pincode || '—'}`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 border-b border-slate-200 py-1">
                    <span className="text-slate-500">{k}:</span>
                    <span className="font-semibold text-slate-800 text-right">{v || '—'}</span>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1">
                <div className="font-bold text-slate-900 mb-1 text-[13px]">Professional Details</div>
                {[
                  ["Qualification", volunteerDetailOpen.professional?.qualification],
                  ["Occupation", volunteerDetailOpen.professional?.occupation],
                  ["Help Area", volunteerDetailOpen.professional?.help_area],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 border-b border-slate-200 py-1">
                    <span className="text-slate-500">{k}:</span>
                    <span className="font-semibold text-slate-800 text-right">{v || '—'}</span>
                  </div>
                ))}
                <div className="pt-3 mt-3 border-t-2 border-red-200">
                  <div className="font-bold text-red-900 mb-1 text-[13px]">Digital Undertaking</div>
                  <div className="text-[11px] text-slate-700 space-y-0.5">
                    <div>Accepted: <span className="font-bold text-emerald-700">{volunteerDetailOpen.undertaking?.accepted ? "YES" : "NO"}</span></div>
                    <div>Signed by: <span className="font-bold italic" style={{ fontFamily: "cursive" }}>{volunteerDetailOpen.undertaking?.signed_name}</span></div>
                    <div>Signed at: <span className="font-mono">{volunteerDetailOpen.undertaking?.signed_at}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents grid */}
            <div className="border-t border-slate-100 pt-4">
              <div className="font-bold text-sm text-slate-900 mb-3">Uploaded Documents</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ["photograph", "Photograph"],
                  ["aadhaar_front", "Aadhaar (Front)"],
                  ["aadhaar_back", "Aadhaar (Back)"],
                  ["pan_card", "PAN Card"],
                  ["bank_proof", "Bank Proof"],
                  ["passport", "Passport (opt.)"],
                ].map(([k, label]) => {
                  const src = volunteerDetailOpen.documents?.[k];
                  return (
                    <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-1" data-testid={`vol-doc-view-${k}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
                        <span>{label}</span>
                        {src ? (
                          <a href={src} download={`${volunteerDetailOpen.code}-${k}.jpg`} className="text-emerald-700 hover:underline">Download</a>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </div>
                      {src ? (
                        <a href={src} target="_blank" rel="noopener noreferrer">
                          <img src={src} alt={label} className="w-full h-32 object-cover rounded border border-slate-300 hover:opacity-90 transition cursor-zoom-in" />
                        </a>
                      ) : (
                        <div className="w-full h-32 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-400 italic">Not uploaded</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Print / PDF actions */}
            <div className="border-t border-slate-100 pt-4 flex flex-wrap gap-2">
              <a
                href={`/volunteer-undertaking/${volunteerDetailOpen.code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold bg-slate-800 hover:bg-black text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                data-testid="admin-print-undertaking"
              >
                <FileText className="w-3.5 h-3.5" /> Signed Undertaking PDF
              </a>
              {volunteerDetailOpen.status === "Approved" && (
                <a
                  href={`/volunteer-id/${volunteerDetailOpen.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-slate-950 px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                  data-testid="admin-print-id-card"
                >
                  <Shield className="w-3.5 h-3.5" /> Volunteer ID Card PDF
                </a>
              )}
            </div>

            {/* Status Actions */}
            <div className="border-t border-slate-100 pt-4 flex flex-wrap gap-2 justify-end">
              {["New", "Under Review", "Approved", "Rejected"].map(st => (
                <button
                  key={st}
                  onClick={async () => {
                    try {
                      await axios.patch(`${API}/volunteers/${volunteerDetailOpen.code}`, { status: st });
                      await fetchData();
                      setVolunteerDetailOpen(prev => ({ ...prev, status: st }));
                      toast.success(`Marked ${st}`);
                    } catch (err) { toast.error("Update failed"); }
                  }}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${volunteerDetailOpen.status === st
                    ? (st === "Approved" ? "bg-emerald-600 text-white border-emerald-700" : st === "Rejected" ? "bg-red-600 text-white border-red-700" : st === "Under Review" ? "bg-amber-500 text-white border-amber-600" : "bg-slate-700 text-white border-slate-800")
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  data-testid={`vol-detail-status-${st.toLowerCase().replace(/\s+/g,'-')}`}
                >
                  Mark {st}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SUPPORT TICKET RESPOND MODAL (admin) */}
      {ticketRespondModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto" data-testid="ticket-respond-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900">Respond to {ticketRespondModal.ticket_code}</h3>
              <button onClick={() => setTicketRespondModal(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="text-xs text-slate-700 space-y-1">
              <div><strong>From:</strong> {ticketRespondModal.name} • +91 {ticketRespondModal.mobile}{ticketRespondModal.email ? ` • ${ticketRespondModal.email}` : ''}</div>
              <div><strong>Subject:</strong> {ticketRespondModal.subject}</div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-slate-800 whitespace-pre-line max-h-40 overflow-y-auto">{ticketRespondModal.message}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Status</label>
              <select
                value={ticketRespondModal._draftStatus}
                onChange={(e) => setTicketRespondModal({ ...ticketRespondModal, _draftStatus: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="ticket-respond-status"
              >
                <option>Open</option>
                <option>In Progress</option>
                <option>Resolved</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Response (will be emailed to citizen from support@hrcpc.org)</label>
              <textarea
                rows="5"
                value={ticketRespondModal._draftResponse}
                onChange={(e) => setTicketRespondModal({ ...ticketRespondModal, _draftResponse: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="ticket-respond-textarea"
              />
            </div>
            <button
              onClick={async () => {
                try {
                  await axios.patch(`${API}/support/tickets/${ticketRespondModal.ticket_code}`, {
                    status: ticketRespondModal._draftStatus,
                    response: ticketRespondModal._draftResponse || null,
                    assigned_to: "HRCPC Legal Team"
                  });
                  await fetchData();
                  toast.success("Ticket updated.");
                  setTicketRespondModal(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Update failed.");
                }
              }}
              className="w-full bg-emerald-700 hover:bg-emerald-600 text-white font-bold py-2.5 rounded-xl shadow text-sm"
              data-testid="ticket-respond-save"
            >
              Save &amp; Notify
            </button>
          </div>
        </div>
      )}

      {/* SUPPORT TICKET MODAL — "Ask HRCPC Legal Assistant" (human review) */}
      {supportTicketModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto" data-testid="support-ticket-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-700" />
                <h3 className="font-bold text-slate-900">Ask HRCPC Legal Assistant (Human Review)</h3>
              </div>
              <button
                onClick={() => { setSupportTicketModalOpen(false); setSupportTicketSubmitted(null); }}
                className="text-slate-400 hover:text-slate-600"
                data-testid="support-ticket-close"
              >✕</button>
            </div>

            {!supportTicketSubmitted ? (
              <>
                <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-3 text-[12px] text-emerald-900 leading-relaxed" data-testid="support-ticket-note">
                  <strong className="block font-bold">Your query will be reviewed by the HRCPC Legal Team.</strong>
                  We will respond via email / phone within 1–2 working days at <strong>support@hrcpc.org</strong>.
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={supportTicketForm.name}
                      onChange={(e) => setSupportTicketForm({ ...supportTicketForm, name: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                      data-testid="support-ticket-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Mobile (10-digit) *</label>
                    <input
                      type="text"
                      maxLength="10"
                      value={supportTicketForm.mobile}
                      onChange={(e) => setSupportTicketForm({ ...supportTicketForm, mobile: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                      data-testid="support-ticket-mobile"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Email (optional, for reply)</label>
                  <input
                    type="email"
                    value={supportTicketForm.email}
                    onChange={(e) => setSupportTicketForm({ ...supportTicketForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="support-ticket-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Category</label>
                  <select
                    value={supportTicketForm.category}
                    onChange={(e) => setSupportTicketForm({ ...supportTicketForm, category: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="support-ticket-category"
                  >
                    <option>General Legal Query</option>
                    <option>Consumer Protection</option>
                    <option>Human Rights Violation</option>
                    <option>RTI / Government Transparency</option>
                    <option>Family / Maintenance</option>
                    <option>Property / Tenancy</option>
                    <option>Environmental / NGT</option>
                    <option>Medico-Legal / Medical Negligence</option>
                    <option>Cheque Bounce / Financial</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={supportTicketForm.subject}
                    onChange={(e) => setSupportTicketForm({ ...supportTicketForm, subject: e.target.value })}
                    placeholder="Short one-line summary of your issue"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="support-ticket-subject"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Describe your issue *</label>
                  <textarea
                    rows="5"
                    value={supportTicketForm.message}
                    onChange={(e) => setSupportTicketForm({ ...supportTicketForm, message: e.target.value })}
                    placeholder="Please describe your legal issue with as much detail as possible. Our advocates will review and respond personally."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                    data-testid="support-ticket-message"
                  />
                </div>

                <button
                  onClick={submitSupportTicket}
                  disabled={supportTicketSaving}
                  className="w-full bg-emerald-700 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-bold py-3 rounded-xl shadow mt-1"
                  data-testid="support-ticket-submit"
                >
                  {supportTicketSaving ? "Submitting…" : "Submit Query to HRCPC Legal Team"}
                </button>
              </>
            ) : (
              <div className="space-y-4 py-2" data-testid="support-ticket-success">
                <div className="text-center space-y-3">
                  <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
                  <h4 className="text-lg font-bold text-slate-900">Query submitted successfully</h4>
                  <div className="inline-block bg-emerald-100 text-emerald-900 px-3 py-1 rounded-full text-xs font-mono font-bold" data-testid="support-ticket-code">
                    {supportTicketSubmitted.ticket?.ticket_code}
                  </div>
                </div>
                <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-3 text-[12px] text-emerald-900 leading-relaxed">
                  <strong>Your query will be reviewed by the HRCPC Legal Team.</strong>
                  <br />We will respond via email / phone within 1–2 working days at
                  <strong> {supportTicketSubmitted.support_email || "support@hrcpc.org"}</strong>.
                </div>
                <button
                  onClick={() => { setSupportTicketModalOpen(false); setSupportTicketSubmitted(null); }}
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 rounded-xl text-sm"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KYC APPROVE/REJECT REMARKS MODAL */}
      {kycActionModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4" data-testid="kyc-action-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-lg text-slate-900">
                {kycActionModal.action === 'approve' ? 'Approve KYC Application' : 'Reject KYC Application'}
              </h3>
              <button onClick={() => setKycActionModal(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="text-sm text-slate-700 space-y-1">
              <p><strong>Applicant:</strong> {kycActionModal.client.full_name}</p>
              <p><strong>Mobile:</strong> +91 {kycActionModal.client.mobile}</p>
              <p><strong>Aadhaar:</strong> {kycActionModal.client.aadhaar_last4 ? `XX${kycActionModal.client.aadhaar_last4}` : 'Not provided'}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Admin Remarks {kycActionModal.action === 'reject' ? '(mandatory)' : '(optional)'}
              </label>
              <textarea
                rows="3"
                value={kycActionModal.remarks}
                onChange={(e) => setKycActionModal({...kycActionModal, remarks: e.target.value})}
                placeholder={kycActionModal.action === 'approve'
                  ? 'e.g. Aadhaar & purpose verified. Assigning Adv. K. Srinivasa Rao.'
                  : 'e.g. Mobile number does not match Aadhaar records. Please re-submit with correct KYC.'}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-sm"
                data-testid="kyc-action-remarks"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setKycActionModal(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-300 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (kycActionModal.action === 'reject' && !kycActionModal.remarks.trim()) {
                    toast.error("Please provide a reason for rejection.");
                    return;
                  }
                  try {
                    const url = kycActionModal.action === 'approve'
                      ? `${API}/clients/${kycActionModal.client.mobile}/approve`
                      : `${API}/clients/${kycActionModal.client.mobile}/reject`;
                    await axios.patch(url, { remarks: kycActionModal.remarks, reviewed_by: "Master Admin" });
                    await fetchData();
                    toast.success(
                      kycActionModal.action === 'approve'
                        ? `KYC approved for ${kycActionModal.client.full_name} & case file generated!`
                        : `KYC rejected for ${kycActionModal.client.full_name}.`
                    );
                    setKycActionModal(null);
                  } catch (err) {
                    console.error(err);
                    toast.error("Action failed. Please retry.");
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow ${
                  kycActionModal.action === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-500'
                    : 'bg-rose-600 hover:bg-rose-500'
                }`}
                data-testid="kyc-action-confirm"
              >
                {kycActionModal.action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADMIN ADD CLIENT MODAL */}
      {showAddClientModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="add-client-modal">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-lg text-slate-900">Manually Onboard New Member (Approved)</h3>
              <button onClick={() => setShowAddClientModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={addClientForm.full_name}
                    onChange={(e) => setAddClientForm({...addClientForm, full_name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                    data-testid="add-client-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Mobile (10-digit) *</label>
                  <input
                    type="text"
                    maxLength="10"
                    value={addClientForm.mobile}
                    onChange={(e) => setAddClientForm({...addClientForm, mobile: e.target.value.replace(/\D/g, '')})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                    data-testid="add-client-mobile"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={addClientForm.email}
                    onChange={(e) => setAddClientForm({...addClientForm, email: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Aadhaar Last 4 *</label>
                  <input
                    type="text"
                    maxLength="4"
                    value={addClientForm.aadhaar_last4}
                    onChange={(e) => setAddClientForm({...addClientForm, aadhaar_last4: e.target.value.replace(/\D/g, '')})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                    data-testid="add-client-aadhaar"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Address</label>
                <input
                  type="text"
                  value={addClientForm.address}
                  onChange={(e) => setAddClientForm({...addClientForm, address: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">City</label>
                  <input type="text" value={addClientForm.city}
                    onChange={(e) => setAddClientForm({...addClientForm, city: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">State</label>
                  <input type="text" value={addClientForm.state}
                    onChange={(e) => setAddClientForm({...addClientForm, state: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Pincode</label>
                  <input type="text" value={addClientForm.pincode}
                    onChange={(e) => setAddClientForm({...addClientForm, pincode: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Legal Purpose</label>
                <input
                  type="text"
                  value={addClientForm.purpose}
                  onChange={(e) => setAddClientForm({...addClientForm, purpose: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg"
                  placeholder="e.g. Consumer dispute against builder"
                />
              </div>
              <button
                onClick={async () => {
                  if (!addClientForm.full_name || !addClientForm.mobile || !addClientForm.aadhaar_last4) {
                    toast.error("Full name, mobile and Aadhaar last-4 are required.");
                    return;
                  }
                  if (addClientForm.mobile.length !== 10) {
                    toast.error("Mobile must be exactly 10 digits.");
                    return;
                  }
                  try {
                    await axios.post(`${API}/clients`, { ...addClientForm, status: "Approved" });
                    await fetchData();
                    toast.success(`Member ${addClientForm.full_name} onboarded — Unique Code generated.`);
                    setShowAddClientModal(false);
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.response?.data?.detail || "Member creation failed.");
                  }
                }}
                className="w-full bg-orange-700 hover:bg-orange-600 text-white font-bold py-3 rounded-xl shadow mt-2"
                data-testid="save-client-btn"
              >
                Onboard Member & Generate Unique Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-black text-slate-400 py-10 border-t border-slate-800 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-3">
            <div className="flex items-center space-x-2 text-white font-bold text-sm">
              <Scale className="w-5 h-5 text-amber-400" />
              <span>HRCPC Trust</span>
            </div>
            <p className="leading-relaxed">Registered Human Rights & Consumer Protection Cell Trust, Hyderabad, Telangana. A public-spirited, non-profit organisation focused on awareness, advocacy and citizen support across consumer rights, human rights, RTI, environmental protection and medico-legal matters. <strong className="text-orange-300">Not a law firm</strong> — does not provide professional legal services or legal representation.</p>
          </div>
          <div className="space-y-2">
            <div className="font-bold text-white uppercase tracking-wider text-[11px]">Quick Access</div>
            <div className="space-y-1.5 flex flex-col">
              <button onClick={() => setActiveTab("public")} className="text-left hover:text-white">Home & Overview</button>
              <button onClick={() => setActiveTab("client-portal")} className="text-left hover:text-white">Member Portal (OTP Login)</button>
              <button onClick={() => setActiveTab("ai-hub")} className="text-left hover:text-white">AI Legal Assistant</button>
              <button onClick={() => setActiveTab("templates")} className="text-left hover:text-white">Templates &amp; Study Material</button>
              <button onClick={() => setActiveTab("volunteer-register")} className="text-left hover:text-white" data-testid="footer-volunteer-link">Volunteer with HRCPC</button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="font-bold text-white uppercase tracking-wider text-[11px]">Trust Office</div>
            <address className="not-italic leading-relaxed text-slate-300" data-testid="footer-trust-office">
              {siteSettings.contact_info?.address_line1}<br/>
              {siteSettings.contact_info?.address_line2}<br/>
              <span className="block mt-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mr-1">Primary:</span>
                <a href={`tel:${(siteSettings.contact_info?.phone_primary || '').replace(/\s+/g, '')}`} className="font-semibold text-white hover:text-orange-300" data-testid="footer-phone-landline">{siteSettings.contact_info?.phone_primary}</a>
                <span className="text-[10px] text-slate-500 ml-1">({siteSettings.contact_info?.phone_primary_label})</span>
              </span>
              <span className="block">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mr-1">{siteSettings.contact_info?.phone_secondary_label}:</span>
                <a href={`tel:${(siteSettings.contact_info?.phone_secondary || '').replace(/\s+/g, '')}`} className="font-semibold text-white hover:text-orange-300" data-testid="footer-phone">{siteSettings.contact_info?.phone_secondary}</a>
              </span>
              <span className="block">
                Email: <a href={`mailto:${siteSettings.contact_info?.email}`} className="font-semibold text-white hover:text-orange-300" data-testid="footer-email">{siteSettings.contact_info?.email}</a>
              </span>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {siteSettings.contact_info?.map_url && (
                  <a
                    href={siteSettings.contact_info.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"
                    data-testid="footer-map-link"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    View on Google Maps
                  </a>
                )}
                <button
                  onClick={() => { setCallbackForm({ name: "", mobile: "", note: "" }); setCallbackModalOpen(true); }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-300 hover:text-orange-200"
                  data-testid="footer-callback-btn"
                >
                  <PhoneCall className="w-3.5 h-3.5" />
                  Request a call-back
                </button>
              </div>
            </address>

            {/* Social media */}
            <div className="pt-3">
              <div className="font-bold text-white uppercase tracking-wider text-[11px] mb-2">Follow HRCPC Trust</div>
              <div className="flex items-center gap-3" data-testid="footer-social-links">
                <a
                  href="https://www.facebook.com/hrcpctrust"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-orange-600 text-slate-200 hover:text-white flex items-center justify-center transition"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@hrcpctrust"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="YouTube"
                  data-testid="social-youtube"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-rose-600 text-slate-200 hover:text-white flex items-center justify-center transition"
                >
                  <Youtube className="w-4 h-4" />
                </a>
                <a
                  href="https://www.instagram.com/hrcpctrust"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-fuchsia-600 text-slate-200 hover:text-white flex items-center justify-center transition"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://twitter.com/hrcpctrust"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Twitter / X"
                  data-testid="social-twitter"
                  className="w-9 h-9 rounded-full bg-slate-800 hover:bg-black text-slate-200 hover:text-white flex items-center justify-center transition"
                >
                  <Twitter className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="pt-2 text-[10px] text-slate-500">© 2026 HRCPC Trust. All rights reserved. Built for production deployment.</div>
          </div>
        </div>

        {/* Site-wide "under development" notice */}
        <div className="mt-8 border-t border-amber-500/30 bg-amber-500/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-center text-[11px] sm:text-xs text-amber-200 font-semibold flex flex-col sm:flex-row items-center justify-center gap-2" data-testid="under-development-banner">
            <span className="inline-flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-amber-300" />
              <span className="uppercase tracking-wider">Notice</span>
            </span>
            <span className="text-amber-100 font-normal">
              This website is currently <strong className="text-amber-200">under development</strong>. Some features may change without notice. For urgent assistance, please email <a href="mailto:support@hrcpc.org" className="underline hover:text-white">support@hrcpc.org</a> or call our helpline.
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
}
